import React, { useState, useEffect, createContext, useContext, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import jsPDF from "jspdf";
import "./App.css";

// Configure axios to send credentials (cookies) with every request
// This is CRITICAL for session-based authentication to work
axios.defaults.withCredentials = true;

// App version for cache busting
const APP_VERSION = "2.0.2"; // Update this when making significant changes

// Import shadcn components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";
// Using native HTML select for better stability
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import { ChevronLeft, Loader2, BookOpen, Upload, FileText, X, User, MessageSquare, Printer, List, Plus } from "lucide-react";
import AdminDashboard from "./AdminDashboard";
import ReleaseFooter from "./components/ReleaseFooter";
import EulaAcceptanceModal from "./components/EulaAcceptanceModal";
import APICostsPage from "./APICostsPage";
import RewardsPage from "./RewardsPage";
import HelpModal from "./components/HelpModal";
import HelpIconTrigger from "./components/HelpIconTrigger";
import BVADMPage from "./BVADMPage";
import PDEPage from "./PDEPage";
import ERMTPage from "./ERMTPage";
import PMPBPage from "./PMPBPage";
import IBGPage from "./IBGPage";
import QRGPage from "./QRGPage";
import AKSWPage from "./AKSWPage";
import MMKGPage from "./MMKGPage";
import SMMKPage from "./SMMKPage";
import CCATPage from "./CCATPage";
import BRECPage from "./BRECPage";
import BOCPPage from "./BOCPPage";
import MMGPage from "./MMGPage";
import ABGPage from "./ABGPage";
import KSGPage from "./KSGPage";
import PrivacyPolicyPage from "./PrivacyPolicyPage";
import DeleteConfirmationModal from "./components/DeleteConfirmationModal";
import TermsOfServicePage from "./TermsOfServicePage";
import HelpCenterPage from "./HelpCenterPage";
import PaymentSuccessPage from "./PaymentSuccessPage";
import PaymentCancelledPage from "./PaymentCancelledPage";
import PaymentMethodSetup from "./components/PaymentMethodSetup";
import ProfileUpgradeModal from "./components/ProfileUpgradeModal";
import PaymentValidationModal from "./components/PaymentValidationModal";
import ServiceChargeModal from "./components/ServiceChargeModal";
import SubscriptionModal from "./components/SubscriptionModal";
import ProcessingModal from "./components/ProcessingModal";
import EmailReceiptModal from "./components/EmailReceiptModal";
import ReferralModal from "./components/ReferralModal";
import ReadAloudButton from "./components/ReadAloudButton";
import PaymentFlow from "./services/paymentFlow";
import OnboardingSurvey from "./OnboardingSurvey";
import DiAiOnboarding from "./DiAiOnboarding";
import { TimezoneProvider, useTimezone } from './contexts/TimezoneContext';
import { isPaymentExempt, hasActiveSubscription, hasPaymentMethod, needsPaymentValidation } from './utils/userUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timestamp Display Component using Timezone Context
const TimestampDisplay = ({ timestamp, includeTime = true }) => {
  const { formatTimestamp } = useTimezone();
  if (!timestamp) return <span>N/A</span>;
  return <span>{formatTimestamp(timestamp, { includeTime })}</span>;
};

// Skip Navigation Component for Accessibility
const SkipNav = () => {
  return (
    <a href="#main-content" className="skip-to-main">
      Skip to main content
    </a>
  );
};

// Amazon KDP Genre options (alphabetically sorted)
const GENRE_OPTIONS = [
  // Fiction Genres
  "Action & Adventure",
  "African American Fiction", 
  "Anthologies",
  "Classics",
  "Coming of Age",
  "Comics & Graphic Novels",
  "Contemporary Fiction",
  "Crime Fiction",
  "Drama & Plays",
  "Erotica",
  "Fairy Tales, Folk Tales & Myths",
  "Fantasy",
  "Historical Fiction",
  "Horror",
  "Humor & Satire",
  "Literary Fiction",
  "Mystery, Thriller & Suspense",
  "Romance",
  "Science Fiction",
  "Short Stories",
  "Teen & Young Adult Fiction",
  "Westerns",
  "Women's Fiction",
  
  // Nonfiction Genres
  "Arts & Photography",
  "Biographies & Memoirs",
  "Business & Money",
  "Children's Nonfiction",
  "Comics & Graphic Novels (Nonfiction)",
  "Computers & Technology",
  "Cookbooks, Food & Wine",
  "Crafts, Hobbies & Home",
  "Education & Teaching",
  "Engineering & Transportation",
  "Health, Fitness & Dieting",
  "History",
  "Humor & Entertainment",
  "Law",
  "LGBTQ+ Books",
  "Literature & Fiction (Guides)",
  "Medical Books",
  "Parenting & Relationships",
  "Politics & Social Sciences",
  "Reference",
  "Religion & Spirituality",
  "Science & Math",
  "Self-Help",
  "Sports & Outdoors",
  "Teen & Young Adult Nonfiction",
  "Travel"
];

// Amazon KDP Theme options (alphabetically sorted by category)
const THEME_OPTIONS = [
  // Emotional/Relational Themes
  "Betrayal",
  "Family Bonds", 
  "Forgiveness",
  "Friendship",
  "Identity",
  "Loss/Grief",
  "Love",
  "Redemption",
  
  // Adventure/Conflict Themes  
  "Escape/Freedom",
  "Hero's Journey",
  "Mystery/Secrets", 
  "Power Struggles",
  "Quest/Exploration",
  "Revenge",
  "Survival",
  "War/Conflict",
  
  // Societal/Moral Themes
  "Corruption",
  "Environmentalism", 
  "Equality/Diversity",
  "Faith/Spirituality",
  "Good vs Evil",
  "Justice/Injustice",
  "Social Change",
  "Technology's Impact",
  
  // Personal Growth Themes
  "Ambition/Success",
  "Coming of Age",
  "Failure/Learning", 
  "Happiness/Well-Being",
  "Mental Health",
  "Motivation",
  "Resilience", 
  "Self-Discovery",
  "Transformation",
  
  // Fantasy/Sci-Fi Themes
  "Alien Encounters",
  "Alternate Realities", 
  "Dystopia/Utopia",
  "Magic/Supernatural",
  "Mythical Creatures",
  "Superpowers",
  "Time Travel",
  
  // Historical/Realism Themes
  "Cultural Heritage",
  "Historical Events",
  "Immigration", 
  "Legacy/Preservation",
  "Poverty/Wealth",
  "Tradition vs Modernity",
  
  // Horror/Thriller Themes
  "Conspiracy",
  "Fear/Terror",
  "Paranormal", 
  "Psychological Trauma",
  "Pursuit/Escape",
  "Survival Horror",
  "Suspense",
  
  // Romance Themes
  "Enemies to Lovers",
  "Fake Relationships",
  "Forbidden Love", 
  "Second Chances",
  "Soulmates",
  "Workplace Romance"
];

// Auth Context
const AuthContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Version tracking
  useEffect(() => {
    const storedVersion = localStorage.getItem('appVersion');
    if (storedVersion !== APP_VERSION) {
      localStorage.setItem('appVersion', APP_VERSION);
    }
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      // Check authentication
      await checkAuth();
    };
    
    initializeApp();
  }, []);

  const checkAuth = async () => {
    try {
      // Restore token from localStorage if exists
      const savedToken = localStorage.getItem('auth_token');
      if (savedToken) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      }
      
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Clear invalid token
      localStorage.removeItem('auth_token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const userData = response.data;
    
    // Store token for Authorization header (K8s compatibility)
    if (userData.token) {
      localStorage.setItem('auth_token', userData.token);
      // Set default Authorization header for all future axios requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
    }
    
    setUser(userData);
    return userData;
  };

  // Global function to update user data (available throughout the app)
  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
  };

  // Make updateUser available globally
  useEffect(() => {
    window.updateAuthUser = updateUser;
    return () => {
      delete window.updateAuthUser;
    };
  }, []);

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      // Capture session data before logging out
      const sessionData = {
        lastLoginTime: new Date().toISOString(),
        userId: user?.id,
        userEmail: user?.email,
        userName: user?.first_name,
        // We'll fetch and store recent activity
        timestamp: Date.now()
      };
      
      // Try to get recent activity before logout
      try {
        const recentActivity = await axios.get(`${API}/user/recent-activity`, { withCredentials: true });
        sessionData.recentActivity = recentActivity.data;
      } catch (error) {
      }
      
      // Store in localStorage for next login
      localStorage.setItem('lastSession', JSON.stringify(sessionData));
      
      // Call logout endpoint
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
    } finally {
      setUser(null);
      localStorage.removeItem('auth_token');  // Clear authentication token
      delete axios.defaults.headers.common['Authorization'];  // Remove Authorization header
      // Redirect to landing page after logout
      window.location.href = '/';
    }
  };

  const handleGoogleAuth = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google`, { session_id: sessionId }, { withCredentials: true });
    setUser(response.data);
    // Return user data including needs_onboarding flag
    return response.data;
  };
  
  const completeGoogleOnboarding = async (onboardingData) => {
    const response = await axios.post(`${API}/auth/google-complete-onboarding`, onboardingData, { withCredentials: true });
    setUser(response.data);
    return response.data;
  };

  const value = {
    user,
    setUser,
    login,
    register,
    logout,
    handleGoogleAuth,
    completeGoogleOnboarding,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
};

const AdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  
  if (user.role !== "admin" && !user.is_admin_designated) {
    return <Navigate to="/homepage" />;
  }

  return children;
};

// Navigation Component
// Landing Page Navigation Component
const LandingNavigation = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200" aria-label="Main navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate('/')} aria-label="Digital Ink AI home">
            <img 
              src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
              alt="Digital Ink AI logo" 
              className="h-12 w-auto hover:opacity-80 transition-opacity"
            />
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="/features" className="text-slate-700 hover:text-blue-600 transition-colors font-medium" aria-label="View features">
              Features
            </a>
            <a href="/about" className="text-slate-700 hover:text-blue-600 transition-colors font-medium" aria-label="About us">
              About
            </a>
            <a href="/prices" className="text-slate-700 hover:text-blue-600 transition-colors font-medium" aria-label="View pricing">
              Pricing
            </a>
            <a href="/faq" className="text-slate-700 hover:text-blue-600 transition-colors font-medium" aria-label="Frequently asked questions">
              FAQ
            </a>
            <a href="/contact" className="text-slate-700 hover:text-blue-600 transition-colors font-medium" aria-label="Contact us">
              Contact
            </a>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-blue-600 hover:bg-blue-700 text-white min-h-[44px] px-4 py-2 btn-mobile-primary"
              aria-label="Sign in to your account"
            >
              Sign In
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
                      className="w-full sm:w-auto"
              onClick={toggleMobileMenu}
              className="text-slate-700"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle mobile menu"
              aria-controls="mobile-menu"
            >
              {isMobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div id="mobile-menu" className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <a 
                href="/features" 
                className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                onClick={closeMobileMenu}
                aria-label="View features"
              >
                Features
              </a>
              <a 
                href="/about" 
                className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                onClick={closeMobileMenu}
                aria-label="About us"
              >
                About
              </a>
              <a 
                href="/prices" 
                className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                onClick={closeMobileMenu}
                aria-label="View pricing"
              >
                Pricing
              </a>
              <a 
                href="/faq" 
                className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                onClick={closeMobileMenu}
                aria-label="Frequently asked questions"
              >
                FAQ
              </a>
              <a 
                href="/contact" 
                className="block px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                onClick={closeMobileMenu}
                aria-label="Contact us"
              >
                Contact
              </a>
              <div className="border-t border-slate-200 mt-3 pt-3">
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white min-h-[44px] py-3 btn-mobile-primary"
                  aria-label="Sign in to your account"
                >
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const Navigation = () => {
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const userMenuRef = React.useRef(null);

  const handleLogout = async () => {
    await logout();
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  // Close user menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogoClick = () => {
    if (user) {
      navigate('/homepage');
    } else {
      navigate('/');
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-2 cursor-pointer" onClick={handleLogoClick}>
            <img 
              src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
              alt="Digital Ink AI" 
              className="h-14 w-auto hover:opacity-80 transition-opacity"
            />
          </div>

          {/* Desktop Navigation Links */}
          {user && (
            <>
              <div className="hidden md:flex items-center space-x-6">
                <button onClick={() => navigate('/homepage')} className="text-slate-700 hover:text-blue-600 transition-colors font-medium bg-transparent border-0 cursor-pointer">
                  Home
                </button>
                {/* Dashboard (formerly Activity) - Hide for admins */}
                {!(user.role === "admin" || user.is_admin_designated) && (
                  <button onClick={() => navigate('/user_dashboard')} className="text-slate-700 hover:text-blue-600 transition-colors font-medium bg-transparent border-0 cursor-pointer">
                    Dashboard
                  </button>
                )}
                {/* Analytics Link - Only show for admins (moved before profile) */}
                {(user.role === "admin" || user.is_admin_designated) && (
                  <button onClick={() => navigate('/admin-mgmt-7f84b')} className="text-blue-600 hover:text-blue-700 transition-colors font-medium border border-blue-200 px-3 py-1 rounded bg-transparent cursor-pointer">
                    Analytics
                  </button>
                )}
                
                {/* API Costs Link - Only show for admins */}
                {(user.role === "admin" || user.is_admin_designated) && (
                  <button 
                    onClick={() => navigate('/api-costs')}
                    className="text-amber-700 hover:text-amber-800 transition-colors font-medium border border-amber-200 px-3 py-1 rounded bg-transparent cursor-pointer"
                  >
                    API Costs
                  </button>
                )}
                
                {/* Transactions - Hide for admins */}
                {!(user.role === "admin" || user.is_admin_designated) && (
                  <button 
                    onClick={() => navigate('/transactions')}
                    className="text-slate-700 hover:text-blue-600 transition-colors font-medium bg-transparent border-0 cursor-pointer"
                  >
                    Transactions
                  </button>
                )}
                
                {/* User Avatar Dropdown */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-slate-700 hover:text-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
                    aria-label="User menu"
                    aria-expanded={isUserMenuOpen}
                  >
                    <Avatar className="h-9 w-9 cursor-pointer border-2 border-slate-200 hover:border-blue-400 transition-colors">
                      <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                        {getInitials(user.first_name, user.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <svg 
                      className={`h-4 w-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-slate-200">
                        <p className="text-sm font-semibold text-slate-900">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{user.email}</p>
                        <p className="text-xs text-blue-600 font-medium mt-1 capitalize">
                          {user.role === 'a_la_carte' ? 'A la Carte' : user.role.replace('_', ' ')}
                        </p>
                      </div>

                      {/* Menu Items */}
                      <div className="py-1">
                        <button
                          onClick={() => {
                            navigate('/profile');
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-600 transition-colors flex items-center space-x-2"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>{(user.role === "admin" || user.is_admin_designated) ? "Admin Profile" : "User Profile"}</span>
                        </button>

                        {!(user.role === "admin" || user.is_admin_designated) && (
                          <button
                            onClick={() => {
                              navigate('/help-center');
                              setIsUserMenuOpen(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-blue-600 transition-colors flex items-center space-x-2"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>Help Center</span>
                          </button>
                        )}
                      </div>

                      {/* Sign Out */}
                      <div className="border-t border-slate-200 py-1">
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsUserMenuOpen(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile Menu Button */}
              <div className="md:hidden flex items-center space-x-3">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                    {getInitials(user.first_name, user.last_name)}
                  </AvatarFallback>
                </Avatar>
                <Button
                  variant="ghost"
                  size="sm"
                      className="w-full sm:w-auto"
                  onClick={toggleMobileMenu}
                  className="text-slate-700"
                >
                  {isMobileMenuOpen ? (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        {user && isMobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white/95 backdrop-blur-md">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button 
                onClick={() => {
                  navigate('/homepage');
                  closeMobileMenu();
                }}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
              >
                Home
              </button>
              {/* Dashboard (formerly Activity) - Hide for admins */}
              {!(user.role === "admin" || user.is_admin_designated) && (
                <button 
                  onClick={() => {
                    navigate('/user_dashboard');
                    closeMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                >
                  Dashboard
                </button>
              )}
              {/* Analytics Link - Only show for admins (moved before profile) */}
              {(user.role === "admin" || user.is_admin_designated) && (
                <button 
                  onClick={() => {
                    navigate('/admin-mgmt-7f84b');
                    closeMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors border border-blue-200 mx-3 mt-2"
                >
                  Analytics
                </button>
              )}
              
              {/* API Costs Link - Only show for admins */}
              {(user.role === "admin" || user.is_admin_designated) && (
                <button 
                  onClick={() => {
                    navigate('/api-costs');
                    closeMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-50 transition-colors border border-amber-200 mx-3 mt-2"
                >
                  API Costs
                </button>
              )}
              
              {/* Transactions - Hide for admins */}
              {!(user.role === "admin" || user.is_admin_designated) && (
                <button 
                  onClick={() => {
                    navigate('/transactions');
                    closeMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors"
                >
                  Transactions
                </button>
              )}
              
              {/* User Section */}
              <div className="border-t border-slate-200 mt-3 pt-3">
                <div className="px-3 py-2 mb-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Account</p>
                </div>
                
                <button 
                  onClick={() => {
                    navigate('/profile');
                    closeMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors flex items-center space-x-2"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{(user.role === "admin" || user.is_admin_designated) ? "Admin Profile" : "User Profile"}</span>
                </button>
                
                {/* Help - Only show for non-admin users */}
                {!(user.role === "admin" || user.is_admin_designated) && (
                  <button 
                    onClick={() => {
                      navigate('/help-center');
                      closeMobileMenu();
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-slate-700 hover:text-blue-600 hover:bg-slate-50 transition-colors flex items-center space-x-2"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Help Center</span>
                  </button>
                )}
                
                <button 
                  onClick={() => {
                    handleLogout();
                    closeMobileMenu();
                  }}
                  className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-2 mt-2"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Landing Page Component
const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/homepage" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-black">
      <SkipNav />
      
      {/* Landing Page Navigation */}
      <LandingNavigation />

      {/* Hero Section with Typewriter Background */}
      <header id="main-content" className="relative overflow-hidden min-h-[85vh] flex items-center" role="banner">
        {/* Background Image */}
        <div className="absolute inset-0" aria-hidden="true">
          <img 
            src="https://images.unsplash.com/photo-1505682634904-d7c8d95cdc50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHx0eXBld3JpdGVyfGVufDB8fHx8MTc1OTI3MTk3OHww&ixlib=rb-4.1.0&q=85" 
            alt=""
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/50 to-black/60"></div>
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 relative z-10 py-8 sm:py-12 md:py-16 lg:py-20">
          {/* Centered Logo - Smaller on Mobile */}
          <div className="text-center mb-4 sm:mb-6 md:mb-8 lg:mb-12 animate-fade-in">
            <img 
              src="/Digital_Ink_AI_Logo.png" 
              alt="Digital Ink AI - AI-Powered Publishing Platform" 
              className="h-16 sm:h-20 md:h-24 lg:h-32 w-auto mx-auto mb-3 sm:mb-4 md:mb-6 lg:mb-8 drop-shadow-2xl"
            />
          </div>

          {/* Hero Content - Consistent Font Sizing */}
          <div className="text-center max-w-4xl mx-auto px-4">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 mb-6 leading-tight animate-fade-in-up px-2">
              Write with purpose.<br /><br />Build marketing into the DNA of your book.<br /><br />Launch smarter with Digital Ink AI.
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-4 leading-relaxed animate-fade-in-up-delay px-2">
              <span className="text-yellow-400 font-bold">Over 8,000 books published daily on Amazon.</span><br />We don't write your book for you—we provide the strategic insights and market intelligence to help your original work stand out in the noise.
            </p>

            <p className="text-sm sm:text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up-delay px-2">
              Digital Ink AI empowers <span className="text-yellow-400 font-semibold">human authors with AI-powered insights</span>—enhancing your creativity, not replacing it. Your voice. Your vision. Smarter positioning.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8 animate-fade-in-up-delay-2 px-2">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold py-3 sm:py-4 px-6 sm:px-10 text-sm sm:text-base rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300 w-full sm:w-auto"
                onClick={() => navigate('/login')}
                aria-label="Get started free without credit card required"
              >
                <span className="hidden sm:inline">Get Started FREE - No Credit Card Required →</span>
                <span className="sm:hidden">Get Started FREE →</span>
              </Button>
            </div>

            <p className="text-sm sm:text-base text-slate-400 animate-fade-in-up-delay-3 px-2">
              ✨ 3 free book concepts monthly • Human creativity enhanced by AI • Not AI ghostwriting
            </p>
          </div>
        </div>
      </header>

      {/* Free ISBN Barcode Generator - Prominent CTA */}
      <div className="py-6 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 rounded-lg p-3 border border-yellow-500 shadow-md">
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <span className="text-2xl">📊</span>
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900">
                Free ISBN Barcode Generator
              </h2>
              <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                ✨ FREE
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 font-semibold mb-3 max-w-xl mx-auto px-2">
              Need a professional ISBN barcode for your book cover? Generate print-ready barcodes instantly—completely free, no signup required!
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
              <Button
                size="sm"
                      className="w-full sm:w-auto"
                onClick={() => navigate('/ibg')}
                className="bg-slate-900 text-white hover:bg-slate-800 font-semibold text-sm px-4 py-2 rounded-full shadow-md transform hover:scale-105 transition-all"
              >
                🆓 Generate Free Barcode Now →
              </Button>
            </div>
            <div className="mt-2 flex items-center justify-center gap-3 text-slate-700 font-medium text-[10px]">
              <span>✓ No Credit Card</span>
              <span>✓ Instant Download</span>
              <span>✓ Print-Ready Quality</span>
            </div>
          </div>
        </div>
      </div>

      {/* Free QR Code Generator - Prominent CTA */}
      <div className="py-6 bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400 rounded-lg p-3 border border-blue-500 shadow-md">
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <span className="text-2xl">📱</span>
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900">
                Free QR Code Generator
              </h2>
              <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                ✨ FREE
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-800 font-semibold mb-3 max-w-xl mx-auto px-2">
              Need a professional QR code for your website, marketing, or promotional materials? Generate high-resolution QR codes instantly—completely free, no signup required!
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
              <Button
                size="sm"
                      className="w-full sm:w-auto"
                onClick={() => navigate('/qrg')}
                className="bg-slate-900 text-white hover:bg-slate-800 font-semibold text-sm px-4 py-2 rounded-full shadow-md transform hover:scale-105 transition-all"
              >
                🆓 Generate Free QR Code Now →
              </Button>
            </div>
            <div className="mt-2 flex items-center justify-center gap-3 text-slate-700 font-medium text-[10px]">
              <span>✓ No Credit Card</span>
              <span>✓ Instant Download</span>
              <span>✓ High Resolution</span>
            </div>
          </div>
        </div>
      </div>

      {/* The "Why" Section - Our Philosophy */}
      <div className="py-20 bg-gradient-to-b from-black to-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              We Believe in Human Creativity
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-4">
              <span className="text-yellow-400 font-bold">Digital Ink AI is not another platform that writes books for you.</span> 
              We're dedicated to originality, creativity, authenticity, and human agency.
            </p>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Our AI provides <span className="text-yellow-400 font-semibold">meaningful insights and strategic support</span> to enhance your publishing process—empowering your creativity, never replacing it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "🎨",
                title: "Your Original Voice",
                description: "We provide market intelligence and strategic insights to inform your creative decisions. You remain the author—we simply help you understand the landscape where your unique work will compete."
              },
              {
                icon: "🧠",
                title: "Strategic Enhancement",
                description: "AI-powered tools analyze trends, competition, and positioning opportunities. Think of us as your research assistant and marketing strategist—not your ghostwriter."
              },
              {
                icon: "⚡",
                title: "Authentic Positioning",
                description: "Data-driven guidance on discoverability, metadata, and audience targeting. We help your authentic work reach the right readers through smarter positioning, not artificial content."
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700 hover:border-yellow-500 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-2xl">
                <div className="text-5xl mb-4">{benefit.icon}</div>
                <h3 className="text-2xl font-bold text-white mb-4">{benefit.title}</h3>
                <p className="text-base text-slate-300 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The "How" Section - The Process */}
      <div className="py-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              How It Works
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              AI-powered insights enhance <span className="text-yellow-400">your creative process</span>
            </p>
          </div>

          <div className="space-y-12">
            {[
              {
                iconSvg: (
                  <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                  </svg>
                ),
                iconBg: "from-blue-500 to-blue-600",
                title: "Discover & Research Your Market",
                description: "Analyze competitive titles, understand market positioning, and access trend insights before you write. Use our Market Research tools to make informed strategic decisions about your book's positioning and potential.",
                badge: "Research"
              },
              {
                iconSvg: (
                  <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21L12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" fill="currentColor"/>
                    <circle cx="7" cy="7" r="2" fill="currentColor" opacity="0.5"/>
                    <circle cx="17" cy="8" r="1.5" fill="currentColor" opacity="0.7"/>
                    <circle cx="15" cy="15" r="1" fill="currentColor" opacity="0.6"/>
                  </svg>
                ),
                iconBg: "from-purple-500 to-purple-600", 
                title: "Create & Refine Your Work",
                description: "Generate book concepts and titles, develop detailed chapter outlines, create character profiles, and enhance your manuscript with professional developmental feedback and viability analysis. Your authentic voice, amplified by strategic insights.",
                badge: "Create"
              },
              {
                iconSvg: (
                  <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" fill="currentColor"/>
                  </svg>
                ),
                iconBg: "from-emerald-500 to-emerald-600",
                title: "Produce & Market Strategically", 
                description: "Format for EPUB and print, optimize Amazon KDP submissions, create professional media kits, generate comprehensive social media campaigns, and craft compelling promotional materials. Launch your book with complete production and marketing support.",
                badge: "Launch"
              }
            ].map((step, index) => (
              <div key={index} className="flex flex-col md:flex-row items-center gap-8 group">
                {/* Creative Icon Representation */}
                <div className="flex-shrink-0 relative">
                  <div className={`w-32 h-32 bg-gradient-to-br ${step.iconBg} rounded-2xl flex items-center justify-center text-white shadow-2xl group-hover:scale-110 transition-all duration-300 transform group-hover:rotate-3`}>
                    {step.iconSvg}
                  </div>
                  {/* Badge Element */}
                  <div className="absolute -bottom-2 -right-2 px-3 py-1 bg-yellow-500 rounded-full flex items-center justify-center text-black text-sm font-bold shadow-lg">
                    {step.badge}
                  </div>
                  {/* Connection Line for Flow */}
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 left-full w-8 h-0.5 bg-gradient-to-r from-yellow-500 to-transparent transform -translate-y-1/2"></div>
                  )}
                </div>
                
                <div className="flex-1 bg-gradient-to-r from-slate-800 to-slate-900 p-8 rounded-xl border border-slate-700 group-hover:border-yellow-500/30 transition-all duration-300">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-yellow-300 transition-colors">{step.title}</h3>
                  <p className="text-slate-300 text-lg leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* What You Get - Honest Value Proposition */}
      <div className="py-20 bg-gradient-to-b from-slate-900 to-black">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Built for Every Author Journey
            </h2>
            <p className="text-xl text-slate-300">
              From concept to marketing—15 AI-powered tools across 5 strategic workflows
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Writing & Ideation",
                description: "Generate market-aligned book concepts and titles, develop detailed chapter-by-chapter outlines, and create compelling character profiles with backstories. Strategic insights to inform your creative decisions.",
                icon: "📝",
                tools: "3 Tools"
              },
              {
                title: "Editing & Enhancement",
                description: "Professional developmental manuscript feedback, comprehensive viability analysis with market positioning, and polished author biography generation. Enhance your work with AI-powered insights.",
                icon: "✍️",
                tools: "3 Tools"
              },
              {
                title: "Design & Production",
                description: "Convert manuscripts to professional EPUB format, merge and optimize PDFs for print-ready files, and streamline Amazon KDP metadata submissions. Professional formatting and production tools.",
                icon: "🎨",
                tools: "3 Tools"
              },
              {
                title: "Market Research & Analysis",
                description: "Analyze competitive titles and market positioning, generate credible book reviews and endorsements, and access comprehensive market metrics for informed decisions. Data-driven publishing intelligence.",
                icon: "📊",
                tools: "3 Tools"
              },
              {
                title: "Marketing & Promotion",
                description: "Create professional manuscript media kits for publishers and agents, generate comprehensive social media campaigns across all major platforms, and craft compelling keynote speeches. Complete marketing toolkit.",
                icon: "📈",
                tools: "3 Tools"
              }
            ].map((category, index) => (
              <div key={index} className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700 hover:border-yellow-500 transition-all duration-300">
                <div className="text-5xl mb-4">{category.icon}</div>
                <h3 className="text-2xl font-bold text-white mb-3">{category.title}</h3>
                <p className="text-base text-slate-300 mb-4 leading-relaxed">{category.description}</p>
                <div className="text-sm text-yellow-400 font-semibold">{category.tools} included</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="py-24 bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-black mb-6 leading-tight">
            Don't Let Your Book Get Lost in the AMAZON Jungle
          </h2>
          <p className="text-xl text-black/80 mb-10 leading-relaxed">
            With 8,000+ books published daily, standing out requires more than great writing. Get the strategic insights and market intelligence to help your authentic voice reach the right readers.
          </p>
          <Button 
            size="lg"
            className="bg-black hover:bg-slate-900 text-yellow-400 font-bold py-6 px-12 text-lg rounded-full shadow-2xl transform hover:scale-105 transition-all duration-300"
            onClick={() => navigate('/login')}
          >
            Get Started FREE - No Credit Card Required →
          </Button>
          <p className="text-black/70 mt-6 text-base text-center">
            ✓ 3 Free Book Concepts Monthly • ✓ Human Creativity Enhanced • ✓ Not AI Ghostwriting
          </p>
        </div>
      </div>
      
      {/* Companion Book Callout */}
      <div className="bg-black py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8 bg-gradient-to-r from-slate-800 to-slate-900 p-8 rounded-2xl border border-slate-700">
            {/* Book Cover */}
            <div className="flex-shrink-0">
              <img 
                src="/Digital_Ink_Book_Cover.jpg" 
                alt="Digital Ink Book Cover" 
                className="w-48 h-auto rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300"
              />
            </div>
            
            {/* Book Info */}
            <div className="flex-1 text-center md:text-left">
              <p className="text-yellow-400 font-semibold mb-3 text-sm uppercase tracking-wide">
                📚 Dive Deeper Into the Methodology
              </p>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                "Digital Ink: How Artificial Intelligence is Rewriting the Rules of Publishing"
              </h3>
              <p className="text-slate-300 mb-6 leading-relaxed">
                The comprehensive guide behind the platform. Learn the proven strategies for embedding marketing into your book's DNA, 
                standing out in a crowded market, and building a sustainable author platform in the AI era.
              </p>
              <div className="space-y-3">
                <a 
                  href="https://books.by/use-what-you-got-publishing?#digital-ink" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl min-h-[44px] btn-mobile-primary"
                >
                  Order Digital Ink Today →
                </a>
                <p className="text-slate-400 text-sm mt-2">
                  <a 
                    href="https://www.rdbrunet.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-yellow-400 hover:text-yellow-300 transition-colors underline"
                  >
                    More titles from R. Dale Brunet →
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <GlobalFooter />
    </div>
  );
};

// Login Component
const LoginPage = () => {
  const { login, register, handleGoogleAuth, completeGoogleOnboarding, user, setUser, logout, getUserDetails } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showPostOnboardingPayment, setShowPostOnboardingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [postOnboardingUser, setPostOnboardingUser] = useState(null);
  const [showInvalidLoginModal, setShowInvalidLoginModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Check for EULA decline flag
  const [showDeclinedBanner, setShowDeclinedBanner] = useState(false);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('declined') === 'true') {
      setShowDeclinedBanner(true);
      // Clear the URL parameter
      window.history.replaceState({}, document.title, '/login');
    }
  }, [location]);

  // Scroll to top on mount (for mobile)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Focus the appropriate field when switching between login/register
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isLogin) {
        // Focus email field for login
        document.getElementById('email')?.focus();
      } else {
        // Focus first name field for registration
        document.getElementById('first_name')?.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [isLogin]);

  useEffect(() => {
    // Check for Google OAuth callback
    const urlFragment = window.location.hash;
    const sessionIdMatch = urlFragment.match(/session_id=([^&]*)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      handleGoogleLogin(sessionId);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleGoogleLogin = async (sessionId) => {
    try {
      setLoading(true);
      const userData = await handleGoogleAuth(sessionId);
      
      // Check if new Google user needs onboarding
      if (userData.needs_onboarding || !userData.onboarding_completed) {
        setShowOnboarding(true);
      } else {
        navigate('/homepage');
      }
    } catch (error) {
      toast.error("Google authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (loading) return;
    
    setLoading(true);

    try {
      if (isLogin) {
        // Validate required fields for login
        if (!formData.email || !formData.password) {
          toast.error("Please fill in all required fields");
          return;
        }
        
        const userData = await login(formData.email, formData.password);
        
        // Skip onboarding check for admin users
        if (userData.role === 'admin') {
          navigate('/homepage');
        } else if (!userData.onboarding_completed) {
          // Non-admin users must complete onboarding
          setShowOnboarding(true);
        } else {
          navigate('/homepage');
        }
      } else {
        // Validate required fields for registration
        if (!formData.email || !formData.password || !formData.first_name || !formData.last_name || !formData.confirm_password) {
          toast.error("Please fill in all required fields");
          return;
        }
        
        if (formData.password !== formData.confirm_password) {
          toast.error("Passwords don't match");
          return;
        }
        
        if (formData.password.length < 6) {
          toast.error("Password must be at least 6 characters long");
          return;
        }
        
        const userData = await register({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name
        });
        
        // After registration, user must accept EULA first, then onboarding
        // Navigate to homepage which will trigger EULA modal check
        navigate('/homepage');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Authentication failed";
      
      // Check if it's an invalid login error (wrong email/password)
      if (isLogin && (error.response?.status === 401 || errorMessage.toLowerCase().includes('invalid') || errorMessage.toLowerCase().includes('incorrect'))) {
        // Show modal for invalid login
        setShowInvalidLoginModal(true);
      } else {
        // Show toast for other errors
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: resetEmail
      }, { withCredentials: true });
      
      toast.success("Password reset link sent! Check your email.");
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuthClick = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/dashboard`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
            alt="Digital Ink AI" 
            className="h-20 w-auto mx-auto mb-4"
          />
        </div>

        <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-900">
              {isLogin ? 'Sign In' : 'Create Account'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* EULA Declined Banner */}
            {showDeclinedBanner && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-900 mb-1">
                      Account Creation Canceled
                    </h3>
                    <p className="text-sm text-amber-800">
                      Your account was not created because you declined the End User License Agreement. 
                      If you'd like to use Digital Ink AI™, please create a new account and accept the terms.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowDeclinedBanner(false)}
                    className="ml-3 flex-shrink-0 text-amber-600 hover:text-amber-800"
                    aria-label="Close"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      type="text"
                      required
                      value={formData.first_name || ''}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      type="text"
                      required
                      value={formData.last_name || ''}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email || ''}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={formData.password || ''}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                  >
                    Forgot password?
                  </button>
                )}
              </div>

              {!isLogin && (
                <div>
                  <Label htmlFor="confirm_password">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="confirm_password"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      value={formData.confirm_password || ''}
                      onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or continue with</span>
              </div>
            </div>

            <Button 
              onClick={handleGoogleAuthClick}
              variant="outline" 
              className="w-full border-slate-300 hover:bg-slate-50"
              disabled={loading}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>

            <Button 
              onClick={() => navigate('/')}
              variant="outline" 
              className="w-full border-slate-300 hover:bg-slate-50 text-slate-600"
            >
              ← Cancel & Return to Home
            </Button>

            {/* Prominent Sign Up Section for New Users */}
            {isLogin ? (
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border-2 border-blue-200">
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-700 mb-2">
                    ✨ New to Digital Ink AI?
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsLogin(false);
                      // Clear form for sign up
                      setFormData({
                        email: '',
                        password: '',
                        first_name: '',
                        last_name: ''
                      });
                    }}
                    className="w-full bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                  >
                    Create Your Free Account →
                  </button>
                  <p className="text-xs text-slate-600 mt-2">
                    Start with 3 free AI-powered book concepts
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(true);
                    setFormData({
                      email: '',
                      password: '',
                      first_name: '',
                      last_name: '',
                      confirm_password: ''
                    });
                  }}
                  className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                >
                  Already have an account? Sign in
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* DiAi Conversational Onboarding Modal */}
      {showOnboarding && user && (
        <DiAiOnboarding
          isGoogleUser={user.email && !user.password}
          hasPassword={!user.onboarding_completed && user.first_name ? true : (user.email && !user.password)}
          isReturningUser={user.registration_status === 'unregistered' && user.unregistered_source}
          existingUserData={!user.onboarding_completed && user.first_name ? {
            first_name: user.first_name,
            last_name: user.last_name,
            email: user.email,
            timezone: user.timezone || 'America/Denver'
          } : null}
          onPaymentSetup={async () => {
            // Update user role to a_la_carte after successful payment setup
            try {
              const updatedUser = await getUserDetails();
              setUser(updatedUser);
            } catch (error) {
              console.error('Error updating user after payment setup:', error);
            }
          }}
          onClose={() => {
            // Emergency escape - allow user to close modal
            setShowOnboarding(false);
            setLoading(false);          }}
          onComplete={async (surveyData) => {
            try {
              setLoading(true);
              console.log('🎯 Step 1: Starting onboarding completion...');
              console.log('🎯 Survey data:', surveyData);
              console.log('🎯 Current user:', user);
              console.log('🎯 User logged in?', !!user);
              console.log('🎯 Has password in survey?', !!surveyData.password);
              
              // Check if this is an existing user (already logged in) completing onboarding
              // If user is logged in, use complete endpoint. If not logged in, use register endpoint.
              if (user && user.email) {
                console.log('🎯 Step 2: Calling backend to complete onboarding for existing user...');
                // Existing user completing onboarding (admin-created, Google, or logged-in user)
                // Uses google-complete-onboarding endpoint which updates existing user
                const updatedUser = await completeGoogleOnboarding(surveyData);
                
                console.log('🎯 Step 3: Backend onboarding complete. User data:', {
                  email: updatedUser.email,
                  onboarding_completed: updatedUser.onboarding_completed,
                  registration_status: updatedUser.registration_status,
                  role: updatedUser.role
                });
                
                // Critical: Ensure onboarding_completed is set before closing modal
                if (!updatedUser.onboarding_completed) {
                  updatedUser.onboarding_completed = true;
                }
                
                setUser(updatedUser); // Update user state immediately
                setShowOnboarding(false);
                
                console.log('🎯 Step 4: Onboarding modal closed. Checking EULA and payment status...');
                
                // Set flag to indicate onboarding was just completed
                sessionStorage.setItem('justCompletedOnboarding', 'true');
                
                // Check if user needs to accept EULA first
                if (!updatedUser.eula_accepted) {
                  console.log('🎯 User needs to accept EULA before payment setup');
                  // Navigate to homepage which will trigger EULA modal
                  navigate('/homepage');
                } else if (isPaymentExempt(updatedUser)) {
                  console.log('🎯 User is payment-exempt (beta_tester), skipping payment prompt');
                  sessionStorage.removeItem('justCompletedOnboarding');
                  navigate('/homepage');
                } else {
                  console.log('🎯 Showing payment prompt for regular user...');
                  // Show post-onboarding payment prompt before navigating
                  setPostOnboardingUser(updatedUser);
                  setShowPostOnboardingPayment(true);
                }
              } else if (!user && surveyData.email && surveyData.password) {
                console.log('🎯 Step 2: Calling backend to register new user...');
                // New user registering with email/password from onboarding modal
                const newUser = await register(surveyData);
                
                console.log('🎯 Step 3: Backend registration complete. User data:', {
                  email: newUser.email,
                  onboarding_completed: newUser.onboarding_completed,
                  registration_status: newUser.registration_status,
                  role: newUser.role
                });
                
                // Critical: Ensure onboarding_completed is set before closing modal
                if (!newUser.onboarding_completed) {
                  newUser.onboarding_completed = true;
                }
                
                setUser(newUser); // Update user state immediately
                setShowOnboarding(false);
                
                console.log('🎯 Step 4: Onboarding modal closed. Checking EULA and payment status...');
                
                // Set flag to indicate onboarding was just completed
                sessionStorage.setItem('justCompletedOnboarding', 'true');
                
                // Check if user needs to accept EULA first
                if (!newUser.eula_accepted) {
                  console.log('🎯 User needs to accept EULA before payment setup');
                  // Navigate to homepage which will trigger EULA modal
                  navigate('/homepage');
                } else if (isPaymentExempt(newUser)) {
                  console.log('🎯 User is payment-exempt (beta_tester), skipping payment prompt');
                  sessionStorage.removeItem('justCompletedOnboarding');
                  navigate('/homepage');
                } else {
                  console.log('🎯 Showing payment prompt for regular user...');
                  // Show post-onboarding payment prompt before navigating
                  setPostOnboardingUser(newUser);
                  setShowPostOnboardingPayment(true);
                }
              } else {
                // Fallback: should not reach here
                console.error('❌ Unexpected onboarding path - no email/password and no existing user');
                toast.error("Unexpected error during onboarding. Please try again.");
              }
            } catch (error) {
              console.error('❌ Onboarding error:', error);
              console.error('❌ Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
              });
              toast.error(error.response?.data?.detail || "Failed to complete onboarding. Please try again.");
            } finally {
              setLoading(false);
            }
          }}
          onCancel={async () => {
            setShowOnboarding(false);
            // If logged-in user cancels, send cancellation email and log them out
            if (user && user.email) {
              try {
                // Call backend to send cancellation email
                await api.post('/api/auth/onboarding-cancelled');
              } catch (error) {
                console.error('Failed to send cancellation email:', error);
                // Continue with cancellation even if email fails
              }
              logout(); // Log them out              navigate('/');
            } else {              navigate('/');
            }
          }}
        />
      )}
      

      {/* Post-Onboarding Payment Prompt Modal */}
      {showPostOnboardingPayment && postOnboardingUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-3">
                One More Step, {postOnboardingUser.first_name}!
              </h3>
              <p className="text-slate-600 mb-2">
                Would you like to add a payment method now and unlock immediate access to all our professional services?
              </p>
              <p className="text-sm text-slate-500">
                You can always add payment later from your profile settings.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-900 mb-1">✨ With Payment Method:</p>
                <p className="text-xs text-blue-700">
                  • Instant access to all 15+ professional services<br />
                  • Pay-per-use pricing (A La Carte)<br />
                  • Option to subscribe for unlimited access
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-900 mb-1">🎁 Free User:</p>
                <p className="text-xs text-slate-700">
                  • 3 free BCTG uses per month<br />
                  • Access to free utilities (IBG, QRG)<br />
                  • Add payment anytime to unlock more
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={async () => {
                  console.log('💳 User clicked "Add Payment Method"');
                  setShowPostOnboardingPayment(false);
                  console.log('💳 Opening payment modal...');
                  setShowPaymentModal(true);
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3"
              >
                Add Payment Method →
              </Button>
              <Button
                onClick={() => {
                  console.log('🎁 User clicked "Continue as Free User"');
                  setShowPostOnboardingPayment(false);
                  setPostOnboardingUser(null);
                  console.log('🎁 Navigating to homepage...');
                  navigate('/homepage');
                }}
                variant="outline"
                className="w-full"
              >
                Continue as Free User
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Invalid Login Modal */}
      {showInvalidLoginModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-3">
                Invalid UserID / Password
              </h3>
              <p className="text-slate-600 mb-2">
                The email address or password you entered doesn't match our records.
              </p>
              <p className="text-sm text-slate-500">
                Please check your credentials and try again, or create a new account if you don't have one yet.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  console.log('🔄 User clicked "Try Again" - clearing form');
                  setShowInvalidLoginModal(false);
                  // Clear password field only
                  setFormData(prev => ({
                    ...prev,
                    password: '',
                    confirm_password: ''
                  }));
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3"
              >
                Try Again
              </Button>
              <Button
                onClick={() => {
                  console.log('📝 User clicked "Create Your FREE Account" - switching to registration');
                  setShowInvalidLoginModal(false);
                  // Switch to registration mode
                  setIsLogin(false);
                  // Clear form
                  setFormData({
                    email: '',
                    password: '',
                    first_name: '',
                    last_name: '',
                    confirm_password: ''
                  });
                }}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Create Your FREE Account
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Payment Validation Modal - Shown after post-onboarding prompt */}
      {showPaymentModal && (
        <PaymentValidationModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            navigate('/homepage');
          }}
          onSuccess={async () => {
            try {
              console.log('💳 Post-onboarding payment success - refreshing user...');
              // Refresh user to get updated role
              const updatedUser = await getUserDetails();
              setUser(updatedUser);
              console.log('💳 User refreshed:', { role: updatedUser.role });
              
              // Close modal and navigate
              setShowPaymentModal(false);
              toast.success('Payment method added! You now have A La Carte access.');
              navigate('/homepage');
            } catch (error) {
              console.error('❌ Error updating user after payment:', error);
              // Still close and navigate even if refresh fails
              setShowPaymentModal(false);
              toast.success('Payment method added!');
              navigate('/homepage');
            }
          }}
          redirectLocation="onboarding"
        />
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-slate-900">Reset Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <div>
                <Label htmlFor="reset_email">Email Address</Label>
                <Input
                  id="reset_email"
                  type="email"
                  placeholder="your@email.com"
                  value={resetEmail || ''}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="mt-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleForgotPassword();
                    }
                  }}
                />
              </div>
              <div className="flex space-x-3">
                <Button
                  onClick={handleForgotPassword}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                  }}
                  variant="outline"
                  className="flex-1"
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Reset Password Page Component
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    if (resetToken) {
      setToken(resetToken);
    } else {
      toast.error("Invalid reset link");
      navigate('/login');
    }
  }, [navigate]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      
      toast.success("Password reset successful! You can now log in.");
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
            alt="Digital Ink AI" 
            className="h-20 w-auto mx-auto mb-4"
          />
        </div>

        <Card className="bg-white/80 backdrop-blur-sm border-slate-200 shadow-xl">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-slate-900">
              Create New Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="confirm_password"
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                ← Back to Login
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// User Home Component (main landing after login with services carousel and stats)
// Note: Route is /dashboard but user-facing label is "Home"
const Homepage = ({ 
  showJourneyModal = false, 
  setShowJourneyModal = () => {}, 
  selectedService = '', 
  setSelectedService = () => {}, 
  setActiveServiceTab = () => {} 
} = {}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { formatTimestamp } = useTimezone();
  const [userUsage, setUserUsage] = useState({ monthly_bctg_uses: 0 });
  const [dashboardAnalytics, setDashboardAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [revenueData, setRevenueData] = useState(null);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalProcessing, setRenewalProcessing] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Use central MST formatter from utils
  // All timestamps automatically use MST timezone with 12-hour format
  
  // Enhanced dashboard state
  const [userStats, setUserStats] = useState({
    totalBctgGenerated: 0,
    totalBvadmAnalyses: 0,
    totalBocpOutlines: 0,
    totalMmgReports: 0,
    totalCcgCasts: 0,
    totalBvadmReports: 0,
    totalAbgBios: 0,
    totalErmtResults: 0,
    totalPmpbResults: 0,
    totalCcatResults: 0,
    totalBrecResults: 0,
    totalPdeResults: 0,
    totalKsgResults: 0,
    totalAkswResults: 0,
    totalMmkgResults: 0,
    totalSpent: 0,
    pointsBalance: 0,
    rewardsPoints: 0,
    lastActivity: null,
    joinedDate: null,
    recentActivity: []
  });
  const [recentActivityData, setRecentActivityData] = useState(null);
  const [recentActivityLoading, setRecentActivityLoading] = useState(true);
  const [adminApiCosts, setAdminApiCosts] = useState(null); // For admin dashboard
  const [lastAdminUpdate, setLastAdminUpdate] = useState(new Date()); // Track last update time
  const [adminAnalytics, setAdminAnalytics] = useState(null); // Real admin analytics data
  const [adminRevenue, setAdminRevenue] = useState(null); // Revenue analytics
  // Initialize with random quote for variety (65 total quotes including promo)
  // Skip promo frame for subscribers
  const [currentQuote, setCurrentQuote] = useState(() => {
    const isSubscriber = user?.role === 'Subscriber_User';
    const maxIndex = isSubscriber ? 58 : 59; // 0-58 for subscribers (59 quotes), 0-59 for non-subscribers (60 quotes including promo)
    return Math.floor(Math.random() * (maxIndex + 1));
  });
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalType, setPaymentModalType] = useState('');
  const [pendingServiceNavigation, setPendingServiceNavigation] = useState(null); // Track where to go after payment setup
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showPaymentSetupModal, setShowPaymentSetupModal] = useState(false);
  
  // Handler to open subscription modal (checks payment method first)
  const handleOpenSubscriptionModal = () => {
    if (!user?.payment_method) {
      // No payment method - show payment setup first
      setPendingServiceNavigation('subscription');
      setShowPaymentSetupModal(true);
    } else {
      // Has payment method - open subscription modal directly
      setShowSubscriptionModal(true);
    }
  };

  // Refresh user data after payment changes
  const refreshUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const updatedUser = response.data;
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      return null;
    }
  };

  const handlePaymentSetupSuccess = async () => {
    // Refresh user data to get updated payment method and role
    const updatedUser = await refreshUserData();
    
    // Close modal immediately
    setShowPaymentSetupModal(false);
    
    // Show success message
    toast.success('Payment method added successfully!');
    
    // If they were trying to subscribe, open subscription modal now
    if (pendingServiceNavigation === 'subscription') {
      setPendingServiceNavigation(null);
      setShowSubscriptionModal(true);
    } else if (pendingServiceNavigation) {
      // Give more time for state to propagate before navigation
      setTimeout(() => {
        navigate(pendingServiceNavigation);
        setPendingServiceNavigation(null);
      }, 1000);
    }
  };

  const handlePaymentSetupCancel = () => {
    setShowPaymentSetupModal(false);
    setPendingServiceNavigation(null);
  };
  
  // Service-focused motivational quotes tied to Digital Ink AI tools
  const motivationalQuotes = [
    // Original Quotes
    {
      quote: "AI is not a replacement for human creativity—it is a collaborative partner.",
      author: "Digital Ink AI Philosophy",
      tip: "Generate market-aligned book concepts while preserving your authentic voice.",
      service: "BCTG",
      action: "Generate Book Concepts",
      actionLink: "/bctg"
    },
    {
      quote: "Success requires strategic development, strong branding, and optimized marketing.",
      author: "Digital Ink AI Mission",
      tip: "Evaluate your manuscript's quality, structure, and market potential.",
      service: "BVADM",
      action: "Analyze Your Manuscript",
      actionLink: "/bvadm"
    },
    {
      quote: "Digital Ink AI solves the hardest part: making your book discoverable, competitive, and positioned for success from the moment you start writing.",
      author: "R. Dale Brunet",
      tip: "Embed marketing intelligence into your creative workflow from day one.",
      service: "BCTG",
      action: "Create Market-Ready Concepts",
      actionLink: "/bctg"
    },
    {
      quote: "Brilliant books disappear in the marketplace noise because authors treat marketing as an afterthought.",
      author: "Digital Ink AI Vision",
      tip: "Integrate marketing strategies into every stage of your publishing process.",
      service: "BVADM",
      action: "Assess Market Position",
      actionLink: "/bvadm"
    },
    {
      quote: "We enhance efficiency, improve content quality, and maximize marketability without compromising creative integrity.",
      author: "Digital Ink AI Promise",
      tip: "Leverage AI-powered tools that preserve your unique voice while boosting discoverability.",
      service: "BCTG",
      action: "Explore AI Publishing Tools",
      actionLink: "/bctg"
    },
    
    // Ideation and Concept Development (5 prompts)
    {
      quote: "Spark your next bestseller: Use BCTG to generate concepts that captivate your audience from day one.",
      author: "Digital Ink AI",
      tip: "Start with compelling concepts that align with market trends and reader expectations.",
      service: "BCTG",
      action: "Generate Book Concepts",
      actionLink: "/bctg"
    },
    {
      quote: "Unlock creative potential: Refine genres and themes with AI to align your story with market trends.",
      author: "Digital Ink AI",
      tip: "Use AI insights to position your book in the most lucrative market segments.",
      service: "BCTG",
      action: "Explore Genres",
      actionLink: "/bctg"
    },
    {
      quote: "Brainstorm with confidence: Let AI craft unique titles that make your book stand out on shelves.",
      author: "Digital Ink AI",
      tip: "Generate SEO-optimized titles that capture attention and drive discoverability.",
      service: "BCTG",
      action: "Create Titles",
      actionLink: "/bctg"
    },
    {
      quote: "From idea to outline: Start with BCTG concepts to build a structured chapter plan effortlessly.",
      author: "Digital Ink AI",
      tip: "Transform your concepts into detailed outlines that guide your writing process.",
      service: "BCTG",
      action: "Build Your Outline",
      actionLink: "/bctg"
    },
    {
      quote: "Ignite your imagination: Discover inspirational content with AI to fuel your writing journey today.",
      author: "Digital Ink AI",
      tip: "Overcome writer's block with AI-generated prompts and creative sparks.",
      service: "BCTG",
      action: "Get Inspired",
      actionLink: "/bctg"
    },
    
    // Viability and Editing (5 prompts)
    {
      quote: "Assess market fit: Run BVADM to evaluate your manuscript's potential and refine for success.",
      author: "Digital Ink AI",
      tip: "Get comprehensive viability analysis with actionable insights for improvement.",
      service: "BVADM",
      action: "Analyze Manuscript",
      actionLink: "/bvadm"
    },
    {
      quote: "Polish your prose: Use AI developmental editing to enhance flow while preserving your voice.",
      author: "Digital Ink AI",
      tip: "Identify structural improvements without compromising your authentic style.",
      service: "BVADM",
      action: "Get Editing Insights",
      actionLink: "/bvadm"
    },
    {
      quote: "Boost book viability: Analyze competitors with AI to position your work for maximum impact.",
      author: "Digital Ink AI",
      tip: "Understand your competitive landscape and find your unique positioning.",
      service: "BVADM",
      action: "Competitive Analysis",
      actionLink: "/bvadm"
    },
    {
      quote: "Refine for readers: Get sentiment insights from AI to ensure your story resonates deeply.",
      author: "Digital Ink AI",
      tip: "Analyze emotional impact and reader engagement throughout your manuscript.",
      service: "BVADM",
      action: "Sentiment Analysis",
      actionLink: "/bvadm"
    },
    {
      quote: "Elevate manuscript quality: Leverage AI for structural edits that make your book unputdownable.",
      author: "Digital Ink AI",
      tip: "Optimize pacing, structure, and narrative flow for maximum reader engagement.",
      service: "BVADM",
      action: "Improve Structure",
      actionLink: "/bvadm"
    },
    
    // Metadata and Research (5 prompts)
    {
      quote: "Optimize discoverability: Generate SEO metadata with AI to help your book reach more readers.",
      author: "Digital Ink AI",
      tip: "Create keywords and tags that boost your book's visibility in search results.",
      service: "BCTG",
      action: "Generate Metadata",
      actionLink: "/bctg"
    },
    {
      quote: "Target your audience: Use AI trend analysis to tailor your content for market demands.",
      author: "Digital Ink AI",
      tip: "Identify trending topics and themes that resonate with your target readers.",
      service: "BVADM",
      action: "Analyze Trends",
      actionLink: "/bvadm"
    },
    {
      quote: "Craft compelling pitches: Quick AI-generated pitches to hook agents and readers alike.",
      author: "Digital Ink AI",
      tip: "Create elevator pitches that capture the essence of your book in seconds.",
      service: "BCTG",
      action: "Create Pitch",
      actionLink: "/bctg"
    },
    {
      quote: "Research smarter: Let AI uncover audience insights to refine your book's unique appeal.",
      author: "Digital Ink AI",
      tip: "Understand reader preferences and tailor your content accordingly.",
      service: "BVADM",
      action: "Audience Research",
      actionLink: "/bvadm"
    },
    {
      quote: "Metadata mastery: AI helps create tags and keywords that boost your book's visibility.",
      author: "Digital Ink AI",
      tip: "Optimize your book's discoverability across all major platforms.",
      service: "BCTG",
      action: "Optimize Keywords",
      actionLink: "/bctg"
    },
    
    // Design and Branding (5 prompts)
    {
      quote: "Design standout covers: AI assists in creating visuals that capture your book's essence.",
      author: "Digital Ink AI",
      tip: "Get AI-powered design insights for covers that sell.",
      service: "BCTG",
      action: "Design Inspiration",
      actionLink: "/bctg"
    },
    {
      quote: "Build your brand: Develop an author toolkit with AI for consistent, professional presence.",
      author: "Digital Ink AI",
      tip: "Create cohesive branding materials that establish your author identity.",
      service: "BCTG",
      action: "Build Brand",
      actionLink: "/bctg"
    },
    {
      quote: "Create media kits: Use AI to assemble branding materials that wow publishers and fans.",
      author: "Digital Ink AI",
      tip: "Generate professional press kits and promotional materials effortlessly.",
      service: "BCTG",
      action: "Create Media Kit",
      actionLink: "/bctg"
    },
    {
      quote: "Visualize interiors: AI suggestions for layouts that make your book polished and professional.",
      author: "Digital Ink AI",
      tip: "Get layout recommendations that enhance readability and visual appeal.",
      service: "BVADM",
      action: "Layout Guidance",
      actionLink: "/bvadm"
    },
    {
      quote: "Brand with impact: AI helps craft bios and strategies to grow your author platform.",
      author: "Digital Ink AI",
      tip: "Develop your author brand and expand your reach strategically.",
      service: "BCTG",
      action: "Author Platform",
      actionLink: "/bctg"
    },
    
    // Marketing and Promotion (5 prompts)
    {
      quote: "Launch powerful campaigns: AI builds marketing strategies to maximize your book's reach.",
      author: "Digital Ink AI",
      tip: "Create comprehensive marketing plans that drive book sales.",
      service: "BCTG",
      action: "Marketing Strategy",
      actionLink: "/bctg"
    },
    {
      quote: "Plan social content: Generate calendars with AI to keep your promotion engaging and timely.",
      author: "Digital Ink AI",
      tip: "Maintain consistent social media presence with AI-generated content plans.",
      service: "BCTG",
      action: "Social Calendar",
      actionLink: "/bctg"
    },
    {
      quote: "Script trailers: AI creates compelling book trailer scripts to hook potential readers.",
      author: "Digital Ink AI",
      tip: "Craft video content that showcases your book's unique value proposition.",
      service: "BCTG",
      action: "Trailer Script",
      actionLink: "/bctg"
    },
    {
      quote: "Press release perfection: Use AI to craft PR materials that grab media attention.",
      author: "Digital Ink AI",
      tip: "Generate professional press releases that get your book noticed.",
      service: "BCTG",
      action: "Press Release",
      actionLink: "/bctg"
    },
    {
      quote: "Webinar wonders: AI generates promotional webinars to showcase your book's value.",
      author: "Digital Ink AI",
      tip: "Create engaging webinar content that converts viewers into readers.",
      service: "BCTG",
      action: "Webinar Content",
      actionLink: "/bctg"
    },
    
    // Post-Publication Success (5 prompts)
    {
      quote: "Track performance: AI analyzes sales data to optimize your book's ongoing success.",
      author: "Digital Ink AI",
      tip: "Monitor metrics and adjust your strategy for sustained growth.",
      service: "BVADM",
      action: "Performance Analytics",
      actionLink: "/bvadm"
    },
    {
      quote: "License for growth: Explore AI insights on rights to expand your book's opportunities.",
      author: "Digital Ink AI",
      tip: "Identify licensing opportunities that can expand your book's reach and potential.",
      service: "BVADM",
      action: "Rights Management",
      actionLink: "/bvadm"
    },
    {
      quote: "Price strategically: Use AI to refine pricing models for competitive positioning.",
      author: "Digital Ink AI",
      tip: "Optimize pricing across platforms to balance market competitiveness and value.",
      service: "BVADM",
      action: "Pricing Strategy",
      actionLink: "/bvadm"
    },
    {
      quote: "Gather feedback: AI beta tools collect reader insights to improve future works.",
      author: "Digital Ink AI",
      tip: "Use reader feedback to refine your craft and grow your audience.",
      service: "BVADM",
      action: "Reader Feedback",
      actionLink: "/bvadm"
    },
    {
      quote: "Sustain momentum: Monitor post-launch trends with AI for long-term author success.",
      author: "Digital Ink AI",
      tip: "Keep your book relevant with ongoing market insights and adjustments.",
      service: "BVADM",
      action: "Long-term Strategy",
      actionLink: "/bvadm"
    },
    
    // R. Dale Brunet Quotes from "Digital Ink" (10 quotes)
    {
      quote: "AI doesn't write your book—it amplifies your voice, turning raw ideas into stories that captivate and convert.",
      author: "R. Dale Brunet",
      tip: "Use AI as a collaborative tool that enhances your authentic creative expression.",
      service: "BCTG",
      action: "Amplify Your Voice",
      actionLink: "/bctg"
    },
    {
      quote: "In publishing's digital storm, market your manuscript like a bestseller from page one; AI lights the path.",
      author: "R. Dale Brunet",
      tip: "Integrate marketing intelligence from the very beginning of your writing process.",
      service: "BVADM",
      action: "Market Analysis",
      actionLink: "/bvadm"
    },
    {
      quote: "Embrace AI not as a crutch, but as a compass: guiding your words to readers waiting in the shadows.",
      author: "R. Dale Brunet",
      tip: "Let AI navigate you toward your ideal audience and market opportunities.",
      service: "BCTG",
      action: "Find Your Readers",
      actionLink: "/bctg"
    },
    {
      quote: "The future of books isn't in algorithms alone, but in human ingenuity fueled by intelligent tools.",
      author: "R. Dale Brunet",
      tip: "Combine your creative vision with AI-powered insights for maximum impact.",
      service: "BCTG",
      action: "Intelligent Creation",
      actionLink: "/bctg"
    },
    {
      quote: "Unlock your story's potential: AI reveals the market's pulse, but your heart sets the rhythm.",
      author: "R. Dale Brunet",
      tip: "Balance data-driven insights with your personal passion and creative vision.",
      service: "BVADM",
      action: "Story Potential",
      actionLink: "/bvadm"
    },
    {
      quote: "From concept to cover, AI streamlines the chaos—leaving room for the magic only you can weave.",
      author: "R. Dale Brunet",
      tip: "Use automation to handle logistics so you can focus on creative excellence.",
      service: "BCTG",
      action: "Streamline Workflow",
      actionLink: "/bctg"
    },
    {
      quote: "Publishing isn't about perfection; it's about connection. AI helps bridge the gap to eager readers.",
      author: "R. Dale Brunet",
      tip: "Focus on creating genuine connections with readers through AI-enhanced discoverability.",
      service: "BVADM",
      action: "Connect with Readers",
      actionLink: "/bvadm"
    },
    {
      quote: "Let AI analyze the trends, but trust your voice to define them—your book changes the game.",
      author: "R. Dale Brunet",
      tip: "Lead with authenticity while leveraging data to inform your creative decisions.",
      service: "BCTG",
      action: "Define Trends",
      actionLink: "/bctg"
    },
    {
      quote: "In an AI world, the best sellers are those born from bold ideas, not borrowed code.",
      author: "R. Dale Brunet",
      tip: "Use AI as a tool for amplification, not as a substitute for original creativity.",
      service: "BCTG",
      action: "Bold Originality",
      actionLink: "/bctg"
    },
    {
      quote: "AI is your silent partner in publishing: sharpening edges, but the soul of the story is yours alone.",
      author: "R. Dale Brunet",
      tip: "Embrace the partnership between human creativity and intelligent assistance.",
      service: "BVADM",
      action: "Your Story, Enhanced",
      actionLink: "/bvadm"
    },
    
    // New Services - KSG, BOCP, MMG, CCAT, BREC, ABG, CCG (14 quotes)
    {
      quote: "Deliver speeches that inspire: Transform your manuscript into captivating keynote presentations.",
      author: "Digital Ink AI",
      tip: "Convert your book's core message into powerful speeches that engage audiences.",
      service: "KSG",
      action: "Generate Keynote Speech",
      actionLink: "/ksg"
    },
    {
      quote: "Your book deserves structure: Create comprehensive outlines and chapter plans with AI precision.",
      author: "Digital Ink AI",
      tip: "Build detailed book frameworks that guide your writing from start to finish.",
      service: "BOCP",
      action: "Plan Your Chapters",
      actionLink: "/bocp"
    },
    {
      quote: "Marketing made simple: Generate complete media guides to promote your book effectively.",
      author: "Digital Ink AI",
      tip: "Get ready-to-use marketing materials tailored to your book's unique selling points.",
      service: "MMG",
      action: "Create Marketing Guide",
      actionLink: "/mmg"
    },
    {
      quote: "Know your competition: Deep-dive analysis reveals how to position your book for success.",
      author: "Digital Ink AI",
      tip: "Understand competitor strengths and find your unique market advantage.",
      service: "CCAT",
      action: "Analyze Competitors",
      actionLink: "/ccat"
    },
    {
      quote: "Endorsements that sell: Generate professional book reviews and testimonial templates.",
      author: "Digital Ink AI",
      tip: "Create compelling review frameworks that attract genuine endorsements.",
      service: "BREC",
      action: "Create Review Templates",
      actionLink: "/brec"
    },
    {
      quote: "Your author story matters: Craft compelling biographies that connect with readers.",
      author: "Digital Ink AI",
      tip: "Build authentic author profiles that establish credibility and relatability.",
      service: "ABG",
      action: "Generate Biography",
      actionLink: "/abg"
    },
    {
      quote: "Breathe life into characters: Create detailed cast profiles that resonate with readers.",
      author: "Digital Ink AI",
      tip: "Develop rich, consistent character backgrounds that enhance your storytelling.",
      service: "CCG",
      action: "Build Character Cast",
      actionLink: "/ccg"
    },
    {
      quote: "From page to stage: Transform your book's message into speeches that inspire action.",
      author: "Digital Ink AI",
      tip: "Leverage your written work for speaking opportunities and expanded reach.",
      service: "KSG",
      action: "Create Speech Content",
      actionLink: "/ksg"
    },
    {
      quote: "Structure equals success: Plan every chapter with AI-powered outline intelligence.",
      author: "Digital Ink AI",
      tip: "Eliminate writer's block with clear, market-aligned chapter structures.",
      service: "BOCP",
      action: "Structure Your Book",
      actionLink: "/bocp"
    },
    {
      quote: "Marketing momentum: Generate comprehensive promotional strategies that drive book sales.",
      author: "Digital Ink AI",
      tip: "Access complete marketing roadmaps tailored to your genre and audience.",
      service: "MMG",
      action: "Marketing Strategy",
      actionLink: "/mmg"
    },
    {
      quote: "Stay ahead of rivals: Comparative analysis shows where your book shines brightest.",
      author: "Digital Ink AI",
      tip: "Position your book strategically against competitors in your niche.",
      service: "CCAT",
      action: "Competitive Edge",
      actionLink: "/ccat"
    },
    {
      quote: "Reviews build credibility: Professional endorsement frameworks that attract influencers.",
      author: "Digital Ink AI",
      tip: "Create review templates that make it easy for others to endorse your work.",
      service: "BREC",
      action: "Endorsement Tools",
      actionLink: "/brec"
    },
    {
      quote: "Your journey inspires: Build author bios that transform readers into loyal fans.",
      author: "Digital Ink AI",
      tip: "Share your story authentically to create deeper connections with your audience.",
      service: "ABG",
      action: "Author Platform",
      actionLink: "/abg"
    },
    {
      quote: "Characters readers remember: Develop cast profiles that bring your story world to life.",
      author: "Digital Ink AI",
      tip: "Create memorable characters with depth, consistency, and reader appeal.",
      service: "CCG",
      action: "Character Development",
      actionLink: "/ccg"
    },
    
    // Subscription Promotional Frame - Only shown to Free_User and A_La_Carte_User
    {
      quote: "Unlimited creativity awaits! Subscribe for unlimited access to all our AI tools.",
      author: "Digital Ink AI",
      tip: "Subscribers get unlimited generations, premium services, priority support, and more!",
      service: "SUBSCRIPTION",
      action: "Subscribe Now",
      actionLink: "subscription",
      isPromo: true
    }
  ];

  // Rotate quotes every 6 seconds (faster transition)
  useEffect(() => {
    const quoteTimer = setInterval(() => {
      setCurrentQuote((prev) => {
        const isSubscriber = user?.role === 'Subscriber_User';
        const maxIndex = isSubscriber ? 59 : 60; // 59 quotes for subscribers (0-58), 60 quotes for non-subscribers (0-59)
        return (prev + 1) % maxIndex;
      });
    }, 6000);
    return () => clearInterval(quoteTimer);
  }, [user?.role]);

  // Fetch enhanced user statistics
  useEffect(() => {
    const fetchUserStats = async () => {
      if (!user) return;
      
      try {
        // Fetch user activity data
        const activityResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/activity/summary`,
          { withCredentials: true }
        );
        
        // Fetch rewards data
        const rewardsResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/rewards/balance`,
          { withCredentials: true }
        );

        // Fetch admin analytics and API costs for admins
        if (user.role === 'admin' || user.is_admin_designated) {
          try {
            // Fetch real analytics data
            const analyticsResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin-mgmt-7f84b/analytics/overview`,
              { 
                params: { date_range: '30d' },
                withCredentials: true 
              }
            );
            setAdminAnalytics(analyticsResponse.data);
            
            // Fetch API costs
            const apiCostsResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin-mgmt-7f84b/api-costs`,
              { 
                params: { date_range: '30d' },
                withCredentials: true 
              }
            );
            setAdminApiCosts(apiCostsResponse.data);
            console.log('Admin API Costs fetched:', apiCostsResponse.data.summary?.total_cost);
            
            // Fetch revenue analytics
            const revenueResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin/revenue-analytics`,
              { 
                params: { date_range: '30d' },
                withCredentials: true 
              }
            );
            setAdminRevenue(revenueResponse.data);
            console.log('Admin Revenue fetched:', revenueResponse.data.totals?.revenue);
            
            setLastAdminUpdate(new Date()); // Update timestamp
          } catch (error) {
            console.error("Failed to fetch admin data:", error);
          }
        }

        setUserStats({
          totalBctgGenerated: activityResponse.data.bctg_count || 0,
          totalBvadmAnalyses: activityResponse.data.bvadm_count || 0,
          totalBocpOutlines: activityResponse.data.bocp_count || 0,
          totalMmgReports: activityResponse.data.mmg_count || 0,
          totalCcgCasts: activityResponse.data.ccg_count || 0,
          totalAbgBios: activityResponse.data.abg_count || 0,
          totalErmtResults: activityResponse.data.ermt_count || 0,
          totalPmpbResults: activityResponse.data.pmpb_count || 0,
          totalCcatResults: activityResponse.data.ccat_count || 0,
          totalBrecResults: activityResponse.data.brec_count || 0,
          totalPdeResults: activityResponse.data.pde_count || 0,
          totalKsgResults: activityResponse.data.ksg_count || 0,
          totalAkswResults: activityResponse.data.aksw_count || 0,
          totalMmkgResults: activityResponse.data.mmkg_count || 0,
          rewardsPoints: rewardsResponse.data.points_balance || 0,
          lastActivity: activityResponse.data.last_activity || null,
          joinedDate: user.created_at || null,
          recentActivity: activityResponse.data.recent_activities || []
        });
      } catch (error) {
        // Set defaults if API calls fail
        setUserStats({
          totalBctgGenerated: 0,
          totalBvadmAnalyses: 0,
          totalBocpOutlines: 0,
          totalMmgReports: 0,
          totalCcgCasts: 0,
          totalAbgBios: 0,
          totalErmtResults: 0,
          totalPmpbResults: 0,
          totalCcatResults: 0,
          totalBrecResults: 0,
          totalPdeResults: 0,
          totalKsgResults: 0,
          totalAkswResults: 0,
          totalMmkgResults: 0,
          rewardsPoints: user.points_balance || 0,
          lastActivity: null,
          joinedDate: user.created_at || null,
          recentActivity: []
        });
      }
    };

    fetchUserStats();
    
    // Listen for custom refresh events from other components
    const handleRefreshStats = () => {
      fetchUserStats();
    };
    
    window.addEventListener('refreshDashboardStats', handleRefreshStats);
    
    // Auto-refresh every 5 seconds for admins
    let refreshInterval;
    if (user && (user.role === 'admin' || user.is_admin_designated)) {
      refreshInterval = setInterval(() => {
        fetchUserStats();
      }, 5000); // 5 seconds
    }
    
    return () => {
      window.removeEventListener('refreshDashboardStats', handleRefreshStats);
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user]);

  // Fetch recent activity for personalized welcome
  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user) return;
      
      setRecentActivityLoading(true);
      
      // First, check if we have stored session data from last logout
      const lastSessionStr = localStorage.getItem('lastSession');
      if (lastSessionStr) {
        try {
          const lastSession = JSON.parse(lastSessionStr);
          
          // Check if this session belongs to the current user
          if (lastSession.userId === user.id && lastSession.recentActivity) {
            // Calculate days since last login
            const lastLoginTime = new Date(lastSession.lastLoginTime);
            const now = new Date();
            const daysSinceLastLogin = Math.floor((now - lastLoginTime) / (1000 * 60 * 60 * 24));
            
            // Use the stored data with updated days count
            const storedData = {
              ...lastSession.recentActivity,
              days_since_last_login: daysSinceLastLogin,
              last_login_time: lastSession.lastLoginTime
            };
            
            setRecentActivityData(storedData);
            setRecentActivityLoading(false);
            
            // Optionally fetch fresh data in background to update
            try {
              const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/user/recent-activity`,
                { withCredentials: true }
              );
              response.data.days_since_last_login = daysSinceLastLogin;
              response.data.last_login_time = lastSession.lastLoginTime;
              setRecentActivityData(response.data);
            } catch (error) {
            }
            return;
          }
        } catch (error) {
        }
      }
      
      // No stored data or different user - fetch fresh data
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/user/recent-activity`,
          { withCredentials: true }
        );
        
        setRecentActivityData(response.data);
      } catch (error) {
        // Set default data on error
        setRecentActivityData({
          most_recent: null,
          days_since_activity: null,
          login_count: user.login_count || 999,
          is_first_login: false
        });
      } finally {
        setRecentActivityLoading(false);
      }
    };

    fetchRecentActivity();
  }, [user]);
  
  // Handle continue with recent project
  const handleContinueProject = () => {
    if (!recentActivityData || !recentActivityData.most_recent) return;
    
    const recent = recentActivityData.most_recent;
    if (recent.type === 'bctg') {
      navigate(`/activity?view=bctg&result=${recent.id}`);
    } else if (recent.type === 'bvadm') {
      navigate(`/activity?view=bvadm&report=${recent.id}`);
    }
  };

  // Validation functions for service access
  const validateBCTGAccess = () => {
    
    if (!user) {
      return false;
    }
    
    const monthlyUses = userUsage?.monthly_bctg_uses ?? 0;
    const hasPaymentMethod = !!(user.payment_method || user.stripe_customer_id);
    
    // STEP 1: Check if user has used their 3 free reports
    if (monthlyUses < 3) {
      return true; // Go to BCTG page
    }
    
    // STEP 2: User has used 3+ reports - check for payment method
    
    if (hasPaymentMethod) {
      return true; // Go to BCTG page (will be charged)
    }
    
    // STEP 3: No payment method - launch payment validation modal
    setPaymentModalType('bctg_payment');
    setShowPaymentModal(true);
    setPendingServiceNavigation('/bctg');
    return false; // Block until payment method added
  };

  const validateBVADMAccess = () => {
    if (!user) return false;
    
    // BVADM always requires payment - check if payment method exists
    // FIX: Check payment_method object existence, not has_payment_method boolean
    if (!user.payment_method && !user.stripe_customer_id) {
      // No payment method on file - show payment setup modal
      setPaymentModalType('bvadm_payment');
      setShowPaymentModal(true);
      setPendingServiceNavigation('/bvadm'); // Save where we want to go
      return false;
    }
    
    return true; // Has payment method, can proceed
  };

  const handleServiceNavigation = (service, path) => {
    
    if (!user) {
      return;
    }
    
    const serviceName = service.toUpperCase();
    
    if (serviceName === 'BCTG') {
      
      // Get monthly uses from user object or usage tracking
      const monthlyUses = user.usage_tracking?.bctg_monthly_count || user.monthly_bctg_uses || 0;
      const hasPaymentMethod = !!(user.payment_method || user.stripe_customer_id);
      
      // STEP 1: Check if user has used their 3 free reports
      if (monthlyUses < 3) {
        navigate(path);
        return;
      }
      
      // STEP 2: User has used 3+ reports - check for payment method
      
      if (hasPaymentMethod) {
        navigate(path);
        return;
      }
      
      // STEP 3: No payment method - show payment modal
      setPendingServiceNavigation(path);
      setShowPaymentSetupModal(true);
      
    } else if (serviceName === 'BVADM') {
      
      // BVADM always requires payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'BOCP') {
      
      // BOCP - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'MMG') {
      
      // MMG - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'CCG') {
      
      // CCG - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'CCAT') {
      
      // CCAT - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Free users are blocked from CCAT
      if (user.role === 'free_user') {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'BREC') {
      
      // BREC - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Free users are blocked from BREC
      if (user.role === 'free_user') {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'ABG') {
      
      // ABG - Standard service, subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Free users are blocked from ABG, PDE
      if (user.role === 'free_user') {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'PDE') {
      
      // PDE - Premium service, subscribers get 50% discount, free users blocked, a la carte need payment
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Free users are blocked from PDE
      if (user.role === 'free_user') {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'ERMT') {
      
      // ERMT - Premium service, subscribers get 50% discount, free users blocked, a la carte need payment
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Free users are blocked from ERMT
      if (user.role === 'free_user') {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else if (serviceName === 'PMPB') {
      
      // PMPB - Premium service, subscribers get 50% discount, free users blocked, a la carte need payment
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(path);
        return;
      }
      
      // Free users are blocked from PMPB
      if (user.role === 'free_user') {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingServiceNavigation(path);
        setShowPaymentSetupModal(true);
        return;
      }
      
      navigate(path);
      
    } else {
      navigate(path);
    }
  };

  // Fetch analytics data for admin dashboard cards
  useEffect(() => {
    const fetchDashboardAnalytics = async () => {
      if (user?.role === 'admin' || user?.is_admin_designated) {
        try {
          setLoadingAnalytics(true);
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin-mgmt-7f84b/analytics/overview?date_range=${dateRange}`,
            { withCredentials: true }
          );
          setDashboardAnalytics(response.data);
          
          // Fetch revenue data
          const revenueResponse = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/revenue-analytics?date_range=${dateRange}`,
            { withCredentials: true }
          );
          setRevenueData(revenueResponse.data);
        } catch (error) {
        } finally {
          setLoadingAnalytics(false);
        }
      }
    };

    fetchDashboardAnalytics();
  }, [user, dateRange]);

  // Check if user just completed payment setup and should be navigated to pending service
  useEffect(() => {
    if (user && pendingServiceNavigation) {
      // Check if payment method is now available
      if (user.payment_method || user.stripe_customer_id) {
        const destination = pendingServiceNavigation;
        setPendingServiceNavigation(null); // Clear the pending navigation
        setTimeout(() => navigate(destination), 500); // Small delay for smooth transition
      }
    }
  }, [user?.payment_method, user?.stripe_customer_id, pendingServiceNavigation, navigate]);

  // Calculate days until subscription renewal
  const getDaysUntilRenewal = () => {
    if (!user?.subscription_end) return null;
    
    try {
      const renewalDate = new Date(user.subscription_end);
      const today = new Date();
      const diffTime = renewalDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays > 0 ? diffDays : 0;
    } catch (error) {
      return null;
    }
  };

  const isSubscriber = user?.role === 'subscriber' && user?.subscription_status && user.subscription_status !== "None";
  const daysUntilRenewal = getDaysUntilRenewal();
  
  // Force refresh user data when dashboard loads to ensure latest subscription data
  useEffect(() => {
    const refreshDashboardUserData = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        const freshUserData = response.data;
        
        // Update global auth context with fresh data
        if (window.updateAuthUser) {
          window.updateAuthUser(freshUserData);
        }
      } catch (error) {
      }
    };
    
    refreshDashboardUserData();
  }, []);

  useEffect(() => {
    // Load user usage data (this could be part of the user session or a separate call)
    if (user) {
      setUserUsage({
        monthly_bctg_uses: user.monthly_bctg_uses || 0
      });
    }
  }, [user]);

  // Determine pricing display based on user role and usage
  const getBCTGPricing = () => {
    if (!user) return "Loading...";
    
    const role = user.role;
    const monthlyUses = userUsage.monthly_bctg_uses;
    
    // Admin, Student, or Subscriber get FREE
    if (role === 'admin' || role === 'student' || role === 'subscriber') {
      return <span className="font-bold text-green-300">FREE</span>;
    }
    
    // Users without subscription (subscription_status == "None") get 3 free uses per month
    const subscriptionStatus = user?.subscription_status || 'None';
    
    if (subscriptionStatus === 'None') {
      // Get free uses count (max 3 per month)
      const freeUsesCount = user?.usage_tracking?.bctg_free_monthly_count || 0;
      const remaining = Math.max(0, 3 - freeUsesCount);
      
      if (remaining > 0) {
        return <span className="font-bold text-green-300">3 Free Uses Per Month / {remaining} remaining this month</span>;
      } else {
        // Used all 3 free uses
        if (role === 'a_la_carte') {
          return <span className="font-bold text-yellow-300">$2.50 Per Use (Free uses exhausted)</span>;
        } else {
          // free_user - should be blocked
          return <span className="font-bold text-red-300">Limit Reached - Upgrade to Continue</span>;
        }
      }
    }
    
    // Has active subscription - unlimited BCTG
    if (subscriptionStatus !== 'None') {
      return <span className="font-bold text-green-300">Unlimited (Subscriber Benefit)</span>;
    }
    
    return <span className="font-bold text-yellow-300">$2.50 Per Use</span>;
  };

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.is_admin_designated;

  // Handle subscription renewal
  const handleSubscriptionRenewal = async () => {
    try {
      setRenewalProcessing(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/renew`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        toast.success("Subscription renewed successfully!");
        setShowRenewalModal(false);
        // Refresh user data
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to renew subscription");
    } finally {
      setRenewalProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 relative">
      {/* Typewriter Background Image */}
      <div 
        className="fixed inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: 'url(/Digital_Ink_Book_Cover.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          pointerEvents: 'none'
        }}
      />
      
      <Navigation />
      
      <main className="relative z-10 max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Enhanced Welcome Section with Personalization */}
        <div className="mb-8">
          {!isAdmin ? (
            <>
              {/* Personalized Welcome */}
              <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 rounded-2xl p-8 text-white shadow-xl mb-6">
                <div className="max-w-5xl mx-auto">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-center gap-6">
                  <div className="flex-1 text-center lg:text-left">
                    {/* Digital Ink AI Logo */}
                    <div className="flex justify-center lg:justify-start mb-4">
                      <img 
                        src="/Digital_Ink_AI_Logo.png" 
                        alt="Digital Ink AI" 
                        className="h-16 w-auto drop-shadow-2xl"
                      />
                    </div>
                    {/* Interactive Welcome Message */}
                    {recentActivityLoading ? (
                      // Loading state
                      <div className="space-y-4">
                        <h1 className="text-4xl font-bold mb-3 animate-fade-in">
                          Welcome, {user?.first_name}! ✨
                        </h1>
                        <p className="text-blue-100 text-lg leading-relaxed">
                          Loading your dashboard...
                        </p>
                      </div>
                    ) : recentActivityData && recentActivityData.login_count === 1 && !recentActivityData.most_recent ? (
                      // First-time user welcome (only if login_count is exactly 1 AND no previous activity)
                      <div className="space-y-4">
                        <h1 className="text-4xl font-bold mb-3 animate-fade-in">
                          Welcome to Digital Ink AI, {user?.first_name}! ✨
                        </h1>
                        <p className="text-blue-100 text-lg leading-relaxed">
                          Let's get started on your publishing journey.
                        </p>
                      </div>
                    ) : recentActivityData && recentActivityData.most_recent ? (
                      // Returning user with recent activity
                      <div className="space-y-4">
                        <h1 className="text-4xl font-bold mb-3 animate-fade-in">
                          Welcome back, {user?.first_name}! ✨
                        </h1>
                        <div className="bg-white/20 backdrop-blur-md rounded-lg px-4 py-3 border border-white/30 mb-4 shadow-lg">
                          <p className="text-white text-base">
                            <strong>{user?.first_name} {user?.last_name}</strong> • {user?.email}
                          </p>
                          {/* User Role and Subscription Information */}
                          <div className="mt-3 pt-3 border-t border-white/30 space-y-2">
                            {/* User Role */}
                            <div className="flex items-start gap-2">
                              <span className="text-blue-100 text-sm min-w-[120px]">User Role:</span>
                              <span className="text-white font-semibold text-sm">
                                {user?.role === 'free_user' && '🆓 Free'}
                                {user?.role === 'a_la_carte' && '💳 A la Carte'}
                                {user?.role === 'subscriber' && '⭐ Subscriber'}
                                {user?.role === 'beta_tester' && '🧪 Beta Tester'}
                                {user?.role === 'admin' && '👑 Admin'}
                              </span>
                            </div>
                            
                            {/* Subscription Type (if subscriber) */}
                            {user?.role === 'subscriber' && user?.subscription_status && user?.subscription_status !== 'None' && (
                              <div className="flex items-start gap-2">
                                <span className="text-blue-100 text-sm min-w-[120px]">Subscription:</span>
                                <span className="text-yellow-300 font-semibold text-sm">
                                  {user.subscription_status === 'Monthly' && '📅 Monthly'}
                                  {user.subscription_status === 'Annual' && '📆 Annual'}
                                </span>
                              </div>
                            )}
                            
                            {/* Trial Days Remaining (calculated from subscription_start) */}
                            {user?.role === 'subscriber' && user?.subscription_start && (() => {
                              try {
                                const subscriptionStart = new Date(user.subscription_start);
                                const trialEndDate = new Date(subscriptionStart);
                                trialEndDate.setDate(trialEndDate.getDate() + 14); // Add 14 days
                                
                                const now = new Date();
                                const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                
                                return daysRemaining > 0 && daysRemaining <= 14;
                              } catch (error) {
                                return false;
                              }
                            })() && (
                              <div className="flex items-start gap-2">
                                <span className="text-yellow-300 font-semibold text-sm">
                                  ⏰ You have {(() => {
                                    const subscriptionStart = new Date(user.subscription_start);
                                    const trialEndDate = new Date(subscriptionStart);
                                    trialEndDate.setDate(trialEndDate.getDate() + 14);
                                    const now = new Date();
                                    const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    return daysRemaining;
                                  })()} day{(() => {
                                    const subscriptionStart = new Date(user.subscription_start);
                                    const trialEndDate = new Date(subscriptionStart);
                                    trialEndDate.setDate(trialEndDate.getDate() + 14);
                                    const now = new Date();
                                    const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    return daysRemaining !== 1 ? 's' : '';
                                  })()} remaining on your 14 day trial.
                                </span>
                              </div>
                            )}
                            
                            {/* Subscription Renewal Date (if subscriber) */}
                            {user?.role === 'subscriber' && user?.subscription_end && (
                              <div className="flex items-start gap-2">
                                <span className="text-blue-100 text-sm min-w-[120px]">Renewal Date:</span>
                                <span className="text-white font-semibold text-sm">
                                  🔄 {new Date(user.subscription_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              </div>
                            )}
                            
                            {/* Renew Subscription Button (if auto-renew OFF and within 72 hours) */}
                            {user?.role === 'subscriber' && user?.subscription_end && user?.auto_renew === false && (() => {
                              try {
                                const renewalDate = new Date(user.subscription_end);
                                const now = new Date();
                                const hoursUntilRenewal = (renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60);
                                return hoursUntilRenewal > 0 && hoursUntilRenewal <= 72;
                              } catch {
                                return false;
                              }
                            })() && (
                              <div className="mt-3 pt-3 border-t border-white/20">
                                <Button
                                  onClick={() => setShowRenewalModal(true)}
                                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold"
                                  size="sm"
                      className="w-full sm:w-auto"
                                >
                                  ⚡ Renew Your Subscription Now
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-blue-100 text-lg leading-relaxed">
                          It's been <strong>
                            {recentActivityData.days_since_last_login !== undefined 
                              ? (recentActivityData.days_since_last_login === 0 ? 'less than a day' : `${recentActivityData.days_since_last_login} day${recentActivityData.days_since_last_login !== 1 ? 's' : ''}`)
                              : (recentActivityData.days_since_activity === 0 ? 'less than a day' : `${recentActivityData.days_since_activity} day${recentActivityData.days_since_activity !== 1 ? 's' : ''}`)
                            }
                          </strong> since we've worked together.
                        </p>
                        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                          <p className="text-white text-base mb-3">
                            📋 Your most recent project: <strong className="text-yellow-300">{recentActivityData.most_recent.title}</strong>
                          </p>
                          <p className="text-blue-200 text-sm mb-4">
                            Last activity: {recentActivityData.most_recent.created_at_display}
                          </p>
                          <p className="text-white text-base mb-4">
                            Would you like to continue with this project or start something new?
                          </p>
                          <div className="flex flex-wrap gap-3">
                            <Button
                              onClick={handleContinueProject}
                              className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-3"
                            >
                              📄 Continue "{recentActivityData.most_recent.title.substring(0, 25)}{recentActivityData.most_recent.title.length > 25 ? '...' : ''}"
                            </Button>
                            <Button
                              onClick={() => navigate('/bctg')}
                              className="bg-white/20 hover:bg-white/30 text-white border-white/40 px-6 py-3"
                            >
                              ✨ Start New Project
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Returning user without recent activity
                      <div className="space-y-4">
                        <h1 className="text-4xl font-bold mb-3 animate-fade-in">
                          Welcome back, {user?.first_name}! ✨
                        </h1>
                        <div className="bg-white/20 backdrop-blur-md rounded-lg px-4 py-3 border border-white/30 mb-4 shadow-lg">
                          <p className="text-white text-base">
                            <strong>{user?.first_name} {user?.last_name}</strong> • {user?.email}
                          </p>
                          {/* User Role and Subscription Information */}
                          <div className="mt-3 pt-3 border-t border-white/30 space-y-2">
                            {/* User Role */}
                            <div className="flex items-start gap-2">
                              <span className="text-blue-100 text-sm min-w-[120px]">User Role:</span>
                              <span className="text-white font-semibold text-sm">
                                {user?.role === 'free_user' && '🆓 Free'}
                                {user?.role === 'a_la_carte' && '💳 A la Carte'}
                                {user?.role === 'subscriber' && '⭐ Subscriber'}
                                {user?.role === 'beta_tester' && '🧪 Beta Tester'}
                                {user?.role === 'admin' && '👑 Admin'}
                              </span>
                            </div>
                            
                            {/* Subscription Type (if subscriber) */}
                            {user?.role === 'subscriber' && user?.subscription_status && user?.subscription_status !== 'None' && (
                              <div className="flex items-start gap-2">
                                <span className="text-blue-100 text-sm min-w-[120px]">Subscription:</span>
                                <span className="text-yellow-300 font-semibold text-sm">
                                  {user.subscription_status === 'Monthly' && '📅 Monthly'}
                                  {user.subscription_status === 'Annual' && '📆 Annual'}
                                </span>
                              </div>
                            )}
                            
                            {/* Trial Days Remaining (calculated from subscription_start) */}
                            {user?.role === 'subscriber' && user?.subscription_start && (() => {
                              try {
                                const subscriptionStart = new Date(user.subscription_start);
                                const trialEndDate = new Date(subscriptionStart);
                                trialEndDate.setDate(trialEndDate.getDate() + 14); // Add 14 days
                                
                                const now = new Date();
                                const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                
                                return daysRemaining > 0 && daysRemaining <= 14;
                              } catch (error) {
                                return false;
                              }
                            })() && (
                              <div className="flex items-start gap-2">
                                <span className="text-yellow-300 font-semibold text-sm">
                                  ⏰ You have {(() => {
                                    const subscriptionStart = new Date(user.subscription_start);
                                    const trialEndDate = new Date(subscriptionStart);
                                    trialEndDate.setDate(trialEndDate.getDate() + 14);
                                    const now = new Date();
                                    const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    return daysRemaining;
                                  })()} day{(() => {
                                    const subscriptionStart = new Date(user.subscription_start);
                                    const trialEndDate = new Date(subscriptionStart);
                                    trialEndDate.setDate(trialEndDate.getDate() + 14);
                                    const now = new Date();
                                    const daysRemaining = Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                    return daysRemaining !== 1 ? 's' : '';
                                  })()} remaining on your 14 day trial.
                                </span>
                              </div>
                            )}
                            
                            {/* Subscription Renewal Date (if subscriber) */}
                            {user?.role === 'subscriber' && user?.subscription_end && (
                              <div className="flex items-start gap-2">
                                <span className="text-blue-100 text-sm min-w-[120px]">Renewal Date:</span>
                                <span className="text-white font-semibold text-sm">
                                  🔄 {new Date(user.subscription_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </span>
                              </div>
                            )}
                            
                            {/* Renew Subscription Button (if auto-renew OFF and within 72 hours) */}
                            {user?.role === 'subscriber' && user?.subscription_end && user?.auto_renew === false && (() => {
                              try {
                                const renewalDate = new Date(user.subscription_end);
                                const now = new Date();
                                const hoursUntilRenewal = (renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60);
                                return hoursUntilRenewal > 0 && hoursUntilRenewal <= 72;
                              } catch {
                                return false;
                              }
                            })() && (
                              <div className="mt-3 pt-3 border-t border-white/20">
                                <Button
                                  onClick={() => setShowRenewalModal(true)}
                                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-black font-semibold"
                                  size="sm"
                      className="w-full sm:w-auto"
                                >
                                  ⚡ Renew Your Subscription Now
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-blue-100 text-lg leading-relaxed">
                          {(() => {
                            // Personalized message based on onboarding data
                            const role = user?.primary_role;
                            const goal = user?.main_goal;
                            const genre = user?.genres;
                            const stage = user?.project_stage;
                            const experience = user?.experience_level;
                            
                            // Build personalized message
                            let message = '';
                            
                            // Add role and genre-specific greeting
                            if (role === 'Author/Writer' && genre) {
                              if (genre.toLowerCase().includes('fiction')) {
                                message += `Ready to bring your fiction story to life? `;
                              } else if (genre.toLowerCase().includes('non-fiction')) {
                                message += `Ready to share your expertise with the world? `;
                              } else {
                                message += `Ready to bring your ${genre} story to life? `;
                              }
                            } else if (role === 'Publisher/Editor') {
                              message += `Let's get your manuscripts market-ready! `;
                            } else if (role === 'Student/Learner') {
                              message += `Ready to enhance your writing skills? `;
                            } else if (role === 'Content Creator') {
                              message += `Time to create amazing content! `;
                            } else {
                              message += `Ready to create something amazing? `;
                            }
                            
                            // Add stage or goal-specific encouragement
                            if (stage === 'Just Starting - Exploring Ideas') {
                              message += `Let's turn your ideas into a solid plan.`;
                            } else if (stage === 'Outlining & Planning') {
                              message += `Let's build a strong foundation for your story.`;
                            } else if (stage === 'Writing First Draft') {
                              message += `Keep that creative momentum going!`;
                            } else if (stage === 'Editing & Revising') {
                              message += `Let's polish your manuscript to perfection.`;
                            } else if (stage === 'Ready to Publish') {
                              message += `You're almost there—let's get your book out into the world!`;
                            } else if (stage === 'Published - Marketing Phase') {
                              message += `Let's boost your book's visibility and reach more readers.`;
                            } else if (goal && goal.toLowerCase().includes('publish')) {
                              message += `Our tools are here to help you complete and publish your book successfully.`;
                            } else if (goal && goal.toLowerCase().includes('writing')) {
                              message += `Our AI-powered tools will help you refine your craft and elevate your writing.`;
                            } else if (goal && goal.toLowerCase().includes('market')) {
                              message += `Let's boost your book's visibility and reach more readers.`;
                            } else if (goal && goal.toLowerCase().includes('platform')) {
                              message += `We'll help you establish your brand and connect with your audience.`;
                            } else {
                              message += `Our AI-powered tools are here to help you succeed.`;
                            }
                            
                            return message;
                          })()}
                        </p>
                      </div>
                    )}
                    
                    {/* Rewards Points Display */}
                    <div className="mt-6">
                      <div className="bg-gradient-to-br from-yellow-500 to-amber-600 backdrop-blur-md rounded-lg p-4 border border-yellow-400/60 shadow-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-yellow-50 text-sm font-semibold">Available Rewards Points</div>
                            <div className="text-white text-3xl font-bold mt-1">{userStats.rewardsPoints || 0}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Button
                              onClick={() => navigate('/rewards')}
                              size="sm"
                              className="bg-white text-amber-600 hover:bg-yellow-50 font-semibold shadow-md transition-all duration-200 hover:scale-105"
                            >
                              💎 Redeem Points
                            </Button>
                            <div className="text-5xl">💎</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Daily Inspiration with Service Connection */}
                  <div className="bg-gradient-to-br from-amber-600/40 to-yellow-800/40 backdrop-blur-md rounded-xl p-6 max-w-md border border-amber-400/40 shadow-lg">
                    <div className="animate-fade-in-up">
                      <div className="text-yellow-300 text-sm font-semibold uppercase tracking-wide mb-2">
                        Daily Inspiration
                      </div>
                      
                      {/* Author/Brand Image */}
                      <div className="flex justify-center mb-4">
                        {motivationalQuotes[currentQuote].author === "R. Dale Brunet" ? (
                          <img 
                            src="/R_Dale_Brunet_Serious.jpg" 
                            alt="R. Dale Brunet" 
                            className="h-20 w-20 rounded-full object-cover border-2 border-yellow-300 shadow-lg"
                          />
                        ) : (
                          <img 
                            src="/Digital_Ink_AI_Logo_Gold.png" 
                            alt="Digital Ink AI" 
                            className="h-20 w-auto object-contain drop-shadow-lg"
                          />
                        )}
                      </div>
                      
                      <blockquote className="text-lg font-medium text-white mb-3 leading-relaxed">
                        "{motivationalQuotes[currentQuote].quote}"
                      </blockquote>
                      <div className="flex justify-between items-center mb-3">
                        <cite className="text-blue-200 text-sm">
                          — {motivationalQuotes[currentQuote].author}
                        </cite>
                      </div>
                      <div className="p-3 bg-amber-800/50 backdrop-blur-sm rounded-lg border border-amber-500/40">
                        <div className="text-xs text-yellow-300 font-semibold">💡 Pro Tip:</div>
                        <div className="text-sm text-white">{motivationalQuotes[currentQuote].tip}</div>
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* ========== ADMIN DASHBOARD CONTROL PANEL ========== */}
              <div className="mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                      Admin Control Panel
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    </h1>
                    <p className="text-sm text-slate-600 mt-1 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1">
                        <span className="text-green-600">●</span> Live updates every 5 seconds
                      </span>
                      <span>•</span>
                      <span>Last updated: {lastAdminUpdate.toLocaleTimeString()}</span>
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/admin')}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    📊 Full Analytics →
                  </Button>
                </div>
              </div>

              {/* Critical Health Snapshot - REAL DATA */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs font-medium text-slate-600">Total Users</div>
                      <div className="text-xl">👥</div>
                    </div>
                    <div className="text-2xl font-bold text-slate-900">
                      {adminAnalytics?.user_metrics?.total_users || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {adminAnalytics?.user_metrics?.period_users > 0 ? `+${adminAnalytics.user_metrics.period_users} this period` : 'No new users'}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs font-medium text-slate-600">Active (7d)</div>
                      <div className="text-xl">✨</div>
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {adminAnalytics?.user_metrics?.active_7_days || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Recently active</div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-emerald-500">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs font-medium text-slate-600">Total Revenue</div>
                      <div className="text-xl">💰</div>
                    </div>
                    <div className="text-2xl font-bold text-emerald-600">
                      ${(adminRevenue?.totals?.revenue || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {adminRevenue?.totals?.transactions > 0 
                        ? `${adminRevenue.totals.transactions} transaction${adminRevenue.totals.transactions !== 1 ? 's' : ''}`
                        : '30 days'}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex justify-between items-start mb-1">
                      <div className="text-xs font-medium text-slate-600">Conversion</div>
                      <div className="text-xl">📊</div>
                    </div>
                    <div className="text-2xl font-bold text-purple-600">
                      {(() => {
                        const totalUsers = adminAnalytics?.user_metrics?.total_users || 0;
                        const paidUsers = (adminAnalytics?.user_metrics?.role_breakdown?.subscriber || 0) + 
                                         (adminAnalytics?.user_metrics?.role_breakdown?.a_la_carte || 0);
                        return totalUsers > 0 ? ((paidUsers / totalUsers) * 100).toFixed(1) : '0';
                      })()}%
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Free → Paid</div>
                  </CardContent>
                </Card>
              </div>

              {/* Service Performance & Quick Actions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Top Services - REAL DATA (DYNAMIC) */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span>⭐</span> Top Performing Services
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {adminAnalytics && adminAnalytics.service_metrics && (() => {
                        // Define all services with their usage counts
                        const allServices = [
                          { code: 'BCTG', icon: '📚', color: 'green', count: adminAnalytics.service_metrics.bctg?.total_generations || 0 },
                          { code: 'BOCP', icon: '📑', color: 'amber', count: adminAnalytics.service_metrics.bocp?.total_outlines || 0 },
                          { code: 'CCG', icon: '🎭', color: 'pink', count: adminAnalytics.service_metrics.ccg?.total_casts || 0 },
                          { code: 'ABG', icon: '👤', color: 'indigo', count: adminAnalytics.service_metrics.abg?.total_bios || 0 },
                          { code: 'BVADM', icon: '📊', color: 'red', count: adminAnalytics.service_metrics.bvadm?.total_analyses || 0 },
                          { code: 'PDE', icon: '✍️', color: 'cyan', count: adminAnalytics.service_metrics.pde?.total_edits || 0 },
                          { code: 'ERMT', icon: '📕', color: 'orange', count: adminAnalytics.service_metrics.ermt?.total_formats || 0 },
                          { code: 'PMPB', icon: '📄', color: 'lime', count: adminAnalytics.service_metrics.pmpb?.total_merges || 0 },
                          { code: 'CCAT', icon: '🔍', color: 'teal', count: adminAnalytics.service_metrics.ccat?.total_analyses || 0 },
                          { code: 'BREC', icon: '📈', color: 'violet', count: adminAnalytics.service_metrics.brec?.total_recommendations || 0 },
                          { code: 'MMG', icon: '🏷️', color: 'fuchsia', count: adminAnalytics.service_metrics.mmg?.total_reports || 0 },
                          { code: 'KSG', icon: '🎤', color: 'rose', count: adminAnalytics.service_metrics.ksg?.total_speeches || 0 },
                          { code: 'AKSW', icon: '📘', color: 'sky', count: adminAnalytics.service_metrics.aksw?.total_reports || 0 },
                          { code: 'MMKG', icon: '📣', color: 'purple', count: adminAnalytics.service_metrics.mmkg?.total_kits || 0 },
                          { code: 'IBG', icon: '💡', color: 'blue', count: adminAnalytics.service_metrics.ibg?.total_generations || 0, isFree: true },
                        ];
                        
                        // Filter services with usage > 0 and sort by count (descending)
                        const topServices = allServices
                          .filter(s => s.count > 0)
                          .sort((a, b) => b.count - a.count)
                          .slice(0, 5); // Show top 5
                        
                        return topServices.length > 0 ? (
                          topServices.map((service, index) => (
                            <div key={service.code} className={`flex justify-between items-center p-2 bg-${service.color}-50 rounded`}>
                              <div className="flex items-center gap-2">
                                <span>{service.icon}</span>
                                <span className="text-sm font-medium">{service.code}{service.isFree ? ' (Free)' : ''}</span>
                              </div>
                              <span className={`text-sm font-bold text-${service.color}-600`}>{service.count} uses</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-slate-500 text-sm">
                            No service usage yet
                          </div>
                        );
                      })()}
                      {!adminAnalytics && (
                        <div className="text-center py-4 text-slate-400 text-sm">
                          Loading services...
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span>⚡</span> Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        onClick={() => navigate('/admin')}
                        variant="outline"
                        className="flex flex-col items-center gap-1 h-auto py-3 text-xs"
                      >
                        <span className="text-xl">👥</span>
                        <span>Users</span>
                      </Button>
                      <Button 
                        onClick={() => navigate('/admin')}
                        variant="outline"
                        className="flex flex-col items-center gap-1 h-auto py-3 text-xs"
                      >
                        <span className="text-xl">💎</span>
                        <span>Rewards</span>
                      </Button>
                      <Button 
                        onClick={() => navigate('/admin')}
                        variant="outline"
                        className="flex flex-col items-center gap-1 h-auto py-3 text-xs"
                      >
                        <span className="text-xl">💳</span>
                        <span>Transactions</span>
                      </Button>
                      <Button 
                        onClick={() => navigate('/admin')}
                        variant="outline"
                        className="flex flex-col items-center gap-1 h-auto py-3 text-xs"
                      >
                        <span className="text-xl">📊</span>
                        <span>Revenue</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* API Cost & Profitability Analysis */}
              <Card className="mb-6 border-l-4 border-l-amber-500">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span>💸</span> Complete Cost Structure & Profitability
                    {!adminApiCosts && <Badge className="ml-2 bg-yellow-600 text-xs">Loading...</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                    {/* Gross Revenue - REAL DATA */}
                    <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <div className="text-xs text-emerald-700 font-medium mb-1">Gross Revenue</div>
                      <div className="text-2xl font-bold text-emerald-600">
                        ${adminRevenue ? (adminRevenue.totals?.revenue || 0).toFixed(2) : '0.00'}
                      </div>
                      <div className="text-xs text-emerald-600 mt-1">
                        {adminRevenue ? `${adminRevenue.totals?.transactions || 0} txns` : '30 days'}
                      </div>
                    </div>

                    {/* Merchant Fees - Calculated from real transactions */}
                    <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="text-xs text-orange-700 font-medium mb-1">Merchant Fees</div>
                      <div className="text-2xl font-bold text-orange-600">
                        ${adminRevenue ? (
                          (adminRevenue.totals?.revenue || 0) * 0.029 + 
                          (adminRevenue.totals?.transactions || 0) * 0.30
                        ).toFixed(2) : '0.00'}
                      </div>
                      <div className="text-xs text-orange-600 mt-1">2.9% + $0.30</div>
                    </div>

                    {/* API Costs - REAL DATA */}
                    <div className="text-center p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="text-xs text-red-700 font-medium mb-1">API Costs</div>
                      <div className="text-2xl font-bold text-red-600">
                        ${adminApiCosts ? (adminApiCosts.summary?.total_cost || 0).toFixed(2) : '0.00'}
                      </div>
                      <div className="text-xs text-red-600 mt-1">
                        {adminApiCosts ? `${adminApiCosts.summary?.total_api_calls || 0} calls` : 'Real-time'}
                      </div>
                    </div>

                    {/* Total Costs - API + Merchant Fees */}
                    <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-xs text-purple-700 font-medium mb-1">Total Costs</div>
                      <div className="text-2xl font-bold text-purple-600">
                        ${(adminApiCosts && adminRevenue) ? (
                          (adminApiCosts.summary?.total_cost || 0) + 
                          ((adminRevenue.totals?.revenue || 0) * 0.029) + 
                          ((adminRevenue.totals?.transactions || 0) * 0.30)
                        ).toFixed(2) : '0.00'}
                      </div>
                      <div className="text-xs text-purple-600 mt-1">API + Fees</div>
                    </div>

                    {/* Net Profit - Revenue minus all costs */}
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-xs text-blue-700 font-medium mb-1">Net Profit</div>
                      <div className={`text-2xl font-bold ${(() => {
                        const revenue = adminRevenue?.totals?.revenue || 0;
                        const apiCost = adminApiCosts?.summary?.total_cost || 0;
                        const merchantFees = (revenue * 0.029) + ((adminRevenue?.totals?.transactions || 0) * 0.30);
                        const profit = revenue - apiCost - merchantFees;
                        return profit >= 0 ? 'text-green-600' : 'text-red-600';
                      })()}`}>
                        ${(adminRevenue && adminApiCosts) ? (() => {
                          const revenue = adminRevenue.totals?.revenue || 0;
                          const apiCost = adminApiCosts.summary?.total_cost || 0;
                          const merchantFees = (revenue * 0.029) + ((adminRevenue.totals?.transactions || 0) * 0.30);
                          return (revenue - apiCost - merchantFees).toFixed(2);
                        })() : '0.00'}
                      </div>
                      <div className="text-xs text-blue-600 mt-1">After costs</div>
                    </div>

                    {/* Net Margin */}
                    <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="text-xs text-slate-700 font-medium mb-1">Net Margin</div>
                      <div className={`text-2xl font-bold ${(() => {
                        const revenue = adminRevenue?.totals?.revenue || 0;
                        if (revenue === 0) return 'text-slate-600';
                        const apiCost = adminApiCosts?.summary?.total_cost || 0;
                        const merchantFees = (revenue * 0.029) + ((adminRevenue?.totals?.transactions || 0) * 0.30);
                        const margin = ((revenue - apiCost - merchantFees) / revenue) * 100;
                        return margin >= 0 ? 'text-green-600' : 'text-red-600';
                      })()}`}>
                        {(adminRevenue && adminApiCosts && adminRevenue.totals?.revenue > 0) ? (() => {
                          const revenue = adminRevenue.totals.revenue;
                          const apiCost = adminApiCosts.summary?.total_cost || 0;
                          const merchantFees = (revenue * 0.029) + ((adminRevenue.totals?.transactions || 0) * 0.30);
                          return (((revenue - apiCost - merchantFees) / revenue) * 100).toFixed(1);
                        })() : '0'}%
                      </div>
                      <div className="text-xs text-slate-600 mt-1">Margin</div>
                    </div>
                  </div>

                  {/* Cost Waterfall - Real Revenue Flow */}
                  <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                    <div className="text-xs font-medium text-slate-700 mb-2">Revenue Waterfall (30 days)</div>
                    {adminRevenue && adminApiCosts && adminRevenue.totals?.revenue > 0 ? (
                      <div className="space-y-1">
                        {(() => {
                          const revenue = adminRevenue.totals.revenue;
                          const merchantFees = (revenue * 0.029) + ((adminRevenue.totals.transactions || 0) * 0.30);
                          const apiCosts = adminApiCosts.summary?.total_cost || 0;
                          const afterFees = revenue - merchantFees;
                          const netProfit = revenue - merchantFees - apiCosts;
                          
                          return (
                            <>
                              <div className="flex items-center gap-2">
                                <div className="w-full bg-emerald-200 h-6 rounded flex items-center justify-center text-xs font-medium text-emerald-900">
                                  Revenue: ${revenue.toFixed(2)} (100%)
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div style={{width: `${(afterFees / revenue * 100).toFixed(0)}%`}} className="bg-yellow-200 h-6 rounded flex items-center justify-center text-xs font-medium text-yellow-900">
                                  After Stripe: ${afterFees.toFixed(2)}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <div style={{width: `${Math.max(5, Math.abs(netProfit / revenue * 100)).toFixed(0)}%`}} className={`${netProfit >= 0 ? 'bg-green-200' : 'bg-red-200'} h-6 rounded flex items-center justify-center text-xs font-medium ${netProfit >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                                  Net: ${netProfit.toFixed(2)}
                                </div>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-slate-500 text-xs">
                        No revenue transactions yet
                      </div>
                    )}
                  </div>

                  {/* Cost Breakdown by Service - REAL DATA */}
                  <div className="space-y-2">
                    <div className="text-xs font-medium text-slate-700 mb-2">Top Services by API Cost (30 days)</div>
                    {adminApiCosts && adminApiCosts.by_service ? (
                      adminApiCosts.by_service.slice(0, 5).map((service, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{service.service}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-red-600 font-medium">${service.total_cost.toFixed(2)}</span>
                            <span className="text-xs text-slate-600">{service.call_count} calls</span>
                            <span className="text-xs text-slate-500">${(service.total_cost / service.call_count).toFixed(4)}/call</span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-slate-500 text-sm">
                        Loading API cost data...
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity & Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Recent Activity Feed */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span>📋</span> Recent Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {adminAnalytics && adminAnalytics.service_metrics && (() => {
                        const activities = [];
                        
                        // Build activity list from service metrics - ALL services
                        const services = [
                          { key: 'bctg', name: 'BCTG', icon: '📚', count: adminAnalytics.service_metrics.bctg?.period_generations || 0 },
                          { key: 'bocp', name: 'BOCP', icon: '📑', count: adminAnalytics.service_metrics.bocp?.period_outlines || 0 },
                          { key: 'ccg', name: 'CCG', icon: '🎭', count: adminAnalytics.service_metrics.ccg?.period_casts || 0 },
                          { key: 'abg', name: 'ABG', icon: '👤', count: adminAnalytics.service_metrics.abg?.period_bios || 0 },
                          { key: 'bvadm', name: 'BVADM', icon: '📊', count: adminAnalytics.service_metrics.bvadm?.period_analyses || 0 },
                          { key: 'pde', name: 'PDE', icon: '✍️', count: adminAnalytics.service_metrics.pde?.period_edits || 0 },
                          { key: 'ermt', name: 'ERMT', icon: '📕', count: adminAnalytics.service_metrics.ermt?.period_formats || 0 },
                          { key: 'pmpb', name: 'PMPB', icon: '📄', count: adminAnalytics.service_metrics.pmpb?.period_merges || 0 },
                          { key: 'ccat', name: 'CCAT', icon: '🔍', count: adminAnalytics.service_metrics.ccat?.period_analyses || 0 },
                          { key: 'brec', name: 'BREC', icon: '📈', count: adminAnalytics.service_metrics.brec?.period_recommendations || 0 },
                          { key: 'mmg', name: 'MMG', icon: '🏷️', count: adminAnalytics.service_metrics.mmg?.period_reports || 0 },
                          { key: 'ksg', name: 'KSG', icon: '🎤', count: adminAnalytics.service_metrics.ksg?.period_speeches || 0 },
                          { key: 'aksw', name: 'AKSW', icon: '📘', count: adminAnalytics.service_metrics.aksw?.period_reports || 0 },
                          { key: 'mmkg', name: 'MMKG', icon: '📣', count: adminAnalytics.service_metrics.mmkg?.period_reports || 0 },
                          { key: 'smmk', name: 'SMMK', icon: '📱', count: adminAnalytics.service_metrics.smmk?.period_kits || 0 },
                          { key: 'ibg', name: 'IBG', icon: '💡', count: adminAnalytics.service_metrics.ibg?.period_generations || 0 },
                        ];
                        
                        services.forEach(service => {
                          if (service.count > 0) {
                            activities.push({
                              service: service.name,
                              icon: service.icon,
                              count: service.count,
                              time: 'Recent'
                            });
                          }
                        });
                        
                        // Sort by count and show top 5 activities
                        const displayActivities = activities.sort((a, b) => b.count - a.count).slice(0, 5);
                        
                        return displayActivities.length > 0 ? (
                          displayActivities.map((activity, index) => (
                            <div key={index} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded text-sm">
                              <span className="text-lg">{activity.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-slate-900 font-medium truncate">
                                  {activity.count}× {activity.service}
                                </div>
                                <div className="text-xs text-slate-500">
                                  Last 30 days
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 text-slate-500 text-sm">
                            No service usage in the last 30 days
                          </div>
                        );
                      })()}
                      {!adminAnalytics && (
                        <div className="text-center py-4 text-slate-400 text-sm">
                          Loading activity...
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* AI-Powered Automated Insights - REAL DATA ANALYSIS */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span>🤖</span> Automated Insights
                      <Badge className="ml-2 bg-purple-600 text-xs">AI-Powered</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {adminAnalytics && (adminAnalytics.user_metrics?.total_users > 1 || adminAnalytics.revenue_metrics?.total_revenue > 0) ? (
                        <>
                          {/* User Growth Pattern */}
                          {adminAnalytics.user_metrics.period_users > 0 && (
                            <div className={`p-3 bg-gradient-to-r ${adminAnalytics.user_metrics.period_users >= 3 ? 'from-green-50 to-emerald-50 border-green-500' : 'from-blue-50 to-cyan-50 border-blue-500'} border-l-4 rounded`}>
                              <div className="flex items-start gap-2">
                                <span className={`${adminAnalytics.user_metrics.period_users >= 3 ? 'text-green-600' : 'text-blue-600'} font-bold text-lg`}>
                                  {adminAnalytics.user_metrics.period_users >= 3 ? '📈' : 'ℹ️'}
                                </span>
                                <div className="text-sm flex-1">
                                  <div className={`font-bold ${adminAnalytics.user_metrics.period_users >= 3 ? 'text-green-900' : 'text-blue-900'}`}>
                                    {adminAnalytics.user_metrics.period_users >= 3 ? 'Strong User Growth Detected' : 'User Growth Tracked'}
                                  </div>
                                  <div className={`text-xs mt-1 ${adminAnalytics.user_metrics.period_users >= 3 ? 'text-green-800' : 'text-blue-800'}`}>
                                    +{adminAnalytics.user_metrics.period_users} new users this period ({((adminAnalytics.user_metrics.period_users / adminAnalytics.user_metrics.total_users) * 100).toFixed(1)}% growth rate)
                                    {adminAnalytics.user_metrics.period_users >= 3 && ' - Consider scaling support capacity'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Revenue & Conversion Analysis */}
                          {adminAnalytics.revenue_metrics?.total_revenue > 0 ? (
                            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 rounded">
                              <div className="flex items-start gap-2">
                                <span className="text-purple-600 font-bold text-lg">💰</span>
                                <div className="text-sm flex-1">
                                  <div className="font-bold text-purple-900">
                                    Revenue Generation Active
                                  </div>
                                  <div className="text-xs text-purple-800 mt-1">
                                    ${(adminAnalytics.revenue_metrics.total_revenue || 0).toFixed(2)} revenue with {adminAnalytics.revenue_metrics?.paying_users || 0} paying user{(adminAnalytics.revenue_metrics?.paying_users || 0) !== 1 ? 's' : ''}
                                    {adminAnalytics.conversion_rate > 0 && ` (${adminAnalytics.conversion_rate.toFixed(1)}% conversion rate)`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="p-3 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded">
                              <div className="flex items-start gap-2">
                                <span className="text-amber-600 font-bold text-lg">⚡</span>
                                <div className="text-sm flex-1">
                                  <div className="font-bold text-amber-900">
                                    Monetization Opportunity
                                  </div>
                                  <div className="text-xs text-amber-800 mt-1">
                                    {adminAnalytics.user_metrics.active_7_days} active users with no revenue yet - Focus on conversion strategies
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Service Performance Pattern */}
                          {(() => {
                            const bctg = adminAnalytics.service_metrics?.bctg?.total_generations || 0;
                            const mmkg = adminAnalytics.service_metrics?.mmkg?.total_kits || 0;
                            const ibg = adminAnalytics.service_metrics?.ibg?.total_generations || 0;
                            const topService = bctg >= mmkg ? (bctg >= ibg ? 'BCTG' : 'IBG') : (mmkg >= ibg ? 'MMKG' : 'IBG');
                            const topCount = Math.max(bctg, mmkg, ibg);
                            
                            if (topCount > 0) {
                              return (
                                <div className="p-3 bg-gradient-to-r from-indigo-50 to-blue-50 border-l-4 border-indigo-500 rounded">
                                  <div className="flex items-start gap-2">
                                    <span className="text-indigo-600 font-bold text-lg">⭐</span>
                                    <div className="text-sm flex-1">
                                      <div className="font-bold text-indigo-900">
                                        {topService} Leading Service Usage
                                      </div>
                                      <div className="text-xs text-indigo-800 mt-1">
                                        {topCount} uses detected - {topService === 'IBG' ? 'Free tool driving leads' : 'Premium service generating revenue'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          })()}

                          {/* Engagement Insight */}
                          {adminAnalytics.user_metrics.active_7_days > 0 && (
                            (() => {
                              const engagementRate = (adminAnalytics.user_metrics.active_7_days / adminAnalytics.user_metrics.total_users) * 100;
                              const isHighEngagement = engagementRate > 50;
                              return (
                                <div className={`p-3 bg-gradient-to-r ${isHighEngagement ? 'from-cyan-50 to-blue-50 border-cyan-500' : 'from-yellow-50 to-amber-50 border-yellow-500'} border-l-4 rounded`}>
                                  <div className="flex items-start gap-2">
                                    <span className={`${isHighEngagement ? 'text-cyan-600' : 'text-yellow-600'} font-bold text-lg`}>
                                      {isHighEngagement ? '🔥' : '💡'}
                                    </span>
                                    <div className="text-sm flex-1">
                                      <div className={`font-bold ${isHighEngagement ? 'text-cyan-900' : 'text-yellow-900'}`}>
                                        {isHighEngagement ? 'High User Engagement' : 'Engagement Opportunity'}
                                      </div>
                                      <div className={`text-xs mt-1 ${isHighEngagement ? 'text-cyan-800' : 'text-yellow-800'}`}>
                                        {adminAnalytics.user_metrics.active_7_days} of {adminAnalytics.user_metrics.total_users} users active ({engagementRate.toFixed(1)}%)
                                        {!isHighEngagement && ' - Consider re-engagement campaigns'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </>
                      ) : (
                        <div className="text-center py-6 bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg border-2 border-dashed border-slate-300">
                          <span className="text-4xl mb-2 block">🤖</span>
                          <div className="text-sm font-medium text-slate-700 mb-1">AI Insights Awaiting Data</div>
                          <div className="text-xs text-slate-500 max-w-sm mx-auto">
                            Intelligent patterns and recommendations will appear here as your platform accumulates user activity, revenue, and service usage data.
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* System Status */}
              <Card className="mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span>🔔</span> System Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                      <span className="text-green-600 text-xl">✓</span>
                      <div>
                        <div className="text-sm font-medium text-green-900">All Systems</div>
                        <div className="text-xs text-green-700">Operational</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <span className="text-blue-600 text-xl">⚡</span>
                      <div>
                        <div className="text-sm font-medium text-blue-900">Response Time</div>
                        <div className="text-xs text-blue-700">Fast (&lt;200ms)</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <span className="text-purple-600 text-xl">📊</span>
                      <div>
                        <div className="text-sm font-medium text-purple-900">Uptime</div>
                        <div className="text-xs text-purple-700">99.9%</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Begin Your Publishing Journey Section */}
        {!isAdmin && (
          <>
            <div className="mb-6 sm:mb-8 max-w-4xl mx-auto px-2 sm:px-0">
              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 border-2 sm:border-4 border-indigo-200 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg sm:shadow-[8px_8px_0px_0px_rgba(99,102,241,0.3)] hover:shadow-xl sm:hover:shadow-[12px_12px_0px_0px_rgba(99,102,241,0.4)] transform hover:-translate-y-1 transition-all duration-300">
                <div className="text-center">
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-2 sm:mb-3 break-words">
                    🚀 YOUR PUBLISHING TOOLKIT
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg text-slate-700 mb-4 sm:mb-6 md:mb-8 break-words px-2">
                    Ready to transform your ideas into published works?
                  </p>
                  <Button
                    onClick={() => setShowJourneyModal(true)}
                    size="lg"
                    className="w-full sm:w-auto bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold text-base sm:text-lg md:text-xl px-6 sm:px-8 md:px-12 py-3 sm:py-4 md:py-6 rounded-lg sm:rounded-xl shadow-xl transform hover:scale-105 transition-all"
                  >
                    ✨ BEGIN YOUR JOURNEY
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* DiAi Welcome Banner - Learn More About Digital Ink AI */}
        {!isAdmin && (
          <div className="mb-6 max-w-5xl mx-auto px-2 sm:px-0">
            <Card className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-2 border-blue-200 shadow-md hover:shadow-lg transition-shadow">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
                  {/* DiAi Image */}
                  <div className="flex-shrink-0">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_author-ai-beta/artifacts/lad4xvs5_DiAi_Image.jpg"
                      alt="DiAi - Your Digital Ink AI Assistant"
                      className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 rounded-full border-4 border-white shadow-lg object-cover"
                    />
                  </div>
                  
                  {/* Welcome Message */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                      <span className="text-xl sm:text-2xl">👋</span>
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-slate-800">
                        Hi {(() => {
                          const fullName = user?.name || user?.first_name;
                          if (!fullName) return 'there';
                          const parts = fullName.trim().split(' ');
                          if (parts.length === 1) return parts[0];
                          const firstPart = parts[0];
                          const isInitial = firstPart.length <= 2 && (firstPart.length === 1 || firstPart.endsWith('.'));
                          if (isInitial && parts.length > 1) return `${parts[0]} ${parts[1]}`;
                          return firstPart;
                        })()}, I'm DiAi. Your Digital Ink AI Guide
                      </h3>
                    </div>
                    <p className="text-slate-700 mb-3 sm:mb-4 text-xs sm:text-sm md:text-base">
                      New to Digital Ink AI? I'm here to help you understand how each service works, discover best practices, and make the most of your authorship journey. Let me guide you through our comprehensive documentation!
                    </p>
                    <Button
                      onClick={() => navigate('/help-center')}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md w-full sm:w-auto"
                      size="lg"
                    >
                      📚 Explore Help Center & Service Guides
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Subscription CTA Banner - For Free & A La Carte Users */}
        {!isAdmin && (user?.role === 'free_user' || user?.role === 'a_la_carte') && (
          <div className="mb-6 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-xl shadow-xl overflow-hidden">
              <div className="p-6 md:p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  {/* Left: Headline & Value Props */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-block bg-yellow-300 text-black text-xs font-bold px-3 py-1 rounded-full mb-2 animate-pulse">
                      💰 SAVE MONEY
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">
                      Subscribe & Save!
                    </h3>
                    <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm text-white">
                      <span className="font-semibold">$20/mo = Unlimited</span>
                      <span>50% Off Premium</span>
                      <span>Daily Rewards</span>
                    </div>
                  </div>

                  {/* Right: CTA Button */}
                  <div className="flex-shrink-0">
                    <Button
                      onClick={handleOpenSubscriptionModal}
                      className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-8 py-4 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200"
                    >
                      🚀 Subscribe TODAY!
                    </Button>
                  </div>
                </div>

                {/* Trust Signals - Compact */}
                <div className="mt-3 pt-3 border-t border-white/20 flex flex-wrap justify-center gap-3 text-xs text-white/90">
                  <span>✅ 14-Day Trial</span>
                  <span>✅ Cancel Anytime</span>
                  <span>✅ Full Refund</span>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Show user-specific sections only for non-admins */}
        {!isAdmin && (
          <>
            {/* Enhanced Activity & Rewards Tracking Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              
              {/* Activity Mini-Dashboard */}
              <div className="lg:col-span-2 p-6 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">📈 Your Creative Journey</h2>
                    <p className="text-sm text-slate-600">Last 30 days</p>
                  </div>
                  <Button 
                    onClick={() => navigate('/user_dashboard')}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 min-h-[44px] btn-mobile-primary"
                  >
                    View Dashboard
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {userStats.totalBctgGenerated > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=bctg')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-blue-600">{userStats.totalBctgGenerated}</div>
                          <div className="text-sm text-blue-700">Concepts Created</div>
                        </div>
                        <div className="text-9xl">💡</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-blue-600 hover:bg-blue-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalBocpOutlines > 0 && (
                    <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=bocp')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-indigo-600">{userStats.totalBocpOutlines}</div>
                          <div className="text-sm text-indigo-700">Outlines Generated</div>
                        </div>
                        <div className="text-9xl">📚</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-indigo-600 hover:bg-indigo-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalCcgCasts > 0 && (
                    <div className="bg-gradient-to-r from-pink-50 to-pink-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=ccg')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-pink-600">{userStats.totalCcgCasts}</div>
                          <div className="text-sm text-pink-700">Character Casts</div>
                        </div>
                        <div className="text-9xl">🎭</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-pink-600 hover:bg-pink-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalAbgBios > 0 && (
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=abg')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-green-600">{userStats.totalAbgBios}</div>
                          <div className="text-sm text-green-700">Author Bios</div>
                        </div>
                        <div className="text-9xl">👤</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-green-600 hover:bg-green-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalErmtResults > 0 && (
                    <div className="bg-gradient-to-r from-rose-50 to-rose-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=ermt')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-rose-600">{userStats.totalErmtResults}</div>
                          <div className="text-sm text-rose-700">EPUBs Created</div>
                        </div>
                        <div className="text-9xl">📕</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-rose-600 hover:bg-rose-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalPmpbResults > 0 && (
                    <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=pmpb')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-amber-600">{userStats.totalPmpbResults}</div>
                          <div className="text-sm text-amber-700">PDFs Merged</div>
                        </div>
                        <div className="text-9xl">📄</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-amber-600 hover:bg-amber-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}

                  {userStats.totalAkswResults > 0 && (
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=aksw')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-blue-600">{userStats.totalAkswResults}</div>
                          <div className="text-sm text-blue-700">KDP Guides</div>
                        </div>
                        <div className="text-9xl">📘</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-blue-600 hover:bg-blue-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalMmkgResults > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=mmkg')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-orange-600">{userStats.totalMmkgResults}</div>
                          <div className="text-sm text-orange-700">Media Kits</div>
                        </div>
                        <div className="text-9xl">📰</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-orange-600 hover:bg-orange-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}

                  
                  {userStats.totalBvadmAnalyses > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=bvadm')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-purple-600">{userStats.totalBvadmAnalyses}</div>
                          <div className="text-sm text-purple-700">Books Analyzed</div>
                        </div>
                        <div className="text-9xl">📊</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-purple-600 hover:bg-purple-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalCcatResults > 0 && (
                    <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=ccat')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-teal-600">{userStats.totalCcatResults}</div>
                          <div className="text-sm text-teal-700">Comp Analyses</div>
                        </div>
                        <div className="text-9xl">🔍</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-teal-600 hover:bg-teal-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalBrecResults > 0 && (
                    <div className="bg-gradient-to-r from-cyan-50 to-cyan-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=brec')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-cyan-600">{userStats.totalBrecResults}</div>
                          <div className="text-sm text-cyan-700">Review Campaigns</div>
                        </div>
                        <div className="text-9xl">📣</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-cyan-600 hover:bg-cyan-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalMmgReports > 0 && (
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=mmg')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-orange-600">{userStats.totalMmgReports}</div>
                          <div className="text-sm text-orange-700">Metadata Reports</div>
                        </div>
                        <div className="text-9xl">🏷️</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-orange-600 hover:bg-orange-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalPdeResults > 0 && (
                    <div className="bg-gradient-to-r from-violet-50 to-violet-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=pde')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-violet-600">{userStats.totalPdeResults}</div>
                          <div className="text-sm text-violet-700">Dev Edits</div>
                        </div>
                        <div className="text-9xl">✍️</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-violet-600 hover:bg-violet-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalKsgResults > 0 && (
                    <div className="bg-gradient-to-r from-fuchsia-50 to-fuchsia-100 rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/user_dashboard?service=ksg')}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <div className="text-2xl font-bold text-fuchsia-600">{userStats.totalKsgResults}</div>
                          <div className="text-sm text-fuchsia-700">Keynote Speeches</div>
                        </div>
                        <div className="text-9xl">🎤</div>
                      </div>
                      <Button size="sm"
                      className="w-full sm:w-auto w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs">View Dashboard →</Button>
                    </div>
                  )}
                  
                  {userStats.totalBctgGenerated === 0 && 
                   userStats.totalBocpOutlines === 0 && 
                   userStats.totalMmgReports === 0 && 
                   userStats.totalBvadmAnalyses === 0 && 
                   userStats.totalCcgCasts === 0 &&
                   userStats.totalAbgBios === 0 &&
                   userStats.totalErmtResults === 0 &&
                   userStats.totalPmpbResults === 0 &&
                   userStats.totalCcatResults === 0 &&
                   userStats.totalBrecResults === 0 &&
                   userStats.totalPdeResults === 0 &&
                   userStats.totalKsgResults === 0 && (
                    <div className="col-span-2 text-center py-8">
                      <div className="text-6xl mb-3">🚀</div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Start Your Creative Journey</h3>
                      <p className="text-sm text-slate-600">
                        No activity in the last 30 days. Create your first project to get started!
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Enhanced Rewards Section */}
              <div className="p-6 bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 rounded-xl border border-orange-200 shadow-sm">
                <h2 className="text-xl font-bold text-slate-900 mb-4">🎁 Rewards Hub</h2>
                
                {/* Points Tracking */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-slate-700">Points Balance</span>
                    <span className="text-sm text-slate-600">{userStats.rewardsPoints}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-yellow-400 to-orange-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((userStats.rewardsPoints / 1000) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-slate-600 mt-2">
                    Earn points through platform usage and referrals
                  </p>
                  <div className="mt-3">
                    <Button
                      onClick={() => navigate('/rewards')}
                      variant="outline"
                      className="w-full text-sm border-orange-300 text-orange-700 hover:border-orange-400 hover:text-orange-800 py-2 rounded-lg min-h-[40px] btn-mobile-secondary"
                    >
                      📊 View Rewards Tracking →
                    </Button>
                  </div>
                </div>

                {/* Referral Incentive */}
                <div className="bg-white/70 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">💫 Invite & Earn</h3>
                  <p className="text-sm text-slate-600 mb-3">
                    Invite a friend, earn <span className="font-bold text-orange-600">200 points</span> when they become a paid subscriber!
                  </p>
                  <Button 
                    onClick={() => setShowReferralModal(true)}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-medium py-2 rounded-lg min-h-[44px] btn-mobile-primary"
                  >
                    👥 Refer A Friend
                  </Button>
                </div>

              </div>
            </div>

            {/* Strategic Advertising Card - Digital Ink Book Promotion */}
            <div className="mt-8 mb-8">
              <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 rounded-2xl border-2 border-yellow-200 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="flex flex-col md:flex-row items-center gap-6 p-6">
                  {/* Book Cover */}
                  <div className="flex-shrink-0">
                    <img 
                      src="/Digital_Ink_Book_Cover.jpg" 
                      alt="Digital Ink Book Cover" 
                      className="w-32 h-auto rounded-lg shadow-xl transform hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  
                  {/* Book Info & CTA */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="inline-flex items-center gap-2 bg-yellow-600 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                      <span>📚</span> Featured Book
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">
                      "Digital Ink: How AI is Rewriting Publishing"
                    </h3>
                    <p className="text-slate-600 mb-4 leading-relaxed">
                      Master the strategies behind this platform. Learn how to embed marketing into your book's DNA and build a sustainable author platform in the AI era.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                      <a 
                        href="https://books.by/use-what-you-got-publishing?#digital-ink" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg min-h-[44px] btn-mobile-primary"
                      >
                        📖 Order Digital Ink Today
                      </a>
                      <a 
                        href="https://www.rdbrunet.com" 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center gap-2 border-2 border-slate-400 text-slate-700 hover:border-slate-600 hover:text-slate-900 font-semibold py-3 px-6 rounded-lg transition-all duration-300 min-h-[44px] btn-mobile-secondary"
                      >
                        🔍 More by R. Dale Brunet
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Old analytics timeframe section removed - replaced by real-time Control Panel above */}

        {/* Service Access Validation Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <div className="text-center mb-4">
                <div className="text-4xl mb-3">
                  {(paymentModalType === 'bctg' || paymentModalType === 'bctg_payment') ? '💡' : '💳'}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {paymentModalType === 'bctg' ? 'BCTG Usage Limit Reached' : 'Payment Method Required'}
                </h3>
                <p className="text-slate-600">
                  {paymentModalType === 'bctg' 
                    ? 'You\'ve used your 3 free BCTG concepts. Subscribe or pay per use to continue.'
                    : paymentModalType === 'bctg_payment'
                    ? 'BCTG requires a payment method on file to proceed with concept generation.'
                    : 'BVADM requires a payment method on file to proceed with manuscript analysis.'
                  }
                </p>
              </div>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => {
                    setShowPaymentModal(false);
                    if (paymentModalType === 'bctg') {
                      navigate('/prices'); // Redirect to subscription options
                    } else {
                      navigate('/profile?tab=payment'); // Redirect to payment setup
                    }
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-lg min-h-[44px] btn-mobile-primary"
                >
                  {paymentModalType === 'bctg' ? '🚀 View Subscription Options' : '💳 Setup Payment Method'}
                </Button>
                
                <Button 
                  onClick={() => setShowPaymentModal(false)}
                  variant="outline"
                  className="w-full border-slate-300 text-slate-700 hover:border-slate-400 hover:text-slate-900 py-3 px-6 rounded-lg min-h-[44px] btn-mobile-secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

      </main>
      
      <GlobalFooter />

      {/* Renewal Confirmation Modal */}
      {showRenewalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">
              Confirm Subscription Renewal
            </h3>
            <div className="mb-6">
              <p className="text-slate-600 mb-4">
                You are about to renew your <strong>{user?.subscription_status}</strong> subscription.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-700">Plan:</span>
                  <span className="font-semibold">{user?.subscription_status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-700">Amount:</span>
                  <span className="font-semibold text-blue-600">
                    ${user?.subscription_status === 'Monthly' ? '10.00' : '100.00'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Your payment method on file will be charged immediately, and your subscription will be extended for another {user?.subscription_status === 'Monthly' ? 'month' : 'year'}.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleSubscriptionRenewal}
                disabled={renewalProcessing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {renewalProcessing ? 'Processing...' : 'Confirm Renewal'}
              </Button>
              <Button
                onClick={() => setShowRenewalModal(false)}
                disabled={renewalProcessing}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Referral Modal */}
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => {
          setShowReferralModal(false);
          // Refresh user stats after sending referral
          const fetchUserStats = async () => {
            try {
              const rewardsResponse = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/rewards/balance`,
                { withCredentials: true }
              );
              setUserStats(prev => ({
                ...prev,
                rewardsPoints: rewardsResponse.data.points_balance || 0
              }));
            } catch (error) {
            }
          };
          fetchUserStats();
        }}
      />

      {/* Payment Setup Modal */}
      <PaymentMethodSetup
        isOpen={showPaymentSetupModal}
        onClose={handlePaymentSetupCancel}
        onSuccess={handlePaymentSetupSuccess}
        pendingService={pendingServiceNavigation === 'subscription' ? 'Subscription' : ''}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        onConfirm={async (subscriptionType) => {
          // Handle subscription confirmation
          setShowSubscriptionModal(false);
          // Optionally refresh user data
          window.location.reload();
        }}
        user={user}
        onPaymentSetupRequired={() => {
          // Close subscription modal and open payment setup modal
          setShowSubscriptionModal(false);
          setShowPaymentSetupModal(true);
          setPendingServiceNavigation('subscription'); // Mark that we need to return to subscription
        }}
      />

      {/* Begin Journey Modal */}
      {showJourneyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <Card className="max-w-full sm:max-w-xl md:max-w-2xl w-full my-4">
            <CardHeader className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-4 sm:p-6">
              <div className="flex flex-col items-center mb-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                  alt="Digital Ink AI logo" 
                  className="h-12 sm:h-16 md:h-20 w-auto mb-2 sm:mb-3"
                />
                <CardTitle className="text-lg sm:text-xl md:text-2xl text-center break-words">🚀 Begin Your Publishing Journey</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <p className="text-slate-900 mb-4 sm:mb-6 text-base sm:text-lg md:text-2xl font-bold text-center break-words">What would you like to do today?</p>
              <select
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base mb-4 sm:mb-6"
              >
                <option value="">-- Choose what you'd like to do --</option>
                <option value="bctg">📚 I want to create a new book based on an idea I have</option>
                <option value="bocp">📑 I need help outlining my book's structure and chapters</option>
                <option value="ccg">🎭 I want to develop compelling characters for my story</option>
                <option value="abg">👤 I need to create a professional author bio</option>
                <option value="pde">✍️ I have a manuscript that needs professional editing feedback</option>
                <option value="ermt">📕 I need to convert my manuscript to EPUB format</option>
                <option value="pmpb">📄 I want to merge multiple PDFs into a print-ready file</option>
                <option value="aksw">📤 I want to prepare my book for Amazon KDP submission</option>
                <option value="bvadm">📊 I want to analyze my manuscript's market viability</option>
                <option value="ccat">🔍 I need competitive analysis for my book's market position</option>
                <option value="brec">📣 I want to launch a book review and endorsement campaign</option>
                <option value="mmg">📈 I need metadata and marketing materials for my book</option>
                <option value="mmkg">📰 I want to create a professional media kit for my book</option>
                <option value="smmk">📱 I want to create a social media marketing campaign for my book</option>
                <option value="ksg">🎤 I want to create keynote speeches about my book</option>
              </select>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => {
                    setShowJourneyModal(false);
                    setSelectedService('');
                  }}
                  variant="outline"
                  className="w-full sm:flex-1 text-sm sm:text-base"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (selectedService) {
                      
                      // Close modal
                      setShowJourneyModal(false);
                      
                      // Navigate to user dashboard with state (NOT URL parameter)
                      navigate('/user_dashboard', { state: { service: selectedService } });
                      
                      // Clear selection
                      setSelectedService('');
                    } else {
                      toast.error('Please select what you would like to do');
                    }
                  }}
                  disabled={!selectedService}
                  className="w-full sm:flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-sm sm:text-base"
                >
                  Let's Go! →
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Helper function to extract Literary Hook (first 2 complete sentences)
// Used by both BCTGPage and ActivityPage components
const extractLiteraryHook = (summary) => {
  if (!summary) return '';
  
  // Regex to match sentences: period/question/exclamation followed by space and capital letter or end of string
  // This handles abbreviations better by looking for space + capital letter after period
  const sentenceRegex = /[^.!?]+[.!?]+(?=\s+[A-Z]|$)/g;
  const sentences = summary.match(sentenceRegex) || [];
  
  // Return first 2 sentences, or all if less than 2
  const hookSentences = sentences.slice(0, 2);
  return hookSentences.join(' ').trim();
};

// Enhanced BCTG Component
const BCTGPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check where user came from
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  const [formData, setFormData] = useState({
    genre: '',
    custom_genre: '',
    theme: '',
    custom_theme: '',
    target_audience: '',
    custom_target_audience: '',
    concept_description: '',
    setting_country: '',
    setting_city: '',
    setting_region: '',
    timeframe_era: ''
  });
  const [loading, setLoading] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [suggestionUsed, setSuggestionUsed] = useState(false);
  const [suggestionCount, setSuggestionCount] = useState(0); // Track suggestion usage
  const [results, setResults] = useState(null);
  const [showChargeConfirmation, setShowChargeConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);

  // Load specific report if report ID is in URL
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadReportById(reportId);
    }
  }, [searchParams]);

  const loadReportById = async (reportId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/bctg/results/${reportId}`,
        { withCredentials: true }
      );
      
      if (response.data) {
        setResults(response.data);
        // Scroll to results
        setTimeout(() => {
          const resultsSection = document.getElementById('bctg-results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      toast.error('Failed to load report');
      navigate('/user_dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewBCTG = async () => {
    // Reset results and form
    setResults(null);
    setFormData({
      genre: '',
      custom_genre: '',
      theme: '',
      custom_theme: '',
      target_audience: '',
      custom_target_audience: '',
      concept_description: '',
      setting_country: '',
      setting_city: '',
      setting_region: '',
      timeframe_era: ''
    });
    
    // Check usage and payment validation
    try {
      const usageResponse = await axios.get(`${API}/bctg/check-usage`, { withCredentials: true });
      const usageInfo = usageResponse.data;
      
      const freeUsesThisMonth = usageInfo.free_usage_count || 0;
      const hasPaymentMethod = !!(user.payment_method || user.stripe_customer_id);
      
      // If user has free uses remaining or has payment method, navigate to form
      if (freeUsesThisMonth < 3 || hasPaymentMethod) {
        navigate('/bctg');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Show payment validation modal
        toast.info('Payment method required to continue');
        navigate('/profile?tab=payment');
      }
    } catch (error) {
      // On error, just navigate to form
      navigate('/bctg');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleImportDescription = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5MB");
      return;
    }

    // Check file type
    const allowedTypes = ['.txt', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error("Please upload a .txt, .doc, or .docx file");
      return;
    }

    try {
      const text = await file.text();
      setFormData(prev => ({
        ...prev,
        concept_description: text
      }));
      toast.success("Description imported successfully!");
    } catch (error) {
      toast.error("Failed to import file. Please try again.");
    }

    // Reset file input
    event.target.value = '';
  };

  const handleSuggestConcept = async () => {
    // Check if suggestion limit has been reached
    if (suggestionCount >= 5) {
      toast.error("Exceeded Content Suggestion Max");
      return;
    }
    
    if (!formData.genre && !formData.custom_genre) {
      toast.error("Please select a genre first");
      return;
    }
    if (!formData.theme && !formData.custom_theme) {
      toast.error("Please select a theme first");
      return;
    }

    setSuggestionLoading(true);
    try {
      const response = await axios.post(`${API}/bctg/suggest-concept`, {
        genre: formData.custom_genre || formData.genre,
        theme: formData.custom_theme || formData.theme,
        target_audience: formData.target_audience === 'Custom Audience' ? formData.custom_target_audience : formData.target_audience,
        setting_country: formData.setting_country,
        setting_city: formData.setting_city,
        setting_region: formData.setting_region,
        timeframe_era: formData.timeframe_era
      }, { withCredentials: true });

      setFormData(prev => ({
        ...prev,
        concept_description: response.data.suggestion
      }));
      setSuggestionUsed(true); // Mark that suggestion has been used
      setSuggestionCount(prev => prev + 1); // Increment suggestion count
      toast.success("Concept suggestion generated!");
    } catch (error) {
      toast.error("Failed to generate suggestion");
    } finally {
      setSuggestionLoading(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const genre = formData.genre || formData.custom_genre;
    const theme = formData.theme || formData.custom_theme;
    const conceptDescription = formData.concept_description;
    
    if (!genre) {
      toast.error("Please select or enter a genre");
      return;
    }
    
    if (!theme) {
      toast.error("Please select or enter a theme");
      return;
    }
    
    if (!conceptDescription || conceptDescription.trim().length === 0) {
      toast.error("Please provide a concept description for your book");
      return;
    }
    
    if (conceptDescription.trim().length < 10) {
      toast.error("Concept description must be at least 10 characters");
      return;
    }
    
    // First check usage limits to determine if we need charge confirmation
    try {
      const usageResponse = await axios.get(`${API}/bctg/check-usage`, { withCredentials: true });
      const usageInfo = usageResponse.data;
      
      // Use backend data (most up-to-date)
      const freeUsesThisMonth = usageInfo.free_usage_count || 0;
      const totalUsesThisMonth = usageInfo.usage_count || 0;
      const remainingFreeUses = usageInfo.remaining_free || 0;
      const userRole = usageInfo.user_role || user?.role;
      
      // Admin and beta_tester get unlimited access
      if (userRole === 'admin' || userRole === 'beta_tester') {
        await proceedWithGeneration();
        return;
      }
      
      // Users with active subscription (subscription_status != "None") get UNLIMITED STANDARD service access
      const subscriptionStatus = user?.subscription_status || 'None';
      if (subscriptionStatus !== 'None') {
        await proceedWithGeneration();
        return;
      }
      
      // For users without subscription (free_user and a_la_carte): 
      // Check if remaining free uses <= 0 (not limit_exceeded)
      // (remainingFreeUses already declared above on line 2921)
      
      if (remainingFreeUses <= 0) {
        setShowChargeConfirmation(true);
        return;
      }
      
      // If free user exceeded limit, redirect to upgrade
      if (usageInfo.limit_exceeded && user?.role === 'free_user') {
        toast.error("You've used your 3 free BCTG generations this month. Please upgrade to continue.");
        setTimeout(() => navigate('/profile?tab=subscription'), 2000);
        return;
      }
      
      // Proceed with generation (user has free uses remaining)
      await proceedWithGeneration();
      
    } catch (error) {
      // If usage check fails, still try to proceed
      await proceedWithGeneration();
    }
  };

  const proceedWithGeneration = async () => {
    setLoading(true);
    setShowChargeConfirmation(false);
    setIsProcessing(true); // Show processing modal

    try {
      // First check usage limits
      const usageResponse = await axios.get(`${API}/bctg/usage-check`, { withCredentials: true });
      const usageInfo = usageResponse.data;

      // If payment is required, show payment capture modal
      if (usageInfo.needs_payment_capture || 
          (usageInfo.limit_exceeded && !usageInfo.has_payment_method)) {
        setLoading(false);
        setIsProcessing(false);
        showPaymentCaptureModal(usageInfo);
        return;
      }

      // Proceed with BCTG generation
      const requestData = {
        core_theme: formData.custom_theme || formData.theme,
        target_audience: formData.target_audience === 'Custom Audience' ? formData.custom_target_audience : formData.target_audience,
        genre: formData.custom_genre || formData.genre,
        custom_genre: formData.custom_genre,
        custom_theme: formData.custom_theme,
        concept_description: formData.concept_description,
        additional_info: formData.concept_description,
        setting_country: formData.setting_country,
        setting_city: formData.setting_city,
        setting_region: formData.setting_region,
        timeframe_era: formData.timeframe_era
      };

      const response = await axios.post(`${API}/bctg/generate`, requestData, { withCredentials: true });
      
      // Verify response has data
      if (!response.data || !response.data.concepts) {
        throw new Error("Invalid response from server");
      }
      
      setResults(response.data);
      
      // Check if there was a charge AND transaction_id exists
      if (response.data.charged === true && response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
        setLastCharge({
          amount: response.data.amount || 2.50,
          serviceName: "Book Concept & Title Generator (BCTG)",
          bookTitle: `${formData.custom_genre || formData.genre} - ${formData.custom_theme || formData.theme}`
        });
        // Show email receipt modal after processing
        setTimeout(() => {
          setIsProcessing(false);
          setShowEmailReceiptModal(true);
        }, 500);
      } else {
        setIsProcessing(false);      }
      
      // Set flag to refresh usage counter when returning to Dashboard
      localStorage.setItem('serviceCompleted', 'BCTG');
      
      // Trigger a custom event to notify Dashboard to refresh stats
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
    } catch (error) {
      setIsProcessing(false);
      
      // Only show error if results weren't actually set
      if (!results || !results.concepts) {
        if (error.response?.status === 402) {
          // Payment required error from backend
          const errorData = error.response.data;
          showPaymentCaptureModal(errorData.usage_info || {});
        } else {
          toast.error(error.response?.data?.detail || error.message || "Failed to generate concepts");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const showPaymentCaptureModal = (usageInfo) => {
    // Create a custom modal for payment capture
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white p-8 rounded-lg max-w-md w-full mx-4">
        <h3 class="text-xl font-bold mb-4">Add Payment Method</h3>
        <p class="text-gray-600 mb-4">
          ${usageInfo.remaining_free === 0 
            ? 'You\'ve used your 3 free BCTG generations this month. Add a payment method to continue using our services.'
            : 'To continue using premium features, please add a payment method.'
          }
        </p>
        <div class="flex space-x-4">
          <button id="add-payment" class="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex-1">
            Add Payment Method
          </button>
          <button id="cancel-payment" class="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 flex-1">
            Cancel
          </button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Handle modal actions
    document.getElementById('add-payment').onclick = () => {
      document.body.removeChild(modal);
      window.location.href = '/profile?tab=payment';
    };
    
    document.getElementById('cancel-payment').onclick = () => {
      document.body.removeChild(modal);
    };
    
    // Close on outside click
    modal.onclick = (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    };
  };

  const handleSendEmailReceipt = async () => {
    try {
      
      if (!lastTransactionId) {
        toast.error("No transaction ID found. Receipt already sent automatically.");
        setShowEmailReceiptModal(false);
        return;
      }
      
      const response = await axios.post(`${API}/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, { withCredentials: true });
      toast.success("Receipt email resent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      toast.error(`Receipt email not sent: ${error.response?.data?.detail || error.message}`);
      // Still close the modal even if email sending fails
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);  };

  const resetForm = () => {
    setResults(null);
    setFormData({
      genre: '',
      custom_genre: '',
      theme: '',
      custom_theme: '',
      target_audience: '',
      custom_target_audience: '',
      concept_description: '',
      setting_country: '',
      setting_city: '',
      setting_region: '',
      timeframe_era: ''
    });
  };

  // Auto-save functionality removed - results are now saved automatically by backend

  const exportToPDF = () => {
    if (!results) {
      toast.error("No results to export");
      return;
    }
    
    try {      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 170;
      const leftMargin = 20;
      let yPosition = 20;
      
      // Helper function for page breaks
      const checkNewPage = (spaceNeeded) => {
        if (yPosition + spaceNeeded > 280) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to add multi-line text
      const addMultiLineText = (text, fontSize = 10, maxWidth = pageWidth) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          checkNewPage(7);
          pdf.text(line, leftMargin, yPosition);
          yPosition += 6;
        });
      };
      
      // HEADER
      const timestamp = new Date().toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(timestamp, leftMargin, yPosition);
      pdf.text('Digital Ink AI - BCTG Report', 190, yPosition, { align: 'right' });
      yPosition += 10;
      
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('DIGITAL INK AI', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Book Concept & Title Generator', 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // GENERATION DETAILS
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Generation Details', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      if (results.genre) {
        pdf.text(`Genre: ${results.genre}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (results.theme) {
        pdf.text(`Theme: ${results.theme}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (results.target_audience) {
        pdf.text(`Target Audience: ${results.target_audience}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (results.physical_location || results.setting_country) {
        pdf.text(`Location: ${results.physical_location || results.setting_country}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (results.region || results.setting_region) {
        pdf.text(`Region: ${results.region || results.setting_region}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (results.timeframe_era) {
        pdf.text(`Timeframe: ${results.timeframe_era}`, leftMargin, yPosition);
        yPosition += 6;
      }
      yPosition += 5;
      
      // MARKET INSIGHTS
      checkNewPage(20);
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Market Insights', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      addMultiLineText(results.market_insights || '');
      yPosition += 5;
      
      if (results.recommended_hashtags && results.recommended_hashtags.length > 0) {
        pdf.setFont(undefined, 'bold');
        pdf.text('Hashtags:', leftMargin, yPosition);
        yPosition += 6;
        pdf.setFont(undefined, 'normal');
        addMultiLineText(results.recommended_hashtags.join(' '));
        yPosition += 5;
      }
      
      // CONCEPTS
      if (results.concepts && results.concepts.length > 0) {
        results.concepts.forEach((concept, index) => {
          checkNewPage(30);
          yPosition += 5;
          
          // Concept Title
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text(`Concept ${index + 1}: ${concept.title}`, leftMargin, yPosition);
          yPosition += 8;
          
          // Subtitle
          if (concept.subtitle) {
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'italic');
            pdf.setTextColor(100, 100, 100);
            addMultiLineText(concept.subtitle, 11);
            yPosition += 3;
          }
          
          pdf.setTextColor(0, 0, 0);
          
          // Literary Hook
          checkNewPage(15);
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          pdf.text('Literary Hook:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          const hookText = concept.literary_hook || extractLiteraryHook(concept.summary);
          addMultiLineText(hookText);
          yPosition += 5;
          
          // Complete Concept
          checkNewPage(15);
          pdf.setFont(undefined, 'bold');
          pdf.text('Complete Book Concept:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          addMultiLineText(concept.summary);
          yPosition += 5;
          
          // Why This Will Sell
          checkNewPage(15);
          pdf.setFont(undefined, 'bold');
          pdf.text('Why This Will Sell:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          addMultiLineText(concept.market_appeal || 'No market appeal data available.');
          yPosition += 5;
          
          // Chapter Outline
          checkNewPage(15);
          pdf.setFont(undefined, 'bold');
          pdf.text('Chapter Outline:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          
          if (concept.chapter_outline && concept.chapter_outline.length > 0) {
            concept.chapter_outline.forEach((chapter, chIdx) => {
              checkNewPage(8);
              pdf.text(`Chapter ${chIdx + 1}: ${chapter}`, leftMargin + 5, yPosition);
              yPosition += 6;
            });
          } else {
            pdf.text('No chapter outline available.', leftMargin + 5, yPosition);
            yPosition += 6;
          }
          
          yPosition += 10;
        });
      }
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: 'center' });
        pdf.text('www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `BCTG_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);    } catch (error) {
      toast.error("PDF generation failed");
    }
  };

  const printResults = () => {
    if (!results) return;
    
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }
    
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>BCTG Report</title>
          <style>
            @page { margin: 0.5in; }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              line-height: 1.6; 
              color: #333;
              margin: 0;
              padding: 20px;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px; 
              margin-bottom: 30px; 
            }
            .header img { height: 60px; margin-bottom: 10px; }
            .header h1 { margin: 10px 0; font-size: 24pt; }
            .header .meta { font-size: 10pt; color: #666; margin-top: 10px; }
            .insights { 
              background: #eff6ff; 
              padding: 20px; 
              border-radius: 8px; 
              margin-bottom: 30px; 
              border-left: 4px solid #3b82f6; 
            }
            .insights h3 { color: #1e40af; margin-top: 0; }
            .hashtags { margin-top: 10px; }
            .hashtag { 
              display: inline-block; 
              background: #dbeafe; 
              padding: 4px 10px; 
              margin: 4px; 
              border-radius: 4px; 
              font-size: 9pt; 
            }
            .concept { 
              page-break-inside: avoid; 
              margin-bottom: 40px; 
              border: 2px solid #e5e7eb; 
              padding: 20px; 
              border-radius: 8px; 
            }
            .concept h2 { color: #1e40af; margin-bottom: 5px; font-size: 18pt; }
            .concept .subtitle { color: #6b7280; font-size: 14pt; margin-bottom: 15px; }
            .section { margin-bottom: 15px; padding: 12px; background: #f9fafb; border-radius: 6px; }
            .section h4 { color: #374151; font-size: 11pt; margin: 0 0 8px 0; }
            .section p { margin: 0; }
            .chapters { margin-top: 10px; }
            .chapter { padding: 6px 10px; margin: 4px 0; background: white; border-left: 3px solid #3b82f6; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" alt="Digital Ink AI" />
            <h1>Book Concept & Title Generator</h1>
            <div class="meta">
              Genre: ${results.inputs?.genre || results.genre || ''} | 
              Theme: ${results.inputs?.theme || results.theme || ''}<br>
              Generated: ${timestamp}
            </div>
          </div>
          
          ${results.results?.market_insights || results.market_insights ? `
            <div class="insights">
              <h3>💡 Market Insights</h3>
              <p>${results.results?.market_insights || results.market_insights}</p>
              ${(results.results?.recommended_hashtags || results.recommended_hashtags || []).length > 0 ? `
                <div class="hashtags">
                  <strong>Recommended Hashtags:</strong><br>
                  ${(results.results?.recommended_hashtags || results.recommended_hashtags || []).map(tag => 
                    `<span class="hashtag">${tag.replace(/^#/, '')}</span>`
                  ).join('')}
                </div>
              ` : ''}
            </div>
          ` : ''}
          
          ${(results.results?.concepts || results.concepts || []).map((concept, index) => `
            <div class="concept">
              <h2>${index + 1}. ${concept.title}</h2>
              ${concept.subtitle ? `<div class="subtitle">${concept.subtitle}</div>` : ''}
              
              ${concept.literary_hook ? `
                <div class="section">
                  <h4>📖 Literary Hook</h4>
                  <p><em>${concept.literary_hook}</em></p>
                </div>
              ` : ''}
              
              <div class="section">
                <h4>📝 Complete Book Concept</h4>
                <p>${concept.summary}</p>
              </div>
              
              <div class="section">
                <h4>💰 Why This Will Sell</h4>
                <p>${concept.market_appeal}</p>
              </div>
              
              ${concept.chapter_outline && concept.chapter_outline.length > 0 ? `
                <div class="section">
                  <h4>📚 Chapter Outline</h4>
                  <div class="chapters">
                    ${concept.chapter_outline.map((chapter, idx) => 
                      `<div class="chapter">Chapter ${idx + 1}: ${chapter}</div>`
                    ).join('')}
                  </div>
                </div>
              ` : ''}
            </div>
          `).join('')}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.onafterprint = () => printWindow.close();
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  localStorage.setItem('serviceCompleted', 'BCTG');
                  navigate('/user_dashboard', { state: { activeServiceTab: 'bctg', selectedWorkflow: 'writing' } });
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Service Title and Description */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Book Concept & Title Generator
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Unlock your next bestseller with the Book Concept and Title Generator. Simply share your initial idea, genre preferences, or target audience, and our AI collaborator crafts tailored concepts, compelling titles, and high-level overviews infused with market trends and reader appeal. You'll get 3 unique ideas per session, complete with viability insights to ensure your story starts strong and stands out—empowering you to create with confidence and commercial savvy.
          </p>
        </div>

        {/* Center logo removed - now in header */}
        <div className="mb-8 text-center hidden">
          <img 
            src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
            alt="Digital Ink AI" 
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Book Concept & Title Generator
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Unlock your next bestseller with the Book Concept and Title Generator. Simply share your initial idea, genre preferences, or target audience, and our AI collaborator crafts tailored concepts, compelling titles, and high-level overviews infused with market trends and reader appeal. You'll get 3 unique ideas per session, complete with viability insights to ensure your story starts strong and stands out—empowering you to create with confidence and commercial savvy.
          </p>
        </div>

        {!results ? (
          /* Enhanced Input Form with Sidebar */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-slate-900 text-center">
                    Tell us about your book idea
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                {/* Genre and Theme Selection - Horizontal Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Genre Section */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="genre" className="text-base font-medium">
                        Genre
                      </Label>
                      <select
                        id="genre"
                        value={formData.genre}
                        onChange={(e) => {
                          setFormData({...formData, genre: e.target.value, custom_genre: ''});
                        }}
                        className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">Select a genre...</option>
                        {GENRE_OPTIONS.map((genre) => (
                          <option key={genre} value={genre}>
                            {genre}
                          </option>
                        ))}
                        <option value="custom">Custom Genre</option>
                      </select>
                    </div>

                    {(formData.genre === 'custom' || formData.custom_genre) && (
                      <div>
                        <Label htmlFor="custom_genre" className="text-base font-medium">
                          Custom Genre
                        </Label>
                        <Input
                          id="custom_genre"
                          type="text"
                          placeholder="Enter your custom genre..."
                          value={formData.custom_genre}
                          onChange={(e) => setFormData({...formData, custom_genre: e.target.value})}
                          className="mt-2"
                        />
                      </div>
                    )}
                  </div>

                  {/* Theme Section */}
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="theme" className="text-base font-medium">
                        Theme
                      </Label>
                      <select
                        id="theme"
                        value={formData.theme}
                        onChange={(e) => {
                          setFormData({...formData, theme: e.target.value, custom_theme: ''});
                        }}
                        className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      >
                        <option value="">Select a theme...</option>
                        {THEME_OPTIONS.map((theme) => (
                          <option key={theme} value={theme}>
                            {theme}
                          </option>
                        ))}
                        <option value="custom">Custom Theme</option>
                      </select>
                    </div>

                    {(formData.theme === 'custom' || formData.custom_theme) && (
                      <div>
                        <Label htmlFor="custom_theme" className="text-base font-medium">
                          Custom Theme
                        </Label>
                        <Input
                          id="custom_theme"
                          type="text"
                          placeholder="Enter your custom theme..."
                          value={formData.custom_theme}
                          onChange={(e) => setFormData({...formData, custom_theme: e.target.value})}
                          className="mt-2"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Target Audience */}
                <div>
                  <Label htmlFor="target_audience" className="text-base font-medium">
                    Target Audience
                  </Label>
                  <select
                    id="target_audience"
                    value={formData.target_audience}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({
                        ...formData, 
                        target_audience: value,
                        custom_target_audience: value === 'Custom Audience' ? formData.custom_target_audience : ''
                      });
                    }}
                    className="mt-2 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select target audience...</option>
                    <option value="Children (Ages 0-12)">Children (Ages 0-12)</option>
                    <option value="Young Adult/Teen (Ages 13-18)">Young Adult/Teen (Ages 13-18)</option>
                    <option value="New Adult (Ages 18-30)">New Adult (Ages 18-30)</option>
                    <option value="Adult Fiction Readers (Ages 25-54)">Adult Fiction Readers (Ages 25-54)</option>
                    <option value="Adult Non-Fiction Readers (Ages 25-54)">Adult Non-Fiction Readers (Ages 25-54)</option>
                    <option value="Mature/Senior Readers (Ages 55+)">Mature/Senior Readers (Ages 55+)</option>
                    <option value="Academic/Professional">Academic/Professional</option>
                    <option value="General Audience (All Ages)">General Audience (All Ages)</option>
                    <option value="Custom Audience">Custom Audience</option>
                  </select>
                  {formData.target_audience === 'Custom Audience' && (
                    <Input
                      type="text"
                      placeholder="Describe your target audience..."
                      value={formData.custom_target_audience || ''}
                      onChange={(e) => setFormData({...formData, custom_target_audience: e.target.value})}
                      className="mt-2"
                      required
                    />
                  )}
                </div>

                {/* Setting & Timeframe Section */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">📍 Setting & Timeframe (Optional but Recommended)</h3>
                  <p className="text-sm text-slate-600 mb-4">Add location and time context to enrich your concepts</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Country */}
                    <div>
                      <Label htmlFor="setting_country" className="text-base font-medium">
                        Country
                      </Label>
                      <Input
                        id="setting_country"
                        type="text"
                        placeholder="e.g., United States, Japan, France"
                        value={formData.setting_country}
                        onChange={(e) => setFormData({...formData, setting_country: e.target.value})}
                        className="mt-2"
                      />
                    </div>

                    {/* City */}
                    <div>
                      <Label htmlFor="setting_city" className="text-base font-medium">
                        City
                      </Label>
                      <Input
                        id="setting_city"
                        type="text"
                        placeholder="e.g., New York, Tokyo, Paris"
                        value={formData.setting_city}
                        onChange={(e) => setFormData({...formData, setting_city: e.target.value})}
                        className="mt-2"
                      />
                    </div>

                    {/* Region */}
                    <div>
                      <Label htmlFor="setting_region" className="text-base font-medium">
                        Region/State/Province
                      </Label>
                      <Input
                        id="setting_region"
                        type="text"
                        placeholder="e.g., California, Tuscany, Hokkaido"
                        value={formData.setting_region}
                        onChange={(e) => setFormData({...formData, setting_region: e.target.value})}
                        className="mt-2"
                      />
                    </div>

                    {/* Timeframe/Era */}
                    <div>
                      <Label htmlFor="timeframe_era" className="text-base font-medium">
                        Timeframe/Era
                      </Label>
                      <Input
                        id="timeframe_era"
                        type="text"
                        placeholder="e.g., Victorian Era, 1980s, Future 2150"
                        value={formData.timeframe_era}
                        onChange={(e) => setFormData({...formData, timeframe_era: e.target.value})}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Concept Description with AI Suggestion */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="concept_description" className="text-base font-medium">
                      Concept Description
                    </Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        id="import-description"
                        accept=".txt,.doc,.docx"
                        style={{ display: 'none' }}
                        onChange={handleImportDescription}
                      />
                      <Button
                        type="button"
                        onClick={handleSuggestConcept}
                        variant="outline"
                        size="md"
                        disabled={suggestionLoading || suggestionCount >= 5 || (!formData.genre && !formData.custom_genre) || (!formData.theme && !formData.custom_theme)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-6 py-2"
                      >
                        {suggestionLoading ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                            Generating...
                          </>
                        ) : suggestionCount >= 5 ? (
                          "Exceeded Content Suggestion Max"
                        ) : suggestionUsed ? (
                          "Try another suggestion 🤖"
                        ) : (
                          "What do you suggest? 🤖"
                        )}
                      </Button>
                      <ReadAloudButton text={formData.concept_description || ""} voice="nova" />
                      <Button
                        type="button"
                        onClick={() => document.getElementById('import-description').click()}
                        variant="outline"
                        size="sm"
                      className="w-full sm:w-auto"
                      >
                        📁 Import
                      </Button>
                    </div>
                  </div>
                  <textarea
                    id="concept_description"
                    placeholder="Describe your book concept, story idea, themes, or vision in 1-2 paragraphs. Use the 'What do you suggest?' button for AI-generated ideas..."
                    value={formData.concept_description}
                    onChange={(e) => setFormData({...formData, concept_description: e.target.value})}
                    className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base leading-relaxed"
                    rows={10}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transform transition hover:scale-105"
                  disabled={loading || (!formData.genre && !formData.custom_genre) || (!formData.theme && !formData.custom_theme)}
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                      Generating Concepts with Digital Ink AI...
                    </>
                  ) : (
                    <>
                      <BookOpen className="mr-2 h-5 w-5" />
                      🚀 Start Digital Ink AI Book Concept
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Generator Features */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-slate-900">✨ Generator Features</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>3 unique AI-generated book concepts per session</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Market-aligned titles and subtitles</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Comprehensive story overviews and character descriptions</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Target audience analysis and appeal insights</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Marketing taglines and positioning strategies</span>
                </div>
                <div className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>Viability scores and market trend integration</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Free Service Information */}
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
            <CardHeader>
              <CardTitle className="text-lg text-green-900">🎁 Freemium Pricing</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p className="text-slate-700">
                <strong>3 Free Uses Monthly:</strong> All users get 3 complimentary BCTG sessions each month.
              </p>
              <p className="text-slate-700">
                <strong>Additional Uses (A la Carte):</strong> $5.00 per session after free limit
              </p>
              <p className="text-slate-700">
                <strong>Subscribers:</strong> Unlimited FREE access included in your subscription plan
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
        ) : (
          /* Enhanced Results Display - v2.0 */
          <div id="bctg-results" className="bg-white rounded-lg shadow-lg p-8">
            {/* Print Header - Hidden on Screen */}
            <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                    alt="Digital Ink AI" 
                    className="h-12 w-auto"
                  />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                    <p className="text-sm text-gray-600">Book Concept & Title Generator</p>
                    <p className="text-xs text-gray-600 mt-1">Genre: {results.genre} | Concepts: {results.concepts?.length || 3}</p>
                  </div>
                </div>
                <div className="text-right text-sm text-gray-600">
                  <p>{new Date().toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Report Metadata - ABG Style Layout */}
            <div className="border-b pb-6 mb-6 print:border-b-2">
              <div className="flex justify-between items-start mb-4 print:mb-2">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">Book Concept Report</h2>
                  <p className="text-slate-600 mt-1">Generated for {results.inputs?.genre || results.genre || formData.genre} genre</p>
                </div>
                <div className="flex gap-3 print:hidden">
                  <button onClick={printResults} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">🖨️ Print</button>
                  <button onClick={exportToPDF} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">📄 Export PDF</button>
                  <button onClick={resetForm} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">✨ New Concept</button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Genre:</span>
                  <p className="font-semibold text-slate-900">{results.inputs?.genre || results.genre || formData.genre || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Theme:</span>
                  <p className="font-semibold text-slate-900">{results.inputs?.theme || results.theme || formData.theme || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Service:</span>
                  <p className="font-semibold text-slate-900">BCTG</p>
                </div>
                <div>
                  <span className="text-slate-500">Concepts:</span>
                  <p className="font-semibold text-slate-900">{results.results?.concepts?.length || results.concepts?.length || 3}</p>
                </div>
              </div>
            </div>

            <style>{`
              @media print {
                body { 
                  font-family: 'Times New Roman', Times, serif; 
                  margin: 10px; 
                  color: #000;
                }
                .no-print { display: none !important; }
                .print-metadata { 
                  background: #f0f4ff !important; 
                  border: 2px solid #93c5fd !important;
                  page-break-inside: avoid;
                  margin-bottom: 20px;
                }
                .print-metadata .bg-white {
                  border: 1px solid #cbd5e1;
                  padding: 8px;
                }
                /* Ensure all cards and content are visible */
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                  color-adjust: exact !important;
                }
                /* Market Insights card */
                .bg-gradient-to-r {
                  background: #3b82f6 !important;
                  color: #fff !important;
                }
                /* Concept cards */
                .bg-white\/80 {
                  background: #fff !important;
                  border: 1px solid #cbd5e1 !important;
                }
                /* Section backgrounds */
                .bg-blue-50 {
                  background: #eff6ff !important;
                }
                .bg-green-50 {
                  background: #f0fdf4 !important;
                }
                .bg-slate-50 {
                  background: #f8fafc !important;
                }
                /* Prevent page breaks inside concepts */
                .grid.gap-8 > * {
                  page-break-inside: avoid;
                  margin-bottom: 20px;
                }
                /* Hide call-to-action section in print */
                .grid.gap-8 ~ * {
                  display: none !important;
                }
              }
            `}</style>

            {/* Market Insights */}
            <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <span className="text-4xl mr-2">💡</span>
                  Market Insights & Why These Will Sell
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-100 text-lg leading-relaxed mb-4">
                  {results.results?.market_insights || results.market_insights}
                </p>
                <div>
                  <h4 className="font-semibold text-blue-100 mb-2">Recommended Marketing Hashtags:</h4>
                  <div className="flex flex-wrap gap-2">
                    {(results.results?.recommended_hashtags || results.recommended_hashtags || []).map((hashtag, index) => (
                      <Badge key={index} variant="secondary" className="bg-blue-400 text-blue-900 hover:bg-blue-300">
                        {hashtag.replace(/^#/, '')}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Book Concepts - Enhanced Layout */}
            <div className="grid gap-8">
              {(results.results?.concepts || results.concepts || []).map((concept, index) => (
                <Card key={index} className="bg-white/80 backdrop-blur-sm border-slate-200 hover:shadow-lg transition-all duration-300">
                  <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Badge variant="default" className="mb-3 bg-blue-600">
                          Concept {index + 1}
                        </Badge>
                        <CardTitle className="text-3xl text-slate-900 mb-3 leading-tight">
                          {concept.title}
                        </CardTitle>
                        {concept.subtitle && (
                          <p className="text-xl text-slate-600 font-medium italic">
                            {concept.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6 p-6">
                    {/* Literary Hook */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h4 className="font-bold text-slate-900 mb-2 text-lg">📖 Literary Hook</h4>
                      <p className="text-slate-700 leading-relaxed font-medium">
                        {concept.literary_hook || extractLiteraryHook(concept.summary)}
                      </p>
                    </div>

                    {/* 500-Word Concept Summary */}
                    <div>
                      <h4 className="font-bold text-slate-900 mb-3 text-lg">📋 Complete Book Concept</h4>
                      <p className="text-slate-700 leading-relaxed">
                        {concept.summary}
                      </p>
                    </div>

                    {/* Why This Will Sell */}
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                      <h4 className="font-bold text-slate-900 mb-2 text-lg">💰 Why This Will Sell</h4>
                      <p className="text-slate-700 leading-relaxed">
                        {concept.market_appeal}
                      </p>
                    </div>

                    {/* Chapter Outline */}
                    <div>
                      <h4 className="font-bold text-slate-900 mb-4 text-lg">📚 Chapter Outline</h4>
                      <div className="grid gap-2">
                        {concept.chapter_outline.map((chapter, chapterIndex) => (
                          <div key={chapterIndex} className="flex items-center p-3 bg-slate-50 rounded-md border-l-4 border-blue-200">
                            <span className="text-blue-600 font-bold w-16 text-sm">
                              Ch. {chapterIndex + 1}
                            </span>
                            <span className="text-slate-700 font-medium">{chapter}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Enhanced Call to Action */}
            <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-blue-200">
              <CardContent className="py-8 text-center">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  🎉 Ready to Write Your Bestseller?
                </h3>
                <p className="text-slate-600 mb-6 text-lg max-w-2xl mx-auto">
                  These AI-generated concepts are your foundation for success. Each concept includes market-tested elements 
                  designed to resonate with your target audience and current publishing trends.
                </p>
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button onClick={resetForm} className="bg-blue-600 hover:bg-blue-700">
                    ✨ Generate More Concepts
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/homepage')}>
                    🏠 Home
                  </Button>
                  <Button variant="outline" onClick={() => {
                    localStorage.setItem('serviceCompleted', 'BCTG');
                    navigate('/user_dashboard');
                  }}>
                    📊 Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      
      <GlobalFooter />

      {/* Charge Confirmation Modal */}
      {showChargeConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-4">
              ⚡ Confirm BCTG Generation
            </h3>
            <div className="space-y-3 mb-6">
              <p className="text-slate-700">
                You are about to generate a new BCTG report.
              </p>
              <div className="bg-blue-50 p-3 rounded-md border-l-4 border-blue-400">
                <p className="font-medium text-blue-900">Service Charge:</p>
                <p className="text-2xl font-bold text-blue-600">$2.50</p>
                <p className="text-sm text-blue-700">This charge will be processed immediately</p>
              </div>
              <p className="text-sm text-slate-600">
                By continuing, you agree to the charge for this BCTG generation service.
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowChargeConfirmation(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={proceedWithGeneration}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Continue & Pay $2.50'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={isProcessing} 
        serviceName="Book Concept & Title Generator (BCTG)"
      />

      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendEmailReceipt}
        serviceName="Book Concept & Title Generator (BCTG)"
        amount={lastCharge?.amount || 2.50}
        bookTitle={lastCharge?.bookTitle || "BCTG Generation"}
      />
    </div>
  );
};

// Workflow Category Card Component for Dashboard
const WorkflowCategoryCard = ({ title, icon, description, hoverDescription, serviceCount, availableCount, services, user }) => {
  const navigate = useNavigate();

  const getServicePricing = (service) => {
    const { role } = user || {};
    
    if (role === 'admin' || role === 'student') {
      return "FREE";
    }
    
    if (role === 'subscriber') {
      if (service.premium) {
        // Premium services at 50% off for subscribers
        return `$${(service.price / 2).toFixed(2)} (50% OFF)`;
      } else {
        // Standard services are FREE for subscribers
        return "FREE";
      }
    }
    
    // A La Carte pricing (full price)
    if (service.premium) {
      return `$${service.price.toFixed(2)} (Premium)`;
    } else {
      return `$${service.price.toFixed(2)} (Standard)`;
    }
  };

  return (
    <div className="mb-8">
      {/* Category Header */}
      <div className="flex items-center mb-4 pb-3 border-b-2 border-slate-200">
        <span className="text-4xl mr-3">{icon}</span>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-slate-900">{title}</h3>
          <p className="text-sm text-slate-600">{description}</p>
        </div>
        <Badge variant="outline" className="text-sm">
          {availableCount}/{serviceCount} Available
        </Badge>
      </div>
      
      {/* Services Grid - Always Visible */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((service) => (
          <Card key={service.code} className={`transition-shadow hover:shadow-lg ${service.available ? 'bg-white border-green-300' : 'bg-slate-50 border-slate-200'}`}>
            <CardContent className="p-4">
              <div className="flex flex-col h-full">
                <div className="flex-1">
                  <h4 className="font-semibold text-base text-slate-900 mb-2">{service.title}</h4>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{service.description}</p>
                  <div className="flex items-center space-x-3 mb-3">
                    <Badge variant={service.available ? 'default' : 'secondary'} className="text-xs">
                      {service.available ? 'Available' : 'Coming Soon'}
                    </Badge>
                    <span className="text-sm font-semibold text-slate-700">
                      {getServicePricing(service)}
                    </span>
                  </div>
                </div>
                <Button 
                  size="sm"
                      className="w-full sm:w-auto"
                  variant={service.available ? 'default' : 'outline'}
                  className="w-full mt-2"
                  disabled={!service.available}
                  onClick={service.available && service.link ? () => navigate(service.link) : undefined}
                >
                  {service.available ? 'Launch Service →' : 'Coming Soon'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Transaction Details Component  
const TransactionDetailsPage = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [serviceFilter, setServiceFilter] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState('writing'); // New state for workflow filtering
  const [dateFilter, setDateFilter] = useState('30'); // '1', '7', '30', or 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const { user } = useAuth();
  const { formatTimestamp } = useTimezone();
  const navigate = useNavigate();
  
  // Email receipt modal state
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);
  
  // Referral modal state
  const [showReferralModal, setShowReferralModal] = useState(false);
  
  // Get service filter from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const service = urlParams.get('service') || '';
    setServiceFilter(service);
    
    // If a specific service is requested, scroll to summary card after a delay
    if (service && service !== 'ALL' && service !== '') {
      setTimeout(() => {
        const summaryAnchor = document.getElementById('transaction-summary-anchor');
        if (summaryAnchor) {
          const elementPosition = summaryAnchor.getBoundingClientRect().top;
          const offsetPosition = window.pageYOffset + elementPosition - 20; // 20px from top
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          
        }
      }, 300); // Delay for DOM rendering
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchTransactionHistory();
    }
  }, [user]);

  useEffect(() => {
    // Filter transactions based on service and date range
    let filtered = transactions;
    
    // Filter by service
    if (serviceFilter && serviceFilter !== 'ALL' && serviceFilter !== '') {
      filtered = filtered.filter(t => 
        t.service && t.service.toUpperCase() === serviceFilter.toUpperCase()
      );
    }
    
    // Filter by date
    const now = new Date();
    if (dateFilter === '1') {
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => new Date(t.created_at) >= oneDayAgo);
    } else if (dateFilter === '7') {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => new Date(t.created_at) >= sevenDaysAgo);
    } else if (dateFilter === '30') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(t => new Date(t.created_at) >= thirtyDaysAgo);
    } else if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date
      filtered = filtered.filter(t => {
        const txDate = new Date(t.created_at);
        return txDate >= startDate && txDate <= endDate;
      });
    }
    
    setFilteredTransactions(filtered);
  }, [transactions, serviceFilter, dateFilter, customStartDate, customEndDate]);

  const fetchTransactionHistory = async () => {
    try {
      const response = await axios.get(`${API}/payments/transactions`, { 
        withCredentials: true,
        timeout: 10000
      });
      
      const transactionsData = response.data?.transactions || response.data || [];
      const transactionsArray = Array.isArray(transactionsData) ? transactionsData : [];
      
      setTransactions(transactionsArray);
      
    } catch (error) {
      toast.error("Failed to load transaction history");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Send receipt email for past transaction - opens modal
  const handleSendReceiptFromHistory = async (transaction) => {
    
    try {
      
      if (!transaction.id) {
        toast.error("Invalid transaction ID");
        return;
      }
      
      // Determine service name
      let serviceName = "Service";
      if (transaction.service) {
        if (transaction.service.toUpperCase() === 'BCTG') {
          serviceName = "Book Concept & Title Generator (BCTG)";
        } else if (transaction.service.toUpperCase() === 'BVADM') {
          serviceName = "Book Viability & Development Model (BVADM)";
        } else if (transaction.service.toUpperCase() === 'CCAT') {
          serviceName = "Competitor & Comparative Analysis Tool (CCAT)";
        } else if (transaction.service.toUpperCase() === 'BOCP') {
          serviceName = "Book Outline & Chapter Planner (BOCP)";
        } else if (transaction.service.toUpperCase() === 'MMG') {
          serviceName = "Manuscript Metadata Generator (MMG)";
        } else if (transaction.service.toUpperCase() === 'CCG' || transaction.service.toUpperCase() === 'CDDA') {
          serviceName = "Character & Cast Generator (CCG)";
        }
      }
      
      // Get book/project title from metadata
      let bookTitle = "";
      if (transaction.metadata) {
        if (transaction.metadata.title) {
          bookTitle = transaction.metadata.title;
        } else if (transaction.metadata.genre && transaction.metadata.theme) {
          bookTitle = `${transaction.metadata.genre} - ${transaction.metadata.theme}`;
        }
      }
      
      // Set modal data
      setLastTransactionId(transaction.id);
      setLastCharge({
        amount: transaction.amount || 0,
        serviceName: serviceName,
        bookTitle: bookTitle
      });
      
      // Show modal
      setShowEmailReceiptModal(true);
      
    } catch (error) {
      toast.error("Failed to open receipt modal");
    }
  };

  // Handle sending receipt from modal
  const handleSendReceiptEmail = async () => {
    try {
      
      if (!lastTransactionId) {
        toast.error("No transaction ID available");
        setShowEmailReceiptModal(false);
        return;
      }
      
      const response = await axios.post(`${API}/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, { withCredentials: true });
      
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      toast.error(`Failed to send receipt: ${error.response?.data?.detail || error.message}`);
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
  };

  // Use central MST formatter - no local formatDate needed

  const getServiceIcon = (service) => {
    const upperService = (service || '').toUpperCase();
    switch (upperService) {
      case 'BCTG': return '📚';
      case 'BVADM': return '📊';
      case 'CCAT': return '📊';
      case 'BREC': return '📚';
      case 'BOCP': return '📖';
      case 'MMG': return '📈';
      case 'ABG': return '📝';
      case 'KSG': return '🎤';
      case 'ERMT': return '📕';
      case 'PMPB':
      case 'PRMPB': return '📄';
      case 'AKSW': return '📤';
      case 'IBG': return '📊';
      case 'MMKG': return '📰';
      case 'SMMK': return '📱';
      case 'CCG':
      case 'CCG': return '🎭';
      default: return '💳';
    }
  };

  const getServiceName = (service) => {
    const upperService = (service || '').toUpperCase();
    switch (upperService) {
      case 'BCTG': return 'Book Concept & Title Generator';
      case 'BVADM': return 'Book Viability & Development Model';
      case 'CCAT': return 'Competitor & Comparative Analysis Tool';
      case 'BREC': return 'Book Review & Endorsement Creator';
      case 'BOCP': return 'Book Outline & Chapter Planner';
      case 'MMG': return 'Manuscript Metadata Generator';
      case 'ABG': return 'Author Biography Generator';
      case 'KSG': return 'Keynote Speech Generator';
      case 'ERMT': return 'EPUB-Ready Manuscript Transformer';
      case 'PMPB':
      case 'PRMPB': return 'Print-Ready Merged PDF Builder';
      case 'AKSW': return 'Amazon KDP Submission Wizard';
      case 'IBG': return 'ISBN Barcode Generator';
      case 'MMKG': return 'Manuscript Media Kit Generator';
      case 'SMMK': return 'Social Media Marketing Kit';
      case 'CCG':
      case 'CCG': return 'Character & Cast Generator';
      default: return upperService;
    }
  };
  
  const getServiceGradient = (service) => {
    const upperService = (service || '').toUpperCase();
    switch (upperService) {
      case 'BCTG': return 'from-blue-500 to-blue-600';
      case 'BVADM': return 'from-purple-500 to-purple-600';
      case 'CCAT': return 'from-teal-500 to-teal-600';
      case 'BREC': return 'from-cyan-500 to-teal-500';
      case 'BOCP': return 'from-indigo-500 to-purple-600';
      case 'MMG': return 'from-orange-500 to-amber-500';
      case 'ABG': return 'from-emerald-500 to-green-500';
      case 'KSG': return 'from-amber-500 to-orange-600';
      case 'ERMT': return 'from-rose-500 to-rose-600';
      case 'PMPB':
      case 'PRMPB': return 'from-pink-500 to-pink-600';
      case 'AKSW': return 'from-red-500 to-red-600';
      case 'IBG': return 'from-green-500 to-green-600';
      case 'MMKG': return 'from-orange-500 to-orange-600';
      case 'SMMK': return 'from-orange-600 to-red-500';
      case 'CCG': return 'from-cyan-500 to-cyan-600';
      default: return 'from-blue-500 to-purple-600'; // Default gradient for 'ALL'
    }
  };

  const getTotalAmount = () => {
    return filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading transaction history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                Transaction History for: {user?.first_name} {user?.last_name}
              </h1>
              <p className="text-base sm:text-lg text-slate-600 mt-3 mb-4">
                Complete financial overview of your Digital Ink AI purchases. Review all service charges, filter transactions by workflow or date range, track spending patterns, download payment receipts, and maintain accurate records for your author business accounting and tax preparation needs.
              </p>
              
              {/* Date Range Filter - Compact Dropdown */}
              <div className="flex items-center gap-3">
                <label htmlFor="date-filter" className="text-sm font-semibold text-slate-700 whitespace-nowrap">
                  Date Range:
                </label>
                <select
                  id="date-filter"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                >
                  <option value="1">Last 24 Hours</option>
                  <option value="7">Last 7 Days</option>
                  <option value="30">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
                
                {dateFilter === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                      placeholder="Start date"
                    />
                    <span className="text-slate-500 text-sm">to</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                      placeholder="End date"
                    />
                  </>
                )}
              </div>
            </div>
            <Button 
              onClick={() => navigate('/user_dashboard')}
              variant="outline"
              className="text-slate-600 hover:text-slate-700"
            >
              ← Return to Dashboard
            </Button>
          </div>
          
          {/* Workflow-Based Filter */}
          <div className="mb-4">
            {/* Service Workflows Header */}
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">💼</span>
                <h3 className="text-base font-semibold text-slate-700">Service Workflows</h3>
              </div>
            </div>

            {/* Workflow Cards - 3 column grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Writing & Ideation Workflow */}
              <Card 
                className={`cursor-pointer transition-all ${
                  selectedWorkflow === 'writing' 
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedWorkflow(selectedWorkflow === 'writing' ? null : 'writing');
                  setServiceFilter(selectedWorkflow === 'writing' ? 'ALL' : 'BCTG');
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">📝</span>
                      <div>
                        <CardTitle className={`text-sm ${selectedWorkflow === 'writing' ? 'text-white' : 'text-blue-900'}`}>Writing & Ideation</CardTitle>
                        <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'writing' ? 'text-slate-300' : 'text-slate-600'}`}>Transform ideas</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={selectedWorkflow === 'writing' ? 'bg-white text-slate-900 border-white' : 'text-blue-600 border-blue-300'}>
                      {transactions.filter(t => t.service && ['BCTG', 'BOCP', 'CCG', 'CDDA'].includes(t.service.toUpperCase())).length}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Editing & Enhancement Workflow */}
              <Card 
                className={`cursor-pointer transition-all ${
                  selectedWorkflow === 'editing' 
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedWorkflow(selectedWorkflow === 'editing' ? null : 'editing');
                  setServiceFilter(selectedWorkflow === 'editing' ? 'ALL' : 'ABG');
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">✏️</span>
                      <div>
                        <CardTitle className={`text-sm ${selectedWorkflow === 'editing' ? 'text-white' : 'text-purple-900'}`}>Editing & Enhancement</CardTitle>
                        <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'editing' ? 'text-slate-300' : 'text-slate-600'}`}>Polish standards</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={selectedWorkflow === 'editing' ? 'bg-white text-slate-900 border-white' : 'text-purple-600 border-purple-300'}>
                      {transactions.filter(t => t.service && ['ABG', 'PDE', 'BVADM'].includes(t.service.toUpperCase())).length}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Design & Production Workflow */}
              <Card 
                className={`cursor-pointer transition-all ${
                  selectedWorkflow === 'design' 
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedWorkflow(selectedWorkflow === 'design' ? null : 'design');
                  setServiceFilter(selectedWorkflow === 'design' ? 'ALL' : 'ERMT');
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">🎨</span>
                      <div>
                        <CardTitle className={`text-sm ${selectedWorkflow === 'design' ? 'text-white' : 'text-rose-900'}`}>Design & Production</CardTitle>
                        <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'design' ? 'text-slate-300' : 'text-slate-600'}`}>Create ready</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={selectedWorkflow === 'design' ? 'bg-white text-slate-900 border-white' : 'text-rose-600 border-rose-300'}>
                      {transactions.filter(t => t.service && ['ERMT', 'PMPB', 'PRMPB', 'AKSW'].includes(t.service.toUpperCase())).length}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Market Research & Analysis Workflow */}
              <Card 
                className={`cursor-pointer transition-all ${
                  selectedWorkflow === 'research' 
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedWorkflow(selectedWorkflow === 'research' ? null : 'research');
                  setServiceFilter(selectedWorkflow === 'research' ? 'ALL' : 'CCAT');
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">📊</span>
                      <div>
                        <CardTitle className={`text-sm ${selectedWorkflow === 'research' ? 'text-white' : 'text-emerald-900'}`}>Market Research</CardTitle>
                        <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'research' ? 'text-slate-300' : 'text-slate-600'}`}>Analyze market</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={selectedWorkflow === 'research' ? 'bg-white text-slate-900 border-white' : 'text-emerald-600 border-emerald-300'}>
                      {transactions.filter(t => t.service && ['CCAT', 'BREC', 'MMG'].includes(t.service.toUpperCase())).length}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* Marketing & Promotion Workflow */}
              <Card 
                className={`cursor-pointer transition-all ${
                  selectedWorkflow === 'marketing' 
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedWorkflow(selectedWorkflow === 'marketing' ? null : 'marketing');
                  setServiceFilter(selectedWorkflow === 'marketing' ? 'ALL' : 'KSG');
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">📈</span>
                      <div>
                        <CardTitle className={`text-sm ${selectedWorkflow === 'marketing' ? 'text-white' : 'text-orange-900'}`}>Marketing & Promotion</CardTitle>
                        <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'marketing' ? 'text-slate-300' : 'text-slate-600'}`}>Promote work</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={selectedWorkflow === 'marketing' ? 'bg-white text-slate-900 border-white' : 'text-orange-600 border-orange-300'}>
                      {transactions.filter(t => t.service && ['KSG', 'MMKG', 'SMMK'].includes(t.service.toUpperCase())).length}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>

              {/* All Services */}
              <Card 
                className={`cursor-pointer transition-all ${
                  serviceFilter === 'ALL'
                    ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
                onClick={() => {
                  setSelectedWorkflow(null);
                  setServiceFilter('ALL');
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-3xl">📚</span>
                      <div>
                        <CardTitle className={`text-sm ${serviceFilter === 'ALL' ? 'text-white' : 'text-slate-900'}`}>All Services</CardTitle>
                        <p className={`text-[10px] mt-0.5 ${serviceFilter === 'ALL' ? 'text-slate-300' : 'text-slate-600'}`}>View all</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={serviceFilter === 'ALL' ? 'bg-white text-slate-900 border-white' : 'text-slate-600 border-slate-300'}>
                      {transactions.length}
                    </Badge>
                  </div>
                </CardHeader>
              </Card>
            </div>
          </div>

          {/* Service Buttons - Show only when workflow is selected */}
          {selectedWorkflow === 'writing' && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm font-semibold text-blue-900 mb-3">📝 Writing & Ideation Services</div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setServiceFilter('BCTG')}
                  variant={serviceFilter === 'BCTG' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'BCTG' ? 'bg-blue-600' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}`}
                >
                  📚 BCTG ({transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('BOCP')}
                  variant={serviceFilter === 'BOCP' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'BOCP' ? 'bg-indigo-600' : 'border-indigo-600 text-indigo-600 hover:bg-indigo-50'}`}
                >
                  📖 BOCP ({transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('CCG')}
                  variant={serviceFilter === 'CCG' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'CCG' ? 'bg-cyan-600' : 'border-cyan-600 text-cyan-600 hover:bg-cyan-50'}`}
                >
                  🎭 CCG ({transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).length})
                </Button>
              </div>
            </div>
          )}

          {selectedWorkflow === 'editing' && (
            <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-sm font-semibold text-purple-900 mb-3">✏️ Editing & Enhancement Services</div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setServiceFilter('ABG')}
                  variant={serviceFilter === 'ABG' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'ABG' ? 'bg-emerald-600' : 'border-emerald-600 text-emerald-600 hover:bg-emerald-50'}`}
                >
                  📝 ABG ({transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('PDE')}
                  variant={serviceFilter === 'PDE' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'PDE' ? 'bg-violet-600' : 'border-violet-600 text-violet-600 hover:bg-violet-50'}`}
                >
                  ✍️ PDE ({transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('BVADM')}
                  variant={serviceFilter === 'BVADM' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'BVADM' ? 'bg-purple-600' : 'border-purple-600 text-purple-600 hover:bg-purple-50'}`}
                >
                  📊 BVADM ({transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').length})
                </Button>
              </div>
            </div>
          )}

          {selectedWorkflow === 'design' && (
            <div className="mb-4 p-4 bg-rose-50 rounded-lg border border-rose-200">
              <div className="text-sm font-semibold text-rose-900 mb-3">🎨 Design & Production Services</div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setServiceFilter('ERMT')}
                  variant={serviceFilter === 'ERMT' ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${serviceFilter === 'ERMT' ? 'bg-rose-600' : 'border-rose-600 text-rose-600 hover:bg-rose-50'}`}
                >
                  📕 ERMT ({transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('PMPB')}
                  variant={serviceFilter === 'PMPB' ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${serviceFilter === 'PMPB' ? 'bg-pink-600' : 'border-pink-600 text-pink-600 hover:bg-pink-50'}`}
                >
                  📄 PMPB ({transactions.filter(t => t.service && ['PMPB', 'PRMPB'].includes(t.service.toUpperCase())).length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('AKSW')}
                  variant={serviceFilter === 'AKSW' ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${serviceFilter === 'AKSW' ? 'bg-blue-600' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}`}
                >
                  📤 AKSW ({transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').length})
                </Button>
              </div>
            </div>
          )}

          {selectedWorkflow === 'research' && (
            <div className="mb-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-sm font-semibold text-emerald-900 mb-3">📊 Market Research & Analysis Services</div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setServiceFilter('CCAT')}
                  variant={serviceFilter === 'CCAT' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'CCAT' ? 'bg-teal-600' : 'border-teal-600 text-teal-600 hover:bg-teal-50'}`}
                >
                  📊 CCAT ({transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('BREC')}
                  variant={serviceFilter === 'BREC' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'BREC' ? 'bg-cyan-600' : 'border-cyan-600 text-cyan-600 hover:bg-cyan-50'}`}
                >
                  📚 BREC ({transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('MMG')}
                  variant={serviceFilter === 'MMG' ? 'default' : 'outline'}
                  size="sm"
                      className="w-full sm:w-auto"
                  className={`text-xs ${serviceFilter === 'MMG' ? 'bg-blue-600' : 'border-blue-600 text-blue-600 hover:bg-blue-50'}`}
                >
                  📈 MMG ({transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').length})
                </Button>
              </div>
            </div>
          )}

          {selectedWorkflow === 'marketing' && (
            <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="text-sm font-semibold text-orange-900 mb-3">📈 Marketing & Promotion Services</div>
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={() => setServiceFilter('KSG')}
                  variant={serviceFilter === 'KSG' ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${serviceFilter === 'KSG' ? 'bg-amber-600' : 'border-amber-600 text-amber-600 hover:bg-amber-50'}`}
                >
                  🎤 KSG ({transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('MMKG')}
                  variant={serviceFilter === 'MMKG' ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${serviceFilter === 'MMKG' ? 'bg-orange-600' : 'border-orange-600 text-orange-600 hover:bg-orange-50'}`}
                >
                  📰 MMKG ({transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').length})
                </Button>
                <Button 
                  onClick={() => setServiceFilter('SMMK')}
                  variant={serviceFilter === 'SMMK' ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${serviceFilter === 'SMMK' ? 'bg-red-600' : 'border-red-600 text-red-600 hover:bg-red-50'}`}
                >
                  📱 SMMK ({transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').length})
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Anchor point for autoscrolling with horizontal separator */}
        <div id="transaction-summary-anchor" className="my-6">
          <div className="border-t-2 border-slate-300"></div>
        </div>

        {/* Summary Card */}
        <Card className={`mb-8 bg-gradient-to-r ${getServiceGradient(serviceFilter)} text-white overflow-hidden`}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center flex-wrap gap-2">
              <span className="text-4xl mr-2">{getServiceIcon(serviceFilter)}</span>
              <span className="text-base sm:text-lg break-words">
                Summary - {(!serviceFilter || serviceFilter === 'ALL' || serviceFilter === '') ? 'All Services' : getServiceName(serviceFilter)}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              <div>
                <div className="text-2xl sm:text-3xl font-bold">{filteredTransactions.length}</div>
                <p className="text-blue-100 text-xs sm:text-sm">Total Transactions</p>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold">${getTotalAmount()}</div>
                <p className="text-blue-100 text-xs sm:text-sm">Total Amount Spent</p>
              </div>
              <div>
                <div className="text-2xl sm:text-3xl font-bold">
                  {filteredTransactions.length > 0 
                    ? `$${(parseFloat(getTotalAmount()) / filteredTransactions.length).toFixed(2)}`
                    : '$0.00'
                  }
                </div>
                <p className="text-blue-100 text-xs sm:text-sm">Average per Transaction</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transactions List */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl text-slate-900">
              Transaction Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">💳</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No transactions found</h3>
                <p className="text-slate-600">
                  {(!serviceFilter || serviceFilter === 'ALL' || serviceFilter === '')
                    ? "You haven't made any payments yet." 
                    : `No payments found for ${getServiceName(serviceFilter)}.`
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredTransactions.map((transaction, index) => (
                  <Card key={transaction.id || index} className="border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center mb-2">
                            <span className="text-4xl mr-3">{getServiceIcon(transaction.service)}</span>
                            <div>
                              <h4 className="font-semibold text-lg text-slate-900">
                                {getServiceName(transaction.service)}
                              </h4>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                            <div>
                              <span className="text-slate-500 text-sm">Amount</span>
                              <div className="font-semibold text-lg text-green-600">
                                ${(transaction.amount || 0).toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <span className="text-slate-500 text-sm">Date</span>
                              <div className="font-medium">{transaction.created_at_display || formatTimestamp(transaction.created_at)}</div>
                            </div>
                            <div>
                              <span className="text-slate-500 text-sm">Status</span>
                              <div className="font-medium text-green-600">
                                {transaction.payment_status || 'Completed'}
                              </div>
                            </div>
                          </div>
                          
                          {/* Send Receipt Button */}
                          <div className="mt-4 flex justify-end">
                            <Button
                              size="sm"
                      className="w-full sm:w-auto"
                              onClick={() => handleSendReceiptFromHistory(transaction)}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              📧 Send Receipt Email
                            </Button>
                          </div>
                          
                          {/* BCTG-specific details */}
                          {transaction.service && transaction.service.toUpperCase() === 'BCTG' && transaction.metadata && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-semibold text-slate-900">Book Concept Details</h5>
                                {transaction.metadata.result_id && (
                                  <Button
                                    size="sm"
                      className="w-full sm:w-auto"
                                    onClick={() => navigate(`/activity?view=bctg&result=${transaction.metadata.result_id}`)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    📚 View Full Concept
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {transaction.metadata.genre && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Genre:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.genre}</span>
                                  </div>
                                )}
                                {transaction.metadata.theme && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Core Theme:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.theme}</span>
                                  </div>
                                )}
                                {transaction.metadata.target_audience && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Target Audience:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.target_audience}</span>
                                  </div>
                                )}
                                {transaction.metadata.concepts_count && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Concepts Generated:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.concepts_count}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* BVADM-specific details */}
                          {transaction.service && transaction.service.toUpperCase() === 'BVADM' && transaction.metadata && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-semibold text-slate-900">Manuscript Details</h5>
                                {transaction.metadata.report_id && (
                                  <Button
                                    size="sm"
                      className="w-full sm:w-auto"
                                    onClick={() => navigate(`/activity?view=bvadm&report=${transaction.metadata.report_id}`)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    📄 View Full Report
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {transaction.metadata.title && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Title:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.title}</span>
                                  </div>
                                )}
                                {transaction.metadata.subtitle && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Subtitle:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.subtitle}</span>
                                  </div>
                                )}
                                {transaction.metadata.author && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Author:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.author}</span>
                                  </div>
                                )}
                                {transaction.metadata.publishing_imprint && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Publishing Imprint:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.publishing_imprint}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* CCAT-specific details */}
                          {transaction.service && transaction.service.toUpperCase() === 'CCAT' && transaction.metadata && (
                            <div className="mt-4 p-4 bg-teal-50 rounded-lg border border-teal-100">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-semibold text-slate-900">Competitive Analysis Details</h5>
                                {transaction.metadata.report_id && (
                                  <Button
                                    size="sm"
                      className="w-full sm:w-auto"
                                    onClick={() => navigate(`/ccat?report=${transaction.metadata.report_id}`)}
                                    className="bg-teal-600 hover:bg-teal-700 text-white"
                                  >
                                    📊 View Full Report
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {transaction.metadata.title && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Title:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.title}</span>
                                  </div>
                                )}
                                {transaction.metadata.subtitle && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Subtitle:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.subtitle}</span>
                                  </div>
                                )}
                                {transaction.metadata.author && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Author:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.author}</span>
                                  </div>
                                )}
                                {transaction.metadata.genre && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Genre:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.genre}</span>
                                  </div>
                                )}
                                {transaction.metadata.publisher && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Publisher:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.publisher}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* BOCP-specific details */}
                          {transaction.service && transaction.service.toUpperCase() === 'BOCP' && transaction.metadata && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-semibold text-slate-900">Book Outline Details</h5>
                                {transaction.metadata.result_id && (
                                  <Button
                                    size="sm"
                      className="w-full sm:w-auto"
                                    onClick={() => navigate(`/bocp?report=${transaction.metadata.result_id}`)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    📖 View Full Outline
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {transaction.metadata.title && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Book Title:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.title}</span>
                                  </div>
                                )}
                                {transaction.metadata.author && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Author:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.author}</span>
                                  </div>
                                )}
                                {transaction.metadata.category && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Category:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.category}</span>
                                  </div>
                                )}
                                {transaction.metadata.chapters_count && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Chapters Generated:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.chapters_count}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* MMG-specific details */}
                          {transaction.service && transaction.service.toUpperCase() === 'MMG' && transaction.metadata && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-semibold text-slate-900">Metadata Report Details</h5>
                                {transaction.metadata.result_id && (
                                  <Button
                                    size="sm"
                      className="w-full sm:w-auto"
                                    onClick={() => navigate(`/mmg?report=${transaction.metadata.result_id}`)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    📈 View Full Report
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {transaction.metadata.title && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Book Title:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.title}</span>
                                  </div>
                                )}
                                {transaction.metadata.author && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Author:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.author}</span>
                                  </div>
                                )}
                                {transaction.metadata.genre && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Genre:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.genre}</span>
                                  </div>
                                )}
                                {transaction.metadata.categories_count && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Categories Generated:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.categories_count}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* CCG-specific details */}
                          {transaction.service && (transaction.service.toUpperCase() === 'CCG' || transaction.service.toUpperCase() === 'CDDA') && transaction.metadata && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                              <div className="flex justify-between items-start mb-3">
                                <h5 className="font-semibold text-slate-900">Character Cast Details</h5>
                                {transaction.metadata.result_id && (
                                  <Button
                                    size="sm"
                      className="w-full sm:w-auto"
                                    onClick={() => navigate(`/activity?view=ccg&result=${transaction.metadata.result_id}`)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                  >
                                    🎭 View Full Cast
                                  </Button>
                                )}
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {transaction.metadata.story_title && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Story Title:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.story_title}</span>
                                  </div>
                                )}
                                {transaction.metadata.genre && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Genre:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.genre}</span>
                                  </div>
                                )}
                                {transaction.metadata.setting && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Setting:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.setting}</span>
                                  </div>
                                )}
                                {transaction.metadata.characters_count && (
                                  <div>
                                    <span className="text-slate-500 text-sm block">Characters Generated:</span>
                                    <span className="font-medium text-slate-900">{transaction.metadata.characters_count}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {transaction.package_name && (
                            <div className="mt-3">
                              <span className="text-slate-500 text-sm">Package:</span>
                              <span className="ml-2 font-medium">{transaction.package_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      
      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendReceiptEmail}
        serviceName={lastCharge?.serviceName || "Service"}
        amount={lastCharge?.amount || 0}
        bookTitle={lastCharge?.bookTitle || ""}
      />
      
      {/* Referral Modal */}
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />
      
      <GlobalFooter />
    </div>
  );
};

// User Dashboard Component (displays activity, results, and workflows)
// Note: Route is /activity but user-facing label is "Dashboard"


// MMKG Reports List Component
const MMKGReportsList = ({ result }) => {
  const [generatingReport, setGeneratingReport] = useState(null);
  const [localReports, setLocalReports] = useState(result.reports || {});

  const reportTypes = [
    { key: 'marketing_analysis', label: 'Marketing Analysis', icon: '📊' },
    { key: 'press_release', label: 'Press Release', icon: '📰' },
    { key: 'author_bio', label: 'Author Bio', icon: '👤' },
    { key: 'one_sheet', label: 'Media One-Sheet', icon: '📄' },
    { key: 'interview_questions', label: 'Interview Questions', icon: '🎤' },
    { key: 'book_description_short', label: 'Book Description (Short)', icon: '📝' },
    { key: 'book_description_long', label: 'Book Description (Long)', icon: '📖' },
    { key: 'social_media_posts', label: 'Social Media Posts', icon: '📱' }
  ];

  const generateReport = async (reportType) => {
    try {
      setGeneratingReport(reportType);
      
      const response = await axios.post(
        `${API}/mmkg/generate-report/${result.id}?report_type=${reportType}`,
        {},
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Update local state with generated report
        setLocalReports(prev => ({
          ...prev,
          [reportType]: {
            generated: true,
            content: response.data.content,
            generated_at: new Date().toISOString()
          }
        }));
        
        toast.success(`${reportType.replace(/_/g, ' ')} generated successfully!`);
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(null);
    }
  };

  const handlePrintReport = (reportType) => {
    // TODO: Implement print functionality with logo, metadata, and date
    toast.info('Print functionality coming soon');
  };

  const handleExportPDF = (reportType) => {
    // TODO: Implement PDF export functionality
    toast.info('PDF export functionality coming soon');
  };

  return (
    <div className="space-y-2">
      {reportTypes.map((report) => {
        const isGenerated = localReports[report.key]?.generated;
        const isGenerating = generatingReport === report.key;
        
        return (
          <div
            key={report.key}
            className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center space-x-3 flex-1">
              <span className="text-2xl">{report.icon}</span>
              <div className="flex-1">
                <button
                  onClick={() => !isGenerated && !isGenerating && generateReport(report.key)}
                  disabled={isGenerated || isGenerating}
                  className={`text-left ${isGenerated ? 'font-bold cursor-default' : 'hover:text-orange-600 cursor-pointer'} ${isGenerating ? 'opacity-50' : ''}`}
                  title={!isGenerated && !isGenerating ? 'Click here to create this report' : ''}
                >
                  {isGenerating && <span className="inline-block animate-spin mr-2">⚙️</span>}
                  {isGenerated && <span className="mr-2 text-green-600">✓</span>}
                  {report.label}
                </button>
                {!isGenerated && !isGenerating && (
                  <p className="text-xs text-slate-500 mt-1">Click to generate this report</p>
                )}
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => handlePrintReport(report.key)}
                disabled={!isGenerated}
                className={`px-3 py-1 text-sm rounded border ${
                  isGenerated
                    ? 'border-blue-600 text-blue-600 hover:bg-blue-50'
                    : 'border-slate-300 text-slate-400 cursor-not-allowed'
                }`}
                title={isGenerated ? 'Print report' : 'Generate report first'}
              >
                🖨️ Print
              </button>
              <button
                onClick={() => handleExportPDF(report.key)}
                disabled={!isGenerated}
                className={`px-3 py-1 text-sm rounded border ${
                  isGenerated
                    ? 'border-red-600 text-red-600 hover:bg-red-50'
                    : 'border-slate-300 text-slate-400 cursor-not-allowed'
                }`}
                title={isGenerated ? 'Export as PDF' : 'Generate report first'}
              >
                📄 PDF
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const UserDashboard = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bctgResults, setBctgResults] = useState([]);
  const [bvadmResults, setBvadmResults] = useState([]);
  const [ccatResults, setCcatResults] = useState([]);
  const [brecResults, setBrecResults] = useState([]);
  const [bocpResults, setBocpResults] = useState([]);
  const [mmgResults, setMmgResults] = useState([]);
  const [abgResults, setAbgResults] = useState([]);
  const [pdeResults, setPdeResults] = useState([]);
  const [ermtResults, setErmtResults] = useState([]);
  const [pmpbResults, setPmpbResults] = useState([]);
  const [ccgResults, setCcgResults] = useState([]);
  const [ksgResults, setKsgResults] = useState([]);
  const [akswResults, setAkswResults] = useState([]);
  const [mmkgResults, setMmkgResults] = useState([]);
  const [smmkResults, setSmmkResults] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);
  const [downloadingEpubId, setDownloadingEpubId] = useState(null);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Payment flow state management
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [showServiceCharge, setShowServiceCharge] = useState(false);
  const [serviceInfo, setServiceInfo] = useState(null);
  const [pendingService, setPendingService] = useState(null);
  
  // Email receipt modal state (for sending receipts from transaction history)
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);
  
  // Referral modal state
  const [showReferralModal, setShowReferralModal] = useState(false);
  
  // Full-screen view state management
  const [showFullScreenView, setShowFullScreenView] = useState(false);
  const [fullScreenResult, setFullScreenResult] = useState(null);
  
  // User usage tracking
  const [userUsage, setUserUsage] = useState({ remaining_free: 3, usage_count: 0 });
  
  // User stats including rewards
  const [userStats, setUserStats] = useState({ rewardsPoints: 0 });
  
  // Active service tab state - default to overview
  const [activeServiceTab, setActiveServiceTab] = useState(null); // Start with no selection for faster load
  
  // Selected workflow state for showing service cards
  const [selectedWorkflow, setSelectedWorkflow] = useState('writing');
  
  // Loading state for workflow data
  const [workflowDataLoading, setWorkflowDataLoading] = useState(false);
  const [countsLoaded, setCountsLoaded] = useState(false);
  
  // Track if URL parameter has been processed (to avoid multiple triggers)
  const urlParamProcessedRef = useRef(false);
  
  // Help modal state
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [helpModalContent, setHelpModalContent] = useState({ title: '', content: '' });

  // Helper function to open help modal
  const openHelpModal = (title, content) => {
    setHelpModalContent({ title, content });
    setHelpModalOpen(true);
  };

  // IHS Help Content Constants
  const helpContentBCTG = `
    <h3>What this service does</h3>
    <p>The Book Concept & Title Generator (BCTG) helps you discover and refine book ideas by generating multiple concepts complete with compelling titles, taglines, and brief descriptions tailored to your chosen genre and theme.</p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Select a genre (e.g., Fiction, Non-Fiction, Self-Help, Mystery, Romance)</li>
      <li>Choose a theme or topic area you want to explore</li>
      <li>Optionally, provide any specific keywords or ideas you'd like incorporated</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Being too vague with your theme—specific themes generate better, more focused concepts</li>
      <li>Not reviewing all generated options—sometimes the best concept isn't the first one</li>
      <li>Forgetting to save concepts you like before generating new ones</li>
      <li>Choosing a genre that doesn't match your intended audience or writing style</li>
    </ul>

    <h3>What you'll get</h3>
    <p>You'll receive 3-5 unique book concepts, each with a catchy title, subtitle/tagline, and a brief description that outlines the core premise. These concepts are designed to inspire your writing and help you choose the direction that resonates most.</p>

    <h3>Processing time</h3>
    <p>Concept generation typically takes 1–2 minutes to complete. Complex requests or high system demand may take slightly longer. You'll see a progress indicator while DiAi works on your concepts.</p>
  `;

  const helpContentBOCP = `
    <h3>What this service does</h3>
    <p>The Book Outline & Chapter Planner (BOCP) helps you structure your book by generating a clear outline and organizing your chapter flow.</p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>A working book concept or idea</li>
      <li>The genre or category of your book</li>
      <li>Any themes or key messages you want to include</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Submitting too little information about the book concept</li>
      <li>Ignoring the genre—this leads to mismatched structure recommendations</li>
      <li>Forgetting to mention target audience when relevant</li>
    </ul>

    <h3>What you'll get</h3>
    <p>A structured outline with chapter-level descriptions designed to guide your writing process.</p>

    <h3>Processing time</h3>
    <p>Outline generation typically takes 2–4 minutes, depending on the complexity of your book concept and the number of chapters. Longer or more detailed outlines may take up to 5 minutes. A progress indicator will keep you updated.</p>
  `;

  const helpContentCCG = `
    <h3>What this service does</h3>
    <p>
      The Character & Cast Generator (CCG) helps you develop compelling characters for your story.
      It creates detailed character profiles and supports cast development for larger narratives.
      This is especially useful for fiction authors who want stronger characterization and consistency throughout their book.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your book's genre and general tone (e.g., dark, humorous, inspirational)</li>
      <li>A brief story concept or premise</li>
      <li>Any specific character roles you want to include (hero, antagonist, mentor, etc.)</li>
      <li>Optional: names, traits, or backstory elements you've already decided on</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Submitting too little information about the story concept, leading to vague character profiles</li>
      <li>Not specifying genre, which affects the type of characters generated</li>
      <li>Requesting too many characters at once—smaller batches create better depth and detail</li>
      <li>Leaving out target audience details when tone is important</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You will receive complete character profiles, including names, backgrounds, motivations, conflicts, and story roles.
      For larger casts, you may also receive relationship notes and dynamics that support your plot structure.
    </p>

    <h3>Processing time</h3>
    <p>
      Most character sets generate in 1–2 minutes.
      Larger or more complex requests may take up to 3 minutes, depending on the level of detail and number of characters.
      A progress indicator will appear while the system works.
    </p>
  `;

  const helpContentBVADM = `
    <h3>What this service does</h3>
    <p>
      The Book Viability & Development Model (BVADM) is our flagship premium service that analyzes your manuscript or book concept to assess market viability, audience fit, and commercial potential.
      It provides detailed recommendations for improving your book's positioning, messaging, and market appeal before you publish.
      This is an essential tool for serious authors who want data-driven insights into their book's success potential.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your manuscript file (PDF, DOCX, or TXT format) or a detailed book concept</li>
      <li>Genre and target audience information</li>
      <li>Your intended publishing path (traditional, self-publishing, hybrid)</li>
      <li>Optional: comparable titles or authors in your market space</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Uploading incomplete manuscripts—analysis quality depends on having sufficient content</li>
      <li>Not providing genre or audience details, which limits market positioning analysis</li>
      <li>Submitting files in unsupported formats (always use PDF, DOCX, or TXT)</li>
      <li>Expecting instant results—this is a comprehensive analysis that takes time</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a detailed viability report including market analysis, audience targeting recommendations, competitive positioning, strengths and weaknesses assessment, and actionable development suggestions.
      The report also includes marketing messaging recommendations and commercial viability scoring.
    </p>

    <h3>Processing time</h3>
    <p>
      Analysis typically takes 3–5 minutes for standard manuscripts (up to 100 pages).
      Longer manuscripts or complex market analyses may take 5–8 minutes.
      File upload and processing time depends on file size.
      You'll see detailed progress updates throughout the analysis.
    </p>
  `;

  const helpContentPDE = `
    <h3>What this service does</h3>
    <p>
      The Professional Developmental Editor (PDE) analyzes your manuscript and provides comprehensive editorial feedback on plot structure, character development, pacing, dialogue, and narrative flow.
      This premium service offers the type of developmental editing typically provided by professional editors, helping you strengthen your manuscript before final publication.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your manuscript file (PDF, DOCX, or TXT format)</li>
      <li>Genre information to ensure appropriate editorial standards</li>
      <li>Target word count or chapter count (if available)</li>
      <li>Any specific areas of concern you want the editor to focus on</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Uploading unfinished drafts—developmental editing works best on complete manuscripts</li>
      <li>Not specifying genre, which affects editorial expectations and standards</li>
      <li>Submitting files with formatting issues that interfere with analysis</li>
      <li>Expecting line editing or proofreading—this service focuses on big-picture development</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a detailed editorial report covering story structure, character arcs, pacing issues, plot holes, dialogue effectiveness, and narrative consistency.
      The report includes specific recommendations for strengthening weak areas and maximizing your manuscript's impact.
    </p>

    <h3>Processing time</h3>
    <p>
      Editorial analysis typically takes 4–6 minutes for manuscripts up to 100 pages.
      Longer manuscripts (100-300 pages) may take 6–10 minutes.
      Very large files or complex narratives may require additional processing time.
      Progress indicators will keep you updated throughout the analysis.
    </p>
  `;

  const helpContentCCAT = `
    <h3>What this service does</h3>
    <p>
      The Competitor & Comparative Analysis Tool (CCAT) researches your book's market space and identifies competing titles, similar authors, and positioning opportunities.
      It helps you understand where your book fits in the marketplace and how to differentiate it from similar works.
      This analysis is essential for developing effective marketing strategies and understanding your competitive landscape.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your book title and a clear description or synopsis</li>
      <li>Genre and sub-genre information</li>
      <li>Target audience demographics (age, interests, reading preferences)</li>
      <li>Optional: comparable titles you're already aware of</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Providing too broad or generic descriptions, which lead to unfocused competitor lists</li>
      <li>Not specifying your genre clearly—this affects which competitors are identified</li>
      <li>Expecting analysis of unpublished or very niche markets with limited data</li>
      <li>Forgetting to mention what makes your book unique or different</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a detailed report identifying 5-10 competing titles, analysis of their positioning and success factors, market gap opportunities, and recommendations for differentiating your book.
      The report also includes audience overlap analysis and suggested positioning strategies.
    </p>

    <h3>Processing time</h3>
    <p>
      Competitive analysis typically takes 2–3 minutes for most genres.
      Complex or highly specialized markets may take up to 4 minutes.
      The system performs real-time market research, so timing may vary based on data availability.
    </p>
  `;

  const helpContentMMG = `
    <h3>What this service does</h3>
    <p>
      The Manuscript & Metadata Generator (MMG) creates professional metadata for your book including descriptions, keywords, categories, and back cover copy.
      It helps you optimize your book's discoverability on retail platforms and ensures your metadata aligns with industry best practices.
      This service is essential for authors preparing to publish or market their books effectively.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your book title and a completed manuscript or detailed synopsis</li>
      <li>Genre and sub-genre information</li>
      <li>Target audience and age range</li>
      <li>Key themes, topics, or selling points you want emphasized</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Submitting vague or incomplete book descriptions—specificity improves metadata quality</li>
      <li>Not identifying your primary genre clearly, which affects keyword and category recommendations</li>
      <li>Forgetting to mention unique selling points or what makes your book different</li>
      <li>Expecting metadata to work across all platforms without customization</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a complete metadata package including a compelling book description, keyword list optimized for search, category recommendations for major platforms, back cover copy, and tagline options.
      All content is designed to maximize discoverability and appeal to your target audience.
    </p>

    <h3>Processing time</h3>
    <p>
      Metadata generation typically takes 1–2 minutes for most books.
      Complex or multi-genre books may take up to 3 minutes.
      The system analyzes your content and generates optimized metadata tailored to your specific book.
    </p>
  `;

  const helpContentERMT = `
    <h3>What this service does</h3>
    <p>
      The EPUB-Ready Manuscript Transformer (ERMT) converts your manuscript into properly formatted EPUB files ready for digital publishing on platforms like Amazon Kindle, Apple Books, and other ebook retailers.
      This premium service handles all the technical formatting, ensures proper chapter breaks, adds metadata, and creates a professional ebook file.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your completed manuscript file (DOCX, PDF, or TXT format)</li>
      <li>Chapter titles and structure information</li>
      <li>Book metadata (title, author name, description)</li>
      <li>Optional: cover image file if you want it embedded</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Uploading manuscripts with inconsistent formatting—clean formatting produces better EPUB files</li>
      <li>Not providing clear chapter markers, which affects the table of contents</li>
      <li>Submitting files with embedded images without high-resolution versions</li>
      <li>Expecting instant conversion—proper EPUB formatting takes time to ensure quality</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a professionally formatted EPUB file with proper chapter navigation, clean typography, embedded metadata, and compatibility across all major ebook platforms.
      The file will be ready for upload to publishing platforms or distribution services.
    </p>

    <h3>Processing time</h3>
    <p>
      EPUB conversion typically takes 3–5 minutes for standard manuscripts (up to 100 pages).
      Longer manuscripts or those with complex formatting may take 5–8 minutes.
      Files with many images or special formatting elements may require additional processing time.
    </p>
  `;

  const helpContentPMPB = `
    <h3>What this service does</h3>
    <p>
      The PDF Merger & Page Break (PMPB) tool combines multiple PDF files into a single document and adds proper page breaks where needed.
      It's useful for authors assembling manuscript sections, combining front matter with body content, or preparing print-ready files.
      This service handles the technical aspects of PDF manipulation so you can focus on your content.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Two or more PDF files you want to merge</li>
      <li>The desired order for your files</li>
      <li>Optional: specific page break locations or section dividers</li>
      <li>All files should be readable PDFs (not scanned images or protected files)</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Uploading password-protected or encrypted PDFs—these cannot be processed</li>
      <li>Not organizing files in the correct order before uploading</li>
      <li>Mixing different page sizes or orientations without specifying handling preferences</li>
      <li>Uploading corrupted or incomplete PDF files</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a single merged PDF file with all your documents combined in the order specified, proper page breaks inserted, and consistent formatting throughout.
      The output file will be ready for printing, distribution, or further editing.
    </p>

    <h3>Processing time</h3>
    <p>
      PDF merging typically takes 1–2 minutes for small to medium files (under 50 pages total).
      Larger documents or multiple files may take 2–4 minutes.
      Processing time increases with file size and number of pages being merged.
    </p>
  `;

  const helpContentAKSW = `
    <h3>What this service does</h3>
    <p>
      The Amazon KDP Submission Wizard (AKSW) prepares all the information you need to publish your book on Amazon's Kindle Direct Publishing platform.
      This premium service generates optimized categories, keywords, book descriptions, and other metadata specifically formatted for Amazon's requirements.
      It streamlines the KDP submission process and helps maximize your book's discoverability on Amazon.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your completed book with title and description</li>
      <li>Genre and target audience information</li>
      <li>Key themes, topics, or selling points</li>
      <li>Optional: comparable books already on Amazon</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Not researching your genre's category structure on Amazon beforehand</li>
      <li>Providing generic descriptions that don't highlight what makes your book unique</li>
      <li>Ignoring keyword research—keywords significantly affect discoverability</li>
      <li>Forgetting to mention your target reader's specific interests or pain points</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a complete KDP submission package including optimized Amazon categories (primary and secondary), 7 backend keywords, a compelling book description formatted for Amazon, author bio suggestions, and pricing recommendations.
      Everything is ready to copy directly into your KDP dashboard.
    </p>

    <h3>Processing time</h3>
    <p>
      KDP package generation typically takes 2–3 minutes.
      Complex books with multiple genres or specialized topics may take up to 4 minutes.
      The system performs Amazon-specific market research to optimize your submission.
    </p>
  `;

  const helpContentABG = `
    <h3>What this service does</h3>
    <p>
      The Author Biography Generator (ABG) creates professional author bios in multiple lengths and formats for use on book covers, retail platforms, websites, and social media.
      It helps you present yourself professionally and compellingly to readers, media, and industry professionals.
      This service is essential for authors building their brand and establishing credibility.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Basic information about your background, experience, and credentials</li>
      <li>Your writing genre and any published works</li>
      <li>Notable achievements, awards, or relevant expertise</li>
      <li>Optional: personal details you want to include (location, interests, influences)</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Providing too little information—specific details make bios more compelling and authentic</li>
      <li>Not mentioning relevant credentials or expertise that establish your authority</li>
      <li>Focusing only on writing accomplishments and ignoring interesting personal details</li>
      <li>Forgetting to specify which length or format you need for specific platforms</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive 3-4 professionally written author bios in different lengths: a short version (50-75 words) for book covers and social media, a medium version (150-200 words) for retail platforms, and a long version (300-400 words) for your website or press materials.
      Each bio is crafted to position you as a credible, engaging author.
    </p>

    <h3>Processing time</h3>
    <p>
      Biography generation typically takes 1–2 minutes to create multiple versions.
      Complex backgrounds or requests for specialized positioning may take up to 3 minutes.
      The system crafts professional copy tailored to your genre and audience.
    </p>
  `;

  const helpContentBREC = `
    <h3>What this service does</h3>
    <p>
      The Book Review & Endorsement Creator (BREC) generates professional-quality book reviews and endorsement quotes that you can use for marketing materials, book covers, and promotional campaigns.
      While these are AI-generated and should be clearly labeled as such, they demonstrate how critics and readers might respond to your book.
      This service helps you develop marketing copy and understand your book's appeal.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your book title, genre, and a detailed description or synopsis</li>
      <li>Key themes, strengths, or unique selling points</li>
      <li>Target audience information</li>
      <li>Optional: the tone or style of reviews you want (scholarly, enthusiastic, analytical)</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Not providing enough detail about your book's content and themes</li>
      <li>Expecting these AI reviews to replace authentic reader reviews</li>
      <li>Forgetting to disclose that reviews are AI-generated when using them publicly</li>
      <li>Not specifying the type of reviewer perspective you want (reader, critic, industry professional)</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive 3-5 professionally crafted review-style responses and endorsement quotes in varying tones and perspectives.
      These can serve as marketing copy examples, placeholder text for design mockups, or inspiration for soliciting real reviews.
      Each review highlights different aspects of your book's appeal.
    </p>

    <h3>Processing time</h3>
    <p>
      Review generation typically takes 1–2 minutes to create multiple perspectives.
      Complex books or requests for specialized reviewer types may take up to 3 minutes.
      The system analyzes your book's content to generate authentic-sounding responses.
    </p>
  `;

  const helpContentKSG = `
    <h3>What this service does</h3>
    <p>
      The Keynote Speech Generator (KSG) creates compelling speeches and presentation content for author events, book launches, conferences, and speaking engagements.
      This premium service helps you develop professional, engaging talks that connect with your audience and promote your work effectively.
      It's perfect for authors who need to prepare for public speaking opportunities.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>The topic or theme of your speech</li>
      <li>Your target audience and event context (book launch, conference, workshop, etc.)</li>
      <li>Desired speech length and tone (inspirational, educational, entertaining)</li>
      <li>Key messages or themes from your book you want to emphasize</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Not specifying your audience—speeches need to be tailored to who's listening</li>
      <li>Providing unclear objectives for the speech (inform, inspire, sell, educate)</li>
      <li>Forgetting to mention time constraints or event format requirements</li>
      <li>Not including personal stories or anecdotes that make speeches more engaging</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a fully structured keynote speech including an attention-grabbing opening, organized main points with supporting content, storytelling elements, calls to action, and a memorable closing.
      The speech is formatted for easy reading and includes timing suggestions and delivery notes.
    </p>

    <h3>Processing time</h3>
    <p>
      Speech generation typically takes 2–3 minutes for standard keynote lengths (15-30 minutes).
      Longer speeches or complex topics may take up to 4 minutes.
      The system crafts cohesive narratives tailored to your audience and objectives.
    </p>
  `;

  const helpContentMMKG = `
    <h3>What this service does</h3>
    <p>
      The Marketing Media Kit Generator (MMKG) creates a comprehensive press and marketing package for your book including press releases, media pitches, one-sheets, and promotional copy.
      This premium service provides all the professional marketing materials you need to approach media outlets, bloggers, podcasters, and reviewers.
      It's essential for authors launching serious marketing campaigns.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your book details (title, description, publication date)</li>
      <li>Author biography and credentials</li>
      <li>Key selling points and what makes your book newsworthy</li>
      <li>Target media outlets or audience demographics</li>
      <li>Optional: any reviews, endorsements, or awards already received</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Not identifying what makes your book newsworthy or timely—media needs angles</li>
      <li>Providing generic information that doesn't differentiate your book</li>
      <li>Forgetting to include author expertise or platform information</li>
      <li>Not specifying your target media types (print, podcast, blog, broadcast)</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive a complete media kit including a press release, one-page book overview, author bio, suggested interview questions, media pitch templates, and promotional copy for various platforms.
      Everything is professionally formatted and ready to send to media contacts.
    </p>

    <h3>Processing time</h3>
    <p>
      Media kit generation typically takes 2–4 minutes to create the complete package.
      Complex books with multiple angles or specialized positioning may take up to 5 minutes.
      The system generates cohesive materials across all components.
    </p>
  `;

  const helpContentSMMK = `
    <h3>What this service does</h3>
    <p>
      The Social Media Marketing Kit (SMMK) creates a complete content package for promoting your book across social media platforms including post templates, hashtag strategies, engagement tactics, and platform-specific content.
      This premium service provides weeks of ready-to-use social media content tailored to your book and audience.
      It's perfect for authors who want to build buzz and engage readers online.
    </p>

    <h3>What you need before you begin</h3>
    <ul>
      <li>Your book details and key themes or messages</li>
      <li>Target audience demographics and interests</li>
      <li>Which social media platforms you plan to use (Instagram, Facebook, Twitter/X, TikTok, etc.)</li>
      <li>Your book's unique selling points or what makes it shareable</li>
      <li>Optional: any existing brand voice or content style guidelines</li>
    </ul>

    <h3>Common mistakes to avoid</h3>
    <ul>
      <li>Not specifying which platforms you'll use—content differs significantly by platform</li>
      <li>Providing too little information about your target audience's interests and habits</li>
      <li>Expecting one-size-fits-all content—effective social media is platform-specific</li>
      <li>Not mentioning your book's most engaging or visual elements for social sharing</li>
    </ul>

    <h3>What you'll get</h3>
    <p>
      You'll receive 20-30 social media post templates, hashtag strategies for each platform, content themes and posting schedules, engagement tactics, caption templates, and platform-specific recommendations.
      Content is designed to build anticipation, engage readers, and drive book sales.
    </p>

    <h3>Processing time</h3>
    <p>
      Social media kit generation typically takes 2–3 minutes to create the complete content package.
      Multi-platform requests or complex audience targeting may take up to 4 minutes.
      The system generates cohesive content across all requested platforms.
    </p>
  `;

  // Helper function to calculate service pricing based on user profile
  const getServicePrice = (serviceCode, basePrice, isPremium) => {
    if (!user) return { price: basePrice, label: `$${basePrice.toFixed(2)}` };
    
    const role = user.role;
    const subscription = user.subscription_status || 'None';
    
    // Free access for admin, beta_tester, and student
    if (role === 'admin' || role === 'beta_tester' || role === 'student') {
      return { price: 0, label: 'FREE' };
    }
    
    // Subscribers get different pricing
    if (subscription !== 'None' && subscription !== null) {
      if (isPremium) {
        // Premium services: 50% off for subscribers
        const discountedPrice = basePrice / 2;
        return { price: discountedPrice, label: `$${discountedPrice.toFixed(2)}` };
      } else {
        // Standard services: FREE for subscribers
        return { price: 0, label: 'FREE' };
      }
    }
    
    // A la carte / free users pay full price (after free uses for standard)
    if (!isPremium && serviceCode === 'BCTG') {
      // BCTG has 3 free uses/month
      return { price: basePrice, label: `$${basePrice.toFixed(2)}*`, note: '*First 3 uses free/month' };
    }
    
    return { price: basePrice, label: `$${basePrice.toFixed(2)}` };
  };

  // Function to refresh user data from backend
  const refreshUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const updatedUser = response.data;
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      return null;
    }
  };

  // Auto-refresh user data when page becomes visible (returns from service)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Page is visible again, refresh user data
        refreshUserData();
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also refresh on mount
    refreshUserData();

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Check for service from navigation state and set active tab - WAIT FOR DATA
  useEffect(() => {
    // Only process when data is fully loaded and we haven't processed yet
    if (countsLoaded && !loading && !urlParamProcessedRef.current && location.state?.service) {
      const serviceFromState = location.state.service;
      
      
      // Mark as processed
      urlParamProcessedRef.current = true;
      
      // Add a small delay to ensure React state has propagated
      setTimeout(() => {
        setActiveServiceTab(serviceFromState);
        
        // Clear the navigation state to prevent re-triggering
        window.history.replaceState({}, document.title);
      }, 500); // 500ms delay for state propagation
    }
  }, [countsLoaded, loading, location.state]); // Re-run when these change

  // Scroll to service dashboard divider when a service tab is selected - UNIVERSAL SOLUTION
  // Autoscroll disabled - anchor points kept for future use
  // useEffect(() => {
  //   // Only scroll after data has loaded
  //   if (!loading && activeServiceTab && activeServiceTab !== 'overview') {
  //     // Delay to ensure DOM is fully updated and rendered
  //     setTimeout(() => {
  //       // Scroll to the visible divider line (fixed scroll point)
  //       const dashboardAnchor = document.getElementById('service-dashboard-anchor');
  //       if (dashboardAnchor) {
  //         // Scroll so the divider appears at top of viewport with small margin
  //         const elementPosition = dashboardAnchor.getBoundingClientRect().top;
  //         const offsetPosition = window.pageYOffset + elementPosition - 20; // 20px from top
  //         
  //         window.scrollTo({
  //           top: offsetPosition,
  //           behavior: 'smooth'
  //         });
  //         
  //       } else {
  //         // Fallback if divider not found
  //         window.scrollTo({ top: 600, behavior: 'smooth' });
  //       }
  //     }, 300); // Increased delay for DOM rendering after data load
  //   } else if (!loading && activeServiceTab === 'overview') {
  //     // Scroll to top for overview
  //     window.scrollTo({ top: 0, behavior: 'smooth' });
  //   }
  // }, [activeServiceTab, loading]);

  // Scroll to service cards when workflow is selected - DISABLED
  // useEffect(() => {
  //   if (selectedWorkflow && !activeServiceTab) {
  //     setTimeout(() => {
  //       const serviceCardsSection = document.getElementById('service-cards-section');
  //       if (serviceCardsSection) {
  //         // Scroll with offset to account for sticky header
  //         const yOffset = -20; // 20px padding from top
  //         const y = serviceCardsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
  //         window.scrollTo({ top: y, behavior: 'smooth' });
  //       }
  //     }, 150);
  //   }
  // }, [selectedWorkflow, activeServiceTab]);

  // Handle URL parameters for direct navigation to results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    const resultId = urlParams.get('result');
    const reportId = urlParams.get('report');
    
    if (view && (resultId || reportId)) {
      // Switch to the appropriate tab
      setActiveServiceTab(view);
      
      // Wait for results to load, then open the specific result
      const timer = setTimeout(() => {
        if (view === 'bctg' && resultId) {
          const result = bctgResults.find(r => r.id === resultId);
          if (result) {
            openFullScreenView(result);
          }
        } else if (view === 'bvadm' && reportId) {
          const report = bvadmResults.find(r => r.id === reportId);
          if (report) {
            setSelectedResult(report);
            navigate('/bvadm');
          }
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [bctgResults, bvadmResults]);

  // Simple delete function for BVADM reports
  const handleDeleteBVADM = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this BVADM report? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API}/bvadm/reports/${reportId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setBvadmResults(prev => prev.filter(r => r.id !== reportId));
        toast.success("BVADM report deleted successfully!");
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast.error("Failed to delete BVADM report");
    }
  };

  const handleDeleteCCAT = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this CCAT report? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/ccat/reports/${reportId}`, { withCredentials: true });
      if (response.status === 200) {
        setCcatResults(ccatResults.filter(r => r.id !== reportId));
        toast.success("CCAT report deleted successfully");
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast.error("Failed to delete CCAT report");
    }
  };

  const handleDeleteReport = async (reportId, serviceType) => {
    const serviceName = serviceType.toUpperCase();
    const itemType = serviceType === 'ermt' ? 'conversion' : 'report';
    if (!window.confirm(`Are you sure you want to delete this ${serviceName} ${itemType}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      let endpoint;
      if (serviceType === 'ermt') {
        endpoint = `${API}/ermt/results/${reportId}`;
      } else {
        endpoint = `${API}/${serviceType}/reports/${reportId}`;
      }
      
      const response = await axios.delete(endpoint, { withCredentials: true });
      if (response.status === 200) {
        // Update the appropriate results state
        if (serviceType === 'pde') {
          setPdeResults(pdeResults.filter(r => r.id !== reportId));
        } else if (serviceType === 'ermt') {
          setErmtResults(ermtResults.filter(r => r.id !== reportId));
        }
        toast.success(`${serviceName} ${itemType} deleted successfully`);
        
        // Reload data to refresh the view
        loadInitialData();
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast.error(`Failed to delete ${serviceName} ${itemType}`);
    }
  };

  // Send receipt email for past transaction - opens modal
  const handleSendReceiptFromHistory = async (transaction) => {
    
    try {
      
      if (!transaction.id) {
        toast.error("Invalid transaction ID");
        return;
      }
      
      // Determine service name
      let serviceName = "Service";
      if (transaction.service) {
        if (transaction.service.toUpperCase() === 'BCTG') {
          serviceName = "Book Concept & Title Generator (BCTG)";
        } else if (transaction.service.toUpperCase() === 'BVADM') {
          serviceName = "Book Viability & Development Model (BVADM)";
        } else if (transaction.service.toUpperCase() === 'CCAT') {
          serviceName = "Competitor & Comparative Analysis Tool (CCAT)";
        } else if (transaction.service.toUpperCase() === 'BOCP') {
          serviceName = "Book Outline & Chapter Planner (BOCP)";
        } else if (transaction.service.toUpperCase() === 'MMG') {
          serviceName = "Manuscript Metadata Generator (MMG)";
        } else if (transaction.service.toUpperCase() === 'CCG' || transaction.service.toUpperCase() === 'CDDA') {
          serviceName = "Character & Cast Generator (CCG)";
        }
      }
      
      // Get book/project title from metadata
      let bookTitle = "";
      if (transaction.metadata) {
        if (transaction.metadata.title) {
          bookTitle = transaction.metadata.title;
        } else if (transaction.metadata.genre && transaction.metadata.theme) {
          bookTitle = `${transaction.metadata.genre} - ${transaction.metadata.theme}`;
        }
      }
      
      // Set modal data
      setLastTransactionId(transaction.id);
      setLastCharge({
        amount: transaction.amount || 0,
        serviceName: serviceName,
        bookTitle: bookTitle
      });
      
      // Show modal
      setShowEmailReceiptModal(true);
      
    } catch (error) {
      toast.error("Failed to open receipt modal");
    }
  };

  // Handle sending receipt from modal
  const handleSendReceiptEmail = async () => {
    try {
      
      if (!lastTransactionId) {
        toast.error("No transaction ID available");
        setShowEmailReceiptModal(false);
        return;
      }
      
      const response = await axios.post(`${API}/send-receipt-email`, {
        transaction_id: lastTransactionId,
        user_email: user.email
      }, { withCredentials: true });
      
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      toast.error(`Failed to send receipt: ${error.response?.data?.detail || error.message}`);
      setShowEmailReceiptModal(false);
    }
  };

  const handleCloseEmailReceiptModal = () => {
    setShowEmailReceiptModal(false);
  };

  // Payment Flow Functions
  const handleServiceAccess = async (serviceName, fromLocation = null) => {
    const queryParam = fromLocation ? `?from=${fromLocation}` : '';
    
    // EARLY EXIT: Payment-exempt users (beta_tester) get free access to all services
    // They should never see payment validation or be charged
    if (isPaymentExempt(user)) {
      navigate(`/${serviceName.toLowerCase()}${queryParam}`);
      return;
    }
    
    if (serviceName === 'BCTG') {
      
      if (!user) {
        return;
      }
      
      // Get monthly uses from user object or usage tracking
      const monthlyUses = user.usage_tracking?.bctg_monthly_count || user.monthly_bctg_uses || 0;
      const hasPaymentMethod = !!(user.payment_method || user.stripe_customer_id);
      
      // STEP 1: Check if user has used their 3 free reports
      if (monthlyUses < 3) {
        navigate(`/bctg${queryParam}`);
        return;
      }
      
      // STEP 2: User has used 3+ reports - check for payment method
      
      if (hasPaymentMethod) {
        navigate(`/bctg${queryParam}`);
        return;
      }
      
      // STEP 3: No payment method - show payment modal
      setPendingService('BCTG');
      setShowPaymentValidation(true);
      
    } else if (serviceName === 'BVADM') {
      
      if (!user) {
        return;
      }
      
      // BVADM always requires payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('BVADM');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/bvadm${queryParam}`);
      
    } else if (serviceName === 'CCAT') {
      
      if (!user) {
        return;
      }
      
      // CCAT - subscribers get free unlimited access
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(`/ccat${queryParam}`);
        return;
      }
      
      // À la carte users need payment method ($8.00 per use)
      // Free users must upgrade to à la carte first
      if (user.role === 'free_user') {
        setPendingService('CCAT');
        setShowPaymentValidation(true);
        return;
      }
      
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('CCAT');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/ccat${queryParam}`);
      
    } else if (serviceName === 'BOCP') {
      
      if (!user) {
        return;
      }
      
      // BOCP - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(`/bocp${queryParam}`);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('BOCP');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/bocp${queryParam}`);
      
    } else if (serviceName === 'MMG') {
      
      if (!user) {
        return;
      }
      
      // MMG - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(`/mmg${queryParam}`);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('MMG');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/mmg${queryParam}`);
      
    } else if (serviceName === 'ABG') {
      
      if (!user) {
        return;
      }
      
      // ABG - Standard service, subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate(`/abg${queryParam}`);
        return;
      }
      
      // Free users are blocked
      if (user.role === 'free_user') {
        setPendingService('ABG');
        setShowPaymentValidation(true);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('ABG');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/abg${queryParam}`);
      
    } else if (serviceName === 'CCG') {
      
      if (!user) {
        return;
      }
      
      // CCG - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual';
      
      if (isSubscriber) {
        navigate('/ccg');
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('CCG');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate('/ccg');
      
    } else if (serviceName === 'BREC') {
      
      if (!user) {
        return;
      }
      
      // BREC - subscribers get free access, others need payment method
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(`/brec${queryParam}`);
        return;
      }
      
      // Free users are blocked
      if (user.role === 'free_user') {
        setPendingService('BREC');
        setShowPaymentValidation(true);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('BREC');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/brec${queryParam}`);
      
    } else if (serviceName === 'KSG') {
      
      if (!user) {
        return;
      }
      
      // KSG - Premium service ($8), subscribers get 50% discount ($4)
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(`/ksg${queryParam}`);
        return;
      }
      
      // Free users are blocked
      if (user.role === 'free_user') {
        setPendingService('KSG');
        setShowPaymentValidation(true);
        return;
      }
      
      // Non-subscribers need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('KSG');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/ksg${queryParam}`);
      
    } else if (serviceName === 'MMKG') {
      
      if (!user) {
        return;
      }
      
      // MMKG - Premium service ($12), subscribers get 50% discount ($6)
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(`/mmkg${queryParam}`);
        return;
      }
      
      // Free users must add payment method and upgrade to a la carte
      if (user.role === 'free_user') {
        setPendingService('MMKG');
        setShowPaymentValidation(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('MMKG');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/mmkg${queryParam}`);
      
    } else if (serviceName === 'SMMK') {
      
      if (!user) {
        return;
      }
      
      // SMMK - Premium service ($15), subscribers get 50% discount ($7.50)
      const isSubscriber = user.subscription_status === 'monthly' || user.subscription_status === 'annual' || user.subscription_status === 'active';
      
      if (isSubscriber) {
        navigate(`/smmk${queryParam}`);
        return;
      }
      
      // Free users must add payment method and upgrade to a la carte
      if (user.role === 'free_user') {
        setPendingService('SMMK');
        setShowPaymentValidation(true);
        return;
      }
      
      // A la carte users need payment method
      if (!user.payment_method && !user.stripe_customer_id) {
        setPendingService('SMMK');
        setShowPaymentValidation(true);
        return;
      }
      
      navigate(`/smmk${queryParam}`);
      
    } else {
      navigate(`/${serviceName.toLowerCase()}`);
    }
  };




  const handlePaymentValidationSuccess = async () => {
    // Refresh user data to get updated payment method and role
    const updatedUser = await refreshUserData();
    
    // Close modal immediately
    setShowPaymentValidation(false);
    
    // Show success message
    toast.success('Payment method added successfully! Role updated to A la Carte.');
    
    // Navigate to the service page after payment method is added
    if (pendingService) {
      const servicePath = `/${pendingService.toLowerCase()}`;
      setTimeout(() => navigate(servicePath), 1000);
    }
  };

  const handleServiceChargeConfirm = async () => {
    try {
      // Payment is now handled by backend service endpoints
      setShowServiceCharge(false);
      toast.success(`Redirecting to ${pendingService}...`);
      setTimeout(() => {
        window.location.href = `/${pendingService.toLowerCase()}`;
      }, 1000);
    } catch (error) {
      toast.error('Payment failed. Please try again.');
    }
  };

  const handleModalClose = () => {
    setShowPaymentValidation(false);
    setShowServiceCharge(false);
    setPendingService(null);
    setServiceInfo(null);
  };

  const openFullScreenView = (result) => {
    setFullScreenResult(result);
    setShowFullScreenView(true);
  };

  const closeFullScreenView = () => {
    setShowFullScreenView(false);
    setFullScreenResult(null);
  };

  const handleCreateNewBCTG = async () => {
    // Close full screen view first
    closeFullScreenView();
    
    // Check usage and payment validation before navigating
    try {
      const usageResponse = await axios.get(`${API}/bctg/check-usage`, { withCredentials: true });
      const usageInfo = usageResponse.data;
      
      const freeUsesThisMonth = usageInfo.free_usage_count || 0;
      const hasPaymentMethod = !!(user.payment_method || user.stripe_customer_id);
      
      // If user has free uses remaining or has payment method, navigate to form
      if (freeUsesThisMonth < 3 || hasPaymentMethod) {
        navigate('/bctg');
      } else {
        // Show payment validation message and navigate to payment setup
        toast.info('Payment method required to continue');
        navigate('/profile?tab=payment');
      }
    } catch (error) {
      // On error, just navigate to form
      navigate('/bctg');
    }
  };

  useEffect(() => {
    // Defer initial data fetch to allow page to render first
    setTimeout(() => {
      fetchCountsOnly();
    }, 100); // Let the page render first, then fetch data
  }, []);

  // Fetch full data when Overview is selected
  useEffect(() => {
    if (activeServiceTab === 'overview') {
      fetchAllResults();
    }
  }, [activeServiceTab]);

  // Fetch workflow-specific data when a workflow is selected
  useEffect(() => {
    if (selectedWorkflow) {
      fetchWorkflowData(selectedWorkflow);
    }
  }, [selectedWorkflow]);

  // Lightweight initial load - just get counts for workflow badges
  const fetchCountsOnly = async () => {
    try {
      const startTime = performance.now();
      
      // Fetch all counts in parallel (minimal data transfer)
      const [
        bctgResponse,
        bvadmResponse,
        ccatResponse,
        brecResponse,
        bocpResponse,
        mmgResponse,
        abgResponse,
        pdeResponse,
        ermtResponse,
        pmpbResponse,
        ksgResponse,
        ccgResponse,
        akswResponse,
        mmkgResponse,
        smmkResponse
      ] = await Promise.allSettled([
        axios.get(`${API}/bctg/results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/bvadm/reports`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/ccat/reports`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/brec/reports`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/bocp/results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/mmg/results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/abg/user-results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/pde/reports`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/ermt/results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/pmpb/results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/ksg/my-speeches`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/ccg/results`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/aksw/reports`, { withCredentials: true, timeout: 2000 }),
        axios.get(`${API}/mmkg/reports`, { withCredentials: true, timeout: 5000 }),
        axios.get(`${API}/smmk/kits`, { withCredentials: true, timeout: 2000 })
      ]);
      
      const endTime = performance.now();
      
      // Set counts for workflow badges (just need array lengths)
      if (bctgResponse.status === 'fulfilled') {
        const bctgData = Array.isArray(bctgResponse.value?.data) ? bctgResponse.value.data : [];
        setBctgResults(bctgData);
      }
      if (bvadmResponse.status === 'fulfilled') {
        const reports = bvadmResponse.value?.data?.reports || bvadmResponse.value?.data || [];
        setBvadmResults(Array.isArray(reports) ? reports : []);
      }
      if (ccatResponse.status === 'fulfilled') {
        const ccatReports = ccatResponse.value?.data?.reports || ccatResponse.value?.data || [];
        setCcatResults(Array.isArray(ccatReports) ? ccatReports : []);
      }
      if (brecResponse.status === 'fulfilled') {
        const brecReports = brecResponse.value?.data?.reports || brecResponse.value?.data || [];
        setBrecResults(Array.isArray(brecReports) ? brecReports : []);
      }
      if (bocpResponse.status === 'fulfilled') {
        const bocpData = bocpResponse.value?.data?.results || bocpResponse.value?.data || [];
        setBocpResults(Array.isArray(bocpData) ? bocpData : []);
      }
      if (mmgResponse.status === 'fulfilled') {
        const mmgData = mmgResponse.value?.data?.results || mmgResponse.value?.data || [];
        setMmgResults(Array.isArray(mmgData) ? mmgData : []);
      }
      if (abgResponse.status === 'fulfilled') {
      if (pdeResponse.status === 'fulfilled') {
        const pdeReports = pdeResponse.value?.data?.reports || pdeResponse.value?.data || [];
        setPdeResults(Array.isArray(pdeReports) ? pdeReports : []);
      }
      if (ermtResponse.status === 'fulfilled') {
        const ermtData = ermtResponse.value?.data?.results || ermtResponse.value?.data || [];
        setErmtResults(Array.isArray(ermtData) ? ermtData : []);
      }
      if (pmpbResponse.status === 'fulfilled') {
        const pmpbData = pmpbResponse.value?.data || [];
        setPmpbResults(Array.isArray(pmpbData) ? pmpbData : []);
      }
        const abgData = abgResponse.value?.data?.results || abgResponse.value?.data || [];
        setAbgResults(Array.isArray(abgData) ? abgData : []);
      }
      if (ksgResponse.status === 'fulfilled') {
        const ksgData = ksgResponse.value?.data?.results || ksgResponse.value?.data || [];
        setKsgResults(Array.isArray(ksgData) ? ksgData : []);
      }
      if (ccgResponse.status === 'fulfilled') {
        const ccgData = ccgResponse.value?.data?.results || ccgResponse.value?.data || [];
        setCcgResults(Array.isArray(ccgData) ? ccgData : []);
      }

      if (akswResponse.status === 'fulfilled') {
        const akswData = akswResponse.value?.data?.reports || akswResponse.value?.data || [];
        setAkswResults(Array.isArray(akswData) ? akswData : []);
      }

      if (mmkgResponse.status === 'fulfilled') {
        const mmkgData = mmkgResponse.value?.data?.reports || mmkgResponse.value?.data || [];
        setMmkgResults(Array.isArray(mmkgData) ? mmkgData : []);
      } else {
        setMmkgResults([]);
      }
      
      if (smmkResponse.status === 'fulfilled') {
        const smmkData = smmkResponse.value?.data?.kits || smmkResponse.value?.data || [];
        setSmmkResults(Array.isArray(smmkData) ? smmkData : []);
      } else {
        setSmmkResults([]);
      }

      setCountsLoaded(true); // Mark counts as loaded
      
    } catch (error) {
      setCountsLoaded(true); // Still mark as loaded even on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch workflow-specific data (transactions for payment summary)
  const fetchWorkflowData = async (workflow) => {
    try {
      setWorkflowDataLoading(true);
      
      // Only fetch transactions if not already loaded
      if (transactions.length === 0) {
        const transactionsResponse = await axios.get(`${API}/payments/transactions`, { 
          withCredentials: true,
          timeout: 5000
        });
        const transactionsData = transactionsResponse.data?.transactions || transactionsResponse.data || [];
        setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
      } else {
      }
      
    } catch (error) {
      setTransactions([]); // Set empty array on error
    } finally {
      setWorkflowDataLoading(false);
    }
  };

  const fetchAllResults = async () => {
    try {
      const startTime = performance.now();
      
      // Fetch additional data needed for Overview (user info, rewards, transactions)
      const [
        usageResponse,
        rewardsResponse,
        transactionsResponse
      ] = await Promise.allSettled([
        axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 5000 }),
        axios.get(`${API}/rewards/points`, { withCredentials: true, timeout: 5000 }),
        axios.get(`${API}/payments/transactions`, { withCredentials: true, timeout: 5000 })
      ]);
      
      const endTime = performance.now();
      
      // Process user usage
      if (usageResponse.status === 'fulfilled' && usageResponse.value?.data?.usage_tracking) {
        const remainingFree = Math.max(0, 3 - (usageResponse.value.data.usage_tracking.bctg_monthly_count || 0));
        setUserUsage({
          remaining_free: remainingFree,
          usage_count: usageResponse.value.data.usage_tracking.bctg_monthly_count || 0
        });
      }
      
      // Process rewards
      if (rewardsResponse.status === 'fulfilled') {
        setUserStats({ rewardsPoints: rewardsResponse.value?.data?.points_balance || 0 });
      } else {
        setUserStats({ rewardsPoints: user?.points_balance || 0 });
      }
      
      // Process Transactions
      if (transactionsResponse.status === 'fulfilled') {
        const transactionsData = transactionsResponse.value?.data?.transactions || transactionsResponse.value?.data || [];
        const transactionsArray = Array.isArray(transactionsData) ? transactionsData : [];
        setTransactions(transactionsArray);
      }
      
    } catch (error) {
      toast.error(`Failed to load overview data: ${error.message}`);
    }
  };

  const exportResultToPDF = (result) => {
    try {      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 170;
      const leftMargin = 20;
      let yPosition = 20;
      
      // Helper function for page breaks
      const checkNewPage = (spaceNeeded) => {
        if (yPosition + spaceNeeded > 280) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to add multi-line text
      const addMultiLineText = (text, fontSize = 10, maxWidth = pageWidth) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          checkNewPage(7);
          pdf.text(line, leftMargin, yPosition);
          yPosition += 6;
        });
      };
      
      // HEADER
      const timestamp = new Date(result.created_at).toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(timestamp, leftMargin, yPosition);
      pdf.text('Digital Ink AI - BCTG Report', 190, yPosition, { align: 'right' });
      yPosition += 10;
      
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('DIGITAL INK AI', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Book Concept & Title Generator', 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // GENERATION DETAILS
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Generation Details', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      const inputs = result.inputs || {};
      if (inputs.genre || inputs.custom_genre) {
        pdf.text(`Genre: ${inputs.genre || inputs.custom_genre}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (inputs.theme || inputs.custom_theme) {
        pdf.text(`Theme: ${inputs.theme || inputs.custom_theme}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (inputs.target_audience) {
        pdf.text(`Target Audience: ${inputs.target_audience}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (inputs.setting_country || inputs.physical_location) {
        pdf.text(`Location: ${inputs.physical_location || inputs.setting_country}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (inputs.setting_region || inputs.region) {
        pdf.text(`Region: ${inputs.region || inputs.setting_region}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (inputs.timeframe_era) {
        pdf.text(`Timeframe: ${inputs.timeframe_era}`, leftMargin, yPosition);
        yPosition += 6;
      }
      yPosition += 5;
      
      // MARKET INSIGHTS
      checkNewPage(20);
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text('Market Insights', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      addMultiLineText(result.results.market_insights || '');
      yPosition += 5;
      
      if (result.results.recommended_hashtags && result.results.recommended_hashtags.length > 0) {
        pdf.setFont(undefined, 'bold');
        pdf.text('Hashtags:', leftMargin, yPosition);
        yPosition += 6;
        pdf.setFont(undefined, 'normal');
        addMultiLineText(result.results.recommended_hashtags.join(' '));
        yPosition += 5;
      }
      
      // CONCEPTS
      if (result.results.concepts && result.results.concepts.length > 0) {
        result.results.concepts.forEach((concept, index) => {
          checkNewPage(30);
          yPosition += 5;
          
          // Concept Title
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text(`Concept ${index + 1}: ${concept.title}`, leftMargin, yPosition);
          yPosition += 8;
          
          // Subtitle
          if (concept.subtitle) {
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'italic');
            pdf.setTextColor(100, 100, 100);
            addMultiLineText(concept.subtitle, 11);
            yPosition += 3;
          }
          
          pdf.setTextColor(0, 0, 0);
          
          // Literary Hook
          checkNewPage(15);
          pdf.setFontSize(10);
          pdf.setFont(undefined, 'bold');
          pdf.text('Literary Hook:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          const hookText = concept.literary_hook || extractLiteraryHook(concept.summary);
          addMultiLineText(hookText);
          yPosition += 5;
          
          // Complete Concept
          checkNewPage(15);
          pdf.setFont(undefined, 'bold');
          pdf.text('Complete Book Concept:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          addMultiLineText(concept.summary);
          yPosition += 5;
          
          // Why This Will Sell
          checkNewPage(15);
          pdf.setFont(undefined, 'bold');
          pdf.text('Why This Will Sell:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          addMultiLineText(concept.market_appeal || 'No market appeal data available.');
          yPosition += 5;
          
          // Chapter Outline
          checkNewPage(15);
          pdf.setFont(undefined, 'bold');
          pdf.text('Chapter Outline:', leftMargin, yPosition);
          yPosition += 6;
          pdf.setFont(undefined, 'normal');
          
          if (concept.chapter_outline && concept.chapter_outline.length > 0) {
            concept.chapter_outline.forEach((chapter, chIdx) => {
              checkNewPage(8);
              pdf.text(`Chapter ${chIdx + 1}: ${chapter}`, leftMargin + 5, yPosition);
              yPosition += 6;
            });
          } else {
            pdf.text('No chapter outline available.', leftMargin + 5, yPosition);
            yPosition += 6;
          }
          
          yPosition += 10;
        });
      }
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPages}`, 105, 287, { align: 'center' });
        pdf.text('www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `BCTG_${new Date(result.created_at).toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const printResult = (result) => {
    const printWindow = window.open('', '_blank');
    
    // Format timestamp for header
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    const generatedDate = now.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Digital Ink AI - BCTG Report</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; margin: 20px; line-height: 1.5; font-size: 12pt; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .bctg-header { text-align: center; margin-bottom: 20px; }
              .bctg-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .bctg-header h1 { font-size: 16pt; margin: 8px 0; font-family: 'Times New Roman', Times, serif; color: #1e40af; font-style: italic; }
              .bctg-header h2 { font-size: 18pt; margin: 5px 0; font-family: 'Times New Roman', Times, serif; font-weight: bold; }
              .bctg-header p { font-size: 11pt; margin: 3px 0; color: #666; }
              .generated-date { text-align: center; font-size: 10pt; color: #666; margin-bottom: 20px; }
              .concept { margin-bottom: 25px; border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
              .title { font-size: 14pt; font-weight: bold; color: #1e40af; margin-bottom: 8px; }
              .subtitle { font-size: 12pt; color: #6b7280; margin-bottom: 10px; font-style: italic; }
              .section { margin-bottom: 12px; }
              .section-title { font-weight: bold; margin-bottom: 5px; color: #374151; }
              .chapter { margin-bottom: 3px; padding: 5px; background: #f8fafc; border-radius: 4px; }
              @media print { body { margin: 10px; } }
            </style>
          </head>
          <body>
            <!-- Standard Header -->
            <div class="print-header">
              <div>${timestamp}</div>
              <div>Digital Ink AI - BCTG Report</div>
            </div>
            
            <div class="bctg-header">
              <img src="/Digital_Ink_AI_Logo.png" alt="Digital Ink AI" />
              <h1>Book Concept & Title Generator</h1>
              <h2>${result.inputs.genre || result.inputs.custom_genre}</h2>
              <p>Theme: ${result.inputs.theme || result.inputs.custom_theme}</p>
            </div>
            
            <div class="generated-date">Generated ${generatedDate}</div>
            
            <div class="section">
              <div class="section-title">Market Insights:</div>
              <p>${result.results.market_insights || 'N/A'}</p>
            </div>
            
            ${result.results.concepts.map((concept, index) => `
              <div class="concept">
                <div class="title">Concept ${index + 1}: ${concept.title}</div>
                ${concept.subtitle ? `<div class="subtitle">${concept.subtitle}</div>` : ''}
                <div class="section">
                  <div class="section-title">Summary:</div>
                  <p>${concept.summary}</p>
                </div>
                <div class="section">
                  <div class="section-title">Market Appeal:</div>
                  <p>${concept.market_appeal}</p>
                </div>
                <div class="section">
                  <div class="section-title">Chapter Outline:</div>
                  ${concept.chapter_outline.map(chapter => `<div class="chapter">${chapter}</div>`).join('')}
                </div>
              </div>
            `).join('')}
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const deleteResult = async (resultId) => {
    if (!window.confirm('Are you sure you want to delete this result?')) return;
    
    try {
      await axios.delete(`${API}/bctg/results/${resultId}`, { withCredentials: true });
      setBctgResults(prev => prev.filter(r => r.id !== resultId));    } catch (error) {
      toast.error("Failed to delete result");
    }
  };

  const exportBVADMReportToPDF = async (report) => {
    try {
      toast.info("Generating BVADM PDF... Please wait");
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 190;
      let yPosition = 20;
      
      // Add Digital Ink AI Logo Header - Enhanced Visibility
      
      // Main Logo Text
      pdf.setFontSize(24);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175); // Blue color
      
      const logoText = 'DIGITAL INK AI';
      const logoWidth = pdf.getTextWidth(logoText);
      const logoX = (210 - logoWidth) / 2;
      
      pdf.text(logoText, logoX, yPosition);
      yPosition += 12;
      
      // Subtitle
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      const subtitleText = 'AI-Powered Publishing Tools';
      const subtitleWidth = pdf.getTextWidth(subtitleText);
      const subtitleX = (210 - subtitleWidth) / 2;
      pdf.text(subtitleText, subtitleX, yPosition);
      yPosition += 8;
      
      // Add decorative separator line
      pdf.setDrawColor(30, 64, 175);
      pdf.setLineWidth(1);
      pdf.line(60, yPosition, 150, yPosition);
      yPosition += 15;
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Header with logo and title
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.text('Digital Ink AI - BVADM Analysis Report', 20, yPosition);
      yPosition += 15;
      
      // Document info
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.text(`Title: ${report.title}`, 20, yPosition);
      yPosition += 6;
      
      if (report.subtitle) {
        pdf.setFont(undefined, 'normal');
        pdf.text(`Subtitle: ${report.subtitle}`, 20, yPosition);
        yPosition += 6;
      }
      
      pdf.text(`Author: ${report.author_name}`, 20, yPosition);
      yPosition += 6;
      pdf.text(`Generated: ${new Date(report.created_at).toLocaleString('en-US', { 
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
        timeZone: 'America/Denver' 
      })}`, 20, yPosition);
      yPosition += 15;
      
      // Key Metrics
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Key Metrics:', 20, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      if (report.overall_score) {
        pdf.text(`Overall Score: ${report.overall_score}/10`, 25, yPosition);
        yPosition += 5;
      }
      if (report.viability_score) {
        pdf.text(`Viability Score: ${report.viability_score}/10`, 25, yPosition);
        yPosition += 5;
      }
      if (report.word_count) {
        pdf.text(`Word Count: ${report.word_count.toLocaleString()}`, 25, yPosition);
        yPosition += 5;
      }
      if (report.development_stage) {
        pdf.text(`Development Stage: ${report.development_stage}`, 25, yPosition);
        yPosition += 5;
      }
      yPosition += 10;
      
      // Analysis Report (if available)
      if (report.analysis_report) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('Analysis Report:', 20, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(16);
        pdf.setFont(undefined, 'normal');
        
        // Split the analysis report into lines that fit the page
        const lines = pdf.splitTextToSize(report.analysis_report, pageWidth - 40);
        lines.forEach(line => {
          if (yPosition > 270) { // Near bottom of page
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, 20, yPosition);
          yPosition += 4;
        });
      }
      
      // Add footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('Generated with Digital Ink AI - www.DigitalInkAI.com', 105, 287, { align: 'center' });
      }
      
      const safeTitle = report.title ? report.title.replace(/[^a-zA-Z0-9]/g, '_') : 'Manuscript';
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${safeTitle}_BVADM_Analysis_${dateStr}.pdf`;
      
      // Try to use File System Access API for better user control (modern browsers)
      if ('showSaveFilePicker' in window) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'PDF files',
              accept: { 'application/pdf': ['.pdf'] }
            }]
          });
          const writable = await fileHandle.createWritable();
          await writable.write(pdf.output('blob'));
          await writable.close();        } catch (err) {
          if (err.name !== 'AbortError') {
            // Fallback to regular download if user doesn't cancel
            pdf.save(filename);          }
        }
      } else {
        // Fallback for older browsers
        pdf.save(filename);      }
    } catch (error) {
      toast.error("Failed to generate BVADM PDF");
    }
  };

  const printBVADMReport = (report) => {
    const printContent = `
      <html>
        <head>
          <title>BVADM Report - ${report.title || 'Manuscript Analysis'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { max-height: 50px; margin-bottom: 10px; }
            .title { font-size: 24px; font-weight: bold; color: #1e293b; }
            .subtitle { color: #64748b; }
            .section { margin-bottom: 20px; }
            .section-title { font-size: 18px; font-weight: bold; color: #1e293b; margin-bottom: 10px; }
            .content { white-space: pre-wrap; }
            .scores { display: flex; gap: 20px; margin: 20px 0; }
            .score-item { text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" alt="Digital Ink AI" class="logo">
            <h1 class="title">BVADM Analysis Report</h1>
            <p class="subtitle">Powered by Digital Ink AI</p>
          </div>
          
          <div class="section">
            <div class="section-title">Manuscript Details</div>
            <p><strong>Title:</strong> ${report.title}</p>
            <p><strong>Author:</strong> ${report.author_name || 'N/A'}</p>
            <p><strong>Word Count:</strong> ${report.word_count?.toLocaleString() || 'N/A'}</p>
            <p><strong>Analysis Date:</strong> ${new Date(report.created_at).toLocaleString('en-US', { 
              year: 'numeric', month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit', hour12: true,
              timeZone: 'America/Denver' 
            })}</p>
          </div>
          
          <div class="section">
            <div class="section-title">Analysis Scores</div>
            <div class="scores">
              <div class="score-item">
                <strong>Overall:</strong> ${report.overall_score || 'N/A'}/10
              </div>
              <div class="score-item">
                <strong>Viability:</strong> ${report.viability_score || 'N/A'}/10
              </div>
            </div>
          </div>
          
          ${report.analysis_report ? `
          <div class="section">
            <div class="section-title">Complete Analysis Report</div>
            <div class="content">${report.analysis_report}</div>
          </div>
          ` : ''}
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const exportAKSWReportToPDF = async (result) => {
    try {
      toast.info("Generating AKSW PDF... Please wait");
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 190;
      let yPosition = 20;
      
      // Add Digital Ink AI Logo Header
      pdf.setFontSize(24);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      
      const logoText = 'DIGITAL INK AI';
      const logoWidth = pdf.getTextWidth(logoText);
      const logoX = (210 - logoWidth) / 2;
      
      pdf.text(logoText, logoX, yPosition);
      yPosition += 12;
      
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      const subtitleText = 'Amazon KDP Submission Wizard';
      const subtitleWidth = pdf.getTextWidth(subtitleText);
      const subtitleX = (210 - subtitleWidth) / 2;
      pdf.text(subtitleText, subtitleX, yPosition);
      yPosition += 8;
      
      pdf.setDrawColor(30, 64, 175);
      pdf.setLineWidth(1);
      pdf.line(60, yPosition, 150, yPosition);
      yPosition += 15;
      
      pdf.setTextColor(0, 0, 0);
      
      // Book Details
      pdf.setFontSize(16);
      pdf.setFont(undefined, 'bold');
      pdf.text(`Book Title: ${result.book_title}`, 20, yPosition);
      yPosition += 8;
      
      if (result.subtitle) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'italic');
        pdf.text(`Subtitle: ${result.subtitle}`, 20, yPosition);
        yPosition += 6;
      }
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.text(`ISBN: ${result.isbn || 'N/A'}`, 20, yPosition);
      yPosition += 5;
      pdf.text(`Book Type: ${result.book_type || 'N/A'}`, 20, yPosition);
      yPosition += 5;
      pdf.text(`Price: $${result.book_price || 'N/A'}`, 20, yPosition);
      yPosition += 10;
      
      // Add sections with proper formatting
      const addSection = (title, content) => {
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text(title, 20, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        
        if (Array.isArray(content)) {
          content.forEach(item => {
            const lines = pdf.splitTextToSize(`• ${item}`, pageWidth);
            lines.forEach(line => {
              if (yPosition > 280) {
                pdf.addPage();
                yPosition = 20;
              }
              pdf.text(line, 20, yPosition);
              yPosition += 5;
            });
          });
        } else {
          const lines = pdf.splitTextToSize(content || 'N/A', pageWidth);
          lines.forEach(line => {
            if (yPosition > 280) {
              pdf.addPage();
              yPosition = 20;
            }
            pdf.text(line, 20, yPosition);
            yPosition += 5;
          });
        }
        
        yPosition += 5;
      };
      
      // Add all AKSW sections
      if (result.book_description) addSection('Book Description', result.book_description);
      if (result.back_cover_description) addSection('Back Cover Description', result.back_cover_description);
      if (result.kdp_keywords) addSection('KDP Keywords', result.kdp_keywords);
      if (result.kdp_categories) addSection('KDP Categories', result.kdp_categories);
      if (result.search_terms) addSection('Search Terms', result.search_terms);
      if (result.bisac_categories) addSection('BISAC Categories', result.bisac_categories);
      if (result.author_bio_enhanced) addSection('Enhanced Author Bio', result.author_bio_enhanced);
      
      pdf.save(`AKSW_${result.book_title.replace(/[^a-z0-9]/gi, '_')}.pdf`);
      toast.success("PDF exported successfully!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const printAKSWReport = (result) => {
    const printContent = `
      <html>
        <head>
          <title>AKSW Report - ${result.book_title || 'KDP Submission Guide'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; }
            .title { font-size: 24px; font-weight: bold; color: #1e293b; }
            .subtitle { color: #64748b; font-style: italic; }
            .section { margin-bottom: 20px; page-break-inside: avoid; }
            .section-title { font-size: 16px; font-weight: bold; color: #ea580c; margin-bottom: 10px; border-bottom: 2px solid #fed7aa; padding-bottom: 5px; }
            .content { white-space: pre-wrap; }
            .metadata { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
            .metadata-item { padding: 10px; background: #f8fafc; border-radius: 5px; }
            ul { margin: 5px 0; padding-left: 20px; }
            li { margin: 3px 0; }
            @media print { .section { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">Amazon KDP Submission Guide</h1>
            <p class="subtitle">Powered by Digital Ink AI</p>
          </div>
          
          <div class="section">
            <div class="section-title">Book Details</div>
            <div class="metadata">
              <div class="metadata-item"><strong>Title:</strong> ${result.book_title}</div>
              <div class="metadata-item"><strong>ISBN:</strong> ${result.isbn || 'N/A'}</div>
              <div class="metadata-item"><strong>Type:</strong> ${result.book_type || 'N/A'}</div>
              <div class="metadata-item"><strong>Price:</strong> $${result.book_price || 'N/A'}</div>
            </div>
            ${result.subtitle ? `<p><strong>Subtitle:</strong> ${result.subtitle}</p>` : ''}
          </div>
          
          ${result.book_description ? `
          <div class="section">
            <div class="section-title">📖 Book Description</div>
            <div class="content">${result.book_description}</div>
          </div>` : ''}
          
          ${result.back_cover_description ? `
          <div class="section">
            <div class="section-title">📄 Back Cover Description</div>
            <div class="content">${result.back_cover_description}</div>
          </div>` : ''}
          
          ${result.kdp_keywords?.length ? `
          <div class="section">
            <div class="section-title">🔑 KDP Keywords</div>
            <ul>${result.kdp_keywords.map(kw => `<li>${kw}</li>`).join('')}</ul>
          </div>` : ''}
          
          ${result.kdp_categories?.length ? `
          <div class="section">
            <div class="section-title">📚 KDP Categories</div>
            <ul>${result.kdp_categories.map(cat => `<li>${cat}</li>`).join('')}</ul>
          </div>` : ''}
          
          ${result.search_terms?.length ? `
          <div class="section">
            <div class="section-title">🔍 Search Terms</div>
            <ul>${result.search_terms.map(term => `<li>${term}</li>`).join('')}</ul>
          </div>` : ''}
          
          ${result.bisac_categories?.length ? `
          <div class="section">
            <div class="section-title">📊 BISAC Categories</div>
            <ul>${result.bisac_categories.map(cat => `<li>${cat}</li>`).join('')}</ul>
          </div>` : ''}
          
          ${result.author_bio_enhanced ? `
          <div class="section">
            <div class="section-title">✍️ Enhanced Author Bio</div>
            <div class="content">${result.author_bio_enhanced}</div>
          </div>` : ''}
        </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const openTransactionDetails = (service) => {
    // Create URL for transaction details page
    const url = service === 'ALL' 
      ? `/transactions` 
      : `/transactions?service=${service}`;
    
    // Navigate in same tab
    window.location.href = url;
  };

  const deleteBVADMReport = async (reportId) => {
    
    try {
      const confirmResult = window.confirm('Are you sure you want to delete this BVADM report? This action cannot be undone.');
      
      if (!confirmResult) {
        return;
      }
      
      const response = await axios.delete(`${API}/bvadm/reports/${reportId}`, { 
        withCredentials: true,
        timeout: 10000  // 10 second timeout
      });
      
      // Update the UI state
      setBvadmResults(prev => {
        const filtered = prev.filter(r => r.id !== reportId);
        return filtered;
      });
      
      toast.success("BVADM report deleted successfully!");
      
    } catch (error) {
      
      if (error.response?.status === 404) {
        toast.error("Report not found - it may have already been deleted");
      } else if (error.response?.status === 401) {
        toast.error("Authentication required - please log in again");
      } else {
        toast.error(`Failed to delete BVADM report: ${error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center pt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
              Service Dashboard for: {user?.first_name} {user?.last_name}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-3">
              Your centralized command center for all Digital Ink AI services. Track your creative progress, access your library of AI-generated content, manage workflow outputs, review transaction history, and seamlessly navigate between writing, editing, production, research, and marketing tools designed specifically for authors.
            </p>
          </div>
        </div>

        {/* Workflow-Based Navigation */}
        <div className="mb-6">
          {/* Service Workflows Header */}
          <div className="mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xl">💼</span>
              <h3 className="text-base font-semibold text-slate-700">Service Workflows</h3>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* 1. WRITING & IDEATION WORKFLOW */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedWorkflow === 'writing' 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
              }`}
              onClick={() => {
                setSelectedWorkflow('writing');
                setActiveServiceTab(null);
              }}
              title="Generate book concepts, create detailed outlines, develop characters and casts. Turn your creative ideas into structured foundations for your next bestselling manuscript."
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">📝</span>
                    <div>
                      <CardTitle className={`text-sm ${selectedWorkflow === 'writing' ? 'text-white' : 'text-blue-900'}`}>Writing & Ideation</CardTitle>
                      <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'writing' ? 'text-slate-300' : 'text-slate-600'}`}>Transform ideas</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={selectedWorkflow === 'writing' ? 'bg-white text-slate-900 border-white' : 'text-blue-600 border-blue-300'}>
                    {transactions.filter(t => t.service && ['BCTG', 'BOCP', 'CCG', 'CDDA'].includes(t.service.toUpperCase())).length}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* 2. EDITING & ENHANCEMENT WORKFLOW */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedWorkflow === 'editing' 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
              }`}
              onClick={() => {
                setSelectedWorkflow('editing');
                setActiveServiceTab(null);
              }}
              title="Polish your manuscript with professional author biographies, developmental editing feedback, and comprehensive viability assessments to ensure your work meets industry standards and market expectations."
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">✏️</span>
                    <div>
                      <CardTitle className={`text-sm ${selectedWorkflow === 'editing' ? 'text-white' : 'text-purple-900'}`}>Editing & Enhancement</CardTitle>
                      <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'editing' ? 'text-slate-300' : 'text-slate-600'}`}>Polish standards</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={selectedWorkflow === 'editing' ? 'bg-white text-slate-900 border-white' : 'text-purple-600 border-purple-300'}>
                    {transactions.filter(t => t.service && ['ABG', 'PDE', 'BVADM'].includes(t.service.toUpperCase())).length}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* 3. DESIGN & PRODUCTION WORKFLOW */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedWorkflow === 'design' 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
              }`}
              onClick={() => {
                setSelectedWorkflow('design');
                setActiveServiceTab(null);
              }}
              title="Transform your manuscript into publication-ready formats. Create professional EPUB files, generate print-ready PDFs, and prepare your book for seamless submission to Amazon KDP and other platforms."
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">🎨</span>
                    <div>
                      <CardTitle className={`text-sm ${selectedWorkflow === 'design' ? 'text-white' : 'text-rose-900'}`}>Design & Production</CardTitle>
                      <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'design' ? 'text-slate-300' : 'text-slate-600'}`}>Finishing touches</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={selectedWorkflow === 'design' ? 'bg-white text-slate-900 border-white' : 'text-rose-600 border-rose-300'}>
                    {transactions.filter(t => t.service && ['ERMT', 'PMPB', 'PRMPB', 'AKSW'].includes(t.service.toUpperCase())).length}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* 4. MARKET RESEARCH & ANALYSIS WORKFLOW */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedWorkflow === 'research' 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
              }`}
              onClick={() => {
                setSelectedWorkflow('research');
                setActiveServiceTab(null);
              }}
              title="Analyze competitive landscapes, discover comparable titles, and generate optimized metadata for Amazon. Make data-driven decisions to position your book strategically in crowded marketplaces."
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">📊</span>
                    <div>
                      <CardTitle className={`text-sm ${selectedWorkflow === 'research' ? 'text-white' : 'text-emerald-900'}`}>Market Research & Analysis</CardTitle>
                      <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'research' ? 'text-slate-300' : 'text-slate-600'}`}>Data insights</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={selectedWorkflow === 'research' ? 'bg-white text-slate-900 border-white' : 'text-emerald-600 border-emerald-300'}>
                    {transactions.filter(t => t.service && ['CCAT', 'BREC', 'MMG'].includes(t.service.toUpperCase())).length}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* 5. MARKETING & PROMOTION WORKFLOW */}
            <Card 
              className={`cursor-pointer transition-all ${
                selectedWorkflow === 'marketing' 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
              }`}
              onClick={() => {
                setSelectedWorkflow('marketing');
                setActiveServiceTab(null);
              }}
              title="Create compelling keynote speeches, build professional media kits, and develop comprehensive marketing campaigns. Amplify your author brand and connect authentically with your target readers."
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">📢</span>
                    <div>
                      <CardTitle className={`text-sm ${selectedWorkflow === 'marketing' ? 'text-white' : 'text-orange-900'}`}>Marketing & Promotion</CardTitle>
                      <p className={`text-[10px] mt-0.5 ${selectedWorkflow === 'marketing' ? 'text-slate-300' : 'text-slate-600'}`}>Amplify reach</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={selectedWorkflow === 'marketing' ? 'bg-white text-slate-900 border-white' : 'text-orange-600 border-orange-300'}>
                    {transactions.filter(t => t.service && ['KSG', 'MMKG', 'SMMK'].includes(t.service.toUpperCase())).length}
                  </Badge>
                </div>
              </CardHeader>
            </Card>

            {/* 6. OVERVIEW & SERVICE HISTORY */}
            <Card 
              className={`cursor-pointer transition-all ${
                activeServiceTab === 'overview' 
                  ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:shadow-md'
              }`}
              onClick={() => {
                setActiveServiceTab('overview');
                setSelectedWorkflow(null);
              }}
              title="View your complete Digital Ink AI activity across all workflows. Track your creative journey, review service usage, access transaction history, and monitor your progress as an author."
            >
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">📊</span>
                    <div>
                      <CardTitle className={`text-sm ${activeServiceTab === 'overview' ? 'text-white' : 'text-slate-900'}`}>Overview & Service History</CardTitle>
                      <p className={`text-[10px] mt-0.5 ${activeServiceTab === 'overview' ? 'text-slate-300' : 'text-slate-600'}`}>All services</p>
                    </div>
                  </div>
                  <Badge variant="outline" className={activeServiceTab === 'overview' ? 'bg-white text-slate-900 border-white' : 'text-slate-600 border-slate-300'}>
                    {bctgResults.length + bvadmResults.length + bocpResults.length + mmgResults.length + pdeResults.length + abgResults.length + brecResults.length + ccatResults.length + ccgResults.length + ermtResults.length + pmpbResults.length + ksgResults.length + akswResults.length + mmkgResults.length + smmkResults.length}
                  </Badge>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>

        {/* Service Cards - Show only for selected workflow */}
        <div id="service-cards-section">
        {selectedWorkflow === 'writing' && !activeServiceTab && (
          workflowDataLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-slate-600">Loading service details...</p>
            </div>
          ) : (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span>📝</span> Writing & Ideation Services
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* BCTG Service Card */}
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('bctg')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📖</span>
                    <div className="flex-1">
                      <div className="font-semibold">Book Concept & Title Generator</div>
                    </div>
                    <HelpIconTrigger 
                      onClick={() => openHelpModal(
                        'Book Concept & Title Generator',
                        helpContentBCTG
                      )}
                      serviceName="BCTG"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{bctgResults.length}</div>
                    <p className="text-blue-100 text-xs">Book concepts</p>
                  </div>
                  <div className="border-t border-blue-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('BCTG', 5.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* BOCP Service Card */}
              <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('bocp')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📝</span>
                    <div className="flex-1">
                      <div className="font-semibold">Book Outline & Chapter Planner</div>
                      <span className="inline-block bg-yellow-400 text-indigo-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger 
                      onClick={() => openHelpModal(
                        'Book Outline & Chapter Planner',
                        helpContentBOCP
                      )}
                      serviceName="BOCP"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{bocpResults.length}</div>
                    <p className="text-indigo-100 text-xs">Book outlines</p>
                  </div>
                  <div className="border-t border-indigo-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('BOCP', 15.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* CCG Service Card */}
              <Card className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ccg')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">🎭</span>
                    <div className="flex-1">
                      <div className="font-semibold">Character & Cast Generator</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Character & Cast Generator',
                        helpContentCCG
                      )}
                      serviceName="CCG"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{ccgResults.length}</div>
                    <p className="text-cyan-100 text-xs">Character casts</p>
                  </div>
                  <div className="border-t border-cyan-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('CCG', 7.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
          )
        )}

        {selectedWorkflow === 'editing' && !activeServiceTab && (
          workflowDataLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
              <p className="text-slate-600">Loading service details...</p>
            </div>
          ) : (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span>✏️</span> Editing & Enhancement Services
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ABG Service Card */}
              <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('abg')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📝</span>
                    <div className="flex-1">
                      <div className="font-semibold">Author Biography Generator</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Author Biography Generator',
                        helpContentABG
                      )}
                      serviceName="ABG"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{abgResults.length}</div>
                    <p className="text-emerald-100 text-xs">Biographies</p>
                  </div>
                  <div className="border-t border-emerald-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('ABG', 5.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* PDE Service Card */}
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('pde')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">✍️</span>
                    <div className="flex-1">
                      <div className="font-semibold">Professional Developmental Editor</div>
                      <span className="inline-block bg-yellow-400 text-purple-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Professional Developmental Editor',
                        helpContentPDE
                      )}
                      serviceName="PDE"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{pdeResults.length}</div>
                    <p className="text-purple-100 text-xs">Manuscripts edited</p>
                  </div>
                  <div className="border-t border-purple-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('PDE', 20.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* BVADM Service Card */}
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('bvadm')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📊</span>
                    <div className="flex-1">
                      <div className="font-semibold">Book Viability & Development Model</div>
                      <span className="inline-block bg-yellow-400 text-purple-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Book Viability & Development Model',
                        helpContentBVADM
                      )}
                      serviceName="BVADM"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{bvadmResults.length}</div>
                    <p className="text-purple-100 text-xs">Analyses complete</p>
                  </div>
                  <div className="border-t border-purple-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('BVADM', 12.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
          )
        )}

        {selectedWorkflow === 'research' && !activeServiceTab && (
          workflowDataLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
              <p className="text-slate-600">Loading service details...</p>
            </div>
          ) : (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span>📊</span> Market Research & Analysis Services
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* CCAT Service Card - Compact */}
              <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ccat')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📊</span>
                    <div className="flex-1">
                      <div className="font-semibold">Competitor Analysis Tool</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Competitor & Comparative Analysis Tool',
                        helpContentCCAT
                      )}
                      serviceName="CCAT"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{ccatResults.length}</div>
                    <p className="text-teal-100 text-xs">Analyses done</p>
                  </div>
                  <div className="border-t border-teal-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('CCAT', 7.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* BREC Service Card - Compact */}
              <Card className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('brec')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📚</span>
                    <div className="flex-1">
                      <div className="font-semibold">Review & Endorsement Creator</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Book Review & Endorsement Creator',
                        helpContentBREC
                      )}
                      serviceName="BREC"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{brecResults.length}</div>
                    <p className="text-cyan-100 text-xs">Reviews generated</p>
                  </div>
                  <div className="border-t border-cyan-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('BREC', 7.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* MMG Service Card - Compact */}
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('mmg')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📄</span>
                    <div className="flex-1">
                      <div className="font-semibold">Manuscript & Metadata Generator</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Manuscript & Metadata Generator',
                        helpContentMMG
                      )}
                      serviceName="MMG"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{mmgResults.length}</div>
                    <p className="text-blue-100 text-xs">Manuscripts generated</p>
                  </div>
                  <div className="border-t border-blue-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('MMG', 10.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                  >
                    View MMG Dashboard →
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
          )
        )}

        {selectedWorkflow === 'design' && !activeServiceTab && (
          workflowDataLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 mx-auto mb-2"></div>
              <p className="text-slate-600">Loading service details...</p>
            </div>
          ) : (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span>🎨</span> Design & Production Services
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ERMT Service Card - Compact */}
              <Card className="bg-gradient-to-r from-rose-500 to-pink-600 text-white hover:shadow-lg transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📕</span>
                    <div className="flex-1">
                      <div className="font-semibold">EPUB-Ready Manuscript</div>
                      <span className="inline-block bg-yellow-400 text-rose-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'EPUB-Ready Manuscript Transformer',
                        helpContentERMT
                      )}
                      serviceName="ERMT"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{ermtResults.length}</div>
                    <p className="text-rose-100 text-xs">EPUB files</p>
                  </div>
                  <div className="border-t border-rose-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('ERMT', 20.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                    onClick={() => {
                      if (!user) {
                        navigate('/login');
                        return;
                      }
                      setActiveServiceTab('ermt');
                    }}
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* PMPB Service Card - Compact */}
              <Card className="bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:shadow-xl transition-shadow">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📄</span>
                    <div className="flex-1">
                      <div className="font-semibold">Print-Ready PDF Builder</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'PDF Merger & Page Break',
                        helpContentPMPB
                      )}
                      serviceName="PMPB"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{user?.usage_tracking?.total_pmpb_uses || 0}</div>
                    <p className="text-pink-100 text-xs">Print-ready PDFs</p>
                  </div>
                  <div className="border-t border-pink-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('PMPB', 7.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'PMPB' || t.service.toUpperCase() === 'PRMPB')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {user?.usage_tracking?.total_pmpb_uses || 0}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                    onClick={() => {
                      if (!user) {
                        navigate('/login');
                        return;
                      }
                      setActiveServiceTab('pmpb');
                    }}
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>

              {/* AKSW Service Card - Compact */}
              <Card 
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:shadow-xl transition-all duration-200 cursor-pointer"
                onClick={() => setActiveServiceTab('aksw')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📦</span>
                    <div className="flex-1">
                      <div className="font-semibold">Amazon KDP Wizard</div>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Amazon KDP Submission Wizard',
                        helpContentAKSW
                      )}
                      serviceName="AKSW"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{akswResults.length}</div>
                    <p className="text-orange-100 text-xs">KDP Guides</p>
                  </div>
                  <div className="border-t border-orange-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('AKSW', 5.00, false).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white text-orange-600 hover:bg-orange-50 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveServiceTab('aksw');
                    }}
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
          )
        )}

        {selectedWorkflow === 'marketing' && !activeServiceTab && (
          workflowDataLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto mb-2"></div>
              <p className="text-slate-600">Loading service details...</p>
            </div>
          ) : (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <span>📈</span> Marketing & Promotion Services
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* KSG Service Card - Compact */}
              <Card className="bg-gradient-to-r from-amber-500 to-amber-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ksg')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">🎤</span>
                    <div className="flex-1">
                      <div className="font-semibold">Keynote Speech Generator</div>
                      <span className="inline-block bg-yellow-400 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Keynote Speech Generator',
                        helpContentKSG
                      )}
                      serviceName="KSG"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{ksgResults.length}</div>
                    <p className="text-amber-100 text-xs">Keynote speeches</p>
                  </div>
                  <div className="border-t border-amber-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('KSG', 15.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>
              
              {/* MMKG Service Card - Active - Compact */}
              <Card 
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setActiveServiceTab('mmkg')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📰</span>
                    <div className="flex-1">
                      <div className="font-semibold">Media Kit Generator</div>
                      <span className="inline-block bg-yellow-400 text-orange-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Marketing Media Kit Generator',
                        helpContentMMKG
                      )}
                      serviceName="MMKG"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{mmkgResults.length}</div>
                    <p className="text-orange-100 text-xs">Media kits</p>
                  </div>
                  <div className="border-t border-orange-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('MMKG', 20.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveServiceTab('mmkg');
                    }}
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>
              
              {/* SMMK Service Card */}
              <Card 
                className="bg-gradient-to-r from-purple-600 to-purple-700 text-white cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setActiveServiceTab('smmk')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="text-3xl">📱</span>
                    <div className="flex-1">
                      <div className="font-semibold">Social Media Marketing Kit</div>
                      <span className="inline-block bg-yellow-400 text-purple-900 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1">PREMIUM</span>
                    </div>
                    <HelpIconTrigger
                      onClick={() => openHelpModal(
                        'Social Media Marketing Kit',
                        helpContentSMMK
                      )}
                      serviceName="SMMK"
                    />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{smmkResults.length}</div>
                    <p className="text-purple-100 text-xs">Marketing kits</p>
                  </div>
                  <div className="border-t border-purple-400 pt-2 space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>Your Price:</span>
                      <span className="font-bold text-yellow-300">
                        {getServicePrice('SMMK', 25.00, true).label}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm"
                    className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveServiceTab('smmk');
                    }}
                  >
                    View Dashboard →
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )
        )}
        </div>

        {/* Visual Divider & Scroll Target - Separates workflow selection from service dashboards */}
        <div className="my-12">
          {/* Scroll anchor point - positioned well above the actual content */}
          <div id="service-dashboard-anchor" className="h-8"></div>
          
          {/* Double-line divider with label */}
          <div className="relative">
            <div className="border-t-4 border-slate-300"></div>
            <div className="border-t-2 border-slate-200 mt-1"></div>
            <div className="absolute left-1/2 top-0 transform -translate-x-1/2 -translate-y-1/2 bg-white px-6 py-1">
              <span className="text-sm font-bold text-slate-600 uppercase tracking-wider">
                ⬇ Service Dashboard ⬇
              </span>
            </div>
          </div>
          
          {/* Extra spacing below divider */}
          <div className="h-6"></div>
        </div>

        {/* QRG and IBG sections completely removed */}

        {/* Initial State - No selection */}
        {!activeServiceTab && !selectedWorkflow && (
          <div className="text-center py-12">
            <div className="text-slate-400 text-lg mb-2">👆</div>
            <p className="text-slate-600 text-base">Select a workflow above to view your services</p>
            <p className="text-slate-500 text-sm mt-1">or click Overview to see all dashboard data</p>
          </div>
        )}

        {/* Overview Tab Content */}
        {activeServiceTab === 'overview' && (
          <>
            {/* Enhanced Dashboard Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">

              {/* BCTG Card */}
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow overflow-hidden" onClick={() => setActiveServiceTab('bctg')}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📚</span>
                      <span className="text-base sm:text-lg">Book Concept & Title Generator</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{bctgResults.length}</div>
                    <p className="text-blue-100 text-sm">Book concepts generated</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-blue-400 pt-3">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).length}
                      </span>
                    </div>
                  </div>
                  
                  {/* Remaining Uses Display */}
                  {user?.role === 'free_user' && (
                    <div className="border-t border-blue-400 pt-3">
                      <div className="text-center text-xs sm:text-sm text-blue-100 break-words">
                        You have <span className="font-bold text-white">{userUsage.remaining_free}</span> remaining Free BCTG services available.
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('bctg');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30 text-xs sm:text-sm"
                    >
                      View BCTG Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* BVADM Card */}
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('bvadm')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📊</span>
                      <span className="text-base sm:text-lg">Book Viability & Development Model</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{bvadmResults.length}</div>
                    <p className="text-purple-100">Manuscripts analyzed</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-purple-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('bvadm');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View BVADM Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* CCAT Card */}
              <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ccat')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📊</span>
                      <span className="text-base sm:text-lg">Competitor & Comparative Analysis Tool</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">NEW</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{ccatResults.length}</div>
                    <p className="text-teal-100">Competitive analyses done</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-teal-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('ccat');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View CCAT Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* BREC Card */}
              <Card className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('brec')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📚</span>
                      <span className="text-base sm:text-lg">Book Review & Endorsement Creator</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">NEW</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{brecResults.length}</div>
                    <p className="text-cyan-100">Reviews & endorsements generated</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-cyan-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('brec');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View BREC Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* MMG Card */}
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('mmg')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <span className="text-4xl mr-2">📈</span>
                        <span className="text-base sm:text-lg font-bold" style={{fontFamily: 'Times New Roman, serif'}}>Metadata & Marketing Generator</span>
                      </div>
                      <div className="text-xs text-black mt-1 ml-9">A la Carte: $4.00 / Subscriber: FREE</div>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Standard</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{mmgResults.length}</div>
                    <p className="text-blue-100">Metadata reports generated</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-blue-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('mmg');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View MMG Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* BOCP Card */}
              <Card className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('bocp')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📚</span>
                      <span className="text-base sm:text-lg">Book Outline & Chapter Planner</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">NEW</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{bocpResults.length}</div>
                    <p className="text-indigo-100">Book outlines created</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-indigo-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('bocp');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View BOCP Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* ERMT Card */}
              <Card className="bg-gradient-to-r from-rose-500 to-pink-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ermt')}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📕</span>
                      <span className="text-base sm:text-lg">EPUB-Ready Manuscript Transformer</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{ermtResults.length}</div>
                    <p className="text-rose-100 text-sm">EPUB files created</p>
                  </div>
                  
                  <div className="border-t border-rose-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').length}
                      </span>
                    </div>
                  </div>
                  
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    onClick={() => {
                      if (!user) {
                        navigate('/login');
                        return;
                      }
                      setActiveServiceTab('ermt');
                    }}
                  >
                    View ERMT Dashboard →
                  </Button>
                </CardContent>
              </Card>
              
              {/* PMPB Card */}
              <Card className="bg-gradient-to-r from-pink-500 to-rose-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('pmpb')}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📄</span>
                      <span className="text-base sm:text-lg">PDF Merger & Page Break</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{pmpbResults.length}</div>
                    <p className="text-pink-100 text-sm">Print-ready PDFs generated</p>
                  </div>
                  
                  <div className="border-t border-pink-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && ['PMPB', 'PRMPB'].includes(t.service.toUpperCase())).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && ['PMPB', 'PRMPB'].includes(t.service.toUpperCase())).length}
                      </span>
                    </div>
                  </div>
                  
                  <Button 
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    onClick={() => {
                      if (!user) {
                        navigate('/login');
                        return;
                      }
                      setActiveServiceTab('pmpb');
                    }}
                  >
                    View PMPB Dashboard →
                  </Button>
                </CardContent>
              </Card>
              
              {/* CCG Card */}
              <Card className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ccg')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">🎭</span>
                      <span className="text-base sm:text-lg">Character & Cast Generator</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Standard</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{ccgResults.length}</div>
                    <p className="text-cyan-100">Character casts generated</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-cyan-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('ccg');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View CCG Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* ABG Card */}
              <Card className="bg-gradient-to-r from-emerald-500 to-green-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('abg')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📝</span>
                      <span className="text-base sm:text-lg">Author Biography Generator</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Standard</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{abgResults.length + pdeResults.length}</div>
                    <p className="text-emerald-100">Biographies generated</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-emerald-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('abg');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View ABG Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* KSG Card */}
              <Card className="bg-gradient-to-r from-amber-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('ksg')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">🎤</span>
                      <span className="text-base sm:text-lg">Keynote Speech Generator</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{ksgResults.length}</div>
                    <p className="text-amber-100">Keynote speeches generated</p>
                  </div>
                  
                  {/* Payment Summary */}
                  <div className="border-t border-amber-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('ksg');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View KSG Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* PDE Card */}
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('pde')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">✍️</span>
                      <span className="text-base sm:text-lg">Professional Developmental Editor</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{pdeResults.length}</div>
                    <p className="text-purple-100 text-sm">Development edits completed</p>
                  </div>
                  
                  <div className="border-t border-purple-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('pde');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View PDE Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* MMKG Card */}
              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('mmkg')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📰</span>
                      <span className="text-base sm:text-lg">Marketing Media Kit Generator</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{mmkgResults.length}</div>
                    <p className="text-orange-100 text-sm">Media kits created</p>
                  </div>
                  
                  <div className="border-t border-orange-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('mmkg');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View MMKG Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* SMMK Card */}
              <Card className="bg-gradient-to-r from-purple-600 to-purple-700 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('smmk')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📱</span>
                      <span className="text-base sm:text-lg">Social Media Marketing Kit</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{smmkResults.length}</div>
                    <p className="text-purple-100 text-sm">Social media kits created</p>
                  </div>
                  
                  <div className="border-t border-purple-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('smmk');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View SMMK Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* AKSW Card */}
              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setActiveServiceTab('aksw')}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-4xl mr-2">📦</span>
                      <span className="text-base sm:text-lg">Amazon KDP Submission Wizard</span>
                    </div>
                    <Badge className="bg-white/20 text-white text-xs">Premium</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{akswResults.length}</div>
                    <p className="text-orange-100 text-sm">KDP guides created</p>
                  </div>
                  
                  <div className="border-t border-orange-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Paid Uses:</span>
                      <span className="font-semibold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').length}
                      </span>
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveServiceTab('aksw');
                      }}
                      size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View AKSW Dashboard →
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Total Activity Card */}
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <span className="text-4xl mr-2">📈</span>
                    Total Activity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <div className="text-3xl font-bold">{bctgResults.length + bvadmResults.length + ccatResults.length + brecResults.length + bocpResults.length + mmgResults.length + ccgResults.length + abgResults.length + ksgResults.length + pdeResults.length + ermtResults.length + pmpbResults.length + mmkgResults.length + smmkResults.length + akswResults.length}</div>
                    <p className="text-green-100">Services provided</p>
                  </div>
                  <div className="border-t border-green-400 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Total Spent:</span>
                      <span className="font-semibold">
                        ${(transactions.reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Transactions:</span>
                      <span className="font-semibold">
                        {transactions.length}
                      </span>
                    </div>
                  </div>
                  <Button 
                    onClick={() => openTransactionDetails('ALL')}
                    size="sm"
                      className="w-full sm:w-auto w-full bg-white/20 text-white border-white/20 hover:bg-white/30"
                  >
                    View All Transactions
                  </Button>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* BCTG Tab Content */}
        {activeServiceTab === 'bctg' && (
          <div id="service-results-section">
            {/* BCTG Service Header */}
            <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📚 Book Concept & Title Generator</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-blue-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{bctgResults.length}</div>
                    </div>
                    <div>
                      <div className="text-blue-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-blue-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && (t.service.toUpperCase() === 'BCTG' || t.service.toLowerCase() === 'bctg')).length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Remaining Uses - centered */}
                  {user?.subscription_status === "None" && (
                    <div className="text-sm bg-white/10 px-4 py-2 rounded text-center">
                      💡 You have <strong>{Math.max(0, 3 - (user?.usage_tracking?.bctg_free_monthly_count || 0))}</strong> remaining free BCTG service{Math.max(0, 3 - (user?.usage_tracking?.bctg_free_monthly_count || 0)) !== 1 ? 's' : ''} for the month.
                    </div>
                  )}
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('BCTG')}
                      size="sm"
                      className="w-full sm:w-auto w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('BCTG', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto w-full sm:w-auto bg-white text-blue-600 hover:bg-blue-50"
                    >
                      📝 Create New BCTG
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BCTG Results Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl text-slate-900 flex items-center">
              <span className="text-4xl mr-2">📚</span>
              Book Concept & Title Generator Results ({bctgResults.length})
            </CardTitle>
            {/* Show remaining free uses message for non-subscribers */}
            {(() => {
              if (user?.subscription_status === "None") {
                const freeUsesCount = user?.usage_tracking?.bctg_free_monthly_count || 0;
                const remaining = Math.max(0, 3 - freeUsesCount);
                
                if (remaining > 0) {
                  return (
                    <div className="mt-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded border border-blue-200">
                      💡 You have <strong>{remaining}</strong> remaining free BCTG service{remaining !== 1 ? 's' : ''} for the month.
                    </div>
                  );
                }
              }
              return null;
            })()}
          </CardHeader>
          <CardContent>
            {bctgResults.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No BCTG results yet</h3>
                <p className="text-slate-600 mb-4">Generate your first book concepts to see them here.</p>
                <Button 
                  onClick={() => {
                    try {
                      handleServiceAccess('BCTG');
                    } catch (error) {
                      toast.error('Button click failed');
                    }
                  }} 
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Your First BCTG
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bctgResults.map((result) => (
                  <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg text-slate-900">
                            {result.inputs.genre || result.inputs.custom_genre} - {result.inputs.theme || result.inputs.custom_theme}
                          </CardTitle>
                          <p className="text-sm text-slate-600 mt-1">
                            Generated on {result.created_at_display || 'Recently'} • 
                            Target: {result.inputs.target_audience}
                          </p>
                        </div>
                        <Badge variant="outline">
                          {result.results.concepts?.length || 0} concepts
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-2 flex-wrap">
                        <Button 
                          size="sm"
                      className="w-full sm:w-auto" 
                          onClick={() => setSelectedResult(selectedResult === result.id ? null : result.id)}
                          variant="outline"
                        >
                          👁️ {selectedResult === result.id ? 'Hide' : 'View'} Details
                        </Button>
                        <Button 
                          size="sm"
                      className="w-full sm:w-auto" 
                          onClick={() => openFullScreenView(result)}
                          variant="outline"
                          className="text-green-600 hover:text-green-700"
                        >
                          📋 View Full Report
                        </Button>
                        <Button 
                          size="sm"
                      className="w-full sm:w-auto" 
                          onClick={() => exportResultToPDF(result)}
                          variant="outline"
                          className="text-blue-600 hover:text-blue-700"
                        >
                          📄 Export PDF
                        </Button>
                        <Button 
                          size="sm"
                      className="w-full sm:w-auto" 
                          onClick={() => printResult(result)}
                          variant="outline"
                          className="text-purple-600 hover:text-purple-700"
                        >
                          🖨️ Print
                        </Button>
                        <Button 
                          size="sm"
                      className="w-full sm:w-auto" 
                          onClick={() => deleteResult(result.id)}
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                        >
                          🗑️ Delete
                        </Button>
                      </div>
                      
                      {selectedResult === result.id && (
                        <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-semibold mb-2">Market Insights:</h4>
                          <p className="text-sm text-slate-700 mb-4">{result.results.market_insights}</p>
                          
                          <h4 className="font-semibold mb-2">Generated Concepts:</h4>
                          <div className="space-y-3">
                            {result.results.concepts?.map((concept, index) => (
                              <div key={index} className="bg-white p-3 rounded border-l-4 border-blue-400">
                                <h5 className="font-semibold text-blue-900">{concept.title}</h5>
                                {concept.subtitle && (
                                  <p className="text-sm text-slate-600 italic">{concept.subtitle}</p>
                                )}
                                <p className="text-sm text-slate-700 mt-2 line-clamp-3">
                                  {concept.summary.substring(0, 200)}...
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* BVADM Tab Content */}
        {activeServiceTab === 'bvadm' && (
          <div id="service-results-section">
            {/* BVADM Service Header */}
            <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📊 Book Viability & Digital Marketing Analysis</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-purple-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{bvadmResults.length}</div>
                    </div>
                    <div>
                      <div className="text-purple-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-purple-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BVADM').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('BVADM')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('BVADM', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-purple-600 hover:bg-purple-50"
                    >
                      📊 Create New BVADM
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BVADM Results Section */}
        <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl text-slate-900 flex items-center">
              <span className="text-4xl mr-2">📊</span>
              BVADM Analysis Reports ({bvadmResults.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {bvadmResults.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">📖</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No BVADM reports yet</h3>
                <p className="text-slate-600 mb-4">Upload your first manuscript for analysis to see reports here.</p>
                <Button onClick={() => handleServiceAccess('BVADM')} className="bg-purple-600 hover:bg-purple-700">
                  Analyze Your First Manuscript
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {bvadmResults.map((report) => (
                  <Card key={report.id} className="border-slate-200 hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg text-slate-900">
                            {report.title}
                          </CardTitle>
                          {report.subtitle && (
                            <p className="text-md text-slate-700 italic">{report.subtitle}</p>
                          )}
                          <p className="text-sm text-slate-600 mt-1">
                            Generated on {report.created_at_display || 'Recently'} 
                            {report.author_name ? `• By ${report.author_name}` : ''}
                          </p>
                        </div>
                        <div className="flex flex-col items-end space-y-2">
                          {report.overall_score && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              Score: {report.overall_score}/10
                            </Badge>
                          )}
                          <div className="flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                      className="w-full sm:w-auto"
                              onClick={() => navigate(`/bvadm?report=${report.id}&from=activity`)}
                            >
                              📖 View Report
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                      className="w-full sm:w-auto"
                              onClick={() => exportBVADMReportToPDF(report)}
                            >
                              📄 Export PDF
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                      className="w-full sm:w-auto"
                              onClick={() => printBVADMReport(report)}
                            >
                              🖨️ Print
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                              onClick={() => handleDeleteBVADM(report.id)}
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div className="flex flex-col">
                          <span className="text-slate-500">Word Count</span>
                          <span className="font-medium">{report.word_count?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-slate-500">Viability Score</span>
                          <span className="font-medium text-green-600">{report.viability_score || 'N/A'}/10</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-slate-500">Development Stage</span>
                          <span className="font-medium">{report.development_stage || 'N/A'}</span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-slate-500">File Size</span>
                          <span className="font-medium">{report.page_count || 'N/A'} pages</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* CCAT Tab Content */}
        {activeServiceTab === 'ccat' && (
          <div id="service-results-section">

            {/* CCAT Service Header */}
            <Card className="bg-gradient-to-r from-teal-500 to-teal-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📊 Competitor & Comparative Analysis Tool</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-teal-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{ccatResults.length}</div>
                    </div>
                    <div>
                      <div className="text-teal-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-teal-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'CCAT').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('CCAT')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('CCAT')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-teal-600 hover:bg-teal-50"
                    >
                      📊 Create New CCAT
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CCAT Results */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📊</span>
                  CCAT Analysis Reports ({ccatResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ccatResults.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">📊</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No CCAT reports yet</h3>
                    <p className="text-slate-600 mb-4">Get expert competitive analysis to position your book strategically.</p>
                    <Button onClick={() => handleServiceAccess('CCAT')} className="bg-teal-600 hover:bg-teal-700">
                      Get Your First Competitive Analysis
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {ccatResults.map((report) => (
                      <Card key={report.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-lg text-slate-900">
                                {report.title}
                              </CardTitle>
                              {report.subtitle && (
                                <p className="text-md text-slate-700 italic">{report.subtitle}</p>
                              )}
                              <p className="text-sm text-slate-600 mt-1">
                                Generated on {report.created_at_display || 'Recently'} 
                                {report.author_name ? `• By ${report.author_name}` : ''}
                              </p>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              <Badge variant="secondary" className="bg-teal-100 text-teal-800">
                                Competitive Analysis
                              </Badge>
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto"
                                  onClick={() => navigate(`/ccat?report=${report.id}&from=activity`)}
                                >
                                  📖 View Report
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                                  onClick={() => handleDeleteCCAT(report.id)}
                                >
                                  🗑️ Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex flex-col">
                              <span className="text-slate-500">Word Count</span>
                              <span className="font-medium">{report.word_count?.toLocaleString() || 'N/A'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-slate-500">Genre</span>
                              <span className="font-medium">{report.genre?.replace('_', ' ').toUpperCase() || 'N/A'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-slate-500">Publisher</span>
                              <span className="font-medium">{report.publisher || 'Independent'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-slate-500">Pages</span>
                              <span className="font-medium">{report.page_count || 'N/A'} pages</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* BREC Tab Content */}
        {activeServiceTab === 'brec' && (
          <div id="service-results-section">

            {/* BREC Service Header */}
            <Card className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📚 Book Review & Endorsement Creator</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-cyan-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{brecResults.length}</div>
                    </div>
                    <div>
                      <div className="text-cyan-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-cyan-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BREC').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('BREC')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button
                      onClick={() => handleServiceAccess('BREC', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-cyan-600 hover:bg-cyan-50"
                    >
                      📚 Create New BREC
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BREC Results */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📚</span>
                  BREC Analysis Reports ({brecResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {brecResults.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">📚</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No BREC reports yet</h3>
                    <p className="text-slate-600 mb-4">Generate professional mock reviews and strategic endorsement recommendations.</p>
                    <Button onClick={() => handleServiceAccess('BREC', 'activity')} className="bg-cyan-600 hover:bg-cyan-700">
                      Create Your First BREC Report
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {brecResults.map((report) => (
                      <Card key={report.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-lg text-slate-900">
                                {report.title}
                              </CardTitle>
                              {report.subtitle && (
                                <p className="text-md text-slate-700 italic">{report.subtitle}</p>
                              )}
                              <p className="text-sm text-slate-600 mt-1">
                                Generated on {report.created_at_display || 'Recently'} 
                                {report.author_name ? `• By ${report.author_name}` : ''}
                              </p>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              <Badge variant="secondary" className="bg-cyan-100 text-cyan-800">
                                Review & Endorsement
                              </Badge>
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto"
                                  onClick={() => navigate(`/brec?report=${report.id}`)}
                                  className="text-cyan-600 border-cyan-300 hover:bg-cyan-50"
                                >
                                  View Report
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto"
                                  onClick={async () => {
                                    if (window.confirm('Are you sure you want to delete this BREC report?')) {
                                      try {
                                        await axios.delete(`${API}/brec/reports/${report.id}`, { withCredentials: true });
                                        setBrecResults(brecResults.filter(r => r.id !== report.id));
                                        toast.success('BREC report deleted');
                                      } catch (error) {
                                        toast.error('Failed to delete report');
                                      }
                                    }
                                  }}
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                >
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* BOCP Tab Content */}
        {activeServiceTab === 'bocp' && (
          <div id="service-results-section">

            {/* BOCP Service Header */}
            <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📚 Book Outline & Chapter Planner</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-indigo-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{bocpResults.length}</div>
                    </div>
                    <div>
                      <div className="text-indigo-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-indigo-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'BOCP').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('BOCP')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('BOCP')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-indigo-600 hover:bg-indigo-50"
                    >
                      📚 Create New BOCP
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* BOCP Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📚</span>
                  Book Outlines ({bocpResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
            {bocpResults.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">No BOCP Results Yet</h3>
                <p className="text-slate-600 mb-4">Create your first book outline with the BOCP tool</p>
                <Button onClick={() => handleServiceAccess('BOCP', 'activity')} className="bg-indigo-600 hover:bg-indigo-700">
                  Create Your First BOCP
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {bocpResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author} • {result.publisher}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className="bg-indigo-100 text-indigo-800">{result.category}</Badge>
                          <Badge className="bg-purple-100 text-purple-800">{result.theme}</Badge>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        {result.chapters?.length && (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                            {result.chapters.length} Chapters
                          </Badge>
                        )}
                        <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto"
                          onClick={() => navigate(`/bocp?report=${result.id}&from=activity`)}
                        >
                          📖 View Outline
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                          onClick={async () => {
                            if (window.confirm('Delete this BOCP result?')) {
                              try {
                                await axios.delete(`${API}/bocp/results/${result.id}`, { withCredentials: true });
                                setBocpResults(prev => prev.filter(r => r.id !== result.id));
                                toast.success('BOCP result deleted');
                              } catch (error) {
                                toast.error('Failed to delete result');
                              }
                            }
                          }}
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-slate-900 mb-1">{result.outline_title}</h4>
                        <p className="text-sm text-slate-600 line-clamp-2">{result.introduction_summary}</p>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          Created: {result.created_at_display || 'Recently'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* MMG Tab Content */}
        {activeServiceTab === 'mmg' && (
          <div id="service-results-section">

            {/* MMG Service Header */}
            <Card className="bg-gradient-to-r from-orange-500 to-amber-500 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📈 Manuscript Metadata Generator</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-orange-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{mmgResults.length}</div>
                    </div>
                    <div>
                      <div className="text-orange-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'MMG').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('MMG')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('MMG')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50"
                    >
                      📈 Create New MMG
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* MMG Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📈</span>
                  Metadata Reports ({mmgResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {mmgResults.length === 0 ? (
              <div className="text-center py-12 bg-orange-50 rounded-lg">
                <p className="text-2xl mb-4">📊</p>
                <p className="text-slate-600 mb-4">No metadata reports generated yet</p>
                <Button onClick={() => handleServiceAccess('MMG', 'activity')} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white">
                  Create Your First MMG Report
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {mmgResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author_name} • {result.publisher}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className="bg-orange-100 text-orange-800">{result.word_count?.toLocaleString()} words</Badge>
                          {result.payment_amount > 0 && (
                            <Badge className="bg-green-100 text-green-800">Paid: ${result.payment_amount}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto"
                          onClick={() => navigate(`/mmg?report=${result.id}&from=activity`)}
                        >
                          📊 View Report
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                          onClick={() => {
                            setDeleteTarget({
                              type: 'mmg',
                              id: result.id,
                              name: result.title
                            });
                            setShowDeleteModal(true);
                          }}
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-slate-600">Comprehensive SEO metadata with keywords, categories, descriptions, and market insights</p>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          Created: {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'Recently'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* ABG Tab Content */}
        {activeServiceTab === 'abg' && (
          <div id="service-results-section">

            {/* ABG Service Header */}
            <Card className="bg-gradient-to-r from-emerald-500 to-green-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📝 Author Biography Generator</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="flex flex-wrap items-center justify-center gap-8 w-full max-w-4xl">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{abgResults.length}</div>
                      <div className="text-sm text-emerald-100">Total Reports</div>
                    </div>
                    <div className="hidden sm:block w-px h-12 bg-white/30"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                      <div className="text-sm text-emerald-100">Total Spent</div>
                    </div>
                    <div className="hidden sm:block w-px h-12 bg-white/30"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'ABG').length}
                      </div>
                      <div className="text-sm text-emerald-100">Paid Uses</div>
                    </div>
                  </div>

                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('ABG')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('ABG', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-emerald-600 hover:bg-emerald-50"
                    >
                      📝 Create New ABG
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ABG Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📝</span>
                  Author Biographies ({abgResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {abgResults.length === 0 ? (
              <div className="text-center py-12 bg-emerald-50 rounded-lg">
                <p className="text-2xl mb-4">📝</p>
                <p className="text-slate-600 mb-4">No author biographies generated yet</p>
                <Button onClick={() => handleServiceAccess('ABG', 'activity')} className="bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white">
                  Create Your First Biography
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {abgResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.author_name}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          {result.length_option === 'brief' ? 'Brief (150 words)' : result.length_option === 'short' ? 'Short (150-300 words)' : 'Long (300-1000 words)'}
                          {result.city_state && ` • ${result.city_state}`}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className="bg-emerald-100 text-emerald-800">{result.manuscript_word_count?.toLocaleString()} words analyzed</Badge>
                          {result.payment_amount > 0 && (
                            <Badge className="bg-green-100 text-green-800">Paid: ${result.payment_amount}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto"
                          onClick={() => navigate(`/abg?report=${result.id}&from=activity`)}
                        >
                          📝 View Biography
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                          onClick={() => {
                            setDeleteTarget({
                              type: 'abg',
                              id: result.id,
                              name: result.author_name
                            });
                            setShowDeleteModal(true);
                          }}
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-slate-600 line-clamp-3">{result.generated_biography}</p>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          Created: {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'Recently'}
                        </span>
                        {result.author_website && (
                          <a href={result.author_website} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-800">
                            🔗 Website
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {activeServiceTab === 'ermt' && (
          <div id="service-results-section">

            {/* ERMT Service Header */}
            <Card className="bg-gradient-to-r from-rose-500 to-pink-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📕 EPUB-Ready Manuscript Transformer</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-rose-100">Total Conversions</div>
                      <div className="text-2xl font-bold mt-1">{ermtResults.length}</div>
                    </div>
                    <div>
                      <div className="text-rose-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-rose-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'ERMT').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('ERMT')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => navigate('/ermt')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-rose-600 hover:bg-rose-50 font-semibold"
                    >
                      📕 Convert New Manuscript
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ERMT Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📕</span>
                  EPUB Conversions ({ermtResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {ermtResults.length === 0 ? (
              <div className="text-center py-12 bg-rose-50 rounded-lg">
                <p className="text-2xl mb-4">📕</p>
                <p className="text-slate-600 mb-4">No EPUB conversions yet</p>
                <Button onClick={() => navigate('/ermt')} className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white">
                  Convert Your First Manuscript
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {ermtResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author_name} • {result.genre || 'N/A'}
                        </p>
                        <div className="flex gap-2 mt-2">
                          {result.validation_status && (
                            <Badge className={`text-xs ${
                              result.validation_status === 'completed' 
                                ? 'bg-green-100 text-green-800 border-green-300' 
                                : 'bg-amber-100 text-amber-800 border-amber-300'
                            }`}>
                              {result.validation_status === 'completed' ? '✓ Validated' : 'Processing'}
                            </Badge>
                          )}
                          {result.word_count && (
                            <Badge variant="outline" className="text-rose-600 border-rose-300">
                              {result.word_count.toLocaleString()} words
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/ermt?report=${result.id}&from=activity`)}
                          variant="outline"
                          size="sm"
                          className="w-full sm:w-auto text-purple-600 border-purple-600 hover:bg-purple-50"
                        >
                          👁️ View Result
                        </Button>
                        <Button
                          onClick={async () => {
                            if (downloadingEpubId) return;
                            setDownloadingEpubId(result.id);
                            const toastId = toast.loading('📥 Preparing EPUB download...');
                            try {
                              const response = await axios.get(`${API}/ermt/download/${result.id}`, {
                                withCredentials: true,
                                responseType: 'blob'
                              });
                              
                              const safeTitle = (result.title || 'EPUB_Download').replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').substring(0, 50);
                              const filename = `${safeTitle}.epub`;
                              
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', filename);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              
                              toast.success(`✅ Download complete! Saved as: ${filename}`, { id: toastId, duration: 5000 });
                            } catch (error) {
                              console.error("Download error:", error);
                              toast.error(`❌ Download failed: ${error.message}`, { id: toastId });
                            } finally {
                              setDownloadingEpubId(null);
                            }
                          }}
                          variant="outline"
                          size="sm"
                          disabled={downloadingEpubId === result.id}
                          className={`w-full sm:w-auto text-rose-600 border-rose-600 hover:bg-rose-50 ${downloadingEpubId === result.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {downloadingEpubId === result.id ? '⏳ Downloading...' : '📥 Download EPUB'}
                        </Button>
                        <Button
                          onClick={() => {
                            setDeleteTarget({
                              type: 'ermt',
                              id: result.id,
                              name: result.title
                            });
                            setShowDeleteModal(true);
                          }}
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 border-red-600 hover:bg-red-50"
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-500">Publisher:</span>
                            <span className="ml-2 font-medium">{result.publisher || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Format:</span>
                            <span className="ml-2 font-medium">EPUB3</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">
                            Converted: {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'Recently'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* PMPB Tab Content */}
        {activeServiceTab === 'pmpb' && (
          <div id="service-results-section">

            {/* PMPB Service Header */}
            <Card className="bg-gradient-to-r from-pink-500 to-rose-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📄 Print-Ready Merged PDF Builder</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-pink-100">Total PDFs</div>
                      <div className="text-2xl font-bold mt-1">{pmpbResults.length}</div>
                    </div>
                    <div>
                      <div className="text-pink-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && ['PMPB', 'PRMPB'].includes(t.service.toUpperCase())).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-pink-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && ['PMPB', 'PRMPB'].includes(t.service.toUpperCase())).length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('PMPB')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => navigate('/pmpb')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-pink-600 hover:bg-pink-50 font-semibold"
                    >
                      📄 Create New PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PMPB Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📄</span>
                  Print-Ready PDFs ({pmpbResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {pmpbResults.length === 0 ? (
              <div className="text-center py-12 bg-pink-50 rounded-lg">
                <p className="text-2xl mb-4">📄</p>
                <p className="text-slate-600 mb-4">No PDFs generated yet</p>
                <Button onClick={() => navigate('/pmpb')} className="bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 text-white">
                  Create Your First PDF
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {pmpbResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.project_title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author_name}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className="bg-pink-100 text-pink-800">{result.trim_size}</Badge>
                          <Badge className="bg-rose-100 text-rose-800">{result.font_family}</Badge>
                          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                            {result.source_file_count} files • ~{result.total_page_count} pages
                          </Badge>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => navigate(`/pmpb?result=${result.id}`)}
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-pink-600 border-pink-600 hover:bg-pink-50"
                        >
                          📄 View PMPB
                        </Button>
                        <Button
                          onClick={() => {
                            setDeleteTarget({
                              type: 'pmpb',
                              id: result.id,
                              name: result.project_title
                            });
                            setShowDeleteModal(true);
                          }}
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 border-red-600 hover:bg-red-50"
                        >
                          🗑️ Delete
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">
                        Created: {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'Recently'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {activeServiceTab === 'ksg' && (
          <div id="service-results-section">

            {/* KSG Service Header */}
            <Card className="bg-gradient-to-r from-amber-500 to-orange-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">🎤 Keynote Speech Generator</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-amber-100">Total Speeches</div>
                      <div className="text-2xl font-bold mt-1">{ksgResults.length}</div>
                    </div>
                    <div>
                      <div className="text-amber-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-amber-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'KSG').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('KSG')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('KSG', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-amber-600 hover:bg-amber-50 font-semibold px-6 py-2"
                    >
                      🎤 Create New Speech
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* KSG Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">🎤</span>
                  Keynote Speeches ({ksgResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {ksgResults.length === 0 ? (
              <div className="text-center py-12 bg-amber-50 rounded-lg">
                <p className="text-2xl mb-4">🎤</p>
                <p className="text-slate-600 mb-4">No keynote speeches generated yet</p>
                <Button onClick={() => handleServiceAccess('KSG', 'activity')} className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white">
                  Generate Your First Speech
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {ksgResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.book_title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author_name} • {result.target_audience}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className="bg-amber-100 text-amber-800">{result.speech_word_count?.toLocaleString()} words (~{Math.floor(result.speech_word_count / 150)} min)</Badge>
                          <Badge className="bg-orange-100 text-orange-800">{result.manuscript_word_count?.toLocaleString()} words analyzed</Badge>
                          {result.payment_amount > 0 && (
                            <Badge className="bg-green-100 text-green-800">Paid: ${result.payment_amount}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto"
                          onClick={() => navigate(`/ksg?report=${result.id}&from=activity`)}
                        >
                          🎤 View Speech
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                          onClick={() => {
                            setDeleteTarget({
                              type: 'ksg',
                              id: result.id,
                              name: result.book_title
                            });
                            setShowDeleteModal(true);
                          }}
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-slate-600 line-clamp-3">{result.keynote_speech ? result.keynote_speech.substring(0, 200) + '...' : 'Speech content'}</p>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          Created: {result.created_at ? new Date(result.created_at).toLocaleDateString() : result.completed_at ? new Date(result.completed_at).toLocaleDateString() : 'Recently'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}


        {/* AKSW Tab Content */}
        {activeServiceTab === 'aksw' && (
          <div id="service-results-section">
            {/* AKSW Service Header */}
            <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📦 Amazon KDP Submission Wizard</h2>
                  
                  {/* Centered Stats Row */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-orange-100">Total Guides</div>
                      <div className="text-2xl font-bold mt-1">{akswResults.length}</div>
                    </div>
                    <div>
                      <div className="text-orange-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'AKSW').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('AKSW')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => navigate('/aksw')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50"
                    >
                      📦 Create New AKSW Guide
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AKSW Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📦</span>
                  KDP Submission Guides ({akswResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {akswResults.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">📦</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No KDP guides yet</h3>
                    <p className="text-slate-600 mb-4">Create your first Amazon KDP submission guide to see it here.</p>
                    <Button 
                      onClick={() => navigate('/aksw')}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      Create Your First Guide
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {akswResults.map((result) => (
                      <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-lg text-slate-900">
                                {result.book_title}
                              </CardTitle>
                              {result.subtitle && (
                                <p className="text-md text-slate-700 italic">{result.subtitle}</p>
                              )}
                              <p className="text-sm text-slate-600 mt-1">
                                Generated on {new Date(result.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                }) || 'Recently'} 
                                {result.publisher_name ? ` • Publisher: ${result.publisher_name}` : ''}
                              </p>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              {result.book_price && (
                                <Badge variant="secondary" className="bg-orange-100 text-orange-800">
                                  ${result.book_price}
                                </Badge>
                              )}
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto"
                                  onClick={() => navigate('/aksw', { state: { reportId: result.id } })}
                                >
                                  📖 View Report
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto"
                                  onClick={() => exportAKSWReportToPDF(result)}
                                >
                                  📄 Export PDF
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto"
                                  onClick={() => printAKSWReport(result)}
                                >
                                  🖨️ Print
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                                  onClick={async () => {
                                    if (window.confirm('Delete this KDP guide? This cannot be undone.')) {
                                      try {
                                        await axios.delete(`${API}/aksw/reports/${result.id}`, { withCredentials: true });
                                        toast.success('Guide deleted');
                                        setAkswResults(prev => prev.filter(r => r.id !== result.id));
                                      } catch (error) {
                                        toast.error('Failed to delete guide');
                                      }
                                    }
                                  }}
                                >
                                  🗑️ Delete
                                </Button>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex flex-col">
                              <span className="text-slate-500">Book Type</span>
                              <span className="font-medium capitalize">{result.book_type || 'N/A'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-slate-500">ISBN</span>
                              <span className="font-medium">{result.isbn || 'N/A'}</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-slate-500">KDP Keywords</span>
                              <span className="font-medium text-orange-600">{result.kdp_keywords?.length || 0} keywords</span>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-slate-500">KDP Categories</span>
                              <span className="font-medium">{result.kdp_categories?.length || 0} categories</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* MMKG Tab Content */}
        {activeServiceTab === 'mmkg' && (
          <div id="service-results-section">
            {/* MMKG Service Header */}
            <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📰 Manuscript Media Kit Generator</h2>
                  
                  {/* Centered Stats Row */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-orange-100">Total Media Kits</div>
                      <div className="text-2xl font-bold mt-1">{mmkgResults.length}</div>
                    </div>
                    <div>
                      <div className="text-orange-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-orange-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'MMKG').length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/mmkg/reports`, { withCredentials: true });
                          const data = response.data?.reports || response.data || [];
                          setMmkgResults(Array.isArray(data) ? data : []);
                          toast.success('Data refreshed!');
                        } catch (error) {
                          toast.error('Failed to refresh data');
                        }
                      }}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      🔄 Refresh
                    </Button>
                    <Button 
                      onClick={() => openTransactionDetails('MMKG')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('MMKG', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-orange-600 hover:bg-orange-50"
                    >
                      📰 Create New Media Kit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* MMKG Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📰</span>
                  Media Kits ({mmkgResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mmkgResults.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">📰</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No media kits yet</h3>
                    <p className="text-slate-600 mb-4">Create your first manuscript media kit to see it here.</p>
                    <Button 
                      onClick={() => handleServiceAccess('MMKG', 'activity')}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      Generate Your First Media Kit
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {mmkgResults.map((result) => (
                      <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg text-slate-900">
                                {result.book_title}
                              </CardTitle>
                              {result.subtitle && (
                                <p className="text-md text-slate-700 italic mt-1">{result.subtitle}</p>
                              )}
                              <p className="text-sm text-slate-600 mt-2">
                                by {result.author_name} • {result.publisher_name || 'Self-Published'}
                              </p>
                              <p className="text-xs text-slate-500 mt-1">
                                Generated on {new Date(result.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                            <Badge variant="outline" className="text-orange-600 border-orange-300">
                              {result.release_date || 'Upcoming'}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              size="sm"
                      className="w-full sm:w-auto" 
                              onClick={() => navigate('/mmkg', { state: { reportId: result.id } })}
                              variant="outline"
                              className="text-green-600 hover:text-green-700"
                            >
                              📋 View Full Media Kit
                            </Button>
                            <Button 
                              size="sm"
                      className="w-full sm:w-auto" 
                              onClick={async () => {
                                if (window.confirm('Delete this media kit? This cannot be undone.')) {
                                  try {
                                    await axios.delete(`${API}/mmkg/reports/${result.id}`, { withCredentials: true });
                                    toast.success('Media kit deleted');
                                    setMmkgResults(prev => prev.filter(r => r.id !== result.id));
                                  } catch (error) {
                                    toast.error('Failed to delete media kit');
                                  }
                                }
                              }}
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}


        {/* SMMK Dashboard */}
        {activeServiceTab === 'smmk' && (
          <div id="service-results-section">
            {/* SMMK Service Header */}
            <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-center">📱 Social Media Marketing Kit Dashboard</h2>
                  
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-purple-100">Total Kits</div>
                      <div className="text-2xl font-bold mt-1">{smmkResults.length}</div>
                    </div>
                    <div>
                      <div className="text-purple-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-purple-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'SMMK').length}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/smmk/kits`, { withCredentials: true });
                          const data = response.data?.kits || response.data || [];
                          setSmmkResults(Array.isArray(data) ? data : []);
                          toast.success('Data refreshed!');
                        } catch (error) {
                          toast.error('Failed to refresh data');
                        }
                      }}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      🔄 Refresh
                    </Button>
                    <Button 
                      onClick={() => openTransactionDetails('SMMK')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('SMMK', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-purple-600 hover:bg-purple-50"
                    >
                      📱 Create New Marketing Kit
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SMMK Kits List */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">📱</span>
                  Marketing Kits ({smmkResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {smmkResults.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">📱</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No marketing kits yet</h3>
                    <p className="text-slate-600 mb-4">Create your first social media marketing kit to see it here.</p>
                    <Button 
                      onClick={() => handleServiceAccess('SMMK', 'activity')}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      Generate Your First Marketing Kit
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {smmkResults.map((kit) => (
                      <Card key={kit.id} className="border-slate-200 hover:shadow-md transition-shadow">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg text-slate-900">
                                {kit.book_title}
                              </CardTitle>
                              {kit.subtitle && (
                                <p className="text-md text-slate-700 italic mt-1">{kit.subtitle}</p>
                              )}
                              <p className="text-sm text-slate-600 mt-2">
                                by {kit.author_name} • Publisher: {kit.publisher_name}
                              </p>
                              <div className="flex gap-4 mt-2">
                                <span className="text-xs text-slate-500">
                                  Status: <span className="font-medium capitalize">{kit.publication_status?.replace('_', ' ')}</span>
                                </span>
                                <span className="text-xs text-slate-500">
                                  Generated: {new Date(kit.created_at).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-purple-600 border-purple-300">
                              {kit.publication_status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="mb-4">
                            <div className="text-xs text-slate-600 mb-2">Reports Generated:</div>
                            <div className="flex flex-wrap gap-2">
                              <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                ✅ Messaging Framework
                              </span>
                              {kit.reports?.platform_posts_1?.generated && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  ✅ Facebook/Instagram
                                </span>
                              )}
                              {kit.reports?.platform_posts_2?.generated && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  ✅ Twitter/TikTok
                                </span>
                              )}
                              {kit.reports?.creative_direction?.generated && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  ✅ Creative Direction
                                </span>
                              )}
                              {kit.reports?.audience_insights?.generated && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  ✅ Audience Insights
                                </span>
                              )}
                              {kit.reports?.action_plans?.generated && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  ✅ Action Plans
                                </span>
                              )}
                              {kit.reports?.launch_package?.generated && (
                                <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  ✅ Launch Package
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button 
                              size="sm"
                              className="w-full sm:w-auto" 
                              onClick={() => navigate('/smmk', { state: { kitId: kit.id } })}
                              className="bg-purple-600 hover:bg-purple-700 text-white"
                            >
                              📋 View Full Kit
                            </Button>
                            <Button 
                              size="sm"
                              className="w-full sm:w-auto" 
                              onClick={async () => {
                                if (window.confirm('Delete this marketing kit? This cannot be undone.')) {
                                  try {
                                    await axios.delete(`${API}/smmk/kits/${kit.id}`, { withCredentials: true });
                                    toast.success('Marketing kit deleted');
                                    setSmmkResults(prev => prev.filter(k => k.id !== kit.id));
                                  } catch (error) {
                                    toast.error('Failed to delete marketing kit');
                                  }
                                }
                              }}
                              variant="outline"
                              className="text-red-600 hover:text-red-700"
                            >
                              🗑️ Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}



        {/* SMMK Tab Content */}



        {/* PDE Tab Content */}
        {activeServiceTab === 'pde' && (
          <div id="service-results-section">

            {/* PDE Service Header */}
            <Card className="bg-gradient-to-r from-purple-500 to-violet-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">✍️ Professional Developmental Editor</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="flex flex-wrap items-center justify-center gap-8 w-full max-w-4xl">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{pdeResults.length}</div>
                      <div className="text-sm text-purple-100">Total Reports</div>
                    </div>
                    <div className="hidden sm:block w-px h-12 bg-white/30"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        ${(transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                      <div className="text-sm text-purple-100">Total Spent</div>
                    </div>
                    <div className="hidden sm:block w-px h-12 bg-white/30"></div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {transactions.filter(t => t.service && t.service.toUpperCase() === 'PDE').length}
                      </div>
                      <div className="text-sm text-purple-100">Paid Uses</div>
                    </div>
                  </div>

                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('PDE')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => navigate('/pde')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-purple-600 hover:bg-purple-50 font-semibold"
                    >
                      ✍️ Create New Analysis
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PDE Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">✍️</span>
                  Developmental Edits ({pdeResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {pdeResults.length === 0 ? (
              <div className="text-center py-12 bg-purple-50 rounded-lg">
                <p className="text-2xl mb-4">✍️</p>
                <p className="text-slate-600 mb-4">No developmental edits generated yet</p>
                <Button onClick={() => navigate('/pde')} className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white">
                  Upload Your First Manuscript
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {pdeResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.title}
                        </CardTitle>
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author_name} • {result.genre || 'N/A'}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            {result.word_count ? result.word_count.toLocaleString() : '0'} words
                          </Badge>
                          <Badge variant="outline" className="text-purple-600 border-purple-300">
                            {result.page_count || 0} pages
                          </Badge>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => navigate(`/pde?report=${result.id}`)}
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-purple-600 border-purple-600 hover:bg-purple-50"
                        >
                          📄 View Report
                        </Button>
                        <Button
                          onClick={() => handleDeleteReport(result.id, 'pde')}
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 border-red-600 hover:bg-red-50"
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm text-slate-600 line-clamp-3">{result.structural_analysis ? result.structural_analysis.substring(0, 200) + '...' : 'Analysis content'}</p>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">
                            Created: {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'Recently'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {activeServiceTab === 'ccg' && (
          <div id="service-results-section">

            {/* CCG Service Header */}
            <Card className="bg-gradient-to-r from-cyan-500 to-cyan-600 text-white mb-6 overflow-hidden">
              <CardContent className="py-6">
                <div className="flex flex-col items-center gap-6">
                  {/* Centered Title */}
                  <h2 className="text-xl sm:text-2xl font-bold text-center">🎭 Character & Cast Generator</h2>
                  
                  {/* Centered Stats Row - distributed horizontally */}
                  <div className="w-full grid grid-cols-3 gap-4 text-center text-sm">
                    <div>
                      <div className="text-cyan-100">Total Reports</div>
                      <div className="text-2xl font-bold mt-1">{ccgResults.length}</div>
                    </div>
                    <div>
                      <div className="text-cyan-100">Total Spent</div>
                      <div className="text-2xl font-bold mt-1">
                        ${(transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).reduce((sum, t) => sum + (t.amount || 0), 0)).toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-cyan-100">Paid Uses</div>
                      <div className="text-2xl font-bold mt-1">
                        {transactions.filter(t => t.service && (t.service.toUpperCase() === 'CCG' || t.service.toUpperCase() === 'CDDA')).length}
                      </div>
                    </div>
                  </div>
                  
                  {/* Centered Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => openTransactionDetails('CCG')}
                      size="sm"
                      className="w-full sm:w-auto bg-white/20 text-white border-white/20 hover:bg-white/30"
                    >
                      View Transaction History
                    </Button>
                    <Button 
                      onClick={() => handleServiceAccess('CCG', 'activity')}
                      size="sm"
                      className="w-full sm:w-auto bg-white text-cyan-600 hover:bg-cyan-50"
                    >
                      🎭 Create New CCG
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CCG Results Section */}
            <Card className="mt-8 bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl text-slate-900 flex items-center">
                  <span className="text-4xl mr-2">🎭</span>
                  Character Cast Reports ({ccgResults.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
            {ccgResults.length === 0 ? (
              <div className="text-center py-12 bg-cyan-50 rounded-lg">
                <p className="text-2xl mb-4">🎭</p>
                <p className="text-slate-600 mb-4">No character casts generated yet</p>
                <Button onClick={() => handleServiceAccess('CCG', 'activity')} className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white">
                  Generate Your First Character Cast
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
              {ccgResults.map((result) => (
                <Card key={result.id} className="border-slate-200 hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900">
                          {result.book_title}
                        </CardTitle>
                        {result.subtitle && (
                          <p className="text-sm text-slate-500 mt-1">{result.subtitle}</p>
                        )}
                        <p className="text-sm text-slate-600 mt-1">
                          by {result.author_name} • {result.category}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge className="bg-cyan-100 text-cyan-800">
                            {result.characters?.length || result.num_characters_generated || 0} Characters
                          </Badge>
                          {result.payment_amount > 0 && (
                            <Badge className="bg-green-100 text-green-800">Paid: ${result.payment_amount}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto"
                          onClick={() => navigate(`/ccg?report=${result.id}&from=activity`)}
                        >
                          🎭 View Report
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                      className="w-full sm:w-auto text-red-600 hover:text-red-800"
                          onClick={() => {
                            setDeleteTarget({
                              type: 'ccg',
                              id: result.id,
                              name: result.book_title
                            });
                            setShowDeleteModal(true);
                          }}
                        >
                          🗑️ Delete
                        </Button>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-slate-700">Theme: {result.theme}</p>
                        <p className="text-sm text-slate-600 mt-1">Character cast with archetypes, personalities, motivations, and relationships</p>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          Created: {result.created_at ? new Date(result.created_at).toLocaleDateString() : 'Recently'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            )}
          </CardContent>
        </Card>
          </div>
        )}

        {/* Refer A Friend Card */}
        <Card className="mt-8 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 hover:shadow-xl transition-shadow">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <div className="text-6xl mb-4">🎁</div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">
                Share Your Success Story!
              </h2>
              <p className="text-lg text-slate-700 mb-6 max-w-2xl mx-auto">
                Love your results? Invite fellow authors to Digital Ink AI and earn <strong className="text-green-600">200 bonus points</strong> when they become paid subscribers. Help others discover powerful AI tools!
              </p>
              <Button 
                onClick={() => setShowReferralModal(true)}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-3 text-lg font-semibold min-h-[44px]"
              >
                🎁 Refer A Friend & Get Rewarded
              </Button>
              <p className="text-sm text-slate-500 mt-3">
                Earn points while helping authors succeed with AI
              </p>
            </div>
          </CardContent>
        </Card>

      </main>
      
      <GlobalFooter />

      {/* Payment Flow Modals */}
      <PaymentValidationModal
        isOpen={showPaymentValidation}
        onClose={handleModalClose}
        onSuccess={handlePaymentValidationSuccess}
        redirectLocation="activity"
      />

      <ServiceChargeModal
        isOpen={showServiceCharge}
        onClose={handleModalClose}
        onConfirm={handleServiceChargeConfirm}
        serviceInfo={serviceInfo}
        userType={user?.role || 'a_la_carte'}
      />

      {/* Full-Screen BCTG Results Viewer */}
      {showFullScreenView && fullScreenResult && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center print:hidden">
            <div className="flex items-center">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI" 
                className="h-10 mr-4"
              />
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Book Concept & Title Generator Results - Full View</h1>
                <p className="text-sm text-slate-600">
                  {fullScreenResult.inputs.genre || fullScreenResult.inputs.custom_genre} - {fullScreenResult.inputs.theme || fullScreenResult.inputs.custom_theme}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => window.location.href = '/homepage'}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => window.location.href = '/user_dashboard'}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="max-w-6xl mx-auto px-6 py-8">
            <div id="bctg-fullscreen-results" className="bg-white rounded-lg shadow-lg p-8">
              {/* Print Header - Hidden on Screen */}
              <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-12 w-auto"
                    />
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                      <p className="text-sm text-gray-600">Book Concept & Title Generator</p>
                      <p className="text-xs text-gray-600 mt-1">Genre: {fullScreenResult.inputs.genre} | Concepts: {fullScreenResult.results.concepts?.length || 3}</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p>{new Date().toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Report Metadata - ABG Style Layout */}
              <div className="border-b pb-6 mb-6 print:border-b-2">
                <div className="flex justify-between items-start mb-4 print:mb-2">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">Book Concept Report</h2>
                    <p className="text-slate-600 mt-1">Generated for {fullScreenResult.inputs.genre || fullScreenResult.inputs.custom_genre} genre</p>
                  </div>
                  <div className="flex gap-3 print:hidden">
                    <button onClick={() => printResult(fullScreenResult)} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">🖨️ Print</button>
                    <button onClick={() => exportResultToPDF(fullScreenResult)} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">📄 Export PDF</button>
                    <button onClick={handleCreateNewBCTG} className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">✨ Create New BCTG</button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Genre:</span>
                    <p className="font-semibold text-slate-900">{fullScreenResult.inputs.genre || fullScreenResult.inputs.custom_genre || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Theme:</span>
                    <p className="font-semibold text-slate-900">{fullScreenResult.inputs.theme || fullScreenResult.inputs.custom_theme || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Service:</span>
                    <p className="font-semibold text-slate-900">BCTG</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Concepts:</span>
                    <p className="font-semibold text-slate-900">{fullScreenResult.results.concepts?.length || 3}</p>
                  </div>
                </div>
              </div>

              {/* Book Concepts - Complete Original Format */}
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-slate-900">Generated Book Concepts</h2>
                {fullScreenResult.results.concepts?.map((concept, index) => (
                  <Card key={index} className="bg-white border-slate-200 hover:shadow-lg transition-all duration-300">
                    <CardHeader className="bg-gradient-to-r from-slate-50 to-blue-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <Badge variant="default" className="mb-3 bg-blue-600">
                            Concept {index + 1}
                          </Badge>
                          <CardTitle className="text-3xl text-slate-900 mb-3 leading-tight">
                            {concept.title}
                          </CardTitle>
                          {concept.subtitle && (
                            <p className="text-xl text-slate-600 font-medium italic">
                              {concept.subtitle}
                            </p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6 p-6">
                      {/* Literary Hook */}
                      {(concept.literary_hook || concept.summary) && (
                        <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-slate-900 text-lg">📖 Literary Hook</h4>
                            <ReadAloudButton text={concept.literary_hook || extractLiteraryHook(concept.summary)} serviceType="bctg" />
                          </div>
                          <p className="text-slate-700 leading-relaxed font-medium">
                            {concept.literary_hook || extractLiteraryHook(concept.summary)}
                          </p>
                        </div>
                      )}

                      {/* 500-Word Concept Summary */}
                      {concept.summary && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-bold text-slate-900 text-lg">📋 Complete Book Concept</h4>
                            <ReadAloudButton text={concept.summary || ""} serviceType="bctg" />
                          </div>
                          <p className="text-slate-700 leading-relaxed">
                            {concept.summary}
                          </p>
                        </div>
                      )}

                      {/* Why This Will Sell */}
                      {concept.market_appeal && (
                        <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-bold text-slate-900 text-lg">💰 Why This Will Sell</h4>
                            <ReadAloudButton text={concept.market_appeal || ""} serviceType="bctg" />
                          </div>
                          <p className="text-slate-700 leading-relaxed">
                            {concept.market_appeal}
                          </p>
                        </div>
                      )}

                      {/* Chapter Outline / Table of Contents */}
                      {concept.chapter_outline && concept.chapter_outline.length > 0 && (
                        <div>
                          <h4 className="font-bold text-slate-900 mb-4 text-lg">📚 Chapter Outline</h4>
                          <div className="grid gap-2">
                            {concept.chapter_outline.map((chapter, chapterIndex) => (
                              <div key={chapterIndex} className="flex items-center p-3 bg-slate-50 rounded-md border-l-4 border-blue-200">
                                <span className="text-blue-600 font-bold w-16 text-sm">
                                  Ch. {chapterIndex + 1}
                                </span>
                                <span className="text-slate-700 font-medium">{chapter}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Footer */}
              <div className="text-center py-8 border-t border-slate-200">
                <p className="text-sm text-slate-500 mb-4">
                  Generated by Digital Ink AI - AI-Powered Publishing Tools
                </p>
                
                {/* Rewards Link with Progress */}
                <div className="mt-6 max-w-md mx-auto">
                  <button
                    onClick={() => navigate('/rewards')}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 px-6 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-xl">🎁</span>
                      <span>Your Rewards: Turn Activity into Publishing Perks</span>
                    </div>
                  </button>
                  
                  {/* Rewards Progress Bar */}
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>Points Earned</span>
                      <span className="font-semibold">{userStats.rewardsPoints || 0} / 1000</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min(((userStats.rewardsPoints || 0) / 1000) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 text-center">
                      Earn rewards for every action you take!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={handleCloseEmailReceiptModal}
        onSendReceipt={handleSendReceiptEmail}
        serviceName={lastCharge?.serviceName || "Service"}
        amount={lastCharge?.amount || 0}
        bookTitle={lastCharge?.bookTitle || ""}
      />
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        itemType={
          deleteTarget?.type === 'mmg' ? 'MMG Report' : 
          deleteTarget?.type === 'abg' ? 'ABG Biography' :
          deleteTarget?.type === 'ccg' ? 'CCG Report' :
          deleteTarget?.type === 'pmpb' ? 'PMPB PDF' :
          deleteTarget?.type === 'ermt' ? 'ERMT Conversion' :
          'Result'
        }
        itemName={deleteTarget?.name || ''}
        onConfirm={async () => {
          if (!deleteTarget) return;
          
          try {
            if (deleteTarget.type === 'mmg') {
              await axios.delete(`${API}/mmg/result/${deleteTarget.id}`, { withCredentials: true });
              setMmgResults(prev => prev.filter(r => r.id !== deleteTarget.id));
              toast.success('MMG report deleted successfully');
            } else if (deleteTarget.type === 'abg') {
              await axios.delete(`${API}/abg/results/${deleteTarget.id}`, { withCredentials: true });
              setAbgResults(prev => prev.filter(r => r.id !== deleteTarget.id));
              toast.success('ABG biography deleted successfully');
            } else if (deleteTarget.type === 'ksg') {
              await axios.delete(`${API}/ksg/results/${deleteTarget.id}`, { withCredentials: true });
              setKsgResults(prev => prev.filter(r => r.id !== deleteTarget.id));
              toast.success('KSG speech deleted successfully');
            } else if (deleteTarget.type === 'ccg') {
              await axios.delete(`${API}/ccg/results/${deleteTarget.id}`, { withCredentials: true });
              setCcgResults(prev => prev.filter(r => r.id !== deleteTarget.id));
              toast.success('CCG report deleted successfully');
            } else if (deleteTarget.type === 'pmpb') {
              await axios.delete(`${API}/pmpb/results/${deleteTarget.id}`, { withCredentials: true });
              setPmpbResults(prev => prev.filter(r => r.id !== deleteTarget.id));
              toast.success('PMPB PDF deleted successfully');
            } else if (deleteTarget.type === 'ermt') {
              await axios.delete(`${API}/ermt/results/${deleteTarget.id}`, { withCredentials: true });
              setErmtResults(prev => prev.filter(r => r.id !== deleteTarget.id));
              toast.success('ERMT conversion deleted successfully');
            }
          } catch (error) {
            toast.error('Failed to delete. Please try again.');
          } finally {
            setDeleteTarget(null);
          }
        }}
      />
      
      {/* Referral Modal */}
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />
      
      {/* Help Modal */}
      <HelpModal 
        isOpen={helpModalOpen}
        onClose={() => setHelpModalOpen(false)}
        title={helpModalContent.title}
        content={helpModalContent.content}
      />
    </div>
  );
};

// Features Page Component
const FeaturesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const serviceCategories = [
    {
      title: "Writing & Ideation",
      icon: "📝",
      description: "Transform your creative vision into structured concepts",
      color: "blue",
      bgColor: "from-blue-100 to-blue-200",
      borderColor: "border-blue-300",
      textColor: "text-blue-900",
      services: [
        { code: "BCTG", title: "Book Concept & Title Generator", description: "Generate book ideas and marketable titles. First 3 uses free each month for all users!", price: 5.00, available: true, premium: false },
        { code: "BOCP", title: "Book Outline and Chapter Planner", description: "Create detailed book outlines and chapter structures.", price: 15.00, available: true, premium: true },
        { code: "CCG", title: "Character & Cast Generator", description: "Generate diverse, impactful characters from your story outline.", price: 7.00, available: true, premium: false }
      ]
    },
    {
      title: "Editing & Enhancement",
      icon: "✏️",
      description: "Polish your manuscript to professional standards",
      color: "purple",
      bgColor: "from-purple-100 to-purple-200",
      borderColor: "border-purple-300",
      textColor: "text-purple-900",
      services: [
        { code: "ABG", title: "Author Biography Generator", description: "Create professional 'About the Author' content that connects your background to your book.", price: 5.00, available: true, premium: false },
        { code: "PDE", title: "Professional Developmental Editor", description: "Professional developmental editing to enhance manuscript structure, flow, and quality.", price: 20.00, available: true, premium: true },
        { code: "BVADM", title: "Book Viability and Development Model", description: "Assess book quality, structure, and market viability.", price: 12.00, available: true, premium: true }
      ]
    },
    {
      title: "Design & Production",
      icon: "🎨",
      description: "Professional finishing touches for market-ready books",
      color: "rose",
      bgColor: "from-rose-100 to-rose-200",
      borderColor: "border-rose-300",
      textColor: "text-rose-900",
      services: [
        { code: "ERMT", title: "EPUB-Ready Manuscript Transformer", description: "Convert your manuscript to professional EPUB format with industry-standard validation.", price: 20.00, available: true, premium: true },
        { code: "PMPB", title: "Print-Ready Merged PDF Builder", description: "Merge multiple manuscript files into a professional print-ready PDF with industry-standard formatting for POD platforms.", price: 3.00, available: true, premium: false },
        { code: "AKSW", title: "Amazon KDP Submission Wizard", description: "Guided submission to Amazon KDP with automated metadata extraction and streamlined publishing.", price: 5.00, available: true, premium: false }
      ]
    },
    {
      title: "Market Research & Analysis",
      icon: "📊",
      description: "Data-driven insights for your manuscript's success",
      color: "emerald",
      bgColor: "from-emerald-100 to-emerald-200",
      borderColor: "border-emerald-300",
      textColor: "text-emerald-900",
      services: [
        { code: "CCAT", title: "Competitor & Comparative Analysis Tool", description: "Expert competitive analysis with market gap identification and differentiation strategies.", price: 7.00, available: true, premium: false },
        { code: "BREC", title: "Book Review & Endorsement Creator", description: "Professional mock reviews and strategic endorsement recommendations for credibility building.", price: 7.00, available: true, premium: false },
        { code: "MMG", title: "Metadata & Marketing Generator", description: "Generate optimized metadata, keywords, and marketing copy for maximum discoverability.", price: 4.00, available: true, premium: false }
      ]
    },
    {
      title: "Marketing & Promotion",
      icon: "📈",
      description: "Amplify your book's reach and sales potential",
      color: "orange",
      bgColor: "from-orange-100 to-orange-200",
      borderColor: "border-orange-300",
      textColor: "text-orange-900",
      services: [
        { code: "KSG", title: "Keynote Speech Generator", description: "Generate a professional 40-minute keynote speech from your book manuscript.", price: 15.00, available: true, premium: true },
        { code: "MMKG", title: "Manuscript Media Kit Generator", description: "Create comprehensive media kit with press releases, author bio, interview questions, and traditional publicity materials.", price: 20.00, available: true, premium: true },
        { code: "SMMK", title: "Social Media Marketing Kit", description: "Generate complete social media campaign with messaging framework, platform strategies, content calendar, and engagement tactics.", price: 25.00, available: true, premium: true }
      ]
    }
  ];

  const getServicePricing = (service) => {
    const { role } = user || {};
    
    if (role === 'admin' || role === 'student') {
      return "FREE";
    }
    
    if (role === 'subscriber') {
      if (service.premium) {
        // Premium services at 50% off for subscribers
        return `$${(service.price / 2).toFixed(2)} (50% OFF)`;
      } else {
        // Standard services are FREE for subscribers
        return "FREE";
      }
    }
    
    // A La Carte pricing (full price)
    if (service.premium) {
      return `$${service.price.toFixed(2)} (Premium)`;
    } else {
      return `$${service.price.toFixed(2)} (Standard)`;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header - Simplified without Features menu item */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI" 
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  // Show text fallback if image fails
                  e.target.parentElement.innerHTML = `
                    <span class="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">DIGITAL</span>
                    <span class="text-2xl font-bold bg-gradient-to-r from-yellow-600 to-yellow-800 bg-clip-text text-transparent italic">Ink</span>
                    <span class="text-2xl font-bold bg-gradient-to-r from-slate-700 to-slate-900 bg-clip-text text-transparent">AI</span>
                  `;
                }}
              />
            </div>
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => navigate('/about')} className="text-slate-600 hover:text-slate-900 font-medium">About</button>
              <button onClick={() => navigate('/prices')} className="text-slate-600 hover:text-slate-900 font-medium">Pricing</button>
              <button onClick={() => navigate('/faq')} className="text-slate-600 hover:text-slate-900 font-medium">FAQ</button>
              <button onClick={() => navigate('/help-center')} className="text-slate-600 hover:text-slate-900 font-medium">Help</button>
              <button onClick={() => navigate('/contact')} className="text-slate-600 hover:text-slate-900 font-medium">Contact</button>
            </nav>
            <Button 
              onClick={() => navigate(user ? '/homepage' : '/login')}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white min-h-[44px]"
            >
              {user ? 'Dashboard' : 'Sign In'}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Digital Ink AI Gold Logo - Page Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <img 
              src="/Digital_Ink_AI_Logo_Gold.png" 
              alt="Digital Ink AI Logo" 
              className="h-24 w-auto"
            />
          </div>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            AI-Powered Publishing Tools
          </h1>
          <p className="text-lg text-slate-600 max-w-4xl mx-auto">
            Digital Ink AI empowers authors and publishers with intelligent workflows that guide your creative journey from initial concept through market success. Our specialized AI tools streamline every phase of publishing, providing strategic insights and positioning advantages while you maintain complete creative control over your unique voice and vision.
          </p>
        </div>

        {/* How To Section */}
        <Card className="mb-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-2xl text-slate-900 flex items-center">
              <span className="mr-3">🚀</span>
              How to Access Digital Ink AI
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {/* Freemium Users (Left) */}
              <div className="bg-white rounded-lg p-6 border-2 border-slate-200">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-slate-400 to-slate-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">
                    🆓
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">Freemium</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>BCTG:</strong> 3 free uses per month</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>ISBN & QR Code:</strong> Unlimited free use</span>
                  </li>
                </ul>
              </div>

              {/* A la Carte (Middle) */}
              <div className="bg-white rounded-lg p-6 border-2 border-slate-200">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">
                    💳
                  </div>
                  <h3 className="font-bold text-lg text-slate-900">A la Carte</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">💰</span>
                    <span><strong>Standard Services:</strong> Pay per use</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-600 mr-2">💰</span>
                    <span><strong>Premium Services:</strong> Full price per use</span>
                  </li>
                </ul>
              </div>

              {/* Subscribers (Right) */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-300 relative">
                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                  ⭐ POPULAR
                </div>
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">
                    ♾️
                  </div>
                  <h3 className="font-bold text-lg text-purple-900">Subscriber</h3>
                </div>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <span className="text-green-600 mr-2">✓</span>
                    <span><strong>Standard Services:</strong> Unlimited access</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-purple-600 mr-2">⭐</span>
                    <span><strong>Premium Services:</strong> 50% off A la Carte price</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Free Tools Banner - Prominent Placement */}
        <Card className="bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 text-slate-900 mb-6 border-2 border-yellow-500 shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl">📊</span>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-bold text-slate-900">ISBN Barcode Generator</h2>
                    <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                      ✨ FREE
                    </span>
                  </div>
                  <p className="text-slate-800 text-base mb-1 font-semibold">
                    Generate professional, print-ready ISBN barcodes instantly
                  </p>
                  <div className="flex gap-3 text-xs text-slate-700 font-medium">
                    <span>✓ EAN-13 Format</span>
                    <span>✓ 300 DPI Quality</span>
                    <span>✓ Unlimited Use</span>
                  </div>
                </div>
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/ibg')}
                className="bg-slate-900 text-white hover:bg-slate-800 font-semibold text-base px-6 py-3 rounded-lg shadow-md whitespace-nowrap"
              >
                🆓 Generate Free Barcode →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Free QR Code Generator Banner */}
        <Card className="bg-gradient-to-r from-blue-400 via-sky-400 to-cyan-400 text-slate-900 mb-6 border-2 border-blue-500 shadow-lg">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="text-4xl">📱</span>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-2xl font-bold text-slate-900">QR Code Generator</h2>
                    <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                      ✨ FREE
                    </span>
                  </div>
                  <p className="text-slate-800 text-base mb-1 font-semibold">
                    Generate professional, high-resolution QR codes instantly
                  </p>
                  <div className="flex gap-3 text-xs text-slate-700 font-medium">
                    <span>✓ High Error Correction</span>
                    <span>✓ 300 DPI Quality</span>
                    <span>✓ Unlimited Use</span>
                  </div>
                </div>
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/qrg')}
                className="bg-slate-900 text-white hover:bg-slate-800 font-semibold text-base px-6 py-3 rounded-lg shadow-md whitespace-nowrap"
              >
                🆓 Generate Free QR Code →
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Service Categories - Color Coded by Workflow */}
        <div className="space-y-12">
          {serviceCategories.map((category) => (
            <div key={category.title}>
              <div className={`flex items-center mb-6 p-4 bg-gradient-to-r ${category.bgColor} rounded-lg border-2 ${category.borderColor}`}>
                <span className="text-4xl mr-4">{category.icon}</span>
                <div>
                  <h2 className={`text-3xl font-bold ${category.textColor}`}>{category.title}</h2>
                  <p className={`text-lg ${category.textColor.replace('900', '700')}`}>{category.description}</p>
                </div>
              </div>
              
              {category.services.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.services.map((service) => (
                    <Card key={service.code} className={`relative ${service.available ? 'border-green-200 bg-green-50/50' : 'border-slate-200'} ${service.premium ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-white' : ''} hover:shadow-lg transition-shadow`}>
                      {service.premium && (
                        <div className="absolute -top-3 -right-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center space-x-1">
                          <span>⭐</span>
                          <span>PREMIUM</span>
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <CardTitle className={`text-lg ${service.premium ? 'text-purple-900' : ''}`}>{service.title}</CardTitle>
                          <Badge variant={service.available ? 'default' : 'secondary'} className={service.available ? 'bg-green-600' : ''}>
                            {service.available ? 'Available' : 'Coming Soon'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 mt-2">{service.description}</p>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {service.available ? (
                            <>
                              {/* A la Carte Pricing */}
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-600">A la Carte:</span>
                                <span className="text-lg font-bold text-slate-900">${service.price.toFixed(2)}</span>
                              </div>
                              
                              {/* Subscriber Pricing for Premium */}
                              {service.premium && (
                                <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                                  <span className="text-sm text-purple-700">Subscriber:</span>
                                  <span className="text-lg font-bold text-purple-700">${(service.price / 2).toFixed(2)} <span className="text-xs">(50% OFF)</span></span>
                                </div>
                              )}
                              
                              {/* Standard Service Note */}
                              {!service.premium && (
                                <div className="text-xs text-green-700 bg-green-50 p-2 rounded">
                                  ✓ Unlimited for Subscribers
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-4">
                              <div className="text-amber-600 font-semibold mb-2">🚧 Coming Soon</div>
                              <p className="text-sm text-slate-600">This service is currently in development</p>
                            </div>
                          )}
                        
                          <Button 
                            variant="outline"
                            size="sm"
                      className="w-full sm:w-auto w-full mt-2"
                            onClick={() => {
                              // Redirect users to sign up or dashboard for service access
                              if (user) {
                                toast.info("Access services from your Dashboard after signing in.");
                                window.location.href = '/homepage';
                              } else {
                                toast.info("Please sign in to access services.");
                                window.location.href = '/login';
                              }
                            }}
                          >
                            {service.available ? 'View Details' : 'Learn More'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="bg-slate-50 border-slate-200">
                  <CardContent className="p-8 text-center">
                    <div className="text-4xl mb-4">🚧</div>
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">Coming Soon</h3>
                    <p className="text-slate-600">Exciting new tools are being developed for this workflow. Stay tuned!</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <Card className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <CardContent className="p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Publishing Journey?</h2>
            <p className="text-xl text-blue-100 mb-6">Join thousands of authors using AI to create, analyze, and market their books more effectively.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                onClick={() => navigate(user ? '/homepage' : '/login')}
                className="bg-white text-blue-600 hover:bg-slate-100 text-lg px-8 py-3 min-h-[44px]"
              >
                {user ? 'Access Dashboard' : 'Sign In to Start'}
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/prices')}
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-3 min-h-[44px]"
              >
                View Pricing
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/faq')}
                className="border-white text-white hover:bg-white/10 text-lg px-8 py-3 min-h-[44px]"
              >
                ❓ FAQ
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <GlobalFooter />
    </div>
  );
};

// Pricing Page Component
const PricingPage = () => {
  const { user } = useAuth();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubscriptionConfirm = async (subscriptionData) => {
    toast.success(subscriptionData.message || `${subscriptionData.subscription.type} subscription activated!`);
    setShowSubscriptionModal(false);
    setSelectedPlan(null);
    
    // Redirect to dashboard after successful subscription with full page reload
    setTimeout(() => {
      window.location.href = '/homepage';
    }, 2000);
  };

  const initiateSubscriptionPayment = async (packageId) => {
    try {
      
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
      
    } catch (error) {
      toast.error(`Subscription setup failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handlePlanAction = (planType) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }
    
    if (planType === 'free') {
      toast.info("You're already on the Free tier! Start creating with BCTG.");
      window.location.href = '/homepage';
    } else if (planType === 'browse') {
      toast.info("Browse our AI tools on the dashboard.");
      window.location.href = '/homepage';
    } else if (planType === 'monthly') {
      setSelectedPlan('Monthly');
      setShowSubscriptionModal(true);
    } else if (planType === 'annual') {
      setSelectedPlan('Annual');
      setShowSubscriptionModal(true);
    } else if (planType === 'student') {
      initiateSubscriptionPayment('subscription_student');
    } else if (planType === 'enterprise') {
      toast.info("Enterprise plans require custom setup. Please contact our sales team.");
      window.location.href = '/contact';
    } else {
      toast.info("Subscription setup coming soon! Check your profile for updates.");
      window.location.href = '/profile';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {user ? <Navigation /> : <LandingNavigation />}
      
      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
            alt="Digital Ink AI" 
            className="h-24 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Choose Your Plan to Unlock AI-Powered Publishing Tools
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            From free trials to full access, get the features you need to bring your book ideas to life.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button 
              onClick={() => window.location.href = '/faq'}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50 px-6 py-2 font-semibold min-h-[44px]"
            >
              ❓ Have Questions? Check our FAQ
            </Button>
          </div>
          {/* Current plan display temporarily removed */}
        </div>

        {/* Pricing Cards - Main 4 Plans Centered */}
        <div className="flex justify-center mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl">
            {/* Free Plan */}
          <Card className="relative bg-white border-2 border-slate-200 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <CardHeader className="text-center">
              <Badge variant="outline" className="w-fit mx-auto mb-2">Start Today</Badge>
              <CardTitle className="text-2xl font-bold text-slate-900">Free Tier</CardTitle>
              <div className="text-4xl font-bold text-blue-600 mt-2">$0</div>
              <p className="text-slate-600">per month</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Book Concept & Title Generator (3 uses/month)</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">ISBN Barcode Generator (Unlimited)</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">QR Code Generator (Unlimited)</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Basic dashboard access</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-slate-600 italic mb-4">
                  "Perfect for testing the waters and brainstorming initial ideas with unlimited ISBN and QR Code generation!"
                </p>
                <Button 
                  onClick={() => handlePlanAction('free')}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Get Started Free
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Pay-Per-Use */}
          <Card className="relative bg-white border-2 border-slate-200 hover:border-green-300 transition-all duration-300 hover:shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-slate-900">À La Carte</CardTitle>
              <div className="text-4xl font-bold text-green-600 mt-2">$3 - $25</div>
              <p className="text-slate-600">per Standard or Premium service</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Unlock individual tools as needed</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">No monthly commitment</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Billed per use via credit card</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Clear pricing shown when selecting functions</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-slate-600 italic mb-4">
                  "Ideal for occasional users who want specific AI assistance without full access."
                </p>
                <Button 
                  onClick={() => handlePlanAction('browse')}
                  variant="outline" 
                  className="w-full border-green-300 text-green-700 hover:bg-green-50"
                >
                  Browse Functions
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Subscription */}
          <Card className={`relative bg-gradient-to-b from-blue-50 to-white border-2 transition-all duration-300 ${
            user?.subscription_status === 'Monthly' 
              ? 'border-green-400 opacity-90' 
              : user?.subscription_status === 'Annual'
              ? 'border-slate-300 opacity-75'
              : 'border-blue-300 hover:border-blue-400 hover:shadow-xl'
          }`}>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              {user?.subscription_status === 'Monthly' ? (
                <Badge className="bg-green-600 text-white">Current Plan</Badge>
              ) : user?.subscription_status === 'Annual' ? (
                <Badge className="bg-slate-500 text-white">Not Available</Badge>
              ) : (
                <Badge className="bg-blue-600 text-white">Recommended</Badge>
              )}
            </div>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-slate-900">Monthly Subscription</CardTitle>
              <div className="text-4xl font-bold text-blue-600 mt-2">$29</div>
              <p className="text-slate-600">per month</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Unlimited access to BCTG</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Unlimited access to all Primary services</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm font-semibold text-orange-600">Premium services at 50% OFF</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">14-day refund window</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Priority support & early access</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-slate-600 italic mb-4">
                  "Best for active authors refining multiple projects."
                </p>
                {user?.subscription_status === 'Monthly' ? (
                  <Button 
                    disabled
                    className="w-full bg-slate-400 cursor-not-allowed"
                  >
                    Current Plan
                  </Button>
                ) : user?.subscription_status === 'Annual' ? (
                  <>
                    <Button 
                      disabled
                      className="w-full bg-slate-400 cursor-not-allowed mb-2"
                    >
                      Downgrade Not Available
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Cancel your Annual plan first to switch
                    </p>
                  </>
                ) : (
                  <Button 
                    onClick={() => handlePlanAction('monthly')}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Subscribe Now
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Annual Subscription */}
          <Card className={`relative bg-gradient-to-b from-purple-50 to-white border-2 transition-all duration-300 ${
            user?.subscription_status === 'Annual' 
              ? 'border-green-400 opacity-90' 
              : user?.subscription_status === 'Monthly'
              ? 'border-slate-300 opacity-75'
              : 'border-purple-300 hover:border-purple-400 hover:shadow-xl'
          }`}>
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              {user?.subscription_status === 'Annual' ? (
                <Badge className="bg-green-600 text-white">Current Plan</Badge>
              ) : user?.subscription_status === 'Monthly' ? (
                <Badge className="bg-slate-500 text-white">Not Available</Badge>
              ) : (
                <Badge className="bg-purple-600 text-white">Best Value</Badge>
              )}
            </div>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-slate-900">Annual Subscription</CardTitle>
              <div className="text-4xl font-bold text-purple-600 mt-2">$288</div>
              <p className="text-slate-600">per year</p>
              <p className="text-sm text-green-600 font-semibold">Save $60! (~$24/month)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Unlimited access to all Primary services</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm font-semibold text-orange-600">Premium services at 50% OFF</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">14-day refund window</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">All monthly benefits included</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  <span className="text-sm">Exclusive premium templates</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <p className="text-xs text-slate-600 italic mb-4">
                  "Great value for committed publishers building long-term."
                </p>
                {user?.subscription_status === 'Annual' ? (
                  <Button 
                    disabled
                    className="w-full bg-slate-400 cursor-not-allowed"
                  >
                    Current Plan
                  </Button>
                ) : user?.subscription_status === 'Monthly' ? (
                  <>
                    <Button 
                      onClick={() => handlePlanAction('annual')}
                      className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 mb-2"
                    >
                      ⬆️ Upgrade to Annual
                    </Button>
                    <p className="text-xs text-green-600 text-center font-semibold">
                      Save $40/year! Upgrade anytime.
                    </p>
                  </>
                ) : (
                  <Button 
                    onClick={() => handlePlanAction('annual')}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    Subscribe Now
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
          </div>
        </div>

        {/* Student Pricing Section - Separate and Centered */}
        <div className="mt-12">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">🎓 Student Pricing</h3>
            <p className="text-slate-600">Special rates for students 18+ with valid ID verification</p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            {/* Single Student Card with Semester Selection */}
            <Card className="bg-gradient-to-b from-blue-50 to-white border-2 border-blue-300 hover:border-blue-400 transition-all duration-300 hover:shadow-xl">
                <CardHeader className="text-center">
                  <CardTitle className="text-xl font-bold text-slate-900">Student Subscription</CardTitle>
                  <div className="text-3xl font-bold text-blue-600 mt-2">$29 / $19</div>
                  <p className="text-slate-600">Fall/Spring Semester | Summer Session</p>
                  <p className="text-xs text-slate-500 mt-1">(5 months | 3 months)</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Semester Selection */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Choose Semester:</Label>
                    <select 
                      className="w-full p-2 border rounded"
                      defaultValue=""
                    >
                      <option value="">Select a semester</option>
                      <option value="current">Fall 2025 (Current - Aug 1 - Dec 31, 2025)</option>
                      <option value="next">Spring 2026 (Upcoming - Jan 1 - Jun 30, 2026)</option>
                      <option value="summer">Summer 2026 (Jun 1 - Aug 31, 2026)</option>
                    </select>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm">All Monthly plan features</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm">6-month semester access</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm">Academic project support</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-blue-500 mr-2">⚠</span>
                      <span className="text-sm">Student ID verification required</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-xs text-slate-600 italic mb-4">
                      "Perfect for academic writing projects and thesis development."
                    </p>
                    <Button 
                      onClick={() => handlePlanAction('student')}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      Apply for Student Subscription
                    </Button>
                  </div>
                </CardContent>
              </Card>

            {/* Student Verification Requirements */}
            <Card className="mt-6 bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <h4 className="font-semibold text-blue-900 mb-3">Student Verification Requirements:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
                  <div>
                    <div className="flex items-center mb-2">
                      <span className="text-blue-600 mr-2">📄</span>
                      <span>Valid Student ID with expiration date</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-blue-600 mr-2">🆔</span>
                      <span>Government-issued Photo ID</span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-2">
                      <span className="text-blue-600 mr-2">🎂</span>
                      <span>Must be 18 years or older</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-blue-600 mr-2">✅</span>
                      <span>Verification by Admin or AI analysis</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Platform Comparison vs Competitors - MOVED TO TOP */}
        <Card className="mb-12 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-300">
          <CardHeader>
            <CardTitle className="text-center text-4xl font-bold text-slate-900 mb-4">Why Choose Digital Ink AI?</CardTitle>
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xl text-slate-800 leading-relaxed mb-4">
                <strong>Unlock the power to craft books positioned for market success!</strong> Digital Ink AI empowers you to weave marketing intelligence into every stage of your creation, preserving your authentic voice while boosting discoverability.
              </p>
              <p className="text-lg text-slate-700 leading-relaxed">
                Unlike generic AI writers, we enhance your <span className="font-semibold text-blue-700">agency</span>—turning ideas into <span className="font-semibold text-green-700">market-ready works</span> that resonate with readers and markets alike. Experience platform development that grows with you, supporting <span className="font-semibold text-purple-700">smarter positioning</span> through marketing-infused publishing.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-2 border-slate-200 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <th className="text-left p-4 font-bold">Benefit</th>
                    <th className="text-center p-4 font-bold">Digital Ink AI</th>
                    <th className="text-center p-4 font-semibold">Sudowrite</th>
                    <th className="text-center p-4 font-semibold">NovelAI</th>
                    <th className="text-center p-4 font-semibold">Jasper</th>
                    <th className="text-center p-4 font-semibold">Reedsy</th>
                    <th className="text-center p-4 font-semibold">Squibler</th>
                    <th className="text-center p-4 font-semibold">ProWritingAid</th>
                  </tr>
                </thead>
                <tbody className="text-slate-800">
                  <tr className="border-b border-slate-200 hover:bg-blue-50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">Enhanced Marketing Integration</td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-blue-50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">Maintains Authenticity & Agency</td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-blue-50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">Market Positioning Focus</td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-blue-50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">Platform Development Scalability</td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                  </tr>
                  <tr className="hover:bg-blue-50 transition-colors">
                    <td className="p-4 font-semibold text-slate-900">Ethical AI Collaboration</td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                    <td className="text-center p-4 bg-red-50">
                      <span className="text-3xl text-red-500">✗</span>
                    </td>
                    <td className="text-center p-4 bg-green-50">
                      <span className="text-3xl text-green-600">✓</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Key Differentiators */}
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              <div className="p-6 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl border-2 border-blue-300 text-center">
                <div className="text-4xl mb-3">🎯</div>
                <h4 className="font-bold text-blue-900 mb-2">Marketing Intelligence</h4>
                <p className="text-sm text-blue-800">Weave market data into every creative decision</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-green-100 to-green-50 rounded-xl border-2 border-green-300 text-center">
                <div className="text-4xl mb-3">💰</div>
                <h4 className="font-bold text-green-900 mb-2">Market Positioning</h4>
                <p className="text-sm text-green-800">Position creative works for market success</p>
              </div>
              <div className="p-6 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl border-2 border-purple-300 text-center">
                <div className="text-4xl mb-3">✨</div>
                <h4 className="font-bold text-purple-900 mb-2">Your Voice, Amplified</h4>
                <p className="text-sm text-purple-800">Preserve authenticity while maximizing reach</p>
              </div>
            </div>

            <div className="mt-6 p-6 bg-gradient-to-r from-yellow-100 via-orange-100 to-pink-100 rounded-xl border-2 border-orange-300">
              <h3 className="font-bold text-orange-900 text-xl mb-3 text-center">🏆 The Digital Ink AI Difference</h3>
              <p className="text-orange-900 text-center text-lg leading-relaxed">
                We don't replace authors—we <strong>empower</strong> them. Unlike tools that generate generic content, Digital Ink AI enhances your <strong>agency</strong> and <strong>authenticity</strong> while embedding commercial intelligence that supports <strong>better market positioning</strong>.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Why Choose section moved above - this duplicate removed */}
        
        {/* Refer A Friend Card */}
        {user && (
          <Card className="mt-12 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 hover:shadow-xl transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <div className="text-6xl mb-4">🎁</div>
                <h2 className="text-3xl font-bold text-slate-900 mb-3">
                  Love Our Platform? Share the Love!
                </h2>
                <p className="text-lg text-slate-700 mb-6 max-w-2xl mx-auto">
                  Refer a friend to Digital Ink AI and earn <strong className="text-green-600">200 bonus points</strong> 
                  when they become paid subscribers. Help fellow authors discover these powerful AI tools!
                </p>
                <Button 
                  onClick={() => setShowReferralModal(true)}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-3 text-lg font-semibold min-h-[44px]"
                >
                  🎁 Refer A Friend & Earn Points
                </Button>
                <p className="text-sm text-slate-500 mt-3">
                  It's a win-win! Your friend gets amazing tools, you get rewarded
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* About Us Card - Only show for registered users */}
        {user && (
          <Card className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 hover:shadow-xl transition-shadow">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <div className="text-6xl mb-4">💡</div>
                <h2 className="text-3xl font-bold text-slate-900 mb-3">
                  Learn More About Digital Ink AI
                </h2>
                <p className="text-lg text-slate-700 mb-6 max-w-2xl mx-auto">
                  Discover our mission, values, and the story behind Digital Ink AI. 
                  Learn how we're empowering authors with AI-powered publishing tools.
                </p>
                <Button 
                  onClick={() => window.location.href = '/about'}
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-semibold min-h-[44px]"
                >
                  📖 About Us
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      
      <GlobalFooter />
      
      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleSubscriptionConfirm}
        user={user}
        preselectedPlan={selectedPlan}
        onPaymentSetupRequired={() => {
          // Close modal and redirect to profile page
          setShowSubscriptionModal(false);
          setSelectedPlan(null);
          toast.info('Please add a payment method to subscribe');
          setTimeout(() => {
            window.location.href = '/profile?tab=payment';
          }, 1500);
        }}
      />
      
      {/* Referral Modal */}
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />
    </div>
  );
};

// Global Footer Component
const GlobalFooter = () => {
  return (
    <footer className="bg-black border-t border-slate-800 text-white py-12 mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Primary Navigation - Full Width Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 lg:gap-12 mb-8">
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Platform</h3>
            <ul className="space-y-2">
              <li><a href="/" className="text-slate-400 hover:text-white transition-colors text-sm">Home</a></li>
              <li><a href='/user_dashboard' className="text-slate-400 hover:text-white transition-colors text-sm">Dashboard</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Company</h3>
            <ul className="space-y-2">
              <li><a href="/about" className="text-slate-400 hover:text-white transition-colors text-sm">About Us</a></li>
              <li><a href="/prices" className="text-slate-400 hover:text-white transition-colors text-sm">Pricing</a></li>
              <li><a href="/faq" className="text-slate-400 hover:text-white transition-colors text-sm">FAQ</a></li>
              <li><a href="/contact" className="text-slate-400 hover:text-white transition-colors text-sm">Contact Us</a></li>
              <li><a href="https://www.uwygpublishing.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors text-sm">UWYG Publishing</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Account</h3>
            <ul className="space-y-2">
              <li><a href="/login" className="text-slate-400 hover:text-white transition-colors text-sm">Sign In</a></li>
              <li><a href="/profile" className="text-slate-400 hover:text-white transition-colors text-sm">Profile</a></li>
              <li><a href="/prices" className="text-slate-400 hover:text-white transition-colors text-sm">Upgrade Plan</a></li>
            </ul>
          </div>
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wide">Connect</h3>
            <ul className="space-y-2">
              <li><a href="/contact" className="text-slate-400 hover:text-white transition-colors text-sm">Contact Support</a></li>
              <li><a href="mailto:Info@DigitalInkAI.com" className="text-slate-400 hover:text-white transition-colors text-sm">Info@DigitalInkAI.com</a></li>
              <li><a href="tel:(719) 205-6381" className="text-slate-400 hover:text-white transition-colors text-sm">(719) 205-6381</a></li>
              <li><a href="https://www.rdbrunet.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors text-sm">R. Dale Brunet</a></li>
            </ul>
          </div>
        </div>

        {/* Copyright and Legal */}
        <div className="border-t border-slate-800 pt-8 text-center">
          <p className="text-slate-400 mb-4 text-sm">
            Copyright © 2025 Use What You Got Publishing, LLC. All rights reserved.
          </p>
          
          {/* Release Information */}
          <ReleaseFooter />
          
          <div className="flex justify-center space-x-6 mb-8">
            <a href="/privacy" className="text-slate-400 hover:text-yellow-400 transition-colors text-sm">
              Privacy Policy
            </a>
            <a href="/terms" className="text-slate-400 hover:text-yellow-400 transition-colors text-sm">
              Terms of Service
            </a>
          </div>
          
          {/* Emergent.sh Promotional Link */}
          <div className="mt-8 pt-6 border-t border-slate-700">
            <a 
              href="https://app.emergent.sh/register?ref=rdbr654428" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex flex-col items-center gap-2 text-slate-400 hover:text-cyan-400 transition-all duration-300 group"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_author-toolkit-2/artifacts/ihhfpfkm_Emergent_SH_Logo.png" 
                alt="Emergent.sh Logo" 
                className="h-6 w-auto opacity-70 group-hover:opacity-100 transition-opacity"
              />
              <span className="text-xs font-medium">This site powered by Emergent.sh</span>
              <span className="text-xs font-semibold text-cyan-500 group-hover:text-cyan-400">Build Your Tomorrow - TODAY!</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// FAQ Page Component
const FAQPage = () => {
  const { user } = useAuth();
  const [openCategory, setOpenCategory] = useState('general');
  const [openQuestion, setOpenQuestion] = useState(null);

  const toggleCategory = (category) => {
    setOpenCategory(openCategory === category ? null : category);
  };

  const toggleQuestion = (questionId) => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  const faqData = {
    general: {
      title: "General Questions About Digital Ink AI",
      icon: "🏢",
      questions: [
        {
          id: "what-is",
          q: "What is Digital Ink AI?",
          a: "Digital Ink AI is an innovative platform that empowers authors, publishers, and creators with AI-driven tools to enhance the publishing process, embedding marketing strategies into every stage while preserving your authentic voice for greater commercial success."
        },
        {
          id: "differ",
          q: "How does Digital Ink AI differ from other AI publishing tools?",
          a: "Unlike platforms that focus on automated content generation, Digital Ink AI acts as an ethical collaborator, integrating marketing intelligence into your creative workflow to boost discoverability and market positioning without compromising your agency or originality."
        },
        {
          id: "who-can-use",
          q: "Who can benefit from Digital Ink AI?",
          a: "Authors, publishers, students, editors, and content creators at any experience level can use Digital Ink AI to streamline ideation, analysis, and marketing, making it ideal for those seeking AI book publishing solutions that prioritize user control and ethical AI use."
        },
        {
          id: "beginners",
          q: "Is Digital Ink AI suitable for beginners in publishing?",
          a: "Yes, Digital Ink AI is beginner-friendly, offering intuitive interfaces and guided processes that help newcomers integrate marketing into their writing from the start, turning ideas into market-ready projects with minimal learning curve."
        },
        {
          id: "services-offered",
          q: "What services does Digital Ink AI offer?",
          a: "Digital Ink AI offers 15+ AI-powered services organized into four categories: (1) Writing & Development Tools including Book Concept Generator, Character Generator, and Professional Developmental Editor; (2) Author & Content Tools including Author Biography Generator and Book Viability Analysis; (3) Publishing Tools including EPUB Transformer and Amazon KDP Wizard; (4) Marketing Tools including Social Media Marketing Kit, Competitive Analysis, and Review Creator. Services range from $3-$25 per use."
        },
        {
          id: "rewards-points",
          q: "What is the Rewards Points system?",
          a: "Digital Ink AI features a rewards points system where users earn points through platform activities like daily logins, service usage, and referrals. Points can be redeemed for discounts on services and subscriptions. Check your dashboard to see your current points balance."
        }
      ]
    },
    account: {
      title: "Account and Subscription Questions",
      icon: "💳",
      questions: [
        {
          id: "create-account",
          q: "How do I create an account on Digital Ink AI?",
          a: "Sign up easily with your email or Google account. After registration, complete our 8-question onboarding survey to personalize your experience. We'll tailor your dashboard and recommendations based on your role (Author/Writer, Publisher/Editor, Student/Learner, or Content Creator), experience level, project stage, and goals."
        },
        {
          id: "user-roles",
          q: "What are the different user roles and access levels?",
          a: "Digital Ink AI has four main user roles: (1) Free User - limited to 3 Book Concept Generator uses per month; (2) À La Carte - pay per service as needed with no monthly commitment; (3) Subscriber (Monthly or Annual) - unlimited access to standard tools and 50% off premium services; (4) Admin/Beta Tester - full platform access. Student plans are also available with verification."
        },
        {
          id: "free-tier",
          q: "What is included in the Free tier?",
          a: "The Free tier includes: (1) Book Concept & Title Generator (BCTG) with 3 free uses per month - perfect for brainstorming book ideas and marketable titles; (2) ISBN Barcode Generator with unlimited free use - generate professional ISBN barcodes for your books; (3) QR Code Generator with unlimited free use - create custom QR codes for book marketing and reader engagement. ISBN and QR Code generators are always free with no monthly limits! After your 3 BCTG uses, you can still use ISBN and QR generators freely or upgrade to À La Carte or Subscription for full platform access."
        },
        {
          id: "alacarte-pricing",
          q: "How does À La Carte pricing work?",
          a: "À La Carte users pay per service without a monthly subscription. Prices range from $3-$25 depending on the service complexity. Standard services (like Character Generator at $7) are less expensive, while Premium services (like Professional Developmental Editor at $20) cost more. You'll see the exact price before confirming each service use."
        },
        {
          id: "subscription-details",
          q: "What do Monthly and Annual subscriptions include?",
          a: "Monthly Subscription ($29/month): Unlimited access to all standard tools (BCTG, Character Generator, Author Biography, etc.) + 50% discount on premium services + 14-day refund window + priority support. Annual Subscription ($288/year - save $60!): Same benefits as Monthly but billed annually at ~$24/month. Both plans require a credit card on file and charge for premium service usage at discounted rates."
        },
        {
          id: "premium-vs-standard",
          q: "What's the difference between Standard and Premium services?",
          a: "Standard services (FREE with subscription, $3-$10 à la carte) include: Book Concept Generator, Character Generator, Author Biography, Competitive Analysis, Review Creator, Amazon KDP Wizard, and others. Premium services (50% OFF with subscription, $15-$25 à la carte) include: Professional Developmental Editor ($20), Book Outline Planner ($15), Social Media Marketing Kit ($25), and EPUB Transformer ($20). Subscribers get unlimited standard services and pay half-price for premium."
        },
        {
          id: "cancel",
          q: "Can I cancel my subscription at any time?",
          a: "Yes, you can cancel anytime through your Profile > Subscription Management. You'll retain access until the end of your current billing period. We offer a 14-day refund window from your subscription start date. After cancellation, your account reverts to À La Carte, where you can still use services by paying per use."
        },
        {
          id: "payment-methods",
          q: "What payment methods are accepted?",
          a: "We accept all major credit and debit cards (Visa, Mastercard, American Express, Discover) through our secure Stripe payment processor. All transactions are encrypted end-to-end. You'll need to add a payment method before using paid services or subscribing. No cryptocurrency or PayPal currently accepted."
        },
        {
          id: "trial-period",
          q: "Is there a free trial for subscriptions?",
          a: "Yes! New subscribers get a 14-day trial period. During this time, you have full access to all standard services and discounted premium services. If you cancel within 14 days, you'll receive a full refund. After 14 days, your subscription continues and you'll be charged monthly or annually based on your plan."
        },
        {
          id: "student-plans",
          q: "Are there special rates for students?",
          a: "Yes, we offer student subscriptions with verification for students 18 years or older. Student plans provide the same benefits as regular subscriptions at a discounted rate. Contact support or visit the Pricing page to apply for student access. You'll need to provide valid student identification for verification. All users must be 18+ to use Digital Ink AI services."
        }
      ]
    },
    platform: {
      title: "Using the Platform Questions",
      icon: "⚡",
      questions: [
        {
          id: "get-started",
          q: "How do I get started with Digital Ink AI?",
          a: "After signing up and completing the 8-question onboarding survey, you'll land on your personalized dashboard. Free users can immediately access Book Concept Generator (3 uses/month). To use other services, add a payment method in your Profile, then browse our 15+ services organized by category: Writing & Development, Author & Content, Publishing, and Marketing tools."
        },
        {
          id: "dashboard-navigation",
          q: "How do I navigate the dashboard?",
          a: "Your homepage displays personalized welcome messages based on your onboarding data, service cards showing your usage metrics (Total Reports, Total Spent, Paid Uses), and your Rewards Points balance in a gold card. Click any service card to access that tool. Use the top navigation to access your Profile, Activity Dashboard, Transaction History, and Rewards."
        },
        {
          id: "service-workflow",
          q: "How do services work?",
          a: "Each service has a dedicated page with a form to input your requirements. For example: Book Concept Generator needs genre and themes; Professional Developmental Editor requires manuscript upload; Amazon KDP Wizard needs book metadata. Fill the form, review pricing (if applicable), confirm, and receive your AI-generated results instantly or within minutes for complex services."
        },
        {
          id: "mobile",
          q: "Can I use Digital Ink AI on mobile devices?",
          a: "Yes! Digital Ink AI is fully responsive and optimized for smartphones and tablets. Access all services, view your reports, manage your subscription, and track rewards on any device with a web browser. Your data syncs across all devices automatically."
        },
        {
          id: "file-upload",
          q: "What file formats can I upload?",
          a: "Most services accept PDF and DOCX files up to 50MB. The Professional Developmental Editor and EPUB Transformer specifically support manuscripts in these formats. Files are processed securely and deleted after analysis—we never store your content permanently. For Book Outline Planner and other text-based services, you'll enter content directly in the form."
        },
        {
          id: "report-management",
          q: "How do I access and manage my generated reports?",
          a: "All generated reports are saved to your Activity Dashboard and the specific service page. Click 'View Report' to see the full analysis, 'Export PDF' to download, or 'Print' for physical copies. Delete reports you no longer need with the trash icon. Reports persist indefinitely unless you delete them—no storage limits!"
        },
        {
          id: "early-stage",
          q: "What if my project is in early stages?",
          a: "Perfect! Start with Book Concept & Title Generator to brainstorm ideas (free for your first 3 uses). Then use Character & Cast Generator to develop your characters. As you progress, leverage Book Outline Planner for structure, and finally Professional Developmental Editor for manuscript feedback. Our tools support you at every stage from ideation to publication."
        },
        {
          id: "multiple-projects",
          q: "Can I work on multiple book projects at once?",
          a: "Absolutely! There are no limits on how many projects you can manage. Each service generates a separate report that's tagged with your project details (title, genre, etc.). Your Activity Dashboard shows all reports across all services, sortable by date, service type, or project. Subscribers can create unlimited reports for all their projects."
        },
        {
          id: "transaction-tracking",
          q: "How do I track my spending and service usage?",
          a: "Your homepage service cards display three key metrics: Total Reports (count), Total Spent (cumulative cost), and Paid Uses (paid transactions). For detailed history, visit Activity Dashboard or Transaction History in your Profile. You'll see itemized charges, dates, service names, and can even email receipts to yourself for accounting."
        }
      ]
    },
    services: {
      title: "Services and Features Questions",
      icon: "🎯",
      questions: [
        {
          id: "all-services",
          q: "What are all the available services and their prices?",
          a: "Writing & Development: Book Concept Generator ($5, FREE 3/month for all users), Character Generator ($7), Book Outline Planner ($15 premium), Professional Developmental Editor ($20 premium). Author & Content: Author Biography Generator ($5), Book Viability Analysis ($12 premium), Competitive Analysis ($7), Review Creator ($7). Publishing: EPUB Transformer ($20 premium), PDF Builder ($3), Amazon KDP Wizard ($5). Marketing: Metadata Generator ($10), Keynote Speech Generator ($15 premium), Media Kit Generator ($20 premium), Social Media Marketing Kit ($25 premium). Subscribers get standard services free and premium at 50% off."
        },
        {
          id: "bctg-details",
          q: "How does the Book Concept & Title Generator work?",
          a: "BCTG is our flagship free service (3 uses/month for all users). Enter your genre, themes, target audience, and story elements. Our AI generates 5 unique book concepts with marketable titles, compelling descriptions, and audience appeal notes. It's perfect for brainstorming, overcoming writer's block, or finding your next project. After 3 monthly uses, upgrade to À La Carte ($5/use) or Subscription (unlimited) to continue. Don't forget: ISBN Barcode Generator and QR Code Generator are always free with unlimited use!"
        },
        {
          id: "pde-details",
          q: "What does Professional Developmental Editor provide?",
          a: "PDE is our premium manuscript analysis service ($20 à la carte, $10 for subscribers). Upload your manuscript (PDF/DOCX, up to 50MB) with book details. You'll receive a comprehensive 9-section analysis covering: Technical Overview, Initial Analysis, Content Structure, Character Development, Narrative Coherence, Technical Aspects, Actionable Recommendations, Enhanced Content Sample, and Final Summary. Processing takes 3-8 minutes using Claude AI. Results include word count, page count, and detailed editorial feedback."
        },
        {
          id: "kdp-wizard",
          q: "How does the Amazon KDP Submission Wizard work?",
          a: "The KDP Wizard ($5 standard) guides you through Amazon publishing. Enter your manuscript details, target categories, and keywords. Our AI generates optimized KDP metadata including title variations, subtitle options, 7-keyword sets, category recommendations (up to 2 primary categories), book description variants, author bio, and BISAC codes. Plus, you get strategic submission guidance for maximum visibility on Amazon."
        },
        {
          id: "marketing-kit",
          q: "What's included in the Social Media Marketing Kit?",
          a: "SMMK ($25 premium, $12.50 for subscribers) creates a complete 30-day social media campaign for your book. You'll get: Platform-specific strategies for Facebook, Instagram, Twitter/X, LinkedIn, TikTok, and YouTube; 30-day content calendar with daily post suggestions; Engagement tactics and community building tips; Hashtag research and trend integration; Visual content recommendations; Analytics tracking guidance. It's your all-in-one book marketing roadmap!"
        },
        {
          id: "epub-transformer",
          q: "Can Digital Ink AI convert my manuscript to EPUB?",
          a: "Yes! The EPUB-Ready Manuscript Transformer ($20 premium) converts your PDF or DOCX manuscript into professional EPUB format with industry-standard validation. The service includes: Table of contents generation, Chapter breaks and formatting, Metadata embedding, Cover integration support, and Validation against EPUB3 standards. Perfect for publishing to Apple Books, Kobo, Google Play Books, and other EPUB retailers."
        }
      ]
    },
    privacy: {
      title: "Privacy and Security Questions",
      icon: "🔒",
      questions: [
        {
          id: "data-protection",
          q: "How does Digital Ink AI protect my data?",
          a: "We use encryption and ephemeral processing for uploads, never storing or selling your content, ensuring secure AI publishing with marketing integration."
        },
        {
          id: "share-info",
          q: "Does Digital Ink AI share my information?",
          a: "No, we do not sell or share personally identifiable information; anonymized usage data supports internal improvements for better AI book publishing experiences."
        },
        {
          id: "uploaded-content",
          q: "What happens to my uploaded content?",
          a: "Uploaded manuscripts are processed temporarily and deleted immediately after, aligning with our ethical AI use in publishing and marketing."
        },
        {
          id: "privacy-settings",
          q: "How do I manage my privacy settings?",
          a: "Access your profile to control data sharing and opt-ins, ensuring personalized AI features with marketing integration respect your preferences."
        },
        {
          id: "compliance",
          q: "Is Digital Ink AI compliant with data laws?",
          a: "Yes, we adhere to GDPR, CCPA, and similar regulations, providing secure, ethical AI tools for publishing and marketing."
        },
        {
          id: "delete-data",
          q: "Can I delete my data from Digital Ink AI?",
          a: "Yes, request data deletion via your account or support, and we'll remove it promptly, including any anonymized usage for AI publishing improvements."
        }
      ]
    },
    support: {
      title: "Support and Troubleshooting Questions",
      icon: "🤝",
      questions: [
        {
          id: "contact-support",
          q: "How do I contact support for Digital Ink AI?",
          a: "Email us at info@UWYGPublishing.com for assistance with account issues, billing questions, technical problems, or feature requests. We typically respond within 24-48 hours on business days. Subscribers receive priority support with faster response times. You can also visit our Contact page to send a message directly through the platform."
        },
        {
          id: "refund-policy",
          q: "What is your refund policy?",
          a: "Subscriptions: 14-day full refund window from subscription start date. After 14 days, no refunds but you can cancel anytime. À La Carte Services: Service fees are non-refundable once processing begins, as our AI analysis costs are incurred immediately. If a service fails due to technical error on our end, contact support for a refund or credit. We stand behind our service quality and will work to resolve any issues."
        },
        {
          id: "tool-issues",
          q: "What if a service doesn't work as expected?",
          a: "If a service fails to generate results, times out, or produces errors, contact support immediately at info@UWYGPublishing.com with: (1) Service name, (2) Time/date of attempt, (3) Screenshot of error, (4) Description of what happened. We'll troubleshoot and either fix the issue, regenerate your report, or issue a refund/credit. Most service issues are resolved within 24 hours."
        },
        {
          id: "feedback",
          q: "How do I provide feedback or request features?",
          a: "We love hearing from users! Submit feedback via: (1) Contact page form, (2) Email to info@UWYGPublishing.com with subject 'Feature Request' or 'Feedback', (3) Direct message from your dashboard Profile section. Tell us what services you'd like to see, what features would improve your experience, or how we can better serve your publishing needs. Active community feedback shapes our roadmap!"
        },
        {
          id: "password-reset",
          q: "What if I forget my password?",
          a: "Click 'Forgot Password?' on the login page. Enter your registered email address. You'll receive a password reset link via email within minutes. Click the link and create a new password. The reset link expires after 1 hour for security. If you don't receive the email, check your spam folder or contact support."
        },
        {
          id: "update-account",
          q: "How do I update my account information?",
          a: "Log in and go to Profile in the top navigation. There you can update: Name and email, Phone and address, Timezone preferences, Payment method (credit card), Subscription settings (upgrade/cancel), Email preferences. Changes save automatically. Your timezone setting ensures timestamps display correctly for your location."
        },
        {
          id: "technical-requirements",
          q: "What are the technical requirements to use Digital Ink AI?",
          a: "Browser: Modern web browser (Chrome, Firefox, Safari, Edge) updated within the last year. Internet: Stable internet connection (broadband recommended for file uploads). Files: PDF or DOCX support, files up to 50MB. No special software needed—Digital Ink AI is entirely web-based. Works on Windows, Mac, Linux, iOS, and Android devices."
        },
        {
          id: "processing-time",
          q: "How long do services take to process?",
          a: "Simple services (Book Concept Generator, Character Generator, Author Biography): Instant to 30 seconds. Medium services (Competitive Analysis, Review Creator, Metadata Generator): 1-3 minutes. Complex services (Professional Developmental Editor, Marketing Kits, EPUB Transformer): 3-10 minutes. You'll see a processing indicator. Don't close the page—results display automatically when complete. If processing exceeds expected time, refresh the page or check your reports list."
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {user ? <Navigation /> : <LandingNavigation />}
      
      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
            alt="Digital Ink AI" 
            className="h-24 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Find answers to common questions about Digital Ink AI's features, pricing, and platform.
          </p>
        </div>

        {/* Quick Navigation */}
        <div className="mb-8">
          <Card className="bg-white">
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4 text-center">Jump to Category:</h3>
              <div className="flex flex-wrap justify-center gap-3">
                {Object.entries(faqData).map(([key, category]) => (
                  <Button
                    key={key}
                    onClick={() => {
                      setOpenCategory(key);
                      window.scrollTo({ top: 300, behavior: 'smooth' });
                    }}
                    variant={openCategory === key ? "default" : "outline"}
                    className={`min-h-[44px] ${openCategory === key ? 'bg-blue-600 hover:bg-blue-700' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                  >
                    {category.icon} {category.title}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-6">
          {Object.entries(faqData).map(([categoryKey, category]) => (
            <Card key={categoryKey} className="bg-white border-2 border-slate-200 hover:border-blue-300 transition-all">
              <CardHeader 
                className="cursor-pointer bg-gradient-to-r from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 transition-all"
                onClick={() => toggleCategory(categoryKey)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-7xl">{category.icon}</span>
                    <CardTitle className="text-2xl font-bold text-slate-900">
                      {category.title}
                    </CardTitle>
                    <Badge variant="outline" className="ml-2">
                      {category.questions.length} questions
                    </Badge>
                  </div>
                  <svg 
                    className={`w-6 h-6 text-slate-600 transition-transform ${openCategory === categoryKey ? 'rotate-180' : ''}`}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </CardHeader>
              
              {openCategory === categoryKey && (
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {category.questions.map((item) => (
                      <div key={item.id} className="border-b border-slate-200 last:border-0 pb-4 last:pb-0">
                        <button
                          onClick={() => toggleQuestion(item.id)}
                          className="w-full text-left flex items-start justify-between py-2 group min-h-[44px]"
                        >
                          <h4 className="text-lg font-semibold text-slate-800 group-hover:text-blue-600 transition-colors pr-4">
                            {item.q}
                          </h4>
                          <svg 
                            className={`w-5 h-5 text-slate-500 flex-shrink-0 mt-1 transition-transform ${openQuestion === item.id ? 'rotate-180' : ''}`}
                            fill="none" 
                            viewBox="0 0 24 24" 
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {openQuestion === item.id && (
                          <div className="mt-2 pl-4 pr-8">
                            <p className="text-slate-700 leading-relaxed bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                              {item.a}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {/* Contact CTA */}
        <section className="mt-12">
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Still Have Questions?
                </h2>
                <p className="text-lg text-slate-700 mb-8 max-w-3xl mx-auto">
                  Our support team is here to help. Reach out and we'll get back to you promptly.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button 
                    onClick={() => window.location.href = '/contact'}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-semibold min-h-[44px]"
                  >
                    📧 Contact Support
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/prices'}
                    variant="outline"
                    className="border-blue-300 text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-semibold min-h-[44px]"
                  >
                    💰 View Pricing Plans
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
      
      <GlobalFooter />
    </div>
  );
};

// Contact Us Page Component
const ContactUsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      const data = await response.json();
      
      toast.success(data.message || "Thank you for your message! We'll get back to you within 24 hours.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast.error("Failed to send message. Please try emailing us directly at info@digitalinkai.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {user ? <Navigation /> : (
        /* Landing Page Navigation for Unauth Users */
        <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
                  alt="Digital Ink AI" 
                  className="h-8 w-auto"
                />
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="/" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">Home</a>
                <a href="/about" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">About</a>
                <a href="/prices" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">Pricing</a>
                <a href="/contact" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">Contact</a>
                <Button onClick={() => window.location.href = '/login'} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </nav>
      )}
      
      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <img 
            src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
            alt="Digital Ink AI" 
            className="h-24 w-auto mx-auto mb-6"
          />
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Have questions about Digital Ink AI? We'd love to help! Reach out to our team for support, 
            feedback, or partnership opportunities.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200 h-fit">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-slate-900">Get in Touch</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Phone */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📞</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Phone</h3>
                    <a 
                      href="tel:(719) 205-6381" 
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      (719) 205-6381
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📧</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <a 
                      href="mailto:Info@DigitalInkAI.com" 
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Info@DigitalInkAI.com
                    </a>
                  </div>
                </div>

                {/* Response Time */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">⏰</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Response Time</h3>
                    <p className="text-slate-600">Within 24 hours</p>
                  </div>
                </div>

                {/* Support Hours */}
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🕐</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Support Hours</h3>
                    <p className="text-slate-600">Monday - Friday, 9 AM - 5 PM MST</p>
                  </div>
                </div>

                {/* Quick Links */}
                <div className="pt-6 border-t border-slate-200">
                  <h3 className="font-semibold text-slate-900 mb-3">Quick Links</h3>
                  <div className="space-y-2">
                    <button onClick={() => navigate('/homepage')} className="block text-blue-600 hover:text-blue-800 text-left bg-transparent border-0 cursor-pointer">
                      → Go to Dashboard
                    </button>
                    <button onClick={() => navigate('/prices')} className="block text-blue-600 hover:text-blue-800 text-left bg-transparent border-0 cursor-pointer">
                      → View Pricing Plans
                    </button>
                    <button onClick={() => navigate('/about')} className="block text-blue-600 hover:text-blue-800 text-left bg-transparent border-0 cursor-pointer">
                      → Learn About Us
                    </button>
                    <a href="https://www.uwygpublishing.com" target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:text-blue-800">
                      → UWYG Publishing Services
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-slate-900">Send us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(optional)"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <select
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      required
                    >
                      <option value="">Select a topic...</option>
                      <option value="technical-support">Technical Support</option>
                      <option value="billing-subscription">Billing & Subscription</option>
                      <option value="feature-request">Feature Request</option>
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <textarea
                      id="message"
                      placeholder="Please describe your question or how we can help you..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={5}
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Sending Message...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    We respect your privacy. Your information will never be shared with third parties.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Refer A Friend Card */}
        {user && (
          <div className="mt-8">
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <div className="text-6xl mb-4">🎁</div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-3">
                    Know Someone Who'd Love Digital Ink AI?
                  </h2>
                  <p className="text-lg text-slate-700 mb-6 max-w-2xl mx-auto">
                    Share the power of AI-assisted publishing! Refer a friend and earn <strong className="text-green-600">200 bonus points</strong> when they become paid subscribers.
                  </p>
                  <Button 
                    onClick={() => setShowReferralModal(true)}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-3 text-lg font-semibold min-h-[44px]"
                  >
                    🎁 Refer A Friend Now
                  </Button>
                  <p className="text-sm text-slate-500 mt-3">
                    Help your author friends succeed—get rewarded for spreading the word!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      
      <GlobalFooter />
      
      {/* Referral Modal */}
      {user && (
        <ReferralModal 
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
        />
      )}
    </div>
  );
};

// About Us Page Component
const AboutUsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showReferralModal, setShowReferralModal] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {user ? <Navigation /> : (
        /* Landing Page Navigation for Unauth Users */
        <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
                  alt="Digital Ink AI" 
                  className="h-8 w-auto"
                />
              </div>
              <div className="hidden md:flex items-center space-x-8">
                <a href="/" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">Home</a>
                <a href="/about" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">About</a>
                <a href="/prices" className="text-slate-700 hover:text-blue-600 transition-colors font-medium">Pricing</a>
                <Button onClick={() => window.location.href = '/login'} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Sign In
                </Button>
              </div>
            </div>
          </div>
        </nav>
      )}
      
      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Animated Background */}
        <div className="text-center mb-16 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-100 via-purple-50 to-blue-100 opacity-50 rounded-3xl -z-10 animate-pulse"></div>
          <div className="py-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_054645c4-7ec8-4ab0-a28f-b8636500d64a/artifacts/7ny7iks8_Digital_Ink_AI_Logo-rbg.png" 
              alt="Digital Ink AI" 
              className="h-24 w-auto mx-auto mb-6 transform hover:scale-110 transition-transform duration-300"
            />
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Empowering Authors with<br />
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AI-Driven Publishing Excellence</span>
            </h1>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed mb-8">
              Discover how Digital Ink AI is transforming ideas into market-ready books through 
              innovative, custom-developed AI tools.
            </p>
            {user && (
              <div className="mt-6">
                <Button 
                  onClick={() => window.location.href = '/homepage'}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 text-lg shadow-lg hover:shadow-xl transition-all"
                >
                  Ready to start? Head to your Dashboard →
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Platform Capabilities Section */}
        <section className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { number: "15", label: "AI-Powered Services", icon: "🚀" },
              { number: "5", label: "Author Workflows", icon: "📚" },
              { number: "24/7", label: "Platform Availability", icon: "⚡" }
            ].map((stat, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-blue-50 p-8 rounded-2xl border-2 border-blue-100 text-center hover:shadow-xl hover:scale-105 transition-all duration-300">
                <div className="text-6xl mb-3">{stat.icon}</div>
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-slate-700 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">What Makes Us Different</h2>
            <p className="text-lg text-slate-600">A comprehensive AI toolkit covering every stage of your publishing journey</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                title: "Complete Workflow Coverage", 
                desc: "From concept generation to social media marketing—15 specialized tools designed specifically for authors, not generic AI assistants.",
                icon: "🎯",
                color: "from-blue-500 to-blue-600"
              },
              { 
                title: "GPT-4o & Claude Powered", 
                desc: "Leveraging the latest AI models from OpenAI and Anthropic, fine-tuned for publishing industry standards and best practices.",
                icon: "🤖",
                color: "from-purple-500 to-purple-600"
              },
              { 
                title: "Flexible Pricing", 
                desc: "Pay-per-use, monthly subscriptions, or annual plans. Standard services FREE for subscribers, Premium services at 50% off.",
                icon: "💎",
                color: "from-emerald-500 to-emerald-600"
              }
            ].map((item, index) => (
              <div key={index} className="relative bg-white p-6 rounded-2xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-xl transition-all duration-300 group">
                <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center text-white text-4xl mb-4 mx-auto shadow-lg group-hover:scale-110 transition-transform`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3 text-center">{item.title}</h3>
                <p className="text-slate-600 text-center leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Our Mission and Benefits */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Why Authors Choose Digital Ink AI</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Custom-developed AI methodologies that streamline your entire publishing journey
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: "📝", title: "Writing & Ideation", desc: "Generate book concepts, detailed outlines with 15+ chapters, and fully-developed character casts with AI assistance.", gradient: "from-blue-400 to-blue-600" },
              { icon: "✍️", title: "Editing & Enhancement", desc: "Professional developmental editing feedback, author biography generation, and comprehensive manuscript viability analysis.", gradient: "from-purple-400 to-purple-600" },
              { icon: "🎨", title: "Design & Production", desc: "Convert manuscripts to EPUB format, merge PDFs for print, and prepare optimized Amazon KDP submissions.", gradient: "from-rose-400 to-rose-600" },
              { icon: "📊", title: "Market Research", desc: "Competitive analysis, strategic review campaigns, and metadata optimization for maximum discoverability.", gradient: "from-teal-400 to-teal-600" },
              { icon: "📈", title: "Marketing & Promotion", desc: "Professional media kits, comprehensive social media campaigns, and keynote speeches about your book.", gradient: "from-orange-400 to-orange-600" },
              { icon: "💰", title: "Flexible Pricing", desc: "7 Standard services FREE for subscribers, 8 Premium services at 50% off. Pay-per-use available starting at $3.", gradient: "from-green-400 to-green-600" }
            ].map((benefit, index) => (
              <div key={index} className="group relative bg-white p-6 rounded-2xl border-2 border-slate-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                <div className="relative">
                  <div className="text-4xl mb-4 transform group-hover:scale-125 group-hover:rotate-12 transition-transform duration-300">{benefit.icon}</div>
                  <h4 className="font-bold text-slate-900 text-lg mb-2">{benefit.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Visual Mission Statement */}
          <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-200">
            <CardContent className="py-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <img 
                    src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d"
                    alt="Modern author using laptop for writing"
                    className="w-full h-64 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                  />
                  <img 
                    src="https://images.unsplash.com/reserve/LJIZlzHgQ7WPSh5KVTCB_Typewriter.jpg"
                    alt="Classic vintage typewriter"
                    className="w-full h-48 object-cover rounded-xl shadow-lg hover:shadow-2xl transition-shadow"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">From Idea to Market-Ready</h3>
                  <p className="text-lg text-slate-700 mb-4 leading-relaxed">
                    Digital Ink AI bridges the gap between creative inspiration and commercial success. Our platform doesn't just help you write—it helps you <strong>publish books that sell</strong>.
                  </p>
                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    Leveraging custom-developed AI methodologies, we streamline the entire publishing process—from ideation to marketing—ensuring your work aligns with current trends, reader expectations, and SEO for maximum discoverability and sales potential.
                  </p>
                  <div className="flex items-center space-x-3 p-4 bg-white rounded-lg border-l-4 border-blue-500">
                    <span className="text-9xl">📚</span>
                    <p className="text-slate-700 font-medium">
                      "The tools professional publishers use, now accessible to every author"
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Why Choose Digital Ink AI - Condensed Mobile-Friendly Version */}
        <section className="mb-16">
          <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 border-2 border-blue-300">
            <CardHeader>
              <CardTitle className="text-center text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Why Choose Digital Ink AI?
              </CardTitle>
              <p className="text-center text-base md:text-lg text-slate-700 max-w-3xl mx-auto">
                Unlike generic AI writers, we enhance your <span className="font-semibold text-blue-700">agency</span> with marketing intelligence—turning ideas into <span className="font-semibold text-green-700">market-ready works</span>.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl border-2 border-blue-300 text-center">
                  <div className="text-3xl mb-2">🎯</div>
                  <h4 className="font-bold text-blue-900 mb-1 text-sm">Marketing Intelligence</h4>
                  <p className="text-xs text-blue-800">Market data in every decision</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-100 to-green-50 rounded-xl border-2 border-green-300 text-center">
                  <div className="text-3xl mb-2">💰</div>
                  <h4 className="font-bold text-green-900 mb-1 text-sm">Market Positioning</h4>
                  <p className="text-xs text-green-800">Position works for market success</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl border-2 border-purple-300 text-center">
                  <div className="text-3xl mb-2">✨</div>
                  <h4 className="font-bold text-purple-900 mb-1 text-sm">Authentic Voice</h4>
                  <p className="text-xs text-purple-800">Preserve your creative identity</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-pink-100 to-pink-50 rounded-xl border-2 border-pink-300 text-center">
                  <div className="text-3xl mb-2">🚀</div>
                  <h4 className="font-bold text-pink-900 mb-1 text-sm">Scalability</h4>
                  <p className="text-xs text-pink-800">Platform grows with you</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Our Standards */}
        <section className="mb-16">
          <Card className="bg-gradient-to-br from-slate-50 to-blue-50 border-2 border-blue-200 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <CardTitle className="text-3xl font-bold text-center">Our Standards for Market-Ready Books</CardTitle>
            </CardHeader>
            <CardContent className="pt-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🎯</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-2">Proprietary AI Methodology</h4>
                        <p className="text-slate-700">
                          Custom-built AI tools developed specifically for this platform, drawing from industry best practices and real market data.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">📊</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-2">Comprehensive Analysis</h4>
                        <p className="text-slate-700">
                          Genre accuracy, thematic depth, SEO-optimized titles, competitive analysis, and viability scoring (0-100) to predict commercial success.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">🚀</span>
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 mb-2">Market-Ready Output</h4>
                        <p className="text-slate-700">
                          All outputs include sentiment analysis, marketing hashtag suggestions, and positioning strategies to help your books stand out in crowded markets.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <img 
                    src="https://images.unsplash.com/photo-1501504905252-473c47e087f8"
                    alt="Modern workspace with laptop and notebook"
                    className="w-full h-80 object-cover rounded-xl shadow-2xl hover:shadow-3xl transition-shadow"
                  />
                  <div className="mt-6 p-4 bg-white rounded-lg border-2 border-blue-200">
                    <p className="text-sm text-slate-600 font-medium">
                      ⭐ Professional-grade tools at indie author prices
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Meet R. Dale Brunet */}
        <section className="mb-16">
          <Card className="bg-gradient-to-r from-blue-50 to-slate-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-slate-900 text-center">Our Founder and Chief Developer: R. Dale Brunet</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                {/* Author Profile Section */}
                <div className="text-center">
                  <a 
                    href="https://www.rdbrunet.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block hover:opacity-90 transition-opacity"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_writesmart-app-8/artifacts/klbjcyb2_R_Dale_Brunet_Serious.jpg"
                      alt="R. Dale Brunet - Chief Developer"
                      className="w-48 h-48 object-cover rounded-full mx-auto mb-6 border-4 border-white shadow-lg hover:shadow-xl transition-shadow"
                    />
                  </a>
                  <p className="text-blue-600 font-semibold mb-2">Click to learn more at:</p>
                  <a 
                    href="https://www.rdbrunet.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 font-medium underline text-lg"
                  >
                    www.RDBrunet.com
                  </a>
                </div>
                
                {/* Bio Text */}
                <div className="lg:col-span-2">
                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    R. Dale Brunet is the visionary behind Digital Ink AI, serving as Chief Developer, Editor, and a prolific author. 
                    With over 40 years in technology and systems development, R. Dale has authored more than 30 books using the exact 
                    AI methodologies powering this platform.
                  </p>
                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    His works include the "Artificial Intelligence Survival Guide" series, "Digital Ink: How Artificial Intelligence 
                    is Re-writing the Rules of Publishing," "The New Canvas: How AI is Redefining Art, Storytelling," 
                    "Beyond Checkers: Educating in an AI-Powered World," "Becoming the A.I. Guy (or Gal)," and "ZERO TRUST: Digital Messiah"—a 
                    chilling story of just what might happen when humanity fully surrenders its freedom and agency for the sake of convenience.
                  </p>
                  <p className="text-lg text-slate-700 leading-relaxed">
                    As an innovator and entrepreneur, R. Dale demystifies AI to empower creators, blending technical expertise with 
                    creative passion to revolutionize publishing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Featured Book: Digital Ink */}
        <section className="mb-16">
          <Card className="bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200">
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-slate-900 text-center">Featured Publication: Digital Ink</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center">
                  <a 
                    href="https://books.by/use-what-you-got-publishing?#digital-ink" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block hover:opacity-90 transition-opacity"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_writesmart-app-8/artifacts/1gtjm1td_DIGITAL_INK_COVER_Front_Cover_PP.jpg"
                      alt="Digital Ink: How AI is Re-writing the Rules of Publishing"
                      className="w-64 h-auto mx-auto rounded-lg shadow-lg hover:shadow-xl transition-shadow border-2 border-slate-200"
                    />
                  </a>
                  <div className="mt-4">
                    <p className="text-blue-600 font-semibold mb-2">Available now - Click to purchase:</p>
                    <a 
                      href="https://books.by/use-what-you-got-publishing?#digital-ink" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                    >
                      Order Digital Ink Today
                    </a>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    "Digital Ink: How AI is Re-writing the Rules of Publishing"
                  </h3>
                  <p className="text-lg text-slate-700 leading-relaxed mb-4">
                    The foundational book that inspired this entire platform! Written by R. Dale Brunet, this groundbreaking 
                    work explores how artificial intelligence is revolutionizing every aspect of the publishing industry.
                  </p>
                  <p className="text-lg text-slate-700 leading-relaxed mb-4">
                    From concept generation to market analysis, "Digital Ink" reveals the AI methodologies and strategies 
                    that are now built into the Digital Ink AI platform. This book serves as both inspiration and instruction 
                    manual for authors ready to embrace the future of publishing.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                    <p className="text-blue-800 font-medium">
                      "This book doesn't just predict the future of publishing—it provides the tools to create it." 
                      <span className="text-blue-600">- Publishing Today Review</span>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Publishing Partner */}
        <section className="mb-16">
          <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-3xl font-bold text-slate-900 text-center">Our Publishing Partner</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center">
                  <a 
                    href="https://www.uwygpublishing.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block hover:opacity-90 transition-opacity mb-6"
                  >
                    <img 
                      src="https://customer-assets.emergentagent.com/job_writesmart-app-8/artifacts/bpprsn72_UWYG_Publishing_Self_Publishing_Services_Header_Logo.jpg"
                      alt="Use What You Got Publishing - Self Publishing Services"
                      className="w-full max-w-md mx-auto rounded-lg shadow-lg border-2 border-slate-200"
                    />
                  </a>
                  <img 
                    src="https://images.unsplash.com/photo-1623021317626-3770c4247925"
                    alt="Professional author at modern workspace"
                    className="w-80 h-48 object-cover rounded-lg shadow-md mx-auto mt-4"
                  />
                </div>
                <div>
                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    Digital Ink AI is powered by <strong>Use What You Got Publishing, LLC</strong>—a company dedicated to empowering 
                    entrepreneurs and authors with innovative, AI-enhanced services like self-publishing, ghostwriting, and legacy preservation.
                  </p>
                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    Founded by R. Dale Brunet, UWYG helps turn manuscripts into polished, market-ready books while preserving 
                    personal stories for generations. Their comprehensive publishing services complement the AI tools available 
                    on this platform, providing authors with a complete publishing solution.
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg border-l-4 border-blue-400 mb-6">
                    <h4 className="font-semibold text-slate-900 mb-2">Full Publishing Services Include:</h4>
                    <ul className="text-slate-700 space-y-1">
                      <li>• Professional editing and proofreading</li>
                      <li>• Book cover design and formatting</li>
                      <li>• Publishing and distribution</li>
                      <li>• Marketing and promotion support</li>
                      <li>• Legacy preservation services</li>
                    </ul>
                  </div>
                  <div className="text-center">
                    <p className="text-blue-600 font-semibold mb-2">Visit for full publishing services:</p>
                    <a 
                      href="https://www.uwygpublishing.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                    >
                      www.UWYGPublishing.com
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Refer A Friend Card */}
        {user && (
          <section className="mb-16">
            <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 hover:shadow-xl transition-shadow">
              <CardContent className="pt-8 pb-8">
                <div className="text-center">
                  <div className="text-6xl mb-4">🎁</div>
                  <h2 className="text-3xl font-bold text-slate-900 mb-3">
                    Share Digital Ink AI with Friends!
                  </h2>
                  <p className="text-lg text-slate-700 mb-6 max-w-2xl mx-auto">
                    Love what you're experiencing? Invite your author friends to join Digital Ink AI 
                    and earn <strong className="text-green-600">200 bonus points</strong> when they become paid subscribers!
                  </p>
                  <Button 
                    onClick={() => setShowReferralModal(true)}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-3 text-lg font-semibold min-h-[44px]"
                  >
                    🎁 Refer A Friend
                  </Button>
                  <p className="text-sm text-slate-500 mt-3">
                    Earn rewards while helping fellow authors discover powerful AI tools
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Pricing Call-to-Action Section */}
        <section className="mb-16">
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="pt-8 pb-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Ready to Transform Your Publishing Journey?
                </h2>
                <p className="text-lg text-slate-700 mb-8 max-w-3xl mx-auto leading-relaxed">
                  Explore our flexible pricing options designed to fit every author's needs. From free trials 
                  to comprehensive annual plans, find the perfect solution for your publishing goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Button 
                    onClick={() => navigate('/prices')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-semibold min-h-[44px]"
                  >
                    💰 View All Pricing Plans
                  </Button>
                  <Button 
                    onClick={() => navigate('/faq')}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-3 text-lg font-semibold min-h-[44px]"
                  >
                    ❓ Browse FAQ
                  </Button>
                  {user && (
                    <Button 
                      onClick={() => navigate('/homepage')}
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-50 px-8 py-3 text-lg font-semibold min-h-[44px]"
                    >
                      Go to Dashboard →
                    </Button>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-4">
                  Free trials available • No setup fees • Cancel anytime
                </p>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
      
      <GlobalFooter />
      
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />
    </div>
  );
};

// Profile Component
const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(user);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || '',
    alt_email: user?.alt_email || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    zip_code: user?.zip_code || '',
    timezone: user?.timezone || 'America/Denver'
  });
  const [loading, setLoading] = useState(false);
  const [showPaymentSetup, setShowPaymentSetup] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Usage statistics state
  const [usageStats, setUsageStats] = useState({
    bctg_used: 0,
    bctg_remaining: 3,
    total_results: 0,
    loading: true
  });

  // Refresh user profile data when component loads
  useEffect(() => {
    const refreshUserProfile = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUserProfile(response.data);
      } catch (error) {
      }
    };
    refreshUserProfile();
  }, []);

  // Fetch usage statistics
  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        // Fetch usage stats for BCTG free usage tracking
        const usageResponse = await axios.get(`${API}/users/usage-stats`, { withCredentials: true });
        const bctgStats = usageResponse.data.services?.bctg || {};
        
        // Fetch activity summary to get total results count (last 30 days)
        const activityResponse = await axios.get(`${API}/activity/summary`, { withCredentials: true });
        const activityData = activityResponse.data || {};
        
        // Calculate total results across all services
        const totalResults = (
          (activityData.bctg_count || 0) +
          (activityData.bvadm_count || 0) +
          (activityData.bocp_count || 0) +
          (activityData.mmg_count || 0) +
          (activityData.ccg_count || 0) +
          (activityData.ccat_count || 0) +
          (activityData.brec_count || 0) +
          (activityData.abg_count || 0) +
          (activityData.ksg_count || 0) +
          (activityData.pde_count || 0) +
          (activityData.ermt_count || 0) +
          (activityData.pmpb_count || 0) +
          (activityData.aksw_count || 0) +
          (activityData.mmkg_count || 0) +
          (activityData.smmk_count || 0) +
          (activityData.qrg_count || 0) +
          (activityData.ibg_count || 0)
        );
        
        setUsageStats({
          bctg_used: bctgStats.free_used || 0,
          bctg_remaining: bctgStats.free_remaining !== undefined ? bctgStats.free_remaining : 3,
          total_results: totalResults,
          loading: false
        });
      } catch (error) {
        setUsageStats({
          bctg_used: 0,
          bctg_remaining: 3,
          total_results: 0,
          loading: false
        });
      }
    };
    
    if (user) {
      fetchUsageStats();
    }
  }, [user]);

  // Sync formData with userProfile when userProfile changes
  useEffect(() => {
    if (userProfile) {
      setFormData({
        first_name: userProfile.first_name || '',
        last_name: userProfile.last_name || '',
        phone: userProfile.phone || '',
        alt_email: userProfile.alt_email || '',
        address: userProfile.address || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zip_code: userProfile.zip_code || '',
        timezone: userProfile.timezone || 'America/Denver'
      });
    }
  }, [userProfile]);

  // Scroll to payment method section if redirected from payment modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    
    if (tab === 'payment') {
      // Wait for the component to fully render before scrolling
      setTimeout(() => {
        const paymentSection = document.getElementById('payment-method-section');
        if (paymentSection) {
          paymentSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 300);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.put(`${API}/users/profile`, formData, { withCredentials: true });
      setUserProfile(response.data);
      
      // Update global auth context if timezone changed
      if (window.updateAuthUser) {
        window.updateAuthUser(response.data);
      }
      
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChangeSubmit = () => {
    // Validate passwords
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords don't match");
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error("New password must be at least 6 characters long");
      return;
    }
    
    // Show confirmation modal
    setShowPasswordConfirmModal(true);
  };

  const handlePasswordChangeConfirm = async () => {
    setPasswordLoading(true);
    
    try {
      await axios.post(`${API}/users/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      
      toast.success("Password changed successfully!");
      
      // Reset form and close modals
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordChange(false);
      setShowPasswordConfirmModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleSubscriptionConfirm = async (subscriptionData) => {
    toast.success(`${subscriptionData.subscription.type} subscription activated! Your 14-day free trial has started.`);
    setShowSubscriptionModal(false);
    
    // Immediately refresh user data to reflect subscription changes
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const freshUserData = response.data;
      setUserProfile(freshUserData);
      
      // Update auth context as well if available
      if (window.updateAuthUser) {
        window.updateAuthUser(freshUserData);
      }
      
      // Always redirect to dashboard after subscription success
      setTimeout(() => {
        window.location.href = '/homepage';
      }, 2000);
    } catch (error) {
      // Fallback to page redirect
      setTimeout(() => {
        window.location.href = '/homepage';
      }, 2000);
    }
  };

  const isAdmin = user?.role === 'admin';

  // Calculate days until subscription renewal
  const getDaysUntilRenewal = () => {
    if (!userProfile?.subscription_end) return null;
    
    try {
      const renewalDate = new Date(userProfile.subscription_end);
      const today = new Date();
      const diffTime = renewalDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays > 0 ? diffDays : 0;
    } catch (error) {
      return null;
    }
  };

  const isSubscriber = userProfile?.role === 'subscriber' && userProfile?.subscription_status && userProfile.subscription_status !== "None";
  const daysUntilRenewal = getDaysUntilRenewal();
  
  // Force refresh user data when component mounts to ensure latest subscription data
  useEffect(() => {
    const forceRefreshUserData = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        const freshUserData = response.data;
        setUserProfile(freshUserData);
        
        // Update global auth context
        if (window.updateAuthUser) {
          window.updateAuthUser(freshUserData);
        }
      } catch (error) {
      }
    };
    
    forceRefreshUserData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <main className="max-w-2xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-bold text-slate-900">Profile Settings</CardTitle>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <div className="text-xs text-slate-500 uppercase tracking-wide">Subscription Type</div>
                  <div className={`text-sm font-semibold px-3 py-1 rounded-full ${
                    !userProfile?.subscription_status || userProfile.subscription_status === "None" 
                      ? "text-slate-700 bg-slate-100" 
                      : userProfile.subscription_status === "Monthly"
                      ? "text-blue-700 bg-blue-100"
                      : userProfile.subscription_status === "Annual"
                      ? "text-purple-700 bg-purple-100"
                      : userProfile.subscription_status === "Student"
                      ? "text-green-700 bg-green-100"
                      : userProfile.subscription_status === "Enterprise"
                      ? "text-orange-700 bg-orange-100"
                      : "text-slate-700 bg-slate-100"
                  }`}>
                    {userProfile?.subscription_status || "None"}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="mt-1 bg-slate-100"
                />
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="alt_email">Alternate Email</Label>
                <Input
                  id="alt_email"
                  type="email"
                  value={formData.alt_email}
                  onChange={(e) => setFormData({...formData, alt_email: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <select 
                    id="state"
                    className="w-full p-2 border rounded mt-1"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                  >
                    <option value="">Select State</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="zip_code">Zip Code</Label>
                  <Input
                    id="zip_code"
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <select 
                  id="timezone"
                  className="w-full p-2 border rounded mt-1"
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                >
                  <option value="America/New_York">Eastern Time (ET)</option>
                  <option value="America/Chicago">Central Time (CT)</option>
                  <option value="America/Denver">Mountain Time (MT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PT)</option>
                  <option value="America/Anchorage">Alaska Time (AKT)</option>
                  <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="Europe/Paris">Central European (CET)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Australia/Sydney">Sydney (AEDT)</option>
                  <option value="UTC">UTC (Coordinated Universal Time)</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">All timestamps in the application will display in your selected timezone</p>
              </div>

              <div>
                <Label htmlFor="role">Role</Label>
                <div className="mt-1 flex items-center space-x-2">
                  <Badge variant={isAdmin ? "default" : "secondary"}>
                    {isAdmin ? "Administrator" : (user?.role === "a_la_carte" ? "A La Carte" : user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || "A La Carte")}
                  </Badge>
                  {isAdmin && (
                    <span className="text-xs text-blue-600">Special admin privileges enabled</span>
                  )}
                </div>
              </div>

              {/* Password Change Section */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-base font-semibold">Password</Label>
                    <p className="text-xs text-slate-500 mt-1">Change your password</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPasswordChange(!showPasswordChange)}
                    className="text-sm"
                  >
                    {showPasswordChange ? 'Cancel' : 'Change Password'}
                  </Button>
                </div>

                {showPasswordChange && (
                  <div className="space-y-4 pt-3 bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <div>
                      <Label htmlFor="current_password">Current Password *</Label>
                      <Input
                        id="current_password"
                        type="password"
                        value={passwordData.current_password}
                        onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                        className="mt-1"
                        placeholder="Enter current password"
                      />
                    </div>

                    <div>
                      <Label htmlFor="new_password">New Password *</Label>
                      <Input
                        id="new_password"
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                        className="mt-1"
                        placeholder="Minimum 6 characters"
                      />
                    </div>

                    <div>
                      <Label htmlFor="confirm_password">Confirm New Password *</Label>
                      <Input
                        id="confirm_password"
                        type="password"
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                        className="mt-1"
                        placeholder="Re-enter new password"
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={handlePasswordChangeSubmit}
                      className="w-full bg-green-600 hover:bg-green-700"
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? 'Updating...' : 'Update Password'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Subscription Renewal Notice - Above Submit Button */}
              {isSubscriber && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="text-lg">📅</div>
                    <div className="text-sm text-blue-800">
                      {daysUntilRenewal !== null ? (
                        daysUntilRenewal === 0 
                          ? "Your subscription renews today"
                          : daysUntilRenewal === 1 
                          ? "You have 1 day until your subscription renewal date"
                          : `You have ${daysUntilRenewal} days until your subscription renewal date`
                      ) : (
                        userProfile?.subscription_status === "Annual" 
                          ? "Your Annual subscription is active - renewal date will be updated shortly"
                          : "Your Monthly subscription is active - renewal date will be updated shortly"
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>

            {/* Current Plan & Subscription Management */}
            <Separator className="my-6" />
            
            <div className="space-y-6">
              {/* Subscription Plan section completely removed */}
              {/* TODO: Implement subscription management with proper data structure later */}

              {/* Payment Method Management */}
              {!isAdmin && (
              <div id="payment-method-section">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Payment Methods</h3>
                <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-900">
                          {userProfile?.payment_method ? 'Payment Method Saved' : 'No Payment Method'}
                        </h4>
                        <p className="text-sm text-green-700">
                          {userProfile?.payment_method ? 
                            `${userProfile.payment_method.brand?.charAt(0)?.toUpperCase() + userProfile.payment_method.brand?.slice(1) || 'Card'} ending in ${userProfile.payment_method.last_four}` : 
                            'Add a payment method for seamless transactions'
                          }
                        </p>
                      </div>
                      <Badge className="bg-green-600 text-white">
                        {userProfile?.payment_method ? '✓ Active' : '+ Add'}
                      </Badge>
                    </div>
                    {userProfile?.payment_method && (
                      <div className="mt-3 text-xs text-green-600 bg-green-100 p-2 rounded">
                        Card expires: {userProfile.payment_method.exp_month}/{userProfile.payment_method.exp_year}
                        {userProfile.payment_method.simulated && (
                          <span className="ml-2 text-blue-600">(Simulated for testing)</span>
                        )}
                      </div>
                    )}
                
                <div className="flex space-x-4 mt-4">
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    onClick={() => setShowPaymentSetup(true)}
                  >
                    {userProfile?.payment_method ? 'Update Payment Method' : 'Add Payment Method'}
                  </Button>
                  {userProfile?.payment_method && (
                    <Button 
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        if (window.confirm('Are you sure you want to remove your payment method? This will downgrade you to a free user and affect your ability to use paid services.')) {
                          try {
                            await axios.delete(`${API}/payment/remove-method`, { withCredentials: true });
                            toast.success('Payment method removed successfully');
                            // Refresh profile data
                            const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
                            setUserProfile(response.data);
                          } catch (error) {
                            toast.error('Failed to remove payment method');
                          }
                        }
                      }}
                    >
                      Remove Method
                    </Button>
                  )}
                </div>
                  </CardContent>
                </Card>
              </div>
              )}
              
              {/* Rewards & Pricing Sections - Hide for admins */}
              {!isAdmin && (
                <>
                  {/* Rewards Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Rewards & Points</h3>
                    <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-purple-900">Manage Your Rewards</h4>
                            <p className="text-sm text-purple-700">Track points, view rewards history, and redeem benefits</p>
                          </div>
                          <Badge className="bg-purple-600 text-white">🎁 Rewards</Badge>
                        </div>
                        <div className="mt-4 space-y-2">
                          <Button 
                            onClick={() => navigate('/rewards')}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                          >
                            🎁 View Rewards Dashboard
                          </Button>
                          <Button 
                            onClick={() => setShowReferralModal(true)}
                            variant="outline"
                            className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                          >
                            👥 Refer A Friend (Earn 200 Points!)
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Subscription Management - Temporarily Removed */}
                  {/* TODO: Re-implement subscription management with clean data structure */}

                  {/* Pricing Plans Access */}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-4">Pricing & Plans</h3>
                    <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
                      <CardContent className="pt-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-green-900">View Pricing Options</h4>
                            <p className="text-sm text-green-700">Explore all available plans and pricing options</p>
                          </div>
                          <Badge className="bg-green-600 text-white">💰 Pricing</Badge>
                        </div>
                        <div className="mt-4">
                          <Button 
                            onClick={() => navigate('/prices')}
                            className="w-full bg-green-600 hover:bg-green-700 text-white"
                          >
                            💰 View All Pricing Plans
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}

              {/* Admin-specific content */}
              {isAdmin && (
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Admin Access</h3>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-red-900">Administrator Account</h4>
                          <p className="text-sm text-red-700">Full access to all platform features and admin dashboard</p>
                        </div>
                        <Badge variant="destructive">Admin</Badge>
                      </div>
                      <div className="mt-4 space-y-2">
                        <Button 
                          onClick={() => navigate('/admin-mgmt-7f84b')}
                          className="w-full bg-red-600 hover:bg-red-700 text-white"
                        >
                          🛡️ Access Admin Dashboard
                        </Button>
                        <Button 
                          onClick={() => navigate('/ai-discovery')}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          🤖 AI Search Discovery Page
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Usage Statistics Card - Only for non-admin users */}
        {!isAdmin && (
        <Card className="mt-6 bg-white/80 backdrop-blur-sm border-slate-200">
          <CardHeader>
            <CardTitle className="text-xl text-slate-900">Usage This Month</CardTitle>
          </CardHeader>
          <CardContent>
            {usageStats.loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-slate-600">Loading usage data...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{usageStats.bctg_used}</div>
                  <p className="text-sm text-blue-700">BCTG Uses</p>
                  <p className="text-xs text-slate-500">{usageStats.bctg_remaining} remaining this month</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{usageStats.total_results}</div>
                  <p className="text-sm text-green-700">Total Results</p>
                  <p className="text-xs text-slate-500">Last 30 days</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600">{usageStats.total_results}</div>
                  <p className="text-sm text-purple-700">Saved Results</p>
                  <p className="text-xs text-slate-500">View in Activity</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </main>
      
      <GlobalFooter />
      
      {/* Payment Method Setup Modal */}
      <PaymentMethodSetup
        isOpen={showPaymentSetup}
        onClose={() => setShowPaymentSetup(false)}
        onSuccess={async (data) => {
          // Refresh profile data to show updated payment method
          try {
            const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
            const updatedUser = response.data;
            setUserProfile(updatedUser);
            
            // Close payment setup modal
            setShowPaymentSetup(false);
            toast.success('Payment method added successfully!');
            
            // Update the auth context as well
            if (window.updateAuthUser) {
              window.updateAuthUser(updatedUser);
            }
          } catch (error) {
            toast.error('Payment method added, but please refresh the page to see subscription options.');
          }
        }}
      />
      
      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setSelectedPlan(null);
        }}
        onConfirm={handleSubscriptionConfirm}
        user={userProfile || user}
        preselectedPlan={selectedPlan}
        onPaymentSetupRequired={() => {
          // Close subscription modal and open payment setup
          setShowSubscriptionModal(false);
          setSelectedPlan(null);
          setShowPaymentSetup(true);
          toast.info('Please add a payment method to subscribe');
        }}
      />

      {/* Referral Modal */}
      <ReferralModal 
        isOpen={showReferralModal}
        onClose={() => setShowReferralModal(false)}
      />

      {/* Password Change Confirmation Modal */}
      {showPasswordConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-slate-900">Confirm Password Change</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Important:</strong> Are you sure you want to change your password?
                </p>
                <p className="text-sm text-yellow-700 mt-2">
                  You will remain logged in on this device, but you'll need to use your new password for future logins.
                </p>
              </div>
              <div className="flex space-x-3">
                <Button
                  onClick={handlePasswordChangeConfirm}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={passwordLoading}
                >
                  {passwordLoading ? 'Updating...' : 'Yes, Change Password'}
                </Button>
                <Button
                  onClick={() => setShowPasswordConfirmModal(false)}
                  variant="outline"
                  className="flex-1"
                  disabled={passwordLoading}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// App Routes Component (inside AuthProvider to access user)
const AppRoutes = () => {
  const { user, register, logout, completeGoogleOnboarding, setUser } = useAuth();
  const location = useLocation();
  
  // Journey modal states
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [activeServiceTab, setActiveServiceTab] = useState(null); // Start with no selection for faster load
  
  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  
  // EULA enforcement state
  const [showEulaModal, setShowEulaModal] = useState(false);
  
  // Onboarding state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  
  // Payment setup state (after onboarding and EULA)
  const [showPaymentSetupModal, setShowPaymentSetupModal] = useState(false);

  // Check EULA acceptance on user change or route change
  useEffect(() => {
    console.log('🔍 EULA Check triggered:', {
      hasUser: !!user,
      eulaAccepted: user?.eula_accepted,
      pathname: location.pathname,
      showEulaModal
    });
    
    if (user) {
      // Check if user needs to accept EULA
      const needsEula = user.eula_accepted === false || user.eula_accepted === undefined;
      
      console.log('🔒 EULA needs check:', {
        needsEula,
        eulaAcceptedValue: user.eula_accepted,
        userId: user.id
      });
      
      if (needsEula) {
        // Only show modal on authenticated routes
        const publicPaths = ['/', '/login', '/reset-password', '/features', '/prices', '/faq', '/about', '/contact', '/help-center', '/privacy-policy'];
        const isProtectedRoute = !publicPaths.includes(location.pathname);
        
        console.log('🚪 Route check:', {
          currentPath: location.pathname,
          isProtectedRoute,
          showEulaModal
        });
        
        if (isProtectedRoute && !showEulaModal) {
          console.log('✅ EULA Check: User needs EULA, showing modal NOW');
          setShowEulaModal(true);
        }
      }
    } else {
      // User logged out, hide EULA modal
      if (showEulaModal) {
        console.log('❌ User logged out, hiding EULA modal');
        setShowEulaModal(false);
      }
    }
  }, [user, location.pathname]);

  const handleEulaAccepted = () => {
    setShowEulaModal(false);
    // Refresh user data to get updated eula_accepted status
    if (setUser) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, { withCredentials: true })
        .then(response => {
          setUser(response.data);
          
          // After EULA accepted, check what to do next
          if (!response.data.onboarding_completed) {
            // User hasn't completed onboarding yet - show onboarding modal
            setShowOnboardingModal(true);
          } else {
            // User has completed onboarding - check if they need payment setup
            // Check sessionStorage for flag indicating user just completed onboarding
            const justCompletedOnboarding = sessionStorage.getItem('justCompletedOnboarding');
            
            if (justCompletedOnboarding === 'true') {
              sessionStorage.removeItem('justCompletedOnboarding');
              
              // Check if user needs payment setup
              const isPaymentExempt = (user) => {
                return user.role === 'beta_tester' || user.role === 'admin' || user.subscription_status !== 'free';
              };
              
              if (!isPaymentExempt(response.data) && !response.data.payment_method) {
                // Show payment setup modal
                console.log('🎯 After EULA: Showing payment setup modal');
                setShowPaymentSetupModal(true);
              } else {
                console.log('🎯 After EULA: User is payment exempt or has payment method');
              }
            }
          }
        })
        .catch(error => console.error("Error refreshing user:", error));
    }
  };

  const handleEulaDeclined = () => {
    // Clear auth state
    if (setUser) {
      setUser(null);
    }
    localStorage.clear();
    sessionStorage.clear();
    
    // Redirect to login with declined flag
    window.location.href = '/login?declined=true';
  };

  return (
    <div className="App">
      <Toaster position="top-right" />
      
      {/* EULA Acceptance Gate - Phase 1 & 3 */}
      {showEulaModal && user && (
        <EulaAcceptanceModal 
          user={user} 
          onAccepted={handleEulaAccepted}
          onDeclined={handleEulaDeclined}
        />
      )}
      
      {/* DiAi Conversational Onboarding Modal - Shows after EULA acceptance */}
      {showOnboardingModal && user && !showEulaModal && (
        <DiAiOnboarding
          user={user}
          onComplete={async (surveyData) => {
            try {
              // Call completeGoogleOnboarding which handles the API call
              await completeGoogleOnboarding(surveyData);
              setShowOnboardingModal(false);
              toast.success(`Welcome, ${user.first_name}! You're all set.`);
            } catch (error) {
              console.error('Error completing onboarding:', error);
              toast.error('Failed to save onboarding data. Please try again.');
            }
          }}
          onSkip={() => {
            setShowOnboardingModal(false);
            toast.info("You can complete your profile later from settings");
          }}
        />
      )}
      
      {/* Payment Setup Modal - Shows after EULA acceptance for new users */}
      {showPaymentSetupModal && user && !showEulaModal && !showOnboardingModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-3">
                One More Step, {user.first_name}!
              </h3>
              <p className="text-slate-600 mb-2">
                Would you like to add a payment method now and unlock immediate access to all our professional services?
              </p>
              <p className="text-sm text-slate-500">
                You can always add payment later from your profile settings.
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-blue-900 mb-1">✨ With Payment Method:</p>
                <p className="text-xs text-blue-700">
                  • Instant access to all 15+ professional services<br />
                  • Pay-per-use pricing (A La Carte)<br />
                  • Option to subscribe for unlimited access
                </p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-slate-900 mb-1">🎁 Free User:</p>
                <p className="text-xs text-slate-700">
                  • 3 free BCTG uses per month<br />
                  • Access to free utilities (IBG, QRG)<br />
                  • Add payment anytime to unlock more
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => {
                  console.log('💳 User clicked "Add Payment Method" after EULA');
                  setShowPaymentSetupModal(false);
                  // Navigate to profile to add payment method
                  navigate('/profile?tab=payment');
                }}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3"
              >
                Add Payment Method →
              </Button>
              <Button
                onClick={() => {
                  console.log('🎁 User clicked "Continue as Free User" after EULA');
                  setShowPaymentSetupModal(false);
                  // User stays on homepage
                }}
                variant="outline"
                className="w-full"
              >
                Continue as Free User
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/homepage" element={
          <ProtectedRoute>
            <Homepage 
              showJourneyModal={showJourneyModal}
              setShowJourneyModal={setShowJourneyModal}
              selectedService={selectedService}
              setSelectedService={setSelectedService}
              setActiveServiceTab={setActiveServiceTab}
            />
          </ProtectedRoute>
        } />
        <Route path="/bctg" element={
          <ProtectedRoute>
            <BCTGPage />
          </ProtectedRoute>
        } />
        <Route path="/ccg" element={
          <ProtectedRoute>
            <CCGPage />
          </ProtectedRoute>
        } />
        <Route path="/user_dashboard" element={
          <ProtectedRoute>
            <UserDashboard />
          </ProtectedRoute>
        } />
        <Route path="/transactions" element={
          <ProtectedRoute>
            <TransactionDetailsPage />
          </ProtectedRoute>
        } />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/prices" element={<PricingPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/help-center" element={<HelpCenterPage user={user} />} />
        <Route path="/about" element={<AboutUsPage />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage user={user} />} />
        <Route path="/terms" element={<TermsOfServicePage user={user} />} />
        <Route path="/payment-success" element={
          <ProtectedRoute>
            <PaymentSuccessPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/payment-cancelled" element={
          <ProtectedRoute>
            <PaymentCancelledPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />
        <Route path="/admin-mgmt-7f84b" element={
          <AdminProtectedRoute>
            <AdminDashboard user={user} />
          </AdminProtectedRoute>
        } />
        <Route path="/admin" element={
          <AdminProtectedRoute>
            <AdminDashboard user={user} />
          </AdminProtectedRoute>
        } />
        <Route path="/api-costs" element={
          <AdminProtectedRoute>
            <APICostsPage user={user} />
          </AdminProtectedRoute>
        } />
        <Route path="/rewards" element={
          <ProtectedRoute>
            <RewardsPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/bvadm" element={
          <ProtectedRoute>
            <BVADMPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/pde" element={
          <ProtectedRoute>
            <PDEPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/ermt" element={
          <ProtectedRoute>
            <ERMTPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/pmpb" element={
          <ProtectedRoute>
            <PMPBPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/ibg" element={<IBGPage user={user} />} />
        <Route path="/qrg" element={<QRGPage user={user} />} />
        <Route path="/aksw" element={<AKSWPage user={user} />} />
        <Route path="/mmkg" element={<MMKGPage user={user} />} />
        <Route path="/smmk" element={<SMMKPage user={user} />} />
        <Route path="/ccat" element={
          <ProtectedRoute>
            <CCATPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/brec" element={
          <ProtectedRoute>
            <BRECPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/bocp" element={
          <ProtectedRoute>
            <BOCPPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/mmg" element={
          <ProtectedRoute>
            <MMGPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/abg" element={
          <ProtectedRoute>
            <ABGPage user={user} />
          </ProtectedRoute>
        } />
        <Route path="/ksg" element={
          <ProtectedRoute>
            <KSGPage user={user} />
          </ProtectedRoute>
        } />

        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      </Routes>
    </div>
  );
};

// ============================================================================
// CCG (CHARACTER & CAST GENERATOR) PAGE
// ============================================================================

const CCGPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatTimestamp } = useTimezone();
  
  // Check where user came from
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  const returnLabel = fromLocation === 'activity' ? 'Return to Dashboard' : 'Return to Home';
  
  // Simple form state
  const [formData, setFormData] = useState({
    book_title: '',
    subtitle: '',
    author_name: '',
    theme: '',
    category: '',
    story_outline: '',
    num_characters: '5-8'
  });
  
  const [results, setResults] = useState(null);
  const [showChargeConfirmation, setShowChargeConfirmation] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);
  const [loading, setLoading] = useState(false);

  // Handle receipt email sending
  const handleSendReceiptEmail = async () => {
    try {
      
      if (!lastTransactionId) {
        toast.error("No transaction ID available");
        setShowEmailReceiptModal(false);
        return;
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/send-receipt-email`,
        {
          transaction_id: lastTransactionId,
          user_email: user.email
        },
        { withCredentials: true }
      );
      
      toast.success("Receipt email sent successfully!");
      setShowEmailReceiptModal(false);
    } catch (error) {
      toast.error(`Failed to send receipt: ${error.response?.data?.detail || error.message}`);
      setShowEmailReceiptModal(false);
    }
  };

  // Load specific report if report ID is in URL
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadReportById(reportId);
    }
  }, [searchParams]);

  const loadReportById = async (reportId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/ccg/results/${reportId}`,
        { withCredentials: true }
      );
      
      if (response.data) {
        setResults(response.data);
        setTimeout(() => {
          const resultsSection = document.getElementById('ccg-results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFormValid = () => {
    // All main fields required
    return formData.book_title.trim() && 
           formData.author_name.trim() && 
           formData.theme.trim() && 
           formData.category.trim() && 
           formData.story_outline.trim();
  };

  const handleGenerateClick = () => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check if subscriber (free) or needs payment
    const isSubscriber = user?.subscription_status === 'monthly' || user?.subscription_status === 'annual';
    
    if (isSubscriber) {
      handleGenerate();
    } else {
      setShowChargeConfirmation(true);
    }
  };

  const handleGenerate = async () => {
    setShowChargeConfirmation(false);
    setIsProcessing(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/ccg/generate`,
        formData,
        { withCredentials: true }
      );

      if (response.data) {
        setResults(response.data);
        
        if (response.data.charged && response.data.transaction_id) {
          setLastTransactionId(response.data.transaction_id);
          setLastCharge(response.data.charge_amount);
          
          // Show email receipt modal for charged transactions
          setTimeout(() => {
            setShowEmailReceiptModal(true);
          }, 1500);
        }

        toast.success('Character cast generated successfully!');
        
        setTimeout(() => {
          const resultsSection = document.getElementById('ccg-results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate character cast');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNewReport = () => {
    setResults(null);
    setFormData({
      book_title: '',
      subtitle: '',
      author_name: '',
      theme: '',
      category: '',
      story_outline: '',
      num_characters: '5-8'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Export CCG results to PDF
  const exportToPDF = () => {
    if (!results) {
      toast.error("No results to export");
      return;
    }
    
    try {      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 170;
      const leftMargin = 20;
      let yPosition = 20;
      
      // Helper function for page breaks
      const checkNewPage = (spaceNeeded) => {
        if (yPosition + spaceNeeded > 280) {
          pdf.addPage();
          yPosition = 20;
        }
      };
      
      // Helper function to add multi-line text
      const addMultiLineText = (text, fontSize = 10, maxWidth = pageWidth) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        lines.forEach(line => {
          checkNewPage(7);
          pdf.text(line, leftMargin, yPosition);
          yPosition += 6;
        });
      };
      
      // HEADER
      const timestamp = new Date().toLocaleString('en-US', {
        month: '2-digit', day: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true
      });
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(timestamp, leftMargin, yPosition);
      pdf.text('Digital Ink AI - CCG Report', 190, yPosition, { align: 'right' });
      yPosition += 10;
      
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175);
      pdf.text('DIGITAL INK AI', 105, yPosition, { align: 'center' });
      yPosition += 10;
      
      pdf.setFontSize(11);
      pdf.setFont(undefined, 'italic');
      pdf.setTextColor(100, 100, 100);
      pdf.text('Character & Cast Generator', 105, yPosition, { align: 'center' });
      yPosition += 15;
      
      // BOOK INFO
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text(results.book_title || 'Untitled', 105, yPosition, { align: 'center' });
      yPosition += 8;
      
      if (results.subtitle) {
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'italic');
        pdf.setTextColor(100, 100, 100);
        addMultiLineText(results.subtitle, 11);
        yPosition += 3;
      }
      
      if (results.author_name) {
        pdf.setFontSize(11);
        pdf.setFont(undefined, 'normal');
        pdf.text(`by ${results.author_name}`, 105, yPosition, { align: 'center' });
        yPosition += 10;
      }
      
      // GENERATION DETAILS
      pdf.setFontSize(12);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Generation Details', leftMargin, yPosition);
      yPosition += 7;
      
      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      if (results.category) {
        pdf.text(`Genre: ${results.category}`, leftMargin, yPosition);
        yPosition += 6;
      }
      if (results.theme) {
        pdf.text(`Theme: ${results.theme}`, leftMargin, yPosition);
        yPosition += 6;
      }
      pdf.text(`Characters: ${results.characters?.length || 0}`, leftMargin, yPosition);
      yPosition += 10;
      
      // CHARACTERS
      if (results.characters && results.characters.length > 0) {
        results.characters.forEach((char, index) => {
          checkNewPage(40);
          
          // Character Name
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.setTextColor(30, 64, 175);
          pdf.text(`${index + 1}. ${char.name}`, leftMargin, yPosition);
          yPosition += 8;
          
          pdf.setTextColor(0, 0, 0);
          
          // Archetype
          if (char.archetype) {
            checkNewPage(10);
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.text('Archetype:', leftMargin, yPosition);
            yPosition += 6;
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            addMultiLineText(char.archetype);
            yPosition += 3;
          }
          
          // Personality Traits
          if (char.personality_traits && char.personality_traits.length > 0) {
            checkNewPage(15);
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.text('Personality Traits:', leftMargin, yPosition);
            yPosition += 6;
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            char.personality_traits.forEach(trait => {
              checkNewPage(6);
              pdf.text(`• ${trait}`, leftMargin + 5, yPosition);
              yPosition += 5;
            });
            yPosition += 3;
          }
          
          // Physical Description
          if (char.physical_description) {
            checkNewPage(10);
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.text('Physical Description:', leftMargin, yPosition);
            yPosition += 6;
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            addMultiLineText(char.physical_description);
            yPosition += 3;
          }
          
          // Core Motivation
          if (char.core_motivation) {
            checkNewPage(10);
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.text('Core Motivation:', leftMargin, yPosition);
            yPosition += 6;
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            addMultiLineText(char.core_motivation);
            yPosition += 3;
          }
          
          // Relationships
          if (char.relationships) {
            checkNewPage(10);
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.text('Relationships:', leftMargin, yPosition);
            yPosition += 6;
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            addMultiLineText(char.relationships);
            yPosition += 3;
          }
          
          // Role in Plot
          if (char.role_in_plot) {
            checkNewPage(10);
            pdf.setFontSize(11);
            pdf.setFont(undefined, 'bold');
            pdf.text('Role in Plot:', leftMargin, yPosition);
            yPosition += 6;
            pdf.setFont(undefined, 'normal');
            pdf.setFontSize(10);
            addMultiLineText(char.role_in_plot);
            yPosition += 3;
          }
          
          yPosition += 8; // Space between characters
        });
      }
      
      // Footer on all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(150, 150, 150);
        pdf.text(
          `Page ${i} of ${totalPages}`,
          105,
          290,
          { align: 'center' }
        );
        pdf.text(
          'Generated by Digital Ink AI',
          105,
          285,
          { align: 'center' }
        );
      }
      
      // Save the PDF
      const filename = `CCG_${results.book_title?.replace(/[^a-z0-9]/gi, '_') || 'Character_Cast'}_${new Date().toISOString().slice(0,10)}.pdf`;
      pdf.save(filename);
      toast.success("PDF exported successfully!");
      
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const printResults = () => {
    if (!results) {
      toast.error("No results to print");
      return;
    }

    // Create a clean print window
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }

    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit', 
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    // Generate HTML for print
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>CCG Report</title>
          <style>
            @page { margin: 0.5in; }
            body { 
              font-family: 'Times New Roman', Times, serif; 
              line-height: 1.6; 
              color: #333;
              margin: 0;
              padding: 20px;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #333; 
              padding-bottom: 20px; 
              margin-bottom: 30px; 
            }
            .header img { height: 60px; margin-bottom: 10px; }
            .header h1 { margin: 10px 0; font-size: 24pt; }
            .header .meta { font-size: 10pt; color: #666; margin-top: 10px; }
            .character { 
              page-break-inside: avoid; 
              margin-bottom: 30px; 
              border-left: 4px solid #4338ca; 
              padding-left: 15px; 
            }
            .character h2 { color: #4338ca; margin-bottom: 15px; font-size: 18pt; }
            .section { margin-bottom: 15px; }
            .section h4 { color: #666; font-size: 11pt; margin-bottom: 5px; }
            .section p { margin: 0; }
            .traits { 
              display: flex; 
              flex-wrap: wrap; 
              gap: 8px; 
              margin-top: 5px; 
            }
            .trait { 
              background: #e0e7ff; 
              padding: 4px 10px; 
              border-radius: 4px; 
              font-size: 9pt; 
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" alt="Digital Ink AI" />
            <h1>Character & Cast Generator</h1>
            <div class="meta">
              <strong>${results.book_title}</strong> by ${results.author_name}<br>
              ${results.category} | ${results.theme}<br>
              Generated: ${timestamp}
            </div>
          </div>
          
          ${results.characters.map((char, index) => `
            <div class="character">
              <h2>${index + 1}. ${char.name}</h2>
              
              ${char.archetype ? `
                <div class="section">
                  <h4>Archetype</h4>
                  <p>${char.archetype}</p>
                </div>
              ` : ''}
              
              ${char.personality_traits && char.personality_traits.length > 0 ? `
                <div class="section">
                  <h4>Personality Traits</h4>
                  <div class="traits">
                    ${char.personality_traits.map(trait => `<span class="trait">${trait}</span>`).join('')}
                  </div>
                </div>
              ` : ''}
              
              ${char.physical_description ? `
                <div class="section">
                  <h4>Physical Description</h4>
                  <p>${char.physical_description}</p>
                </div>
              ` : ''}
              
              ${char.core_motivation ? `
                <div class="section">
                  <h4>Core Motivation</h4>
                  <p>${char.core_motivation}</p>
                </div>
              ` : ''}
              
              ${char.relationships ? `
                <div class="section">
                  <h4>Relationships</h4>
                  <p>${char.relationships}</p>
                </div>
              ` : ''}
              
              ${char.role_in_plot ? `
                <div class="section">
                  <h4>Role in Plot</h4>
                  <p>${char.role_in_plot}</p>
                </div>
              ` : ''}
            </div>
          `).join('')}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load, then print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Close window after printing
      printWindow.onafterprint = () => printWindow.close();
    }, 500);
  };

  const isSubscriber = user?.subscription_status === 'monthly' || user?.subscription_status === 'annual';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {!results ? (
        /* Input Form - BCTG Style Layout */
        <>
          {/* Header with Logo and Navigation */}
          <div className="bg-white border-b border-slate-200 py-4 print:hidden">
            <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
              {/* Logo */}
              <div>
                <img 
                  src="/Digital_Ink_AI_Logo.png" 
                  alt="Digital Ink AI" 
                  className="h-12 w-auto"
                />
              </div>
              
              {/* Return Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate('/homepage')}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  🏠 Home
                </Button>
                <Button
                  onClick={() => navigate('/user_dashboard')}
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                >
                  📊 Dashboard
                </Button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-7xl mx-auto px-4 py-12">
            {/* Centered Title and Description */}
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-4">
                Character & Cast Generator
              </h1>
              <p className="text-lg text-slate-600 max-w-4xl mx-auto leading-relaxed">
                Transform your story outline into a compelling cast of characters. Share your narrative arc, themes, and setting, and our AI collaborator creates rich, memorable characters with distinct personalities, motivations, and relationships. Each character is designed to support your plot and enrich your narrative with authentic human archetypes that fit your story world—empowering you to build a cast that brings your story to life.
              </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Form */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl text-slate-900">
                      Tell us about your story
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Genre and Theme Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Genre
                        </label>
                        <select
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select a genre...</option>
                          <option value="Fantasy">Fantasy</option>
                          <option value="Science Fiction">Science Fiction</option>
                          <option value="Mystery">Mystery</option>
                          <option value="Thriller">Thriller</option>
                          <option value="Romance">Romance</option>
                          <option value="Horror">Horror</option>
                          <option value="Historical Fiction">Historical Fiction</option>
                          <option value="Contemporary Fiction">Contemporary Fiction</option>
                          <option value="Literary Fiction">Literary Fiction</option>
                          <option value="Young Adult">Young Adult</option>
                          <option value="Middle Grade">Middle Grade</option>
                          <option value="Adventure">Adventure</option>
                          <option value="Drama">Drama</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Theme
                        </label>
                        <select
                          name="theme"
                          value={formData.theme}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                        >
                          <option value="">Select a theme...</option>
                          {THEME_OPTIONS.map(theme => (
                            <option key={theme} value={theme}>{theme}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Book Title and Author Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Book Title
                        </label>
                        <input
                          type="text"
                          name="book_title"
                          value={formData.book_title}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter your book title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Author Name
                        </label>
                        <input
                          type="text"
                          name="author_name"
                          value={formData.author_name}
                          onChange={handleInputChange}
                          className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Your name"
                        />
                      </div>
                    </div>

                    {/* Subtitle */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Subtitle (Optional)
                      </label>
                      <input
                        type="text"
                        name="subtitle"
                        value={formData.subtitle}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter subtitle if you have one"
                      />
                    </div>

                    {/* Number of Characters */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Number of Characters
                      </label>
                      <select
                        name="num_characters"
                        value={formData.num_characters}
                        onChange={handleInputChange}
                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="3-5">3-5 characters</option>
                        <option value="5-8">5-8 characters (recommended)</option>
                        <option value="8-10">8-10 characters</option>
                        <option value="10-12">10-12 characters</option>
                      </select>
                    </div>

                    {/* Story Outline */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Story Outline
                      </label>
                      <textarea
                        name="story_outline"
                        value={formData.story_outline}
                        onChange={handleInputChange}
                        rows={10}
                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Describe your story outline, plot points, narrative arc, setting, and conflicts. The more detail you provide, the better we can create characters that fit your narrative..."
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Include key plot points, conflicts, and the overall narrative arc
                      </p>
                    </div>

                    {/* Generate Button */}
                    <Button
                      onClick={handleGenerateClick}
                      disabled={!isFormValid() || isProcessing}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 text-lg font-semibold"
                    >
                      {isProcessing ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2 inline-block" />
                          Generating Character Cast...
                        </>
                      ) : (
                        <>🎭 Generate Character Cast</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Generator Features */}
              <div className="lg:col-span-1">
                <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 sticky top-4">
                  <CardHeader>
                    <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                      <span className="text-xl">✨</span>
                      Generator Features
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Multiple unique characters generated per session (3-12 characters)
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Rich personality profiles with distinct traits
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Detailed physical descriptions for visualization
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Core motivations and character arcs
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Character relationships and dynamics
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Archetype-based development (Hero, Mentor, Shadow, etc.)
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Diverse, authentic character names (no AI clichés)
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="text-green-600 text-xl mt-0.5">✓</div>
                      <p className="text-sm text-slate-700">
                        Plot role integration and narrative support
                      </p>
                    </div>

                    {/* Pricing Badge */}
                    <div className="pt-4 mt-4 border-t border-blue-200">
                      <div className="bg-white rounded-lg p-4 text-center">
                        <div className="text-sm text-slate-600 mb-1">Service Pricing</div>
                        {isSubscriber ? (
                          <div className="text-lg font-bold text-green-600">FREE for Subscribers</div>
                        ) : (
                          <div className="text-2xl font-bold text-blue-600">$5.00</div>
                        )}
                        <div className="text-xs text-slate-500 mt-1">
                          {isSubscriber ? 'Included in your plan' : 'One-time charge per cast'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </>
      ) : (
          /* Results Display - ABG Standard Layout */
          <div id="ccg-results" className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
            {/* Header - Visible on Screen, Hidden in Print */}
            <header className="bg-white shadow-sm border-b print:hidden">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-8">
              {/* Print Header - Hidden on Screen */}
              <div className="hidden print:block mb-6 pb-4 border-b-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-12 w-auto"
                    />
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">Digital Ink AI</h1>
                      <p className="text-sm text-gray-600">Character & Cast Generator</p>
                      <p className="text-xs text-gray-600 mt-1">Book: {results.book_title} | Author: {results.author_name}</p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <p>{new Date().toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* Report Metadata - ABG Style */}
              <div className="border-b pb-6 mb-6 print:border-b-2">
                <div className="flex justify-between items-start mb-4 print:mb-2">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">Character Cast Report</h2>
                    <p className="text-slate-600 mt-1">Generated for {results.book_title}</p>
                    <p className="text-slate-600 text-sm">by {results.author_name}</p>
                  </div>
                  <div className="flex gap-3 print:hidden">
                    <button onClick={printResults} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">🖨️ Print</button>
                    <button onClick={exportToPDF} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">📄 Export PDF</button>
                    <button 
                      onClick={() => {
                        setResults(null);
                        setCcgFormData({ bookTitle: '', subtitle: '', authorName: '', theme: 'professional' });
                        navigate('/ccg');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      ✨ New Character Cast
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Book Title:</span>
                    <p className="font-semibold text-slate-900">{results.book_title || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Author:</span>
                    <p className="font-semibold text-slate-900">{results.author_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Service:</span>
                    <p className="font-semibold text-slate-900">CCG</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Characters:</span>
                    <p className="font-semibold text-slate-900">{results.characters?.length || 0}</p>
                  </div>
                </div>
              </div>

              {/* Generation Details */}
              <div className="py-8 flex justify-center">
                  <div className="inline-block bg-blue-50 rounded-lg px-8 py-6 space-y-4">
                    <div className="flex items-center gap-3 justify-center">
                      <span className="text-9xl">📋</span>
                      <h4 className="text-xl font-semibold text-slate-900">Generation Details</h4>
                    </div>
                    
                    <div className="space-y-3 text-left">
                      <div className="flex justify-between gap-12">
                        <span className="text-slate-600">Genre:</span>
                        <span className="font-semibold text-slate-900">{results.category}</span>
                      </div>
                      <div className="flex justify-between gap-12">
                        <span className="text-slate-600">Theme:</span>
                        <span className="font-semibold text-slate-900">{results.theme}</span>
                      </div>
                      <div className="flex justify-between gap-12">
                        <span className="text-slate-600">Characters:</span>
                        <span className="font-semibold text-slate-900">{results.characters?.length || results.num_characters_generated || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
            </main>

            {/* Character Details Section - Below the fold */}
            <div className="border-t border-slate-200 bg-slate-50 py-12">
              <div className="max-w-6xl mx-auto px-4">
                <h3 className="text-2xl font-bold text-center text-slate-900 mb-8">
                  🎭 Character Cast Details
                </h3>

                {/* Print Styles */}
                <style>{`
                  @media print {
                    body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; }
                    #ccg-results { font-family: 'Times New Roman', Times, serif; }
                    h1, h2, h3, h4, h5 { font-family: 'Times New Roman', Times, serif; }
                    .character-card { page-break-inside: avoid; margin-bottom: 12px; border-left: 3px solid #3b82f6; padding-left: 10px; }
                    .character-card h3 { font-size: 14pt; font-weight: bold; margin: 0 0 6px 0; font-family: 'Times New Roman', Times, serif; color: #1e40af; }
                    .character-card h4 { font-size: 12pt; font-weight: bold; margin: 6px 0 3px 0; font-family: 'Times New Roman', Times, serif; }
                    .character-card p, .character-card li { font-size: 11pt; line-height: 1.4; font-family: 'Times New Roman', Times, serif; margin: 2px 0; }
                    .character-card ul { margin: 4px 0; padding-left: 20px; }
                  }
                `}</style>

                <div className="space-y-6">
                  {results.characters && results.characters.map((char, idx) => {
                    // Alternate colors for visual variety
                    const colors = [
                      'border-blue-500 bg-blue-50',
                      'border-purple-500 bg-purple-50',
                      'border-green-500 bg-green-50',
                      'border-amber-500 bg-amber-50',
                      'border-rose-500 bg-rose-50',
                      'border-cyan-500 bg-cyan-50'
                    ];
                    const colorClass = colors[idx % colors.length];
                    
                    return (
                      <div key={idx} className={`border-l-4 ${colorClass} pl-6 py-4 rounded-r-lg character-card print:bg-white print:border-blue-500`}>
                        <h3 className="text-2xl font-bold text-slate-900 mb-4 print:text-blue-900">
                          {idx + 1}. {char.name}
                        </h3>
                        
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-semibold text-blue-900 mb-1">Archetype</h4>
                            <p className="text-slate-700">{char.archetype}</p>
                          </div>

                          <div>
                            <h4 className="font-semibold text-purple-900 mb-1">Personality Traits</h4>
                            <ul className="list-disc list-inside text-slate-700 space-y-1">
                              {char.personality_traits && char.personality_traits.map((trait, i) => (
                                <li key={i}>{trait}</li>
                              ))}
                            </ul>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-green-900">Physical Description</h4>
                              <div className="print:hidden">
                                <ReadAloudButton text={char.physical_description} />
                              </div>
                            </div>
                            <p className="text-slate-700">{char.physical_description}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-amber-900">Core Motivation</h4>
                              <div className="print:hidden">
                                <ReadAloudButton text={char.core_motivation} />
                              </div>
                            </div>
                            <p className="text-slate-700">{char.core_motivation}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-rose-900">Relationships</h4>
                              <div className="print:hidden">
                                <ReadAloudButton text={char.relationships} />
                              </div>
                            </div>
                            <p className="text-slate-700">{char.relationships}</p>
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold text-cyan-900">Role in Plot</h4>
                              <div className="print:hidden">
                                <ReadAloudButton text={char.role_in_plot} />
                              </div>
                            </div>
                            <p className="text-slate-700">{char.role_in_plot}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Service Charge Confirmation Modal */}
      {showChargeConfirmation && (
        <ServiceChargeModal
          isOpen={showChargeConfirmation}
          onClose={() => setShowChargeConfirmation(false)}
          onConfirm={handleGenerate}
          serviceInfo={{
            name: 'CCG Character & Cast Generator',
            amount: 5.00,
            description: 'Generate a rich cast of characters from your story outline'
          }}
          user={user}
        />
      )}

      {/* Processing Modal */}
      {isProcessing && (
        <ProcessingModal
          isOpen={isProcessing}
          serviceName="Character & Cast Generator (CCG)"
          bookTitle={formData.bookTitle}
        />
      )}




      {/* Email Receipt Modal */}
      {showEmailReceiptModal && lastTransactionId && (
        <EmailReceiptModal
          isOpen={showEmailReceiptModal}
          onClose={() => setShowEmailReceiptModal(false)}
          onSendReceipt={handleSendReceiptEmail}
          transactionId={lastTransactionId}
          amount={lastCharge}
          serviceName="Character & Cast Generator (CCG)"
        />
      )}
    </div>
  );
};

// Main App Component
function App() {
  return (
    <TimezoneProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </TimezoneProvider>
  );
}

export default App;

